/* eslint-disable @typescript-eslint/ban-ts-comment */
import {
  GetProStudioNodesQuery,
  Tenancy_Node_Type_Enum_Ps_Enum,
  Tenancy_Rich_Asset_Type_Enum_Ps_Enum,
  proStudioUrqlGql as urqlGql,
} from '@pypestream/api-services';
import type { ProStudioNodeDataStore } from '@pypestream/pro-studio-collab-client';
import type { CollabWriter } from '../collab/collab-writer';
import {
  dismissToasts,
  sendToast,
  Slide,
  toast,
  updateToast,
} from '@pypestream/design-system';
import {
  ERROR_HANDLER_NODE,
  nodeKey,
  type HealthFix,
} from './utils/solution-health';
import Fuse from 'fuse.js';
import { produce } from 'immer';
import {
  action,
  comparer,
  computed,
  transaction as emitOnce,
  makeAutoObservable,
  observable,
  reaction,
  when,
  runInAction,
  set,
  toJS,
} from 'mobx';
import { computedFn } from 'mobx-utils';
import {
  UndoRedoManager,
  type HistoryDirection,
  type Command as UndoCommand,
} from './undo-redo-manager';

import { SortingState, Table } from '@tanstack/react-table';
import hotkeys from 'hotkeys-js';
import { JSONSchema7, validate as validateJsonSchema } from 'json-schema';
import { isEqual } from 'lodash';
import debounce from 'lodash.debounce';
import { objectEntries } from 'ts-extras';
import { v4 as uuidv4 } from 'uuid';

import { RichAssets } from '.';
import {
  getOrderedJson,
  planAssetTypeChange,
  type AssetTypeChangePlan,
} from '../components/table/utils';
import { reportStoreFailure } from './utils/report-store-failure';
// The i18next singleton the app configures: the store has no hook to call, and
// the node-write toasts are user-facing text.
import { i18n } from '@pypestream/translations';
import { WORKFLOWS_NS } from '../utils/i18n-namespaces';
import { formatLocaleList } from '../utils/format-list';
import { shouldRestoreGridFocusAfterPanelToggle } from '../components/table/expanded-cell-view/restore-grid-focus-after-toggle';
import { composedContains } from '../components/table/expanded-cell-view/composed-contains';
import {
  blobToFile,
  BotCompilerError,
  checkForErrors,
  createCsvBlob,
  json2csvParser,
  readLegacyBotSolutionId,
  solutionScopeOfVersionPsId,
  uploadCsv,
  validateCsv,
  validateVersion,
  versionPsIdBelongsToBot,
} from '../utils';
import {
  readAgentCompilerMode,
  shouldRunBotCompiler,
} from '../utils/agent-compiler-mode';
import {
  planImportedGroups,
  type ImportedGroupMeta,
  type ImportGroupingOutcome,
  type ImportGroupPlan,
} from '../utils/csv-grouping/csv-group-meta';
import { buildGroupedCsvRecords } from '../utils/csv-grouping/grouped-csv-record-builder';
import { DebouncedQueue } from '../utils/debounced-queue';
import { json2csvParserWithGrouping } from '../utils/json-to-csv-parser';
import { proStudioService } from '../xstate/app.xstate';
import { sendUserMessage } from '../xstate/app.xstate-utils';
import { ProStudioContext } from '../xstate/prostudio.xstate.types';
import {
  loadAutoRenumberOnPaste,
  persistAutoRenumberOnPaste,
} from './auto-renumber-storage';
import {
  loadShowUngroupedNodes,
  persistShowUngroupedNodes,
} from './ungrouped-nodes-preferences';
import {
  loadExpandedCellViewOpen,
  persistExpandedCellViewOpen,
} from './expanded-cell-view-storage';
import {
  loadExpandedCellViewWidth,
  persistExpandedCellViewWidth,
  clampExpandedCellViewWidth,
} from './expanded-cell-view-width-storage';
import { nativeCellRawEquals, parseNativeCellRaw } from './native-cell-value';
// Straight from the defining module, not the `./utils` barrel: this file sits
// in that barrel's require cycle (table-store → utils → … → table-store).
import { normalizeBehaviorTokens } from './utils/behaviors-vocabulary';
import { NODE_SEARCH_FUSE_MATCH_OPTIONS } from './node-search';
import {
  appendNumberingAnchorOf,
  numberingNumbersOf,
} from './node-numbering-inputs';
import {
  collectReferencedNodeNumbers,
  computePasteNumberPlan,
  rewriteNodeNumberReferences,
  toPreservedNodeNumber,
  toValidNodeNumber,
} from './paste-node-numbering';
import { assignNodeNumberStrings } from './smart-node-numbering';
import {
  canonicalNodeNumber,
  compareNodeNumbers,
  isCanonicalNodeNumber,
  isNumberingNodeNumber,
  nodeNumberToGoTo,
  toNodeNumberCell,
} from './utils/node-number';
import { prepareResourceLibRows } from './resource-lib-insert-rows';
import {
  buildNodeClipboardHtml,
  buildNodeRowsFromCellMaps,
  buildRowsTsvGrid,
  gridToTsv,
  parseNodeEnvelopeFromHtml,
  parseNodeEnvelopeFromText,
  parseTsvToCellMaps,
  serializeNodeEnvelope,
  TSV_EXPORT_COLUMNS,
  type NodeColumnKey,
} from './table-clipboard';
import { getArrayJoinDelimiter } from './table-copy-utils';
import { tableRowSchema } from './table-store-schema';
import {
  assertGroupInvariants,
  buildScriptFileNames,
  buildWorkflowErrors,
  findNodeNumberProblem,
  deriveNodeNumberErrors,
  cellErrorKey,
  createTaskNode,
  deleteSolutionTask,
  excludeGroupNodes,
  getColumnEditKind,
  getTasksFromSolutionData,
  hasRowWithEmptyLiveCorpusId,
  isGroupRow,
  isTaskRow,
  buildJsonFieldErrors,
  PifPartialValidationService,
  prepareRowsDataForPifCompiler,
  processBatch,
  readCachedActionNodeNames,
  resolveActionNodeNames,
  updateSolutionForTaskRow,
  updateTaskNode,
  type Diagnostic,
} from './utils';
import { hasActiveCellEdit } from './utils/active-cell-edit';
import { isClaimedByDelete } from './utils/claimed-by-delete';
import {
  findAutoGroupRows,
  findStaleAutoGroupRows,
  hasAgentGroups,
  hasAnyGrouping,
  planAutoAgentGroups,
} from './utils/auto-agent-grouping';
import {
  planUseCaseAgentGroups,
  nextUseCaseAgentColor,
} from './utils/use-case-agent-numbering';
import { DEFAULT_STAGE, type WorkflowStage } from './utils/workflow-stage';
import { resolveBobcatAccess } from '../utils/bobcat-flags';
import { BOBCAT_UNRELEASED } from '../utils/bobcat-unreleased';
import { autoPublishSolutionVersion } from '../utils/auto-publish-on-create';
import {
  agentTemplateById,
  matchAgentTemplate,
} from '../utils/agent-templates';
import type { AgentSpec } from '../utils/agent-spec';
import { requestAgentDraft } from '../utils/draft-agent-api';
import { uploadPlaceholderScripts } from '../utils/placeholder-scripts';
import { specRowCount, specToRows } from './utils/agent-spec-rows';
import type { BotType } from '../xstate/prostudio.api-service.types';
import {
  defaultColumnWrapSettings,
  resolveColumnWrapSettings,
  toggleColumnWrap,
  type ColumnWrapSettings,
} from './utils/column-wrap-settings';
import {
  coerceRowArrayColumns,
  collectRowArrayColumnFixes,
  type ConvertCellText,
} from './utils/coerce-row-array-columns';
import { computeCellFillPlan } from './utils/compute-cell-fill';
import { isPartOfContinuousMultiSelection } from './utils/continuous-multi-selection';
import {
  readGroupColorPreview,
  writeGroupColorPreview,
} from './utils/group-color-preview';
import { arrangeGroupsContiguously } from './utils/group-row-ordering';
import { isWrappableTextColumn } from './utils/column-edit-kind';
import { isJsonCapableColumn } from './utils/is-json-capable-column';
import { broadcastNodesChanged } from './utils/node-sync-channel';
import {
  resolveCellPasteText,
  type SystemClipboardRead,
} from './utils/resolve-cell-paste-text';
import { resolveCellRangePasteSource } from './utils/resolve-cell-range-paste-source';
import { resolveRowPasteTargets } from './utils/resolve-row-paste-targets';
import { transformPifDiagnostics } from './utils/transform-pif-diagnostics';
import {
  findUncompilableNodeNumberRow,
  findUncompilableParameterInputRow,
  hasDegradedDiagnostic,
  type ValidationFailureReason,
} from './utils/validation-outcome';
import { resolvePanelCellValue } from './utils/resolve-panel-cell-value';
import {
  buildDisplayNumberMap,
  buildVisibleRowIndexMap,
} from './utils/row-index-maps';
import { applyRowDelta, computeRowDelta } from './utils/undo-row-delta';
import {
  canRestoreKnowledgeSearch,
  hasUnprovisionedKnowledgeSearch,
  restoreKnowledgeSearchRows,
  skipKnowledgeSearchRows,
  type KnowledgeRow,
} from './utils/knowledge-search-toggle';
import { markKnowledgeUploadRows } from './utils/knowledge-corpus-setup';

export { emitOnce };

// PSTUDIO-2998: the old hardcoded `standardFunctions` list is now the dynamic
// `tableStore.actionNodeNames` — see stores/utils/action-node-names.ts.

// Tracks whether the next flush of saveRowsToDatabaseQueue requires
// validateAndSync to run after the network write. Defaults to false (no
// validation needed); any call to saveRowsToDatabase that omits the
// skipValidation flag flips it to true. Reset on every flush so each batch
// is decided independently.
//
// Motivation (PSTUDIO-3063 #1): updateRow's `skipValidationRules` flag only
// gated the synchronous validators (applyValidationRules and the manual-mode
// dirty banner). saveBulkRowsToDatabase runs validateAndSync after the save
// regardless, so collapsing/expanding a group still triggered validation.
let saveBatchNeedsValidation = false;

const processDatabaseTransactions = (tableRows: Partial<TableRow>[]) => {
  const skipValidation = !saveBatchNeedsValidation;
  saveBatchNeedsValidation = false;
  tableStore.saveBulkRowsToDatabase(tableRows, skipValidation);
};

const saveRowsToDatabaseQueue = new DebouncedQueue(
  processDatabaseTransactions,
  0 // Changed from 2000 to 0 (immediate) since we now save on blur
);

// Order-only save path. Reorder operations (drag-drop, normalizeRowOrder,
// normalizeInitialRows) push compact `{id, order, solutionVersionId}` tuples
// here instead of full rows. The handler routes them through
// updateProStudioNodeOrders, whose `on_conflict.update_columns: [order]`
// makes the payload roughly 50× smaller than the full-row upsert — the
// difference between a 5MB request that Hasura rejects and one small enough
// to pass (see SAVE_REQUEST_BYTE_LIMIT for the size that is accepted).
//
// solutionVersionId is required even though GraphQL's insert_input marks it
// as optional: Hasura compiles upserts as `INSERT ... ON CONFLICT DO UPDATE`,
// and PostgreSQL evaluates NOT NULL constraints on the INSERT row BEFORE
// resolving the conflict. solutionVersionId is NOT NULL on the table, so
// omitting it produces a constraint-violation even when the row already
// exists and we only want to update `order`.
//
// PSTUDIO-4168: every producer stamps `this.solutionVersionId` at ENQUEUE time,
// so a tuple whose id no longer matches the store at FLUSH time is by
// definition one the store moved out from under. See
// saveBulkRowOrdersToDatabase for why that tuple must not be written.
type RowOrderUpdate = {
  id: string;
  order: number;
  solutionVersionId: string;
};
const processOrderTransactions = (orders: RowOrderUpdate[]) => {
  tableStore.saveBulkRowOrdersToDatabase(orders);
};
const saveRowOrdersToDatabaseQueue = new DebouncedQueue<RowOrderUpdate>(
  processOrderTransactions,
  0
);

/** Colors for accepted "Suggest agents" groups, in turn — the table's group presets, less the two the core agents use. */
const SUGGESTED_AGENT_COLORS = [
  '#004DDB',
  '#B841B6',
  '#FFA800',
  '#A13337',
  '#62697C',
];
const UNTITLED_SUGGESTED_AGENT = 'Untitled agent';

/**
 * PSTUDIO-3968 — how long `armKeyboardPasteHandover` waits for the browser's own
 * `paste` event before falling back to the Async Clipboard / in-memory path.
 *
 * Exported so tests can assert the ORDER of the race rather than a magic number.
 * A real Chrome serves `paste` from the OS clipboard over an inter-process hop, so
 * a `setTimeout(…, 0)` fallback fires first and steals every keypress; anything in
 * the low hundreds of ms clears that comfortably while remaining imperceptible in
 * the rare case where no event arrives at all.
 */
export const KEYBOARD_PASTE_FALLBACK_MS = 250;

/**
 * PSTUDIO-4342 — ceiling on the serialized `objects` of ONE node-save request,
 * shared by both save tracks (full rows and order tuples).
 *
 * Measured 2026-10-16 on the candidate backend only — not repeated on release,
 * UAT, main or production: a request body of 100,842 bytes is accepted and one
 * of 100,890 bytes is answered `Invalid response from authorization hook`
 * (HTTP 200), whatever the operation (a `query Probe { __typename }` plus
 * padding, three runs per side). Real group saves through UpdateProStudioNodes
 * agreed with it on the same day: a body of 99,992 bytes was accepted and one
 * of 101,146 bytes was refused the same way. PSTUDIO-2908 (comment 306707)
 * records the backend side as the Hasura authorization webhook's default
 * 100 KB request limit, which can be raised to 1 MB; that statement was not
 * re-checked against the backend here. An earlier, independent measurement on
 * candidate (2026-09-30, commit 2f88cc39bb on the project-bobcat branches, not
 * merged anywhere else) found the same edge for full-row saves: ~89 KB saved,
 * ~100 KB refused; it set this track to 64_000 and left the order track at
 * 512_000. The operation text of UpdateProStudioNodes is ~1.3 KB in the body,
 * so 80 KB of objects leaves ~19 KB under the accepted size for what the
 * estimate does not count. This is a margin under a measured edge, not a
 * derivation from a documented limit.
 *
 * The same ceiling chunks node deletes (`nodeIds`), which grow by ~39 bytes
 * an id and cross the edge at ~2,600 ids (arithmetic, not a measured delete).
 *
 * Limitations. The ceiling applies BETWEEN rows: a single row larger than it
 * is its own chunk, and it is refused only once its request crosses the edge
 * above (~99 KB of objects), so a row of 80-99 KB should still be saved
 * (inferred from that edge; no single row of that size was sent). A row past
 * the edge cannot be saved at all (PSTUDIO-2908 reproduced one: a Message cell
 * of ~94,000 characters). Such a refused single-row chunk is reported on its
 * own (`_tooLargeRows`, the "Node too large to save" toast naming the node)
 * and does NOT stop the chunks after it. Chunks go out
 * one after another, so a failure in chunk k leaves chunks 1..k-1 committed and
 * sends none of the rest — a group header and its members can land in
 * different chunks (before this change the same batch was one request that was
 * refused whole, so the header was never saved at all). What is left is
 * recorded (_failedNodeSaves / _failedNodeOrderSaves / _failedNodeDeletes):
 * the "Autosave Error" ("Reorder Save Error" on the order track) stays up while
 * any of it is unrepaired, and the next publish re-sends it and is refused if
 * it fails again. The failed path still returns before the cross-tab
 * broadcast, so another tab is not told about the chunks that did commit.
 * Chunks of one batch are separate commits, so every node write is ordered
 * through _nodeWriteTail, within the limits stated there.
 */
const SAVE_REQUEST_BYTE_LIMIT = 80_000;

const textEncoder = new TextEncoder();

/**
 * Splits an array of objects into chunks whose serialized JSON size stays
 * under `byteLimit` AND whose row count stays under `rowLimit`. Either limit
 * can trigger a chunk boundary — whichever applies first. Used by both
 * node-save tracks (updateProStudioNodes, updateProStudioNodeOrders) to keep
 * a request under the body size the backend accepts (SAVE_REQUEST_BYTE_LIMIT);
 * the row cap is a second, independent bound on the number of rows per request.
 *
 * Byte counting uses TextEncoder so it reports actual UTF-8 bytes (what
 * goes on the wire) rather than UTF-16 code units (what String.length
 * returns). Matters for non-ASCII content like localized messages or
 * utterances — a multi-byte emoji or accented character can be up to 4×
 * larger in UTF-8 than its UTF-16 code-unit count suggests.
 *
 * A single object larger than `byteLimit` is its own chunk — it cannot be
 * split further. Whether it is accepted depends on the backend's own size
 * limit, not on `byteLimit`; a refusal reaches the caller as the request's
 * error.
 */
function chunkBySerializedBytes<T>(
  items: T[],
  byteLimit: number,
  rowLimit: number
): T[][] {
  if (!items.length) return [];
  const chunks: T[][] = [];
  let current: T[] = [];
  let currentBytes = 2; // account for the surrounding `[]`

  for (const item of items) {
    // +1 for the comma separator between items (overestimate by 1 for the
    // first item is harmless — keeps the math simple and conservative).
    const itemBytes = textEncoder.encode(JSON.stringify(item)).length + 1;
    const byteOverflow =
      current.length > 0 && currentBytes + itemBytes > byteLimit;
    const rowOverflow = current.length >= rowLimit;
    if (byteOverflow || rowOverflow) {
      chunks.push(current);
      current = [];
      currentBytes = 2;
    }
    current.push(item);
    currentBytes += itemBytes;
  }
  if (current.length) chunks.push(current);
  return chunks;
}

/**
 * PSTUDIO-4342: whether a node-write response was a refusal, as an Error, or
 * `undefined` when it landed. A RESOLVED urql mutation is not a success — the
 * gateway can answer 200 with an `errors` array inside `data`, which
 * `result.error` alone does not see — so this asks the repo's own predicate,
 * `checkForErrors`, which every node-write track shares through here.
 */
function nodeWriteRefusal(
  response: Parameters<typeof checkForErrors>[0]
): Error | undefined {
  // The transport error is returned as it is, so the toast and the report keep
  // the message they always showed; `checkForErrors` (whose thrown message
  // gains an error id) is what catches the 200-with-`errors` case.
  if (response.error) return response.error;
  try {
    checkForErrors(response);
    return undefined;
  } catch (refusal) {
    return refusal instanceof Error ? refusal : new Error(String(refusal));
  }
}

/**
 * PSTUDIO-4342: records the outcome of chunk `index` of a chunked node write in
 * its failure map. A chunk that landed clears its rows; a refused one records
 * its own rows AND every row of the chunks after it, which the refusal stops
 * from being sent at all.
 */
function recordChunkOutcome<T>(
  failures: Map<string, string>,
  outcome: {
    chunks: T[][];
    index: number;
    refusal: Error | undefined;
    solutionVersionId: string;
  },
  idOf: (item: T) => string | undefined
): void {
  const { chunks, index, refusal, solutionVersionId } = outcome;
  if (!refusal) {
    for (const item of chunks[index]) {
      const id = idOf(item);
      if (id) failures.delete(id);
    }
    return;
  }
  for (const item of chunks.slice(index).flat()) {
    const id = idOf(item);
    if (id) failures.set(id, solutionVersionId);
  }
}

/**
 * Awaits an urql mutation source: through `toPromise()` where it has one (the
 * cold source needs a subscriber, and that is what subscribes it), otherwise
 * as the PromiseLike it is — a test double may offer either.
 */
function settleMutation<R>(
  source: PromiseLike<R> | { toPromise(): Promise<R> }
): Promise<R> {
  return 'toPromise' in source && typeof source.toPromise === 'function'
    ? source.toPromise()
    : Promise.resolve(source as PromiseLike<R>);
}

/**
 * PSTUDIO-4342: a string of the node-write toasts, from
 * `pro-studio/workflows` → `node_writes.toast` in `@pypestream/translations`.
 */
function nodeWriteToastText(
  key: string,
  options?: Record<string, unknown>
): string {
  // The key is composed, so i18next's typed-key overloads cannot check it; the
  // keys are pinned by the tests instead (they assert the key the store asks
  // for), and every one exists in all five locales.
  const translate = i18n.t as unknown as (
    k: string,
    o?: Record<string, unknown>
  ) => string;
  return String(translate(`${WORKFLOWS_NS}:node_writes.toast.${key}`, options));
}

hotkeys.filter = function (event) {
  const target = event.target as HTMLElement;
  const tagName = target?.tagName?.toUpperCase();

  // Allow Cmd+G / Ctrl+G only when not editing a cell, so the shortcut can
  // trigger Go to Node. Otherwise, let the browser handle the shortcut
  // (e.g., Find Next).
  if ((event.metaKey || event.ctrlKey) && event.key?.toLowerCase() === 'g') {
    return !tableStore.isCellBeingEdited;
  }

  // Block hotkeys when inside a modal (allow normal typing)
  if (target?.closest('ps-modal')) {
    return false;
  }

  // PSTUDIO-2154: undo/redo (Cmd/Ctrl+Z, Ctrl+Y) should fire even when a cell
  // <select> dropdown is focused — selects have no meaningful native undo, and
  // after changing one (e.g. Asset Type) the user expects Cmd+Z to undo it
  // without first clicking away. Only INPUT/TEXTAREA/contentEditable keep their
  // native text-undo, so still block there.
  const key = event.key?.toLowerCase();
  if ((event.metaKey || event.ctrlKey) && (key === 'z' || key === 'y')) {
    return !(
      tagName === 'INPUT' ||
      tagName === 'TEXTAREA' ||
      target?.isContentEditable
    );
  }

  // Preserve default hotkeys-js behavior: block shortcuts in form elements
  // This prevents arrow keys, backspace, etc. from firing while typing
  if (
    tagName === 'INPUT' ||
    tagName === 'SELECT' ||
    tagName === 'TEXTAREA' ||
    target?.isContentEditable
  ) {
    return false;
  }

  return true;
};

type Nil = null | undefined;
type NotNil<T> = Nil extends T ? never : T extends Nil ? never : T;
export type HasValue<T> = { value: NotNil<T> };

// type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };

export type _Node = NonNullable<
  GetProStudioNodesQuery['tenancy_solution_version_nodes_ps']
>[0];

export type ColumnsWidths = Record<
  Exclude<
    NodeColumns,
    | 'richAssetContentMap'
    | 'parameterInputMap'
    | 'analyticsEventMap'
    | 'settings'
  >,
  number
>;

type ColumnPinningSettings = {
  left?: string[];
  right?: string[];
};

const defaultColumnPinningSettings: ColumnPinningSettings = {
  left: ['index', 'nodeNumber'],
};

/**
 * Project Bobcat — where a use-case agent came from, kept on its group row as
 * `settings.agentSource` (agent templates): built from an agent
 * template, or drafted by AI. `connect` is what is left for a person to do
 * before it works for real. An AI draft is `drafting` until it lands.
 */
export interface AgentSource {
  kind: 'template' | 'ai';
  /** The template's name, for `template`. */
  template?: string;
  status?: 'drafting' | 'done' | 'failed';
  connect?: string[];
  summary?: string;
}

/** A task Create declared, waiting to be built into a real agent. */
export interface PendingAgentTask {
  name: string;
  phrases: string[];
  /** The agent template the onboarding model chose, if any. */
  template?: string;
}

export enum NodeColumns {
  id = 'id',
  nodeNumber = 'nodeNumber',
  order = 'order',
  nodeType = 'nodeType',
  nodeName = 'nodeName',
  nluDisabled = 'nluDisabled',
  nextNodes = 'nextNodes',
  message = 'message',
  richAssetType = 'richAssetType',
  answerRequired = 'answerRequired',
  behaviors = 'behaviors',
  command = 'command',
  description = 'description',
  output = 'output',
  nodeInput = 'nodeInput',
  parameterInput = 'parameterInput',
  decisionVariable = 'decisionVariable',
  whatsNext = 'whatsNext',
  nodeTags = 'nodeTags',
  skillTag = 'skillTag',
  variables = 'variables',
  platformFlag = 'platformFlag',
  cssClassname = 'cssClassname',
  analyticsEvent = 'analyticsEvent',
  richAssetContent = 'richAssetContent',

  utterances = 'utterances',
  intent = 'intent',
  solutionVersionId = 'solutionVersionId',

  // ReadOnly field to control sync between workflow and Supervisor Config
  settings = 'settings',
}

export enum NodeColumnsMap {
  richAssetContentMap = 'richAssetContentMap',
  parameterInputMap = 'parameterInputMap',
  analyticsEventMap = 'analyticsEventMap',

  hidden = 'hidden',
}

export const columnDefaultSizes = {
  width: 130,
  minWidth: 50,
  maxWidth: 800,
};

export const defaultColumnWidths: ColumnsWidths = {
  [NodeColumns.id]: columnDefaultSizes.width,
  [NodeColumns.nodeNumber]: 88,
  [NodeColumns.order]: 84,
  [NodeColumns.nodeName]: 285,
  [NodeColumns.utterances]: 125,
  [NodeColumns.nodeType]: 90,
  [NodeColumns.nluDisabled]: 112,
  [NodeColumns.nextNodes]: 96,
  [NodeColumns.message]: 450,
  [NodeColumns.richAssetType]: 194,
  [NodeColumns.richAssetContent]: 306,
  [NodeColumns.answerRequired]: 78,
  [NodeColumns.behaviors]: 230,
  [NodeColumns.command]: 220,
  [NodeColumns.description]: 319,
  [NodeColumns.output]: 90,
  [NodeColumns.nodeInput]: 93,
  [NodeColumns.parameterInput]: 382,
  [NodeColumns.decisionVariable]: 127,
  [NodeColumns.whatsNext]: 258,
  [NodeColumns.nodeTags]: 88,
  [NodeColumns.skillTag]: 72,
  [NodeColumns.variables]: 241,
  [NodeColumns.platformFlag]: 104,
  [NodeColumns.cssClassname]: 129,
  [NodeColumns.analyticsEvent]: 373,

  [NodeColumns.intent]: columnDefaultSizes.width,
  [NodeColumns.solutionVersionId]: columnDefaultSizes.width,
};

// interface Foo = Object.keys(NodeColumns);
export type WorkflowError = {
  colId: keyof typeof NodeColumns;
  colIndex: number | null;
  rowIndex: number | null;
  errorMessage: string;
  cellNumber: number | null;
  nodeNumber: string | null;
  rowNumber?: number | null;
  // Stable node id carried through from the diagnostic so a cell can be
  // addressed even when its node number is blank (PSTUDIO-3991).
  nodeId?: string | null;
};

export const platformFieldNameToNodeColumnMap: Record<string, NodeColumns> = {
  'Decision Variable': NodeColumns.decisionVariable,
  Command: NodeColumns.command,
  'Node Input': NodeColumns.nodeInput,
  'What Next?': NodeColumns.whatsNext,
  'Node Type': NodeColumns.nodeType,
  'Node Name': NodeColumns.nodeName,
  Output: NodeColumns.output,
  'Node Number': NodeColumns.nodeNumber,
  Intent: NodeColumns.intent,
  'NLU Disabled?': NodeColumns.nluDisabled,
  'Example Phrases': NodeColumns.utterances,
  'Next Nodes': NodeColumns.nextNodes,
  Message: NodeColumns.message,
  'Rich Asset Type': NodeColumns.richAssetType,
  'Rich Asset Content': NodeColumns.richAssetContent,
  'Answer Required?': NodeColumns.answerRequired,
  Behaviors: NodeColumns.behaviors,
  Description: NodeColumns.description,
  'Parameter Input': NodeColumns.parameterInput,
  'Node Tags': NodeColumns.nodeTags,
  'Skill Tag': NodeColumns.skillTag,
  Variable: NodeColumns.variables,
  'Platform Flag': NodeColumns.platformFlag,
  'CSS Classname': NodeColumns.cssClassname,
  // 'Entity Type': NodeColumns.entityType,
  // 'Entity': NodeColumns.entity,
  // 'Flows': NodeColumns.flows,
};

export interface TableNode extends _Node {
  [NodeColumns.answerRequired]: NonNullable<boolean>;
  [NodeColumns.behaviors]?: NonNullable<_Node['behaviors']>;
  [NodeColumns.nextNodes]?: NonNullable<_Node['nextNodes']>;
  [NodeColumns.command]?: NonNullable<_Node['command']>;
  [NodeColumns.cssClassname]?: NonNullable<_Node['cssClassname']>;
  [NodeColumns.decisionVariable]?: NonNullable<_Node['decisionVariable']>;
  [NodeColumns.description]?: NonNullable<_Node['description']>;
  [NodeColumns.intent]?: NonNullable<_Node['intent']>;
  [NodeColumns.message]?: NonNullable<_Node['message']>;
  [NodeColumns.nodeType]: Tenancy_Node_Type_Enum_Ps_Enum;
  [NodeColumns.nluDisabled]?: NonNullable<_Node['nluDisabled']>;
  [NodeColumns.nodeInput]?: NonNullable<_Node['nodeInput']>;
  [NodeColumns.nodeName]?: NonNullable<_Node['nodeName']>;
  // PSTUDIO-4453: a canonical decimal-integer string (stores/utils/
  // node-number.ts), not the generated `numeric` scalar's `any`.
  [NodeColumns.nodeNumber]?: string | null;
  [NodeColumns.nodeTags]?: NonNullable<_Node['nodeTags']>;
  [NodeColumns.order]: NonNullable<_Node['order']>;
  [NodeColumns.id]: NonNullable<_Node['id']>;
  [NodeColumns.parameterInput]?: object;
  [NodeColumnsMap.parameterInputMap]?: NonNullable<_Node['parameterInputMap']>;
  [NodeColumns.output]?: NonNullable<_Node['output']>;
  [NodeColumns.platformFlag]?: NonNullable<_Node['platformFlag']>;
  [NodeColumns.utterances]?: string;
  [NodeColumns.richAssetType]: NonNullable<Tenancy_Rich_Asset_Type_Enum_Ps_Enum>;
  [NodeColumns.richAssetContent]?: object;
  [NodeColumnsMap.richAssetContentMap]?: NonNullable<
    _Node['richAssetContentMap']
  >;
  [NodeColumns.skillTag]?: NonNullable<_Node['skillTag']>;
  [NodeColumns.solutionVersionId]: NonNullable<_Node['solutionVersionId']>;
  [NodeColumns.variables]?: NonNullable<_Node['variables']>;
  [NodeColumns.whatsNext]?: NonNullable<_Node['whatsNext']>;
  [NodeColumns.analyticsEvent]?: object;
  [NodeColumnsMap.analyticsEventMap]?: NonNullable<_Node['analyticsEventMap']>;
  // @TODO: Remove this, added for new pif-compiler
  // flows?: Array<unknown>;
  // nluRouting?: null;
  // Free-form JSON bag persisted to Hasura as a `jsonb` column on
  // `tenancy.solution_version_nodes_ps`. The backend does NOT validate the
  // shape of this field — it's the frontend's responsibility to keep the
  // keys here in sync with the values the rest of the app consumes.
  //
  // Grouping fields (PSTUDIO-2220 / PSTUDIO-2349) live here on purpose:
  //   - Group rows themselves (nodeType='g') own `subRows`, `groupExpanded`,
  //     `groupName`.
  //   - Regular subRows own `groupId` pointing at their parent group row id.
  // Keeping them inside `settings` means a single `UpdateProStudioNodes`
  // upsert (`update_columns: [..., settings]`) covers all grouping writes —
  // no schema migration, no separate tables. The columnOrder array below
  // intentionally omits `settings`, which is what keeps these UI-only
  // fields from leaking into the Bot Compiler payload.
  [NodeColumns.settings]?: {
    shouldSyncWithSupervisorConfig?: boolean;
    taskId?: string;
    // set on subRows; references the parent group row id. `null` (not just
    // undefined) is a legal persisted value: leaving a group writes an
    // explicit null so the key survives JSON.stringify → GraphQL → Hasura
    // UPSERT (see syncGroupMembershipForRow). PSTUDIO-3159.
    groupId?: string | null;
    subRows?: string[]; // set on group rows; ordered ids of member subRows
    groupExpanded?: boolean; // set on group rows; controls subRow visibility
    groupName?: string; // set on group rows; user-editable label
    // set on group rows; user-picked row color (hex). `null` clears it back to
    // the neutral band (parity with groupId's explicit-null persistence). UI
    // only — excluded from columnOrder so it never reaches the Bot Compiler.
    // PSTUDIO-3393.
    groupColor?: string | null;
    // Project Bobcat — set on a Knowledge Agent's answer step when its
    // knowledge search was skipped: what the skip removed and rewrote, so
    // adding a knowledge base later can put it back exactly. UI-only, never
    // sent to the Bot Compiler. See stores/utils/knowledge-search-toggle.ts.
    skippedKnowledgeSearch?: unknown;
    // Project Bobcat (agent templates) — set on a use-case agent's
    // group row: built from an agent template or drafted by AI, and what is
    // left to connect. UI-only, never sent to the Bot Compiler.
    agentSource?: AgentSource;
  };
  hidden?: boolean;
}

// Whitelist consumed by the PIF serializer (`prepareRowsDataForPifCompiler`)
// and the legacy CSV exporter (`json2csvParser`). `NodeColumns.settings` is
// deliberately absent — it carries UI-only data (grouping, supervisor sync
// flag, taskId) that the Bot Compiler / agent-compiler must never see.
// Anything that should reach the compiler MUST be a top-level field on
// `tenancy.solution_version_nodes_ps`.
export const columnOrder = [
  NodeColumns.nodeNumber,
  NodeColumns.nodeType,
  NodeColumns.nodeName,
  NodeColumns.intent,
  NodeColumns.utterances,
  NodeColumns.nluDisabled,
  // 'nluRouting',
  NodeColumns.nextNodes,
  NodeColumns.message,
  NodeColumns.richAssetType,
  NodeColumns.richAssetContent,
  NodeColumns.behaviors,
  NodeColumns.answerRequired,
  NodeColumns.command,
  NodeColumns.description,
  NodeColumns.output,
  NodeColumns.nodeInput,
  NodeColumns.parameterInput,
  NodeColumns.decisionVariable,
  NodeColumns.whatsNext,
  NodeColumns.nodeTags,
  NodeColumns.skillTag,
  NodeColumns.variables,
  NodeColumns.platformFlag,
  // 'flows',
  NodeColumns.cssClassname,
  NodeColumns.analyticsEvent,
  NodeColumns.id,
];

/**
 * The DATA columns in the order they RENDER in the table — mirrors the
 * TanStack column-def order in `table.tsx` (which is what
 * `row.getVisibleCells()` walks and what table-nav's sibling-based focus
 * movement follows). This is deliberately DIFFERENT from `columnOrder` above,
 * which is the legacy logical/serialization order (`cssClassname` and
 * `variables` sit elsewhere, and `intent` has no rendered column at all).
 *
 * PSTUDIO-2372: ALL cell-selection geometry (`getCellsBetween`,
 * `_captureCells`, `pasteSelectedCells`) must use THIS order via
 * `visualColumnOrder` / `getVisualColumnIndex` — computing the rectangle in
 * `columnOrder` space while focus moves in DOM space made Shift+Arrow ranges
 * over-extend onto columns the user never crossed and drop columns they did
 * (e.g. extending onto Variables also selected Skill Tag; the next press
 * unselected Variables).
 *
 * Keep in sync with the column defs in `table.tsx` (grep `accessorKey:`).
 */
export const VISUAL_COLUMN_ORDER = [
  NodeColumns.nodeNumber,
  NodeColumns.nodeType,
  NodeColumns.nodeName,
  NodeColumns.utterances,
  NodeColumns.message,
  NodeColumns.richAssetType,
  NodeColumns.richAssetContent,
  NodeColumns.behaviors,
  NodeColumns.answerRequired,
  NodeColumns.nluDisabled,
  NodeColumns.nextNodes,
  NodeColumns.cssClassname,
  NodeColumns.command,
  NodeColumns.description,
  NodeColumns.output,
  NodeColumns.nodeInput,
  NodeColumns.parameterInput,
  NodeColumns.decisionVariable,
  NodeColumns.whatsNext,
  NodeColumns.variables,
  NodeColumns.skillTag,
  NodeColumns.analyticsEvent,
  NodeColumns.nodeTags,
  NodeColumns.platformFlag,
];

/**
 * PSTUDIO-1345 — the single "hide a column from the whole UI" lever. A column
 * listed here is removed from: the rendered table (table.tsx filters its def),
 * keyboard navigation + cell-range geometry (visualColumnOrder filters it), and
 * the system-clipboard / Google-Sheets export (VISIBLE_TSV_EXPORT_COLUMNS).
 *
 * Intentionally NOT applied to the domain/serialization layer, so a column
 * revives by deleting one line here: the NodeColumns enum member, the _Node /
 * TableNode / TableRowBase types, defaultRowData, the legacy `columnOrder`
 * (Bot-Compiler serialization), json-to-csv-parser (bot.csv) and the TSV import
 * fallback all keep the column.
 *
 * `order` is NOT here — it is debug-conditional (`hidden: !isDebugMode`) and
 * already absent from VISUAL_COLUMN_ORDER.
 */
export const HIDDEN_COLUMNS: ReadonlySet<NodeColumns> = new Set([
  NodeColumns.nodeTags,
  NodeColumns.platformFlag,
]);

// PSTUDIO-1345: hidden columns never reach the system-clipboard / Sheets export
// (text/plain TSV + text/html <td> grid). The JSON envelope keeps full rows, so
// PS→PS paste round-trips losslessly — no NODE_CLIPBOARD_VERSION bump.
export const VISIBLE_TSV_EXPORT_COLUMNS = TSV_EXPORT_COLUMNS.filter(
  (c) => !HIDDEN_COLUMNS.has(c as NodeColumns)
);

export interface TableRowBase {
  [NodeColumns.id]: string;
  [NodeColumns.order]: number;
  [NodeColumns.nodeNumber]: string | null;
  [NodeColumns.nodeName]: string;

  [NodeColumns.nodeType]: 'decision' | 'action';

  [NodeColumns.nodeTags]?: string;
  [NodeColumns.skillTag]?: string;
  [NodeColumns.variables]?: string;
  [NodeColumns.platformFlag]?: string;
  [NodeColumns.cssClassname]?: string;
  [NodeColumns.analyticsEvent]?: string;

  [NodeColumns.solutionVersionId]: string;
}

export interface TableRowAction extends TableRowBase {
  [NodeColumns.nodeType]: 'action';
  [NodeColumns.command]: string;
  [NodeColumns.description]?: string;
  [NodeColumns.output]: string;
  [NodeColumns.nodeInput]?: string;
  [NodeColumns.parameterInput]?: object;
  [NodeColumns.decisionVariable]?: string;
  [NodeColumns.whatsNext]: string;
  // [NodeColumns.richAssetType]: undefined;
}

export interface TableRowDecision extends TableRowBase {
  [NodeColumns.nodeType]: 'decision';
  [NodeColumns.message]: string;
  // [NodeColumns.richAssetType]: 'button' | 'selection' | 'secure' | 'png' | 'video' | 'carousel' | 'webview' | 'imagebutton' | 'document' | 'datepicker' | 'location' | 'file_upload' | 'quick_reply' | 'listpicker' | 'persistent_menu' | 'image';
  [NodeColumns.richAssetType]:
    | undefined
    | 'button'
    | 'listpicker'
    | 'webview'
    | 'quick_reply'
    | 'secure';
  [NodeColumns.richAssetContent]?: string; // highly structured based on the richAssetType
  [NodeColumns.nextNodes]?: number | string;
  // [NodeColumns.answerRequired]?: number | boolean;
  [NodeColumns.answerRequired]: boolean;
  // [NodeColumns.behaviors]?: string;
  [NodeColumns.behaviors]?:
    | 'none'
    | 'xfer_to_agent'
    | 'disable_input'
    | 'multiple_choice'
    | 'inline'
    | 'voice_enable_barge_in';
}

// export type TableRow = TableRowAction | TableRowDecision;

export type TableRow = TableNode;

export const defaultRowData: TableNode = {
  [NodeColumns.nodeNumber]: undefined,
  [NodeColumns.nodeType]: Tenancy_Node_Type_Enum_Ps_Enum.D,
  [NodeColumns.nodeName]: '',
  [NodeColumns.nluDisabled]: false,
  [NodeColumns.nextNodes]: [],
  [NodeColumns.message]: '',
  [NodeColumns.richAssetType]: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.None,
  // [NodeColumns.richAssetContent]: {},
  [NodeColumns.answerRequired]: false,
  [NodeColumns.behaviors]: [],
  [NodeColumns.command]: '',
  [NodeColumns.description]: '',
  [NodeColumns.output]: undefined,
  [NodeColumns.nodeInput]: '',
  [NodeColumns.parameterInput]: undefined,
  [NodeColumns.decisionVariable]: '',
  [NodeColumns.whatsNext]: undefined,
  [NodeColumns.nodeTags]: '',
  [NodeColumns.skillTag]: '',
  [NodeColumns.variables]: [''],
  [NodeColumns.platformFlag]: '',
  [NodeColumns.cssClassname]: '',
  [NodeColumns.analyticsEvent]: undefined,

  [NodeColumns.order]: 0,
  [NodeColumns.id]: uuidv4(),
  [NodeColumns.solutionVersionId]: '',

  // flows: [],
  // nluRouting: null,

  [NodeColumns.intent]: '',
  [NodeColumns.utterances]: '',
};

export const nodeTypeDependentCells: Record<
  Tenancy_Node_Type_Enum_Ps_Enum,
  NodeColumns[]
> = {
  a: [
    NodeColumns.nluDisabled,
    NodeColumns.nextNodes,
    NodeColumns.message,
    NodeColumns.richAssetType,
    NodeColumns.richAssetContent,
    NodeColumns.answerRequired,
    NodeColumns.behaviors,
    NodeColumns.cssClassname,
  ],
  d: [
    NodeColumns.command,
    NodeColumns.description,
    NodeColumns.output,
    NodeColumns.nodeInput,
    NodeColumns.parameterInput,
    NodeColumns.decisionVariable,
    NodeColumns.whatsNext,
  ],
  /** Generic (survey / other); reset fields when switching to this type — extend if product defines G-specific columns */
  g: [],
};

/**
 * Does the grid show `column` as cleared for a row of this node type? An unknown
 * type (a legacy value, a row that arrived half-formed) clears nothing.
 */
function isClearedForNodeType(
  nodeType: Tenancy_Node_Type_Enum_Ps_Enum | undefined,
  column: NodeColumns
): boolean {
  return nodeType
    ? !!nodeTypeDependentCells[nodeType]?.includes(column)
    : false;
}

/**
 * PSTUDIO-4348: the columns whose PASTE stops at a cell the grid shows as
 * cleared. Command used to paste as '' (harmless where it is cleared); the
 * converter now keeps the text, and a pasted type writes behaviors / NLU /
 * content next to it. Deliberately just these two: every other cleared column
 * still accepts a paste, as it did before.
 */
const NODE_TYPE_GATED_PASTE_COLUMNS = [
  NodeColumns.command,
  NodeColumns.richAssetType,
] as const;

export interface RowSelection {
  rowId: string;
}

export type RowSelectionState = Record<string, boolean>;

/**
 * PSTUDIO-3848: bring a contiguous range of visible rows into view. Set by
 * <Table/> (it owns the scroller/virtualizer); called by the store after an
 * insert/paste. Receives the first/last row ids (for the reliable DOM scroll)
 * AND their visible indices (fallback scroll when a row is virtualized out).
 */
export type RevealRange = (
  firstRowId: string,
  lastRowId: string,
  firstIndex: number,
  lastIndex: number
) => void;

type TableState = 'loading' | 'editing' | 'adding' | 'migrating' | 'viewing';

/**
 * PSTUDIO-4451: a cell id can reach a selector from a coworker's presence; an
 * unescaped quote would throw inside the focus code (or match another cell).
 */
const cssEscape = (value: string): string =>
  typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
    ? CSS.escape(value)
    : value.replace(/["\\]/g, '\\$&');

/** PSTUDIO-4450: the slice of the collab store the table writes view settings to. */
export type LiveViewSettingsSink = Pick<
  ProStudioNodeDataStore,
  | 'viewSettings'
  | 'updateViewSettings'
  | 'accessStatus'
  | 'editorStatus'
  | 'canUndo'
  | 'canRedo'
  | 'undo'
  | 'redo'
  | 'breakUndoGroup'
  | 'presence'
  | 'config'
  | 'updateConfig'
  | 'nodes'
  | 'connectionStatus'
>;

/** PSTUDIO-4452: how long publish waits for the live document to be in sync. */
const LIVE_SYNC_BEFORE_PUBLISH_MS = 10_000;
const LIVE_RESET_FOCUS_MS = 5_000;

export enum ValidationMode {
  Auto = 'auto',
  Manual = 'manual',
}

// PSTUDIO-2154: the cell to reveal (scroll into view + select + focus) after a
// command runs via undo/redo, so the user sees what changed. The same target is
// used for both directions — `_revealCommandFocus` no-ops when the row isn't
// currently visible (e.g. a row removed by an undo). `columnId` is optional and
// falls back to the node-number cell for row-level commands.
type FocusTarget = { rowId: string; columnId?: NodeColumns };

// PSTUDIO-3723: the command shape is the generic UndoRedoManager's Command
// specialized to this store's focus target. Kept named `Command` so the ~20
// command call sites (inline `{ execute, undo, description, focusTarget }`
// literals) are unchanged.
type Command = UndoCommand<FocusTarget>;

// PSTUDIO-2154: an explicit before/after snapshot of a group row's expanded
// state and each of its subRows' `hidden` flags. Captured so undo restores the
// exact prior per-subRow visibility (subRows can be individually hidden), not a
// blanket toggle.
type GroupVisibilitySnapshot = {
  groupRowId: string;
  groupExpanded: boolean;
  subRowHidden: Map<string, boolean>;
};

type CellBeingEdited =
  | undefined
  | {
      cellId: string;
      rowId: string;
      columnId: NodeColumns;
      input: HTMLTextAreaElement | HTMLInputElement;
    };

type CellSelection = {
  cellId: string;
  rowId: string;
  columnId: NodeColumns;
};

const nodeColumnValues = new Set<string>(Object.values(NodeColumns));

function getDataColumnIds(cell: HTMLTableCellElement): string[] | null {
  const tr = cell.closest('tr');
  if (!tr) return null;
  const cols: string[] = [];
  tr.querySelectorAll<HTMLTableCellElement>('td[data-cell-id]').forEach(
    (td) => {
      const id = td.dataset.cellId;
      if (id && nodeColumnValues.has(id) && td.dataset.hidden !== 'true')
        cols.push(id);
    }
  );
  return cols.length > 0 ? cols : null;
}

// @todo - Use the validator from pif-compiler
function isValidGlobalVariableName(value: string) {
  const regex = /^[A-Z][A-Z0-9_]+$/;
  return regex.test(value);
}

// Grace period before a cell click opens the property editor, so a DOUBLE-click
// can cancel it first. Sits between a comfortable double-click interval and the
// point where a genuine single click feels laggy — and it is only ever paid
// when the panel is CLOSED, which is the rare case.
const DOUBLE_CLICK_GRACE_MS = 250;

/**
 * Whether focus currently sits inside the Expanded Cell View panel, piercing
 * the JSON editor's shadow root. Used to decide whether closing the panel has
 * to hand focus back to the grid (PSTUDIO-1433).
 */
function focusIsInsideExpandedCellPanel(): boolean {
  if (typeof document === 'undefined') return false;
  let active: Element | null = document.activeElement;
  while (active?.shadowRoot?.activeElement) {
    active = active.shadowRoot.activeElement;
  }
  return composedContains(
    document.querySelector('.c-config-panel'),
    active as Node | null
  );
}

/**
 * PSTUDIO-4081 — bring a row's array-valued columns back to their column
 * contract, IN PLACE, writing only the columns that need it.
 *
 * Used where the row must not be rebuilt: an immer draft (`pasteRow`,
 * `duplicateRow`). Assigning a whole copy would carry every other key the row
 * holds, and a row that came off the system clipboard can own a `__proto__`
 * key — which immer's set trap turns into a thrown
 * `Object.setPrototypeOf() cannot be used on an Immer draft`.
 */
function applyArrayColumnFixes(row: Record<string, unknown>): void {
  const fixes = collectRowArrayColumnFixes(row, (text, column) =>
    convertTextToColumnValue(text, column as keyof TableNode)
  );
  if (fixes) Object.assign(row, fixes);
}

/** The plain workflow CSV of `rows`, group header rows stripped (PSTUDIO-2220). */
const toPlainCsv = (rows: TableRow[]): string | null => {
  const nodes = excludeGroupNodes(rows);
  return nodes.length ? json2csvParser.parse(nodes) : null;
};

/**
 * The grouped CSV of `rows` (PSTUDIO-3443), or `null` when nothing is grouped —
 * then the file is just bot.csv + empty group columns (redundant).
 */
const toGroupedCsv = (rows: TableRow[]): string | null => {
  const records = buildGroupedCsvRecords(rows);
  return records.some((r) => Boolean(r.__groupId))
    ? json2csvParserWithGrouping.parse(records)
    : null;
};

export class TableStore {
  table: Table<TableNode> | null = null;
  cellRefs: React.MutableRefObject<
    Map<string, HTMLTableCellElement | null>
  > | null = null;
  rowRefs: React.MutableRefObject<
    Map<string, HTMLTableRowElement | null>
  > | null = null;

  rows = observable.array<TableRow>([]);
  rowMap = observable.map<string, TableRow>();

  // Boundaries of the data slice fed to useReactTable. Updated by <Table/>
  // from the virtualizer's range, SNAPPED to chunks so micro-scrolls don't
  // change the window. The `windowedRows` getter is a MobX computed so
  // identical (start, end, rows, searchQuery) returns the SAME array
  // reference — without that, every scroll tick would trigger a
  // useReactTable row-model rebuild.
  virtualWindowStart: number = 0;
  virtualWindowEnd: number = 200;

  setVirtualWindow = action((start: number, end: number) => {
    if (this.virtualWindowStart === start && this.virtualWindowEnd === end) {
      return;
    }
    this.virtualWindowStart = start;
    this.virtualWindowEnd = end;
  });

  // Set by <Table/> so other parts of the store (e.g. scrollToAndFocusCell)
  // can ask the virtualizer to scroll a specific row index into view.
  scrollToVirtualIndex: ((rowIndex: number) => void) | null = null;

  setScrollToVirtualIndex = action(
    (cb: ((rowIndex: number) => void) | null) => {
      this.scrollToVirtualIndex = cb;
    }
  );

  // Set by <Table/> so the store can bring a CONTIGUOUS range of rows into
  // view, not just a single centered row (PSTUDIO-3848). Reveals the whole
  // freshly inserted/pasted block after a multi-node insert or paste; <Table/>
  // owns the scroll math (it has the scroller + sticky-header height).
  scrollRangeIntoView: RevealRange | null = null;

  setScrollRangeIntoView = action((cb: RevealRange | null) => {
    this.scrollRangeIntoView = cb;
  });

  searchQuery: string = '';

  // Native action-node command names (system + library), loaded dynamically
  // (PSTUDIO-2998). Seeded synchronously from the last-known-good cache (or the
  // static bootstrap default) so the compiler and command picker always have a
  // non-empty list before the live fetch resolves; refreshed via
  // setActionNodeNames once the XState invoke completes.
  actionNodeNames: string[] = resolveActionNodeNames({
    fetched: null,
    cached: readCachedActionNodeNames(),
  });

  // Replace the live command list. Guarded so an equal list does not fire a
  // needless observable write (the table is a top-level observer — even an
  // equal-value set re-renders). An empty/absent input is a no-op: the fallback
  // policy lives in the invoke (resolveActionNodeNames), never here, so an empty
  // update can't clobber a loaded list back to the static default.
  setActionNodeNames = action((list: string[]) => {
    if (!list || !list.length) return;
    if (
      list.length === this.actionNodeNames.length &&
      list.every((name, i) => name === this.actionNodeNames[i])
    ) {
      return;
    }
    this.actionNodeNames = list;
  });
  fuse: Fuse<TableRow>;
  searchResultIds: Set<string> = new Set();

  featureFlags: Record<string, boolean> = {};

  setFeatureFlags = action(
    (newFeatureFlags: Record<string, boolean> | undefined) => {
      this.featureFlags = newFeatureFlags ?? {};
    }
  );

  isViewer: boolean = false;

  /**
   * PSTUDIO-4450: what blocks editing in the table — the viewer role, or a
   * table fed by the collab document (read-only until the write path,
   * PSTUDIO-4451). Kept apart from `isViewer`, which is the user's role.
   */
  get isReadOnly(): boolean {
    return this.isViewer || (this.liveEditingSource && !this.canEditLive);
  }

  /**
   * PSTUDIO-4451: a collab-fed table is editable only while the document
   * accepts this user's writes. Before the first sync there is no store here
   * yet — "not known" — and the table is on its loader anyway.
   */
  get canEditLive(): boolean {
    const store = this.liveViewSettings;
    return (
      !!store &&
      !!this.liveWriter &&
      store.accessStatus === 'read-write' &&
      store.editorStatus === 'editing-allowed'
    );
  }

  setIsViewer = action((newIsViewer: boolean) => {
    this.isViewer = newIsViewer;

    if (newIsViewer) {
      this.setState('viewing');
    } else {
      this.setState('editing');
    }
  });

  /** PSTUDIO-3602 — "Auto-renumber pasted nodes" toggle. true = Smart
   *  (preserve-when-free / renumber+rewrite on collision); false = Raw
   *  (preserve verbatim; a duplicate node number blocks the paste). Persisted
   *  to localStorage; initialised from it so the choice survives a reload. */
  autoRenumberOnPaste: boolean = loadAutoRenumberOnPaste();

  setAutoRenumberOnPaste = action((value: boolean) => {
    this.autoRenumberOnPaste = value;
    persistAutoRenumberOnPaste(value);
  });

  toggleAutoRenumberOnPaste = action(() => {
    this.setAutoRenumberOnPaste(!this.autoRenumberOnPaste);
  });

  /**
   * Project Bobcat — "Enable Ungrouped Nodes".
   *
   * Whether canvas shows its "Ungrouped" indicator's expanded node list (and,
   * before Agent View was deleted 2026-09-23, its own trailing "Ungrouped"
   * card — the two shared this one preference rather than each keeping an
   * independently configured copy). Default OFF; see
   * `ungrouped-nodes-preferences.ts` for why, and for the note that this is
   * the seam a real profile setting would repoint at. Purely a DISPLAY
   * filter — the underlying data still contains every node either way, so
   * nothing here can hide a node from the table or from the compiler.
   */
  showUngroupedNodes: boolean = loadShowUngroupedNodes();

  setShowUngroupedNodes = action((value: boolean) => {
    // Write-minimal, same reason as setExpandedCellViewOpen: the table is a
    // top-level observer and re-renders on any observable write.
    if (this.showUngroupedNodes === value) return;
    this.showUngroupedNodes = value;
    persistShowUngroupedNodes(value);
  });

  toggleShowUngroupedNodes = action(() => {
    this.setShowUngroupedNodes(!this.showUngroupedNodes);
  });

  /**
   * Project Bobcat — which of the five stages (Design/Build/Validate/
   * Deploy/Observe) is selected. The cross-component bridge between the new
   * header stage nav (`StageNav`, in `PrimaryNav`) and the page content that
   * decides what to render (`builder.tsx`) — the two live in different parts
   * of the tree, same reason `showUngroupedNodes` lives here rather than in
   * either component alone. Not persisted, same as the existing `viewMode`
   * it sits alongside — an ephemeral per-session choice, not a saved
   * preference. See `workflow-stage.ts` for the full grounding.
   */
  activeStage: WorkflowStage = DEFAULT_STAGE;

  setActiveStage = action((stage: WorkflowStage) => {
    this.activeStage = stage;
  });

  /**
   * A node to open in the detail panel once Build is showing. Moving between
   * screens closes the panel (`builder.tsx`), so a jump from another stage
   * can't open it directly — it asks here and `builder.tsx` opens it after
   * the switch.
   */
  pendingNodePanelRowId: string | null = null;

  openNodeInBuild = action((rowId: string) => {
    this.pendingNodePanelRowId = rowId;
    // Opens in the table, where the node's row is, even though Build
    // otherwise opens on the canvas.
    this.pendingBuildViewMode = 'table';
    this.setActiveStage('build');
  });

  consumePendingNodePanel = action(() => {
    const rowId = this.pendingNodePanelRowId;
    this.pendingNodePanelRowId = null;
    if (!rowId || !this.getRowById(rowId)) return;
    this.deselectAllCells();
    this.deselectAllRows();
    this.selectRow(rowId);
    this.setExpandedCellViewOpen(true);
  });

  /**
   * Opens an agent's rows in the Build table (the Design page's agent pills).
   * Expands the group if it's collapsed, the same as its chevron, then asks
   * the table to scroll to its first member once it's showing.
   */
  openAgentInBuild = action((groupRowId: string) => {
    const group = this.getRowById(groupRowId);
    if (!group) return;
    if (!group.settings?.groupExpanded) {
      this.toggleRowGroupVisibility(groupRowId);
    }
    const firstMember = this.rows.find(
      (row) => row.settings?.groupId === groupRowId && row.hidden !== true
    );
    const nodeNumber = nodeNumberToGoTo(firstMember?.nodeNumber);
    if (firstMember && nodeNumber !== undefined) {
      this.setPendingGoToNodeNumber({ nodeNumber });
    }
    this.pendingBuildViewMode = 'table';
    this.setActiveStage('build');
  });

  /**
   * An agent to open on the Build canvas (the Design page's agent pills, with
   * the canvas on): the canvas selects it, opens its card and brings it into
   * view once it's drawn. See `openAgentInBuild` for the table's version.
   */
  pendingCanvasAgentId: string | null = null;

  openAgentOnCanvas = action((groupRowId: string) => {
    if (!this.getRowById(groupRowId)) return;
    this.pendingCanvasAgentId = groupRowId;
    this.pendingBuildViewMode = 'canvas';
    this.setActiveStage('build');
  });

  consumePendingCanvasAgent = action((): string | null => {
    const id = this.pendingCanvasAgentId;
    this.pendingCanvasAgentId = null;
    return id;
  });

  /** A node inside the pending agent to select and open the details of. */
  pendingCanvasNodeRowId: string | null = null;

  /**
   * One node on the Build canvas (Agent settings' "Edit in …" links): its
   * agent opened and in view, the node selected with its details open. A
   * node in no agent opens in the table instead, where it can be seen.
   */
  openNodeOnCanvas = action((rowId: string) => {
    const row = this.getRowById(rowId);
    const groupId = row?.settings?.groupId as string | undefined;
    if (!row) return;
    if (!groupId || !this.getRowById(groupId)) {
      this.openNodeInBuild(rowId);
      return;
    }
    this.pendingCanvasNodeRowId = rowId;
    this.openAgentOnCanvas(groupId);
  });

  consumePendingCanvasNode = action((): string | null => {
    const id = this.pendingCanvasNodeRowId;
    this.pendingCanvasNodeRowId = null;
    return id;
  });

  /**
   * A manual Validate run in progress. Shared so every Publish button (the
   * header's and Deploy's) holds off while it runs; automatic validation
   * reports through `isValidating` instead.
   */
  isManualValidating = false;

  setManualValidating = action((value: boolean) => {
    this.isManualValidating = value;
  });

  get isValidationInProgress(): boolean {
    return this.validationMode === ValidationMode.Auto
      ? this.isValidating
      : this.isManualValidating;
  }

  /**
   * A Build view to open on, set by a redirect from a retired standalone
   * page (the old Files route opens Build on its Files tab). Build's view
   * mode is `builder.tsx` component state, so the redirect can't set it
   * directly; `builder.tsx` consumes this once on mount.
   */
  pendingBuildViewMode: 'files' | 'table' | 'canvas' | null = null;

  setPendingBuildViewMode = action(
    (mode: 'files' | 'table' | 'canvas' | null) => {
      this.pendingBuildViewMode = mode;
    }
  );

  consumePendingBuildViewMode = action(
    (): 'files' | 'table' | 'canvas' | null => {
      const mode = this.pendingBuildViewMode;
      this.pendingBuildViewMode = null;
      return mode;
    }
  );

  // PSTUDIO-3624: Expanded Cell View panel open/closed. Persisted so the choice
  // survives a reload, mirroring autoRenumberOnPaste.
  expandedCellViewOpen: boolean = loadExpandedCellViewOpen();

  setExpandedCellViewOpen = action((value: boolean) => {
    // Project Bobcat: any explicit CLOSE abandons a deferred open from a cell
    // click. Without this, closing the panel (Esc, the toolbar toggle, or
    // opening Supervisor / Resource Library) inside the 250ms grace let the
    // timer re-open it afterwards — and the "one sidebar panel at a time"
    // reaction then closed the panel the user had just opened. Before the
    // equality guard, because the flag may already be false.
    if (!value) this.cancelPendingPanelOpen();

    // PSTUDIO-3624: write-minimal guard — the table is a top-level observer and
    // re-renders on ANY observable write, including setting an equal value. This
    // setter is called defensively with `false` in a few places even when
    // already `false`, so bail out when the flag is unchanged.
    if (this.expandedCellViewOpen === value) return;
    // PSTUDIO-1433: read the focus position BEFORE the flag flips — the panel
    // unmounts on the same tick, taking the focused editor with it. The
    // question is specifically "was focus INSIDE the panel", not "was it
    // outside the grid": this setter is also the one the toolbar toggle, the
    // Resource Library, Supervisor and ⌘/ call, and there focus sits on a
    // button — outside the grid, but not in the panel either. Handing focus
    // back there would run `scrollToAndFocusCellById`, which deselects every
    // cell and row first, so a user with a range selected would watch it
    // collapse and the table jump. `composedContains` because the focused node
    // lives in `ps-code-editor`'s shadow root.
    const focusWasInsideGrid = !focusIsInsideExpandedCellPanel();
    this.expandedCellViewOpen = value;
    persistExpandedCellViewOpen(value);
    // PSTUDIO-3623: one panel at a time — close the shortcuts panel through its
    // OWN setter (equality-guarded, write-minimal), symmetric with how
    // setKeyboardShortcutsOpen closes this one. No recursion: the inner call
    // passes `false`, which does not re-enter this setter's `if (value)` branch.
    if (value) this.setKeyboardShortcutsOpen(false);
    // PSTUDIO-1433: closing the panel unmounts the editor that HOLDS focus, so
    // the browser drops focus on <body>: the cell stays painted as selected but
    // every grid key handler is dead until the user reaches for the mouse — a
    // keyboard dead end entered by keyboard alone. The hand-back lives HERE, in
    // the single setter every close entrance goes through (Esc, Shift+Enter,
    // the toolbar toggle, the resource-library and Supervisor opens, and
    // `closeOverlaySidePanels`), rather than at each call site: wiring it per
    // entrance is exactly the shape that leaves one path behind.
    const rowId = this.selectedRowId();
    const columnId = this.selectedCell()?.columnId;
    if (
      shouldRestoreGridFocusAfterPanelToggle({
        isPanelOpenAfterToggle: value,
        rowId,
        columnId,
        focusWasInsideGrid,
      })
    ) {
      // Deliberately NOT `scrollToAndFocusCellById`: that helper defers through
      // a setTimeout plus two animation frames — long enough for the user's
      // NEXT keystroke to land first and then be undone (measured: Esc, then
      // ArrowDown, left the selection pinned to the cell Esc had closed on) —
      // and it deselects every cell and row on the way, dropping any range
      // selection. One frame is enough here: React unmounts the panel in this
      // tick, and the editor losing focus on unmount would immediately override
      // a synchronous focus() (measured — focus ended up on <body>).
      const targetRowId = rowId;
      const targetColumnId = columnId;
      requestAnimationFrame(() => {
        // If the user has already moved focus themselves in the meantime,
        // leave it alone — a hand-back that overrides a deliberate keystroke is
        // worse than no hand-back at all.
        if ((document.activeElement as HTMLElement | null)?.closest?.('td')) {
          return;
        }
        const cell = document.querySelector(
          // PSTUDIO-4451: ids can come from a coworker's presence.
          `td[data-row-id="${cssEscape(String(targetRowId))}"][data-cell-id="${cssEscape(String(targetColumnId))}"]`
        );
        if (cell instanceof HTMLElement) cell.focus();
      });
    }
  });

  toggleExpandedCellViewOpen = action(() => {
    this.setExpandedCellViewOpen(!this.expandedCellViewOpen);
  });

  /**
   * Open the property panel from a plain cell click, if the flag allows.
   *
   * The gate lives here rather than in `cell.tsx` on purpose: the cell renderer
   * is the hot path (~25 columns x ~50 rendered rows), so it must not grow a
   * hook or a per-cell flag read. It calls this unconditionally and the store
   * decides.
   *
   * The panel is SELECTION-driven — the node panel reads `selectedNodeRowId`,
   * which follows the click — so this only supplies the open gesture.
   * `setExpandedCellViewOpen` is equality-guarded, so clicking around with the
   * panel already open writes nothing and re-renders nothing.
   *
   * Project Bobcat, node property editor.
   */
  /**
   * Pending deferred open from a cell click, if any. Not observable — it is a
   * timer handle, never rendered.
   */
  private pendingPanelOpenTimer: ReturnType<typeof setTimeout> | null = null;

  openPropertyPanelFromClick = action((options?: { immediate?: boolean }) => {
    if (resolveBobcatAccess('fiveStageNav', this.featureFlags) !== 'allowed') {
      return;
    }

    // Already open: nothing changes, so there is no reflow to defer around.
    // This is the COMMON case — `expandedCellViewOpen` persists, so the panel
    // is open on every load after a user first opens it. Keeping this path
    // instant matters more than the one below.
    if (this.expandedCellViewOpen) return;

    // `immediate` skips the grace entirely, for a click on an element that has
    // NO double-click gesture to protect — the row-number cell in column 0,
    // whose only double-click handler sits on the row-resize handle, a separate
    // sibling element at the row's bottom edge.
    //
    // This is the answer to "is 250ms the right number?" for the gesture that
    // matters most: the row click is now the primary way to open the panel, and
    // the right delay for it is none. The grace stays where it is actually
    // needed — a normal cell, which opens an inline editor on double-click.
    if (options?.immediate) {
      this.cancelPendingPanelOpen();
      // Only flip the flag if the click actually produced a node to show.
      // `expandedCellViewOpen` is PERSISTED and the "one sidebar panel at a
      // time" reaction fires on it, so opening for nothing is not free: it
      // would close an open Supervisor / Resource Library panel and render
      // nothing in its place. Clicking a GROUP row's number is the case —
      // `selectedNodeRowId` excludes group rows. The caller must therefore
      // select first and call this last; see `handleIndexCellClick`.
      if (this.selectedNodeRowId === null) return;
      this.setExpandedCellViewOpen(true);
      return;
    }

    // Closed: defer, so a DOUBLE-click can cancel it. `onClick` fires for the
    // first click of a double-click and is indistinguishable from a single one
    // at that moment, so without this, double-clicking a cell to inline-edit
    // opens the panel BETWEEN the two clicks — the grid reflows and the second
    // click can land on a different cell. Product decision (Gary, 2026-09-09):
    // a double-click edits the field and must not touch the panel.
    //
    // The delay is only ever paid on the closed -> open transition. Note this
    // timer must not be confused with the PSTUDIO-3624 trap: it does not touch
    // `isCellBeingEdited`, so it cannot swallow an undo.
    this.cancelPendingPanelOpen();
    this.pendingPanelOpenTimer = setTimeout(() => {
      this.pendingPanelOpenTimer = null;
      // Same "is there anything to show?" gate as the immediate path above.
      // Evaluated HERE rather than when the click arrived, because by now the
      // selection has settled — which is also why the cell path can keep
      // calling this before its own selection branches run.
      if (this.selectedNodeRowId === null) return;
      runInAction(() => this.setExpandedCellViewOpen(true));
    }, DOUBLE_CLICK_GRACE_MS);
  });

  /**
   * Abandon a deferred open. Called when a second mousedown arrives, i.e. the
   * gesture turned out to be a double-click.
   */
  cancelPendingPanelOpen = action(() => {
    if (this.pendingPanelOpenTimer === null) return;

    clearTimeout(this.pendingPanelOpenTimer);
    this.pendingPanelOpenTimer = null;
  });

  /**
   * The row the NODE property panel is bound to, or null if there is none.
   *
   * The panel is node-scoped, but the grid has TWO selection models and the
   * node can arrive through either:
   *
   * - **`selectedCells`** — a click on any normal cell. A multi-cell selection
   *   is fine as long as it stays inside one row (a drag across that row's
   *   cells); the moment it spans rows there is no single node to show.
   * - **`selectedRows`** — a click on the row-number cell in column 0. That
   *   gesture goes through `handleIndexCellClick`, which calls
   *   `deselectAllCells()` and then `selectRow()`, so it leaves `selectedCells`
   *   EMPTY and the node id only in `selectedRows`. Reading cells alone made
   *   column 0 the one place in a ROW-driven panel where clicking the row did
   *   nothing.
   *
   * Cells win when both are populated: clicking a normal cell calls
   * `deselectAllRows()`, so a stale row highlight cannot outvote the cell the
   * user just clicked.
   *
   * Group rows (`nodeType === 'g'`) are excluded from both paths: they are a UI
   * grouping construct, not a node, and `nodeTypeDependentCells.g` is empty —
   * so every column would read as applicable and the type tab would be
   * meaningless.
   */
  get selectedNodeRowId(): string | null {
    const cells = this.selectedCells;

    let rowId: string | undefined;

    if (cells.length > 0) {
      ({ rowId } = cells[0]);
      for (let i = 1; i < cells.length; i += 1) {
        if (cells[i].rowId !== rowId) return null;
      }
    } else if (this.selectedRows.size === 1) {
      [rowId] = [...this.selectedRows.values()];
    }

    if (!rowId) return null;

    const row = this.getRowById(rowId);
    if (!row) return null;
    if (row.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.G) return null;

    return rowId;
  }

  /**
   * Whether the legacy single-cell Expanded Cell View should be MOUNTED.
   *
   * With the property-editor flag ON the node panel REPLACES it — they are both
   * sidebar panels and cannot share the slot, and the node panel is a superset
   * (it shows the clicked cell's column among the node's other fields). Flag
   * OFF is byte-identical to today: open means visible, empty state and all.
   */
  get shouldRenderExpandedCellView(): boolean {
    if (!this.expandedCellViewOpen) return false;

    return resolveBobcatAccess('fiveStageNav', this.featureFlags) !== 'allowed';
  }

  /**
   * Whether the Bobcat NODE property panel should be MOUNTED.
   *
   * `expandedCellViewOpen` alone is not enough. That flag persists to
   * localStorage, so once a user has opened the panel it stays open across
   * every reload — and with nothing selected the sidebar would sit there at a
   * fixed width showing an empty state, squeezing the grid and earning nothing.
   * Requiring a bound node as well means the panel appears when it has
   * something to say and gets out of the way when it does not (product
   * decision, Gary 2026-09-09).
   *
   * Consequence worth knowing: with the flag ON, Shift+Enter (or the toolbar
   * toggle) while NOTHING is selected flips the open flag with no visible
   * result. That is a behaviour change, not an oversight.
   */
  get shouldRenderNodePropertyPanel(): boolean {
    if (!this.expandedCellViewOpen) return false;

    if (resolveBobcatAccess('fiveStageNav', this.featureFlags) !== 'allowed') {
      return false;
    }

    return this.selectedNodeRowId !== null;
  }

  // PSTUDIO-1433: Expanded Cell View panel WIDTH (px). Resizable via a Separator
  // drag handle; persisted so the chosen width survives a reload (mirrors
  // expandedCellViewOpen). Clamped to [MIN, MAX] on read AND write inside the
  // pure storage module.
  expandedCellViewWidth: number = loadExpandedCellViewWidth();

  setExpandedCellViewWidth = action((value: number) => {
    const clamped = clampExpandedCellViewWidth(value);
    // Write-minimal guard: the Separator's onResize fires on every drag tick and
    // the table is a top-level observer, so bail when the clamped width is
    // unchanged to avoid churning a re-render. (table.tsx also debounces the
    // drag stream; this is the store-side backstop.)
    if (this.expandedCellViewWidth === clamped) return;
    this.expandedCellViewWidth = clamped;
    persistExpandedCellViewWidth(clamped);
  });

  // PSTUDIO-1433: one-shot signal that the Expanded Cell View panel was AUTO
  // opened by entering edit on a JSON-capable cell, so the panel should move
  // focus into its own editor. A monotonically increasing nonce (not a boolean)
  // so each auto-open re-triggers the panel's focus effect even if the panel is
  // already mounted. Read ONLY by the panel (not in table.tsx's render), so
  // bumping it does not re-render the whole table. Transient (never persisted).
  expandedCellViewAutofocusNonce = 0;

  requestExpandedCellViewAutofocus = action(() => {
    this.expandedCellViewAutofocusNonce += 1;
  });

  // PSTUDIO-3623 — keyboard-shortcuts help panel open flag. Reference-only
  // overlay; deliberately NOT persisted (a help panel must never re-open itself
  // on reload — contrast expandedCellViewOpen, which is persisted). Part of the
  // "one sidebar panel at a time" group (behavior contract §1): opening it
  // closes the Expanded Cell View here, and the Supervisor/ResourceLib React
  // panels are closed by table.tsx's closeOverlaySidePanels() at the call site.
  keyboardShortcutsOpen = false;

  setKeyboardShortcutsOpen = action((value: boolean) => {
    if (this.keyboardShortcutsOpen === value) return;
    this.keyboardShortcutsOpen = value;
    if (value) this.setExpandedCellViewOpen(false);
  });

  toggleKeyboardShortcutsOpen = action(() => {
    this.setKeyboardShortcutsOpen(!this.keyboardShortcutsOpen);
  });

  coworkerFocusByCellId = observable.map<
    string,
    { label: string; colorIndex: number }
  >();

  /**
   * Cells inside a COWORKER's selected range (PSTUDIO-2372 presence):
   * cellId → colorIndex. Painted as a translucent tint
   * (`c-editor-table-td--coworker-range-N`) — the solid border + name label
   * stay on the coworker's focused/anchor cell only (coworkerFocusByCellId).
   * Populated by use-presence-mobx-bridge from pageParams.selection.cellRange
   * (bounding corners, expanded locally via getCellsBetween).
   */
  coworkerRangeByCellId = observable.map<string, number>();

  /**
   * Rows a COWORKER has selected (PSTUDIO-2372 presence): rowId → colorIndex.
   * Painted as a row tint (`c-editor-table-tr--coworker-range-N`).
   */
  coworkerRangeByRowId = observable.map<string, number>();

  private _columnWidths = observable.object<ColumnsWidths>(defaultColumnWidths);

  private _columnPinningSettings = observable.object<ColumnPinningSettings>(
    defaultColumnPinningSettings
  );

  private _columnWrapSettings = observable.object<ColumnWrapSettings>({
    ...defaultColumnWrapSettings,
  });
  private pifPartialValidationService = new PifPartialValidationService();

  // TODO: NEED IT UNTIL WE SWITCH UI TO USE PIF COMPILER
  private _dataForCompiling: string = '';
  // TODO: NEED IT UNTIL WE SWITCH UI TO USE PIF COMPILER
  private _originalPifCompilerDiagnostics: Diagnostic[] = [];
  // TODO: NEED IT UNTIL WE SWITCH UI TO USE PIF COMPILER
  set dataForCompiling(newDataForCompiling: string) {
    this._dataForCompiling = newDataForCompiling;
  }
  // TODO: NEED IT UNTIL WE SWITCH UI TO USE PIF COMPILER
  set originalPifCompilerDiagnostics(
    newOriginalPifCompilerDiagnostics: Diagnostic[]
  ) {
    this._originalPifCompilerDiagnostics = newOriginalPifCompilerDiagnostics;
  }
  // TODO: NEED IT UNTIL WE SWITCH UI TO USE PIF COMPILER
  get dataForCompiling() {
    return this._dataForCompiling;
  }
  // TODO: NEED IT UNTIL WE SWITCH UI TO USE PIF COMPILER
  get originalPifCompilerDiagnostics() {
    return this._originalPifCompilerDiagnostics;
  }

  tableScrollerRef?: HTMLDivElement = undefined;
  pendingRowsToRender: TableRow[] = [];

  /**
   * Rows that a delete has already claimed but that are still physically in
   * `rows` (PSTUDIO-3990). `_deleteRowsInternal` flips `hidden = true`
   * synchronously and defers the actual removal — plus the error cleanup — to
   * an idle callback; this set is what makes that window addressable.
   *
   * It exists because `hidden` is overloaded: it also marks a row hidden by a
   * COLLAPSED GROUP, and such a row still ships to the backend, so it must keep
   * gating publish (the contract stated on `findNodeNumberProblem`). Filtering
   * the node-number checks on `hidden` would therefore let a numberless row
   * inside a collapsed group publish. Filled where `hidden = true` is set on
   * delete, emptied in the same idle callback that removes the rows (and by
   * `_reinsertRowIntoMemory`, so an undo inside the window un-marks the row it
   * brings back).
   */
  pendingDeletionRowIds = observable.set<string>();

  defaultRows = observable.array<TableRow>([]);

  currentState: TableState = 'loading';
  previousState: TableState = 'editing';

  solutionVersionPublishingInProgress = false;

  setSolutionVersionPublishingInProgress = action((value: boolean) => {
    this.solutionVersionPublishingInProgress = value;
  });

  solutionVersionSubscriptionUnsubscribe: (() => void) | null = null;
  coworkerFocusSubscriptionUnsubscribe: (() => void) | null = null;
  isWaitingForBackendData = false;
  /** True when we have version data but a row has parameterInput.live_corpus_id === '' and we're waiting for backend to fill it */
  isWaitingForLiveCorpusIdOnly = false;

  /**
   * PSTUDIO-4240: the counter that makes a RE-ENTRY an event.
   *
   * `solutionVersionId` is the version's identity, and mobx invokes a
   * reaction's effect only when the tracked value CHANGED — so the two gestures
   * that come back into a solution without an explicit solution pick (browser
   * Back onto /workflows; Workflows clicked from a page inside the same
   * solution) leave the string untouched and issue no fetch at all. Neither one
   * clears `draftVersion`, which is what releases the id on the way back INTO
   * a solution (`table-store-route-sync`, the `!draftVersion` branch — the
   * whole of the PSTUDIO-4215 fix). That is not the only release in that file:
   * the `isFirstLevelPage` branch assigns `''` on the way OUT.
   * `clearSolutionScope` leaves `draftVersion` alone by design
   * (`xstate/prostudio.xstate.ts`).
   *
   * It is NOT `versionVisitEpoch` (module scope, above the reaction), and the
   * two are easy to confuse. That one is the reaction's OWN bookkeeping: it
   * stamps each in-flight fetch so a late response from a previous visit is
   * dropped, and nothing outside the reaction writes it. This one is the INPUT
   * signal — an entrance asking for a new visit of the id the store already
   * holds. The reaction tracks it beside the identity and bumps the epoch
   * itself when it runs.
   */
  versionRevisitRequests = 0;

  /**
   * PSTUDIO-4240: the visit ended without data, and the waiting flags are down.
   *
   * `isWaitingForBackendData` answers "is a visit still in flight", which is
   * NARROWER than the state the user is stuck in. Five arms in the reaction
   * below lower that flag over an EMPTY table — the live-corpus subscription
   * failing to initialise, the no-initial-data subscription reporting an
   * error, a throw while handling one of its pushes, that subscription failing
   * to initialise, and the initial fetch's `.catch`. Each leaves exactly the
   * screen this ticket describes (0 nodes, no spinner, no error) with the
   * flags DOWN, so an entrance that asked "is the store waiting" answered NO
   * and refused to help; only a hard reload recovered.
   *
   * So the entrance policy asks two questions, not one: is a visit still
   * waiting, OR did the last one end without ever applying data. Reset at the
   * START of every visit (the `startsNewVisit` arm), which is why a visit that
   * succeeds leaves it false without anyone clearing it.
   *
   * It does NOT gate Publish. A failed visit leaves no rows to publish, and
   * `canPublish` already refuses an empty table; adding a second gate here
   * would be a new way to block publishing, which this ticket has no mandate
   * for.
   */
  versionVisitFailed = false;

  /**
   * Ask for a new visit of the version the store is ALREADY on (PSTUDIO-4240).
   * The entrance policy — WHEN this is worth doing — lives in
   * `stores/utils/table-store-builder-entry.ts`, not here.
   *
   * It raises a SIGNAL and nothing else. Every consequence of a revisit —
   * closing the previous visit's subscription, dropping the live-corpus wait,
   * the loader, the fetch — belongs to the reaction, in the one place where a
   * fetch is already unconditional (`endVisitForRevisit` below, and the
   * `startsNewVisit` arm that follows it).
   *
   * That split is the fix for a hole this method had in review. Tearing the
   * visit down HERE ran it before the reaction's three early returns
   * (`!botId`, `currentState === 'migrating'`, and the id/botId mismatch), so a
   * restart that any of them swallowed left the store with
   * `isWaitingForBackendData` raised, the subscription slot emptied and no
   * fetch on the way: the ticket's own symptom, now with nothing left that
   * could deliver the data. The dropped `isWaitingForLiveCorpusIdOnly` made it
   * worse than a stall — three readers treat that flag as permission to lower
   * `isWaitingForBackendData` (`processVersionData` twice, `mergeVersionData`
   * once), so a message from a sibling tab could unblock Publish for a version
   * whose `live_corpus_id` is still empty. That is the fail-open this ticket
   * refuses on the way out, reached from the way in.
   *
   * What it still deliberately does NOT do, wherever it runs: lower
   * `isWaitingForBackendData`. Publish is gated on that flag
   * (`components/buttons/publish-button/publish-button.tsx`), and the wait has
   * not ended just because the user walked back in — it ends when the new
   * response says so.
   */
  restartVersionVisit = action(() => {
    if (!this.solutionVersionId) {
      return;
    }

    this.versionRevisitRequests += 1;
  });

  /**
   * PSTUDIO-4450: the rows come from the draft's collab document
   * (`collab/use-collab-table-feed`), not from Hasura. While this is on, the
   * visit below issues no fetch and leaves the table on its loader until the
   * feed delivers; the table's save funnels write to the document through
   * `liveWriter` (PSTUDIO-4451). Set by the editor and by route sync on every
   * other page of the solution (PSTUDIO-4449).
   *
   * Flipping it on the version already open starts a new visit, so the table
   * swaps source rather than keeping the other one's rows.
   */
  liveEditingSource = false;

  setLiveEditingSource = action((on: boolean) => {
    if (this.liveEditingSource === on) {
      return;
    }

    this.liveEditingSource = on;
    this.restartVersionVisit();
  });

  /**
   * PSTUDIO-4450: while the table is fed by the collab document, this user's
   * view settings — collapsed groups, column widths, pinning and wrap — are
   * written to the document's per-user view settings instead of Hasura. Set
   * and cleared by the feed (`collab/collab-table-feed`). The feed brings the
   * change back into the rows, so a collapse is a view-settings write only.
   */
  liveViewSettings: LiveViewSettingsSink | null = null;

  setLiveViewSettings = action((sink: LiveViewSettingsSink | null) => {
    this.liveViewSettings = sink;
  });

  /**
   * PSTUDIO-4451: where the save funnels write while the table is collab-fed —
   * row saves, order saves and deletes become document mutations. Set and
   * cleared by the feed with `liveViewSettings`; with none, a write is
   * dropped, never sent to Hasura.
   */
  liveWriter: CollabWriter | null = null;

  setLiveWriter = action((writer: CollabWriter | null) => {
    this.liveWriter = writer;
  });

  /**
   * PSTUDIO-4449: the cell-text converter the collab writer coerces a draft
   * string in an array column with — the same one `saveBulkRowsToDatabase`
   * hands `coerceRowArrayColumns` on the Hasura path, so both paths share ONE
   * conversion contract.
   */
  convertCellText: ConvertCellText = (text, column) =>
    convertTextToColumnValue(text, column as keyof TableNode);

  /**
   * End the visit a revisit replaces — called by the reaction, AFTER its early
   * returns, immediately before the fetch that pays for it.
   *
   * The subscription is closed without going through
   * `cleanupSolutionVersionSubscription` because neither of its two modes fits:
   * unforced (`force === false`) it returns early on `isWaitingForBackendData`,
   * the very flag this path keeps raised, and forced it drops that flag. This
   * is the only other writer of `solutionVersionSubscriptionUnsubscribe`'s
   * teardown; a change to what closing a subscription means has to land in both.
   *
   * `isWaitingForLiveCorpusIdOnly` goes down here for a reason that is not
   * symmetry: it is derived from the PREVIOUS payload, and left raised it would
   * stop `processVersionData` from ever lowering `isWaitingForBackendData` when
   * the new payload arrives complete (both of its arms lower it only
   * `if (!this.isWaitingForLiveCorpusIdOnly)`, table-store.ts — search
   * `isWaitingForLiveCorpusIdOnly` in `processVersionData`), and Publish would
   * stay gated for the rest of the session.
   */
  endVisitForRevisit = action(() => {
    if (this.solutionVersionSubscriptionUnsubscribe) {
      this.solutionVersionSubscriptionUnsubscribe();
      this.solutionVersionSubscriptionUnsubscribe = null;
    }

    this.isWaitingForLiveCorpusIdOnly = false;
  });

  selectedCells = observable.array<CellSelection>([]);
  selectedRows = observable.set<string>([]);

  resizingRowId: string | null = null;

  /**
   * PSTUDIO-3866: transient, non-persisted live preview of a group's color while
   * its custom HSV/hue/alpha drag is in flight. The committed color lives in
   * `settings.groupColor` and is written once, on drag release (`setGroupColor`)
   * so the whole drag stays a single undo + DB write. This slot lets BOTH the
   * group header AND its subrow guide lines paint the in-flight color live,
   * without touching the store's persisted/undo state. Keyed by groupId so a
   * reader (`getGroupColorPreview`) subscribes only to ITS group's key — a drag
   * on one group never re-renders another group's header/subrows. Only one
   * picker is open at a time, so at most one key is populated. Absent = no drag.
   */
  groupColorPreview = observable.map<string, string>();

  copiedCells = observable.set<string>([]);

  /** Callback to scroll table to a cell (set by Table, uses table-nav scrollToCell). */
  private scrollToCellCallback: ((el: HTMLElement) => void) | null = null;
  /** When set, Table will select this cell and scroll to it (e.g. after navigating from validation popover). */
  pendingScrollToCell: { rowIndex: number; columnId: NodeColumns } | null =
    null;
  /** When set, Table will resolve rowId to rowIndex and scroll to it (e.g. after navigating via coworker avatar click). */
  pendingScrollToCellByRowId: { rowId: string; columnId: NodeColumns } | null =
    null;
  /**
   * When set, Table will scroll to and focus the node with this number.
   *
   * NOTE: no producer since PSTUDIO-3951. Its only caller was the "Go to node"
   * button on the voice channel's Failover Node # field, which was removed when
   * AUTO-4676 moved failover routing to a CSV system node. `setPendingGoToNodeNumber`
   * and the consumer effect in `table.tsx` are therefore currently unreachable —
   * kept, not deleted, because this file is under the route-A extraction in
   * `docs/WORKFLOWS_STABILIZATION.md`. Delete the layer there, or give it a caller.
   */
  pendingGoToNodeNumber: { nodeNumber: string } | null = null;
  /**
   * Project Bobcat — set by `createSolution()` right after a brand-new
   * solution is created, consumed once inside the `solutionVersionId`
   * reaction the first time THIS solution's version becomes publish-ready
   * (immediately for a Supervisor-only solution, or once its
   * `live_corpus_id` subscription resolves). See `auto-publish-on-create.ts`.
   * `uniqueId` is the solution's own id (not a version id) — the one
   * identity known synchronously right after creation, before the
   * route/loader has resolved a `solutionVersionId`. See
   * `consumePendingAutoPublish` for how it's matched at consumption time.
   */
  pendingAutoPublish: { uniqueId: string; botType: BotType } | null = null;

  /**
   * Project Bobcat — set right after a brand-new solution is created, by the
   * create wizard (`use-solution-wizard-form.tsx`, Supervisor solutions) and
   * by the conversational flow (`use-conversational-create.tsx`), and only
   * while the master `bobcat-five-stage-nav` is on or the flags have not loaded
   * (`markerAllowed`). Consumed the moment that solution's rows are next
   * loaded into `this.rows` (`processVersionData`, right after
   * `detectAutoAgentGrouping()` runs). Same shape and matching discipline as
   * `pendingAutoPublish` — see `consumePendingAutoAgentGrouping`.
   *
   * Consumed and applied BEFORE `pendingUseCaseAgentGrouping` at the load
   * site — order matters here, not just convention: `applyAutoAgentGrouping`
   * declines whenever `hasAnyGrouping()` is already true (a blanket check
   * over every row, not scoped to Supervisor/Knowledge), while
   * `applyUseCaseAgentGrouping` deliberately is NOT gated on that. Applying
   * the use-case stubs first would make the solution's `hasAnyGrouping()`
   * true before Supervisor/Knowledge ever got a turn, so Supervisor/
   * Knowledge would silently never group. Found live, 2026-09-24, while
   * wiring this in — not a hypothetical.
   */
  pendingAutoAgentGrouping: string | null = null;

  /**
   * Project Bobcat — set by `createSolution()` when a Supervisor + Knowledge
   * Agent solution is created with no documents and no pasted knowledge (the
   * onboarding "skip"). The backend still builds the Knowledge Agent's RAG
   * lookup with `live_corpus_id: ''`, and no corpus is ever provisioned for
   * it, so Publish would wait on it forever. Consumed the first time this
   * solution's rows load, BEFORE auto-agent grouping, to drop the lookup —
   * see `applySkipKnowledgeCorpus`. Same matching discipline as
   * `pendingAutoPublish`.
   */
  pendingSkipKnowledgeCorpus: string | null = null;

  /**
   * Project Bobcat — set by "Create a blank solution" (`home-blank.tsx`).
   * The backend builds its standard rows (Start, Supervisor and Knowledge
   * Agent steps, error handling) even for a `blank` solution, so they are all
   * deleted the first time this solution's rows load — see
   * `applyEmptySolution`. Same matching discipline as `pendingAutoPublish`.
   */
  pendingEmptySolution: string | null = null;

  /**
   * Project Bobcat — set right after a brand-new solution is created, by the
   * create wizard (Supervisor solutions with tasks) and by the conversational
   * flow, and only while the master `bobcat-five-stage-nav` is on or the flags
   * have not loaded (`markerAllowed`). Consumed the moment that solution's
   * rows are next loaded into `this.rows` (`processVersionData`, right after
   * `detectUseCaseAgentNumbering()` runs). Same shape and matching
   * discipline as `pendingAutoPublish` — see `consumePendingUseCaseAgentGrouping`.
   */
  pendingUseCaseAgentGrouping: string | null = null;

  /**
   * Project Bobcat — set by the conversational flow next to the grouping
   * markers, but always (they are set only under the master), consumed by
   * `builder.tsx` once the route has resolved to this solution, to open it
   * directly on Canvas instead of the default Table View. Deliberately NOT
   * flag-gated at consumption like `tryConsumePendingAutoPublish` is —
   * `builder.tsx` already runs every `viewMode` through
   * `resolveEffectiveViewMode`, which falls back to Table View on its own
   * when the master is off, so gating here too would just be the
   * same check twice.
   */
  pendingCanvasLanding: string | null = null;

  /**
   * Project Bobcat (agent templates) — set by Talk it through right
   * after it creates a solution: the tasks to build into real agents and the
   * brief an AI draft works from. Consumed at the same load as the use-case
   * grouping, right after it (the agents it fills are those groups). Same
   * matching as `pendingAutoPublish`.
   */
  pendingAgentDrafts: {
    uniqueId: string;
    brief: string;
    tasks: PendingAgentTask[];
  } | null = null;

  // selectedStartCell = observable.box<CellSelection>();
  // selectionStart: CellSelection | null = null;
  // rowToDelete: string | null = null;

  // Command history — the stack engine lives in a dedicated, unit-tested
  // UndoRedoManager (PSTUDIO-3723). tableStore keeps thin delegators
  // (executeCommand / pushCommandWithoutExecute / undo / redo / clearHistory /
  // canUndo / canRedo) so the ~20 command call sites stay unchanged.
  private history = new UndoRedoManager<FocusTarget>({
    maxHistorySize: 100,
    onReveal: (target) => this._revealCommandFocus(target),
  });

  // PSTUDIO-3723: backward-compat accessors — the command-history stacks moved
  // into `this.history` (UndoRedoManager). Existing tests reset them via
  // `.replace([])` and assert `.length`, so expose the live observable arrays.
  get undoStack() {
    return this.history.undoStack;
  }

  get redoStack() {
    return this.history.redoStack;
  }

  isValidating: boolean = false;

  // PSTUDIO-4242: one sidebar commit starts two overlapping `validateAndSync`
  // passes (the direct call in `saveUnsavedChanges`, then another once the DB
  // save lands), with no abort between them. A single boolean reset in each
  // pass's `finally` went false when the FIRST pass ended, while the second was
  // still running — and a Publish click queued behind validation then acted on
  // a pass that had not finished. Only the most recently STARTED pass may clear
  // the flag now: it carries the verdict for the newest rows. Deliberately not
  // an in-flight counter — a pass whose request never settles would then hold
  // the flag (and the Validate button, and a queued Publish) up for good.
  private _latestValidationId = 0;

  /** Template uploads still in flight (`upload`), for `templateUploadsSettled`. */
  private _templateUploads = new Set<Promise<unknown>>();

  validationMode: ValidationMode = ValidationMode.Auto;

  private readonly _validationModePerBot = new Map<string, ValidationMode>();

  /**
   * Called when switching between bots/solutions. Saves the current
   * validation mode for the outgoing bot and restores the previously
   * saved mode for the incoming bot (defaults to Auto for unknown bots).
   * Does NOT show a toast — callers are responsible for UI feedback.
   */
  switchBotValidationMode = action(
    (
      prevBotId: string | undefined,
      nextBotId: string | undefined
    ): ValidationMode => {
      if (prevBotId) {
        if (this.validationMode === ValidationMode.Manual) {
          this._validationModePerBot.set(prevBotId, ValidationMode.Manual);
        } else {
          this._validationModePerBot.delete(prevBotId);
        }
      }

      const restoredMode = nextBotId
        ? (this._validationModePerBot.get(nextBotId) ?? ValidationMode.Auto)
        : ValidationMode.Auto;

      this.validationMode = restoredMode;
      this.hasUnvalidatedChangesInManualMode = false;

      return restoredMode;
    }
  );

  setValidationMode = action((newValidationMode: ValidationMode) => {
    this.validationMode = newValidationMode;

    if (newValidationMode === ValidationMode.Auto) {
      this.validateAndSync();
    } else {
      this.hasUnvalidatedChangesInManualMode = false;
    }

    const toastId = 'toast-automatic-validation';

    if (toast.isActive(toastId)) {
      updateToast({
        toastId,
        text: `Automatic validation is ${newValidationMode === ValidationMode.Auto ? 'ON' : 'OFF'}`,
      });
    } else {
      sendUserMessage({
        toastId,
        type: 'success',
        text: `Automatic validation is ${newValidationMode === ValidationMode.Auto ? 'ON' : 'OFF'}`,
        autoClose: false,
      });
    }
  });

  private _isKeyboardNav: boolean = false;

  get isKeyboardNav() {
    return this._isKeyboardNav;
  }

  setKeyboardNav = action((newKeyboardNavState: boolean) => {
    this._isKeyboardNav = newKeyboardNavState;
  });

  // True while the CURRENT keydown is a Shift+Arrow range extension
  // (PSTUDIO-2372). Set by table.tsx's capture-phase keydown — which runs
  // BEFORE table-nav moves focus — so cell.tsx's handleFocus/handleBlur can
  // skip their keyboard-nav selection reset and keep the range anchor alive.
  // Deliberately NOT the broader isShiftKeyBeingPressed: Shift+Tab and
  // Shift+Enter must keep their normal re-anchoring behavior, and the '*'
  // hotkeys tracker that maintains that flag runs at document level AFTER the
  // focus has already moved.
  private _isShiftArrowNav: boolean = false;

  get isShiftArrowNav() {
    return this._isShiftArrowNav;
  }

  setShiftArrowNav = action((newState: boolean) => {
    this._isShiftArrowNav = newState;
  });

  get hasUnsavedChanges() {
    return this.unsavedRowIds.size > 0;
  }

  /**
   * PSTUDIO-4342: what the beforeunload guard asks — an uncommitted edit, OR a
   * node write queued, waiting its turn behind another (_nodeWriteTail) or in
   * flight (every node write registers in one of the maps
   * `hasPendingNodeSaves` reads, single-row deletes included), OR a refused
   * write of the version on screen not repaired yet (`_unrepairedFailures`). A
   * write waiting its turn has not left the browser, so closing the tab then
   * would lose it silently.
   */
  get hasUnsavedWork(): boolean {
    return (
      this.hasUnsavedChanges ||
      this.hasPendingNodeSaves ||
      this._unrepairedFailures() > 0
    );
  }

  // Mark entire row as dirty (not individual cells)
  markRowDirty = action((rowId: string) => {
    this.unsavedRowIds.add(rowId);
  });

  // Clear specific row after saving
  clearRowDirty = action((rowId: string) => {
    this.unsavedRowIds.delete(rowId);
  });

  // Clear all unsaved changes
  clearAllUnsavedChanges = action(() => {
    this.unsavedRowIds.clear();
  });

  // Save all unsaved rows
  saveUnsavedChanges = action(() => {
    if (this.unsavedRowIds.size === 0) return;

    const rowsToSave = this.rows.filter((row) =>
      this.unsavedRowIds.has(row.id)
    );

    this.saveRowsToDatabase(rowsToSave);
    this.clearAllUnsavedChanges();
    this.validateAndSync();
  });

  errors: WorkflowError[] = [];
  pifErrors: WorkflowError[] = [];

  // errorsResource: 'api' | 'pif' = 'api';

  // setErrorsResource = action((newErrorsResource: 'api' | 'pif') => {
  //   this.errorsResource = newErrorsResource;
  //   this.errors = [];
  //   this.validateAndSync();
  // });

  cellBeingEdited?: CellBeingEdited;

  projectProStudioSettingId: string = '';

  // Track rows with unsaved changes (row-level, not cell-level)
  private unsavedRowIds = observable.set<string>();

  // get nodeHash() {
  //   const foo = hash(toJS(this.sortedRows));
  //   return foo;
  // }

  lastServerSync?: Date;
  lastServerSyncHash?: number;

  lastServerValidation?: Date;
  lastServerValidationHash?: number;

  lastServerValidationMessages?: string[];
  /** In manual validation mode: true when any cell was edited after the last validation. */
  hasUnvalidatedChangesInManualMode = false;

  /**
   * Why the last validation pass could not produce a verdict worth trusting —
   * `null` while the error lists ARE the latest verdict (PSTUDIO-4120).
   *
   * Before this existed, a pass that never reached `setErrors`/`setPifErrors`
   * simply left the previous lists standing, so "validation threw" and
   * "validation ran and found nothing new" were the same thing to the badge and
   * to `canPublish`. The lists are still left standing — blanking them would be
   * fail-OPEN in the other direction, a clean badge over an unchecked solution —
   * but they are now labelled stale, and the two readers act on the label:
   * `canPublish` refuses, and the popover says the pass did not complete.
   *
   * Written by `validate()` and by `validateAndSync`'s own catch (which sits
   * ABOVE it and swallows anything thrown before the pass starts), and cleared
   * by `clearMetadata` when the dataset it describes goes away.
   *
   * `validate()`'s no-op exits deliberately do NOT all behave alike, and the
   * list is exhaustive: the Manual-mode short-circuit leaves the flag untouched
   * (a skipped pass cannot make a previously-stale list fresh); the
   * mid-validation solution-switch exit leaves it untouched too, because its
   * answer belongs to a solution no longer on screen; and the empty-table exits
   * CLEAR it, because they write a verdict that is trivially true — no rows, no
   * errors.
   */
  validationFailure: ValidationFailureReason | null = null;
  lastServerSyncError?: string[];

  focusCellOnMount: {
    rowIndex: number | null;
    colId: string;
  } | null = null;

  hasPendingRowsToRender: boolean = false;

  private _isMetaKeyPressed: boolean = false;

  /** Tracks the in-flight GraphQL promise from the most recent saveBulkRowsToDatabase call. */
  private _inflightSavePromise: Promise<unknown> | null = null;
  /** Tracks the in-flight GraphQL promise from the most recent saveBulkRowOrdersToDatabase call. */
  private _inflightOrderSavePromise: Promise<unknown> | null = null;

  /**
   * PSTUDIO-4342: the tail of every node write issued so far — both save tracks
   * AND every node delete (`_deleteNodesInDb`, the deferred delete of
   * `_deleteRowsInternal`, `deleteRow`, the replace-paste). Each write starts
   * sending only once the previous one has settled (`_inNodeWriteOrder`), so
   * writes reach the backend in the order they were issued.
   *
   * Why it exists: since a batch is split into chunks (SAVE_REQUEST_BYTE_LIMIT),
   * its chunks are separate commits. Without the ordering, a write issued while
   * chunk 1 was in flight — the order request of the same gesture, the user's
   * next edit, or a delete — committed before chunk 2, which then put back the
   * older snapshot it carried (the full-row upsert writes `order` too) or
   * re-inserted a row that had just been deleted.
   *
   * The tail is cleared a couple of microtasks after the last write settles;
   * from then on a write is sent at once, in the same tick as before (one
   * issued inside those microtasks waits for that already-settled write, i.e.
   * the same couple of microtasks). The tail never rejects: a failed write
   * still lets the next one go.
   *
   * Limitations, stated rather than implied:
   * - It orders writes by when they were ISSUED (a queue flush, a delete
   *   gesture), not by when each row was snapshotted: a content batch that
   *   flushes after an order batch still writes the `order` its rows held when
   *   they were enqueued. Grouping is not such a case — it enqueues the members
   *   before it reorders them.
   * - The wait is bounded: if NO node write makes progress (no request of any
   *   kind answers) for NODE_WRITE_ORDER_WAIT_MS, the waiting write goes out
   *   anyway and a warning is logged; from then on the order between it and the
   *   stuck write is not guaranteed. A long batch that keeps answering never
   *   trips it.
   * - Serial writes cost latency: a write waits for everything issued before
   *   it, so the window in which its rows are in `pendingSaveRowIds` (a coworker
   *   merge skips them), `hasPendingNodeSaves` is true (a cross-tab reload is
   *   held back) and `waitForPendingSaves` waits (publish) is the sum of the
   *   writes ahead of it rather than one round trip.
   * - A write waiting here has not left the browser: closing the tab in that
   *   window loses it. Every node write is registered from the moment it is
   *   issued — the two save tracks in `_inflight*Batches`, every delete in
   *   `_pendingDeletes` (`_deleteOneNode` for the two single-row deletes) —
   *   so `hasUnsavedWork` (the beforeunload guard) and `waitForPendingSaves`
   *   (publish) both see it. The guard is mounted with the Workflows table
   *   only: a tab closed on another page while writes still wait is not warned.
   */
  private _nodeWriteTail: Promise<void> | null = null;

  /** See _nodeWriteTail: how long a write waits without ANY node write answering. */
  static readonly NODE_WRITE_ORDER_WAIT_MS = 30_000;

  /** Re-arms every waiting bound; called whenever a node-write request answers. */
  private _nodeWriteProgressListeners = new Set<() => void>();
  private _noteNodeWriteProgress() {
    this._nodeWriteProgressListeners.forEach((rearm) => rearm());
  }

  /**
   * Resolves when `previous` settles, or once NODE_WRITE_ORDER_WAIT_MS has
   * passed with no node-write request answering — whichever is first.
   */
  private _waitForPreviousNodeWrite(previous: Promise<void>): Promise<void> {
    return new Promise<void>((resolve) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const stop = () => {
        clearTimeout(timer);
        this._nodeWriteProgressListeners.delete(arm);
      };
      const arm = () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          stop();
          console.warn(
            '[TableStore] a node write waited %dms with no node write answering; sending it without the previous one having settled, so their order is not guaranteed',
            TableStore.NODE_WRITE_ORDER_WAIT_MS
          );
          resolve();
        }, TableStore.NODE_WRITE_ORDER_WAIT_MS);
      };
      this._nodeWriteProgressListeners.add(arm);
      arm();
      void previous.then(() => {
        stop();
        resolve();
      });
    });
  }

  /** Appends `write` to _nodeWriteTail and clears the tail once it is the last. */
  private _chainNodeWrite(write: Promise<unknown>) {
    const tail = write.then(
      () => undefined,
      () => undefined
    );
    this._nodeWriteTail = tail;
    void tail.then(() => {
      if (this._nodeWriteTail === tail) this._nodeWriteTail = null;
    });
  }

  /**
   * Runs `send` after every node write issued before it (see _nodeWriteTail)
   * and makes it the new tail. With nothing in flight `send` starts in this
   * tick. The returned promise is `send`'s own, rejections included.
   */
  private _inNodeWriteOrder<T>(send: () => Promise<T>): Promise<T> {
    const previous = this._nodeWriteTail;
    const run = previous
      ? this._waitForPreviousNodeWrite(previous).then(send)
      : send();
    this._chainNodeWrite(run);
    return run;
  }

  /**
   * PSTUDIO-4342: rows (`_failedNodeSaves`) and order tuples
   * (`_failedNodeOrderSaves`) whose save was refused or never sent because an
   * earlier chunk of the same batch was refused, keyed by row id, valued by the
   * solution version they were written for.
   *
   * The pending-saves gate only holds a save while its promise lives, so after
   * the "Autosave Error" toast a publish found nothing to refuse and compiled a
   * group whose header and members had landed in different chunks — half of it
   * saved. `waitForPendingSaves` re-sends these from the CURRENT rows instead
   * (`_requeueFailedNodeSaves`): the retry repairs the state or fails again and
   * refuses the publish. An entry also leaves the map when a later save of the
   * same row succeeds.
   *
   * What counts as UNREPAIRED is one rule, `_refusedLiveRowIds`, read by
   * the toast, by `hasUnsavedWork` and by the re-send alike: an entry of the
   * version on screen whose row is still live there. The loss report counts
   * the same LIVENESS without the version (see _dropRefusedWrites).
   * A row the user deleted (or an insert they undid) has nothing left to
   * repair. While an unrepaired entry remains, nothing dismisses the error
   * toast (`_reconcileNodeWriteToasts`) — it is the user's only sign that
   * data did not reach the database.
   *
   * An entry is good for ONE visit of the rows, because what it repairs is
   * the user's value in `rows`, not the row id. The two replacements — an
   * applied `processVersionData` (a re-entry, the fetch after a version
   * change, a non-silent reload such as the CSV import, the backend-data
   * subscriptions) and `clearTable` (the identity reaction's other branch) —
   * drop every entry (`_dropRefusedWrites`): kept, an entry re-sent the
   * SERVER's copy and read as repaired (measured). Dropping one is a LOSS,
   * and it is said so in a persistent warning that counts what was dropped.
   * A refusal that ANSWERS after its visit ended (a save still in flight
   * across a re-entry) is not recorded either — it is reported as a loss
   * the same way (`_refusalVisit`).
   *
   * A merge (`mergeVersionData`: a coworker's push, and the same user's
   * SILENT cross-tab reload) is not a replacement. It leaves a refused row's
   * position alone (Re-sort), and the fields of a refused CONTENT save
   * (Updates), so the re-send still writes the user's value; an order refusal
   * re-sends `{id, order}` alone and does not shield the fields, and a kept
   * row's group link the merge made dangling takes the server's. A refused row absent from the server's snapshot is
   * removed only if the server had KNOWN it (`_serverKnownIds`) — a coworker
   * deleted it, re-sending would re-insert it, and its entry goes as a
   * reported loss; a refused INSERT the server never had stays, for the
   * publish to re-send. The refused row is deliberately NOT in
   * `pendingSaveRowIds`: that set holds a row for one round trip
   * (KEYBOARD_CLIPBOARD_BEHAVIOR #14), this one for as long as the refusal
   * stands.
   */
  private _failedNodeSaves = new Map<string, string>();
  private _failedNodeOrderSaves = new Map<string, string>();

  /**
   * PSTUDIO-4342: the refused saves of rows that are too large to save at all —
   * a single-row chunk past SAVE_REQUEST_BYTE_LIMIT that the authorization hook
   * refused — with the size that was refused. They are ordinary entries of
   * _failedNodeSaves (re-sent, publish refused), kept apart only for what the
   * user is TOLD: the generic "Autosave Error" cannot say what to do, and
   * retrying cannot help; shortening the node can. A row leaves it the moment
   * it is sent again, so a later ordinary refusal reads as an ordinary one,
   * and when it is no longer an unrepaired refusal on screen — saved, deleted
   * on any path, removed by a merge, or replaced (_reconcileNodeWriteToasts).
   */
  private _tooLargeRows = new Map<string, number>();

  /**
   * PSTUDIO-4342: ids the server is known to hold for the rows on screen. In:
   * every id of an applied snapshot (`processVersionData`), every id a merge
   * received (`mergeVersionData`), every row of a content chunk that landed
   * and every tuple of an order chunk that landed (that upsert INSERTs a
   * missing id — see `saveBulkRowOrdersToDatabase`). Out: an id a merge no
   * longer received, an id a delete removed (`_sendNodeDeleteChunks`,
   * `_deleteOneNode`), and all of them when the rows are replaced
   * (`_dropRefusedWrites`; the snapshot that follows re-fills it). A refused
   * row absent from a later snapshot was deleted there only if it is in here;
   * otherwise it is an insert that never landed. What it cannot see: a row a
   * coworker created and deleted between two snapshots this tab received.
   */
  private _serverKnownIds = new Set<string>();

  /**
   * PSTUDIO-4342: the visit the refused-write records belong to. Bumped by
   * every `_dropRefusedWrites`; a save pins it when it starts, and a refusal
   * answering after it moved is a loss, not a record (see _failedNodeSaves).
   */
  private _refusalVisit = 0;

  /**
   * PSTUDIO-4342: what the "Unsaved changes discarded" warning reports, summed
   * while it stays up — a second loss UPDATES it rather than being dropped —
   * and reset when the user closes it. Sets of row ids, not counters: the
   * same row can be counted by a replacement AND by a refusal of its save
   * that answers after it, or by both save tracks, and it is one loss.
   */
  private _lost = {
    edits: new Set<string>(),
    deletes: new Set<string>(),
    coworker: new Set<string>(),
  };

  /** A fresh `updateId` for each raise of a fixed-id toast that is not up. */
  private _toastSeq = 0;

  /**
   * The text the "Node too large to save" toast was last raised with, or null
   * once it is down — dismissed here, or closed by the user. It lets
   * `_reconcileNodeWriteToasts` re-word the toast when its list shrinks
   * without raising again one the user closed.
   */
  private _tooLargeShown: string | null = null;

  /**
   * True while `id` is a row on screen that is not claimed by a delete — the
   * conjunction (`isClaimedByDelete`: marked AND hidden) the validation gate
   * applies to `rows`, because the mark alone outlives a row a merge or an
   * undo brought back. For a MARKED row it is read off the element of `rows`,
   * not off `rowMap`: the delete flips `hidden` on the `rows` element, and
   * after a load the two hold different observable clones of the row
   * (measured, mobx 6.15), so the `rowMap` copy read every row inside the
   * soft-delete window as live. It reads the rows of the version on screen,
   * so every caller first keeps only the entries recorded for that version.
   */
  private _isLiveRow(id: string): boolean {
    if (!this.rowMap.has(id)) return false;
    if (!this.pendingDeletionRowIds.has(id)) return true;
    const row = this.rows.find((r) => r.id === id);
    return !!row && !isClaimedByDelete(row, this.pendingDeletionRowIds);
  }

  /** How many rows carry an unrepaired refused save (_refusedLiveRowIds). */
  private _unrepairedSaves(): number {
    return this._refusedLiveRowIds().size;
  }

  /**
   * Every refused write of the version on screen still outstanding: the saves
   * above, plus that version's failed deletes (_failedNodeDeletes) — a failed
   * delete is outstanding whether or not its row is on screen, since the row
   * is alive in the database.
   */
  private _unrepairedFailures(): number {
    const onScreen = this.solutionVersionId;
    let count = this._unrepairedSaves();
    for (const version of this._failedNodeDeletes.values()) {
      if (version === onScreen) count += 1;
    }
    return count;
  }

  /**
   * Takes down — never raises — what the node-write error toasts no longer
   * have standing to say, judged from the STATE (`_refusedLiveRowIds`), not
   * from the event that changed it. Called by: both save tracks on success,
   * the content track on a refusal (the order track's refusal raises
   * `'saved'` itself and never touches `_tooLargeRows`), the three
   * column-setting saves, `_dropRefusedWrites`, the merge's Removes when it
   * removed a refused row, and the three node-delete paths
   * (`_deleteRowsInternal`, `_deleteOneNode` — that is `deleteRow` and the
   * single-row replace-paste —, and `_deleteNodesInDb` — undo/redo and the
   * rest). NOT by `_requeueFailedNodeSaves`, which relies on the re-send it
   * issues to call it; an order entry whose row has no numeric `order` is
   * dropped there without a re-send, so `'saved'` can outlive it until the
   * next write. NOT by a merge's Adds or Re-sort either, so a too-large
   * toast that names a node by its ROW number can lag a coworker's reorder
   * until the next write.
   *
   * - A too-large entry whose row is no longer an unrepaired refusal on screen
   *   is forgotten.
   * - `'saved'` — the id "Autosave Error" and "Reorder Save Error" share with
   *   the success path — goes once no refusal OTHER than a too-large one
   *   stands: a write that did not repair the refusal has no standing to take
   *   away the only sign of it, and a too-large node has its own toast, which
   *   says what to do. A failed DELETE does not hold it either: a delete
   *   raises its own toast.
   * - The too-large toast, while up, is re-worded when its list changed and
   *   taken down when the list is empty.
   */
  private _reconcileNodeWriteToasts(): void {
    const refused = this._refusedLiveRowIds();
    for (const id of [...this._tooLargeRows.keys()]) {
      if (!refused.has(id)) this._tooLargeRows.delete(id);
    }
    let others = 0;
    for (const id of refused) if (!this._tooLargeRows.has(id)) others += 1;
    if (others === 0) dismissToasts('saved');
    if (this._tooLargeShown === null) return;
    const text = this._tooLargeText();
    if (text === null) {
      this._tooLargeShown = null;
      dismissToasts('node-too-large');
    } else if (text !== this._tooLargeShown) {
      this._showTooLarge();
    }
  }

  /**
   * Raises one of the fixed-id node-write toasts so that it is SEEN, without
   * taking keyboard focus away from one that is already up. Measured against
   * react-toastify 11.0.5 in Chrome (2026-10-16):
   * - a toast that is UP is updated (`updateToast`), which keeps its DOM node,
   *   so focus on its close button stays put; a changed text is announced by
   *   the live region, an unchanged one is not repeated. It passes `Slide`,
   *   the transition `sendToast` showed it with: an update naming another
   *   transition (`updateToast`'s default is `Flip`) remounted the node and
   *   dropped focus to <body>, as a fresh `toast()` call does;
   * - a toast that is NOT up is raised with a fresh `updateId`. That includes
   *   one still playing its exit animation, during which `toast.isActive` is
   *   already false while react-toastify still holds its id: without an
   *   `updateId` such a raise was dropped, with one it was shown and stayed.
   *   `toast.update` in that window was lost.
   */
  private _raiseNodeWriteToast(options: {
    toastId:
      | 'saved'
      | 'node-too-large'
      | 'refused-writes-lost'
      | 'delete-failed'
      | 'undo-delete-failed';
    type: 'error' | 'warning';
    header: string;
    text: string;
    onClose?: () => void;
  }): void {
    // No `closeButton` override: `true` replaced the design system's close
    // button (a translated name) with react-toastify's own — named "close" in
    // every locale and drawn at 0.3 opacity, ~2.1:1 against the toast — and
    // these toasts do not close by themselves (measured in Chrome, review
    // round 11). Left out, the DS button is drawn and an update keeps it.
    const shown = { ...options, transition: Slide };
    if (toast.isActive(options.toastId)) {
      // `delay: undefined`, not omitted and not 0: `toast.update` defaults to
      // `delay: 100` (react-toastify 11.0.5), and a dismiss inside those 100 ms
      // fired the old `onClose`, then the deferred update brought the toast
      // back with the new text until the exit animation removed it — its own
      // `onClose` never fired, and a refusal raised in that window went into
      // the update and was lost (measured in Chrome, review round 10). An
      // explicit undefined overrides the default and applies it at once; 0 is
      // still a timer.
      updateToast({ ...shown, delay: undefined });
      return;
    }
    this._toastSeq += 1;
    sendToast({ ...shown, updateId: this._toastSeq });
  }

  /** The `'saved'` error for a refused node save (_raiseNodeWriteToast). */
  private _showSaveRefusal(header: string, text: string): void {
    this._raiseNodeWriteToast({
      toastId: 'saved',
      type: 'error',
      header,
      text,
    });
  }

  /** The "Node too large to save" error (_tooLargeText), raised or updated. */
  private _showTooLarge(): void {
    const text = this._tooLargeText();
    if (text === null) return;
    this._tooLargeShown = text;
    this._raiseNodeWriteToast({
      toastId: 'node-too-large',
      type: 'error',
      header: nodeWriteToastText('too_large_header'),
      text,
      onClose: () => {
        this._tooLargeShown = null;
      },
    });
  }

  /**
   * The text naming EVERY node that stands refused for its size — not only
   * the last batch's — each with its own size: by its number, or, when it has
   * none, by the row number the grid's index column shows for it
   * (`displayNumberById`: group headers skipped, the pending tail counted). A
   * row with no display number is named without one rather than as "row 0".
   * Null when no such node is left.
   */
  private _tooLargeText(): string | null {
    if (!this._tooLargeRows.size) return null;
    const rowNumbers = this.displayNumberById;
    const entries: string[] = [];
    for (const [id, bytes] of this._tooLargeRows) {
      const size = Math.ceil(bytes / 1024);
      const number = this.rowMap.get(id)?.nodeNumber;
      if (number !== null && number !== undefined && String(number) !== '') {
        entries.push(
          nodeWriteToastText('too_large_node_numbered', {
            number: String(number),
            size,
          })
        );
        continue;
      }
      const row = rowNumbers.get(id);
      entries.push(
        row
          ? nodeWriteToastText('too_large_node_unnumbered', { row, size })
          : nodeWriteToastText('too_large_node_unplaced', { size })
      );
    }
    return nodeWriteToastText('too_large_text', {
      count: entries.length,
      nodes: formatLocaleList(entries, i18n.language || 'en', 'long'),
    });
  }

  /**
   * Re-enqueues every refused save of the version on screen from the current
   * rows, so the flush that follows in `waitForPendingSaves` re-sends them.
   * Every entry of this version leaves the map: a live one is re-sent (and is
   * recorded again if it is refused again), one whose row is gone or on its
   * way out has nothing left to write. An entry of another version cannot be
   * repaired from here; the next replacement drops it.
   */
  private _requeueFailedNodeSaves(): void {
    const version = this.solutionVersionId;
    const live = this._refusedLiveRowIds();
    const takeLive = (map: Map<string, string>): TableRow[] => {
      const rows: TableRow[] = [];
      for (const [id, failedFor] of [...map]) {
        if (failedFor !== version) continue;
        map.delete(id);
        const row = live.has(id) ? this.rowMap.get(id) : undefined;
        if (row) rows.push(row);
      }
      return rows;
    };
    const rows = takeLive(this._failedNodeSaves);
    if (rows.length) {
      this.saveRowsToDatabase(
        rows.map((row) => ({ ...row })),
        { skipValidation: true }
      );
    }
    const orders: RowOrderUpdate[] = takeLive(this._failedNodeOrderSaves)
      .filter((row) => typeof row.order === 'number')
      .map((row) => ({
        id: row.id,
        order: row.order as number,
        solutionVersionId: version,
      }));
    if (orders.length) this.saveRowOrdersToDatabase(orders);
  }

  /** Ids of the live rows of the version on screen with a refused save. */
  private _refusedLiveRowIds(): Set<string> {
    const onScreen = this.solutionVersionId;
    const ids = new Set<string>();
    for (const map of [this._failedNodeSaves, this._failedNodeOrderSaves]) {
      for (const [id, version] of map) {
        if (version === onScreen && this._isLiveRow(id)) ids.add(id);
      }
    }
    return ids;
  }

  /**
   * Drops every refused-write record and ends the visit they belong to: the
   * rows are being replaced wholesale, so what they would re-send is no longer
   * the value that was refused (see _failedNodeSaves). Called BEFORE the rows
   * go, so the loss is counted against them: an entry whose row is still live
   * (`_isLiveRow` — a row the user deleted is not a loss). Not by the version
   * on screen: on a version change the identity has already moved when this
   * runs, while the rows being replaced are still the old version's. The
   * failed deletes go too — a row such a delete left alive in the database
   * comes back with the reload, visible, instead of being deleted from under
   * the user later — and are reported.
   */
  private _dropRefusedWrites(): void {
    // A refused row the user edited again has a newer write of its own on the
    // way, and that write — not this replacement — decides whether the value
    // is lost: it lands, and nothing is; it is refused, and the late refusal
    // is reported (`_refusalVisit`). So queued writes are sent first, which
    // pins them to the visit that is ending (`saveBulk*` read it before their
    // first await), and a row with a write in flight that would repair its
    // refusal is not counted here (review round 11: the warning called a
    // change discarded that then landed). A full-row write carries `order`
    // too, so it repairs an ORDER refusal as well (saveBulkRowsToDatabase);
    // an order tuple repairs only an order refusal (review round 12).
    saveRowsToDatabaseQueue.flush();
    saveRowOrdersToDatabaseQueue.flush();
    const contentInFlight = this._idsInFlight(this._inflightSaveBatches);
    const orderInFlight = new Set([
      ...contentInFlight,
      ...this._idsInFlight(this._inflightOrderSaveBatches),
    ]);
    const edits = new Set<string>();
    for (const [map, pending] of [
      [this._failedNodeSaves, contentInFlight],
      [this._failedNodeOrderSaves, orderInFlight],
    ] as const) {
      for (const id of map.keys()) {
        if (this._isLiveRow(id) && !pending.has(id)) edits.add(id);
      }
    }
    const deletes = [...this._failedNodeDeletes.keys()];
    this._failedNodeSaves.clear();
    this._failedNodeOrderSaves.clear();
    this._failedNodeDeletes.clear();
    // Emptied, not kept: the snapshot that follows re-fills it, and an id from
    // the rows being replaced must not outlive them (it would make a row the
    // next visit inserts under that id read as known). With UUIDs that never
    // happens, which is why no test can tell this line from its absence.
    this._serverKnownIds.clear();
    this._refusalVisit += 1;
    this._tooLargeRows.clear();
    // Nothing is left to repair, so neither error toast may keep saying so;
    // the warning below is what says what was given up.
    this._reconcileNodeWriteToasts();
    this._reportRefusedWritesLost({ edits: [...edits], deletes });
  }

  /**
   * The one sign that refused writes were given up rather than repaired:
   * persistent, under its own id, and SUMMED while it stays up — a later loss
   * updates it instead of being dropped (_raiseNodeWriteToast) — until the
   * user closes it.
   */
  private _reportRefusedWritesLost(lost: {
    edits?: string[];
    deletes?: string[];
    coworker?: string[];
  }): void {
    const before =
      this._lost.edits.size +
      this._lost.deletes.size +
      this._lost.coworker.size;
    lost.edits?.forEach((id) => this._lost.edits.add(id));
    lost.deletes?.forEach((id) => this._lost.deletes.add(id));
    lost.coworker?.forEach((id) => this._lost.coworker.add(id));
    const after =
      this._lost.edits.size +
      this._lost.deletes.size +
      this._lost.coworker.size;
    if (after === before) return;
    const parts: string[] = [];
    for (const [key, ids] of [
      ['refused_lost_edits', this._lost.edits],
      ['refused_lost_coworker', this._lost.coworker],
      ['refused_lost_deletes', this._lost.deletes],
    ] as const) {
      if (ids.size) parts.push(nodeWriteToastText(key, { count: ids.size }));
    }
    this._raiseNodeWriteToast({
      toastId: 'refused-writes-lost',
      type: 'warning',
      header: nodeWriteToastText('refused_lost_header'),
      text: parts.join(' '),
      onClose: () => {
        this._lost = {
          edits: new Set<string>(),
          deletes: new Set<string>(),
          coworker: new Set<string>(),
        };
      },
    });
  }

  /**
   * A refused write that answered after its visit ended (`_refusalVisit`):
   * the rows now hold the server's copy, so it is a loss to report — the
   * refused chunk and every chunk it stopped — not a record to re-send. The
   * one rule both save tracks apply.
   */
  private _reportLateRefusal(late: {
    lostIds: string[];
    error: { message: string } | undefined;
    track: 'autosave-nodes' | 'reorder-nodes';
    solutionVersionId: string;
  }): void {
    const { lostIds, error, track, solutionVersionId } = late;
    this._reportRefusedWritesLost({ edits: lostIds });
    reportStoreFailure(error, track, {
      solutionVersionId,
      refusedAfterVisitEnded: new Set(lostIds).size,
    });
  }

  /**
   * PSTUDIO-3929 (a): the row ids in flight for each save track, keyed by the
   * save's own `outer` promise, mirrored alongside the inflight promises above.
   * mergeVersionData reads these (plus each queue's pendingIds) to skip
   * clobbering a row whose local write hasn't committed yet — the inflight
   * promise alone doesn't say WHICH rows it covers.
   *
   * Keyed by promise (NOT a single Set) so two OVERLAPPING saves each keep
   * their own id set: a second save starting while the first is still in flight
   * must not drop the first's rows from the pending set (that reopened the very
   * clobber window this guards — a stale coworker merge landing between the
   * second save's start and the first's completion). Each entry is deleted when
   * its own promise settles, so the union is always exactly the rows with an
   * uncommitted write.
   */
  private _inflightSaveBatches = new Map<Promise<unknown>, Set<string>>();
  private _inflightOrderSaveBatches = new Map<Promise<unknown>, Set<string>>();

  /**
   * PSTUDIO-4103: the THIRD write track — `_deleteRowsInternal`'s deferred DB
   * delete. Keyed by that delete's own promise (which resolves when the
   * GraphQL call settles), valued by the function that runs its deferred body
   * ahead of the browser. Registered at the GESTURE, deleted when the promise
   * settles, so two overlapping deletes are both visible and each clears only
   * its own entry.
   *
   * A Map<promise, flush> rather than the Map<promise, Set<rowId>> shape the
   * two save tracks use, because these row ids deliberately do NOT join
   * `pendingSaveRowIds` — see the comment there. The value carries the flush
   * instead, which is what keeps `waitForPendingSaves` from parking on an idle
   * callback that has not fired.
   */
  private _pendingDeletes = new Map<Promise<unknown>, () => void>();

  /**
   * PSTUDIO-3610: synchronous "are local node writes still queued/in flight?"
   * indicator for the cross-tab reload guard. Mirrors what waitForPendingSaves
   * awaits — a reload snapshot taken before local queued writes commit would
   * clobber them via rows.replace (one-shot replay race). Covers both save
   * tracks: the content-save queue + its in-flight promise, and the
   * order-save queue + its in-flight promise. Also consults the overlap-safe
   * `_inflightSaveBatches`/`_inflightOrderSaveBatches` maps so two overlapping
   * saves can't under-report: the single-slot promise is last-writer-wins and a
   * later save settling first would null it while an earlier save is still in
   * flight — the per-promise batch maps keep every uncommitted write visible.
   *
   * PSTUDIO-4103: plus the deferred-delete track. A delete is pending from the
   * gesture (before its idle callback has even fired) until the GraphQL delete
   * settles — a reload or a publish inside that window would read rows the
   * server has not been told to remove yet.
   */
  get hasPendingNodeSaves(): boolean {
    return (
      saveRowsToDatabaseQueue.size > 0 ||
      saveRowOrdersToDatabaseQueue.size > 0 ||
      this._inflightSavePromise !== null ||
      this._inflightOrderSavePromise !== null ||
      this._inflightSaveBatches.size > 0 ||
      this._inflightOrderSaveBatches.size > 0 ||
      this._pendingDeletes.size > 0
    );
  }

  /**
   * PSTUDIO-3929 (a): row ids with a not-yet-committed local write — queued OR
   * in flight, across BOTH the content-save and order-save tracks (the same
   * content/order sources hasPendingNodeSaves ORs, but resolved to the row
   * ids — the count is deliberately not restated here, it drifted twice). A
   * coworker subscription push merges server state over live rows; without
   * this, mergeVersionData would overwrite a cell the user JUST pasted but
   * whose save is still queued/in flight, so the content vanishes with no
   * reload. mergeVersionData skips exactly these rows — symmetric to the
   * cross-tab reload guard (PSTUDIO-3610), and narrower than blocking the
   * whole merge so a coworker's edits to OTHER rows still apply live.
   *
   * PSTUDIO-4103: the deferred-delete track is deliberately NOT included, even
   * though it joins `hasPendingNodeSaves`. This set exists so a merge PRESERVES
   * a row whose local write has not committed — and these rows are being
   * REMOVED (they leave `this.rows` in the idle callback). Listing them would
   * ask the merge to keep alive exactly the rows we are deleting, and would
   * shield them from the Removes phase once the server snapshot has dropped
   * them. The two questions differ: "is a write still in flight?" (yes —
   * publish must wait) versus "must this row survive a merge?" (no — it is on
   * its way out).
   *
   * PSTUDIO-4342: a row whose save was REFUSED is deliberately not here
   * either. Holding it would stretch this window from one round trip to "until
   * the next publish", and would keep a row a coworker deleted alive for the
   * re-send to INSERT again. The merge protects a refused row's fields and
   * position itself, and lets a coworker's delete through (see
   * _failedNodeSaves).
   */
  pendingSaveRowIds(): Set<string> {
    const ids = new Set<string>();
    for (const id of saveRowsToDatabaseQueue.pendingIds()) ids.add(id);
    for (const id of saveRowOrdersToDatabaseQueue.pendingIds()) ids.add(id);
    for (const id of this._idsInFlight(this._inflightSaveBatches)) ids.add(id);
    for (const id of this._idsInFlight(this._inflightOrderSaveBatches)) {
      ids.add(id);
    }
    return ids;
  }

  /** The row ids of the batches of one save track that are in flight. */
  private _idsInFlight(batches: Map<Promise<unknown>, Set<string>>) {
    const ids = new Set<string>();
    for (const batch of batches.values()) {
      for (const id of batch) ids.add(id);
    }
    return ids;
  }

  isCellBeingEdited: boolean = false;
  pendingEditCellId: string | null = null;
  editingCellId: string | null = null;
  editingRowId: string | null = null;
  editingColumnId: string | null = null;
  // PSTUDIO-1433: which cell the EXPANDED CELL VIEW panel is editing, when it
  // is. The panel keeps the edit's payload (baseline + undo snapshot) in a React
  // ref, but the fact that an edit is open has to live HERE, because every
  // cross-cutting guard asks the store — and the panel sets none of the fields
  // above (`toggleCellInput` does, and the panel's own focus handler then
  // commits the inline edit, clearing them). Without this the panel's draft was
  // invisible to the coworker merge, to the subscription reload and to
  // `hasActiveCellEdit`, so all three happily overwrote it — and the panel's
  // later flush then wrote the OTHER party's value back as if the user had
  // typed it, undoing their undo. Registered on the first draft write, cleared
  // by the commit that consumes the session (and by `cancelCellEditing`, which
  // is how undo/redo tears an edit down).
  panelEditingRowId: string | null = null;
  panelEditingColumnId: string | null = null;
  // PSTUDIO-1433: bumped when something should close every open row/cell
  // context menu — any event that would otherwise unmount a row while its
  // portal-hosted dropdown is still projected (the `removeChild` crash).
  // PSTUDIO-4023: the raisers are a table scroll, the remote merge's remove
  // phase, `clearTable`, `undo()`/`redo()`, the search input, a version reload,
  // and the two writers of a subRow's `hidden` flag (`_applyGroupVisibility`
  // and `applyGroupCollapseVisibility`); the canonical list lives in
  // `.claude/rules/cross-cutting-invariants.md`, row `context-menu close`.
  // A single counter here replaces a per-cell scroll listener: the
  // table is virtualized with ~24 columns × ~50 rows, so a hook in every cell
  // meant ~1200 subscriptions being created and torn down ON the scroll path
  // this is trying to keep cheap.
  contextMenuCloseNonce: number = 0;
  // How many row/cell context menus are currently mounted-and-not-closed. The
  // table subscribes to scroll ONLY while this is non-zero, so the virtualized
  // scroll path carries no listener at all in the common case.
  openContextMenuCount: number = 0;

  // Latched so a SERIES of scroll events raises the signal once. A trackpad
  // flick delivers 6-12 `scroll` events inside the menu's own close window
  // (50-100ms), and every nonce bump re-renders each observer that reads it —
  // on the scroll path, the one place this whole mechanism promised to keep
  // cheap. Not observable: nothing renders from it.
  contextMenuCloseRequested = false;

  /**
   * The cell an editor currently holds open, as ONE pair — the inline grid
   * editor if it has both ids, else the Expanded Cell View's panel session.
   *
   * Resolving row and column with two independent `??` could mix them: an
   * inline row with a panel column names a cell nobody is editing, so the guard
   * would protect the wrong cell AND leave the real draft open to be
   * overwritten. Only one editor is ever open, so the pair is chosen together.
   */
  activeEditCell(): { rowId: string | null; columnId: string | null } {
    if (this.editingRowId && this.editingColumnId) {
      return { rowId: this.editingRowId, columnId: this.editingColumnId };
    }
    return {
      rowId: this.panelEditingRowId,
      columnId: this.panelEditingColumnId,
    };
  }

  registerOpenContextMenu() {
    this.openContextMenuCount += 1;
    // A newly opened menu re-arms the signal: the previous series is over, and
    // this menu has its own close to ask for.
    this.contextMenuCloseRequested = false;
  }

  unregisterOpenContextMenu() {
    if (this.openContextMenuCount > 0) this.openContextMenuCount -= 1;
  }

  requestCloseAllContextMenus() {
    if (this.openContextMenuCount === 0) return;
    if (this.contextMenuCloseRequested) return;
    this.contextMenuCloseRequested = true;
    this.contextMenuCloseNonce += 1;
  }
  // PSTUDIO-2821: in-progress (uncommitted) raw value of the ACTIVE editing
  // cell. Persisted here — not only in the uncontrolled DOM control — so a
  // virtualization unmount (the editing row scrolled out of the rendered
  // window) does not lose the user's typing; the cell rehydrates from this on
  // remount. Non-reactive: nothing reads it in render, it is set per keystroke.
  draftCellValue: string | null = null;
  // PSTUDIO-3624: OBSERVABLE mirror of draftCellValue, read ONLY by the Expanded
  // Cell View panel so it live-updates while a cell is edited inline. Kept
  // separate from (not a replacement for) draftCellValue so the table — a
  // top-level observer that never reads this field — does NOT re-render per
  // keystroke, preserving PSTUDIO-2821's perf goal. Set with an equality guard
  // (setDraftCellValue) and cleared on edit teardown (_clearCellEditingState).
  liveCellDraft: string | null = null;
  // PSTUDIO-2821: how the NEXT programmatic blur of the active editor should
  // close it. Tab and Esc blur the control BEFORE focus lands on the next
  // element, so the blur handler cannot tell from relatedTarget whether it was
  // an explicit commit/cancel — these set the intent first. Cleared after use.
  cellCloseIntent: 'commit' | 'cancel' | null = null;
  pendingInitialChar: string | null = null;
  // PSTUDIO-1433: the cell (`${rowId}-${columnId}`) the pendingInitialChar was
  // typed into. Set ONLY on the JSON-capable path, where the character is applied
  // by the Expanded Cell View panel (no inline control exists to inject it into),
  // so the seed is ADDRESSED: a character whose panel never opened is ignored
  // instead of firing into an unrelated cell on a later auto-open. Null on the
  // inline (rAF-injection) path.
  pendingInitialCharCellId: string | null = null;

  // Project Bobcat (Resource Library drag-and-drop): the Resource Library
  // item currently being dragged from the side panel onto a table row. Set on
  // the card's native `dragstart`, read by table.tsx's `drop` handler, and
  // cleared on drop/dragend. Deliberately separate from `dragSession` below —
  // that one is @dnd-kit's row-reorder state; this is a plain field because
  // the drag source (the panel) lives outside the @dnd-kit provider entirely.
  // Carries `name` alongside `data` so the drop handler can label the
  // sub-group it wraps the dropped nodes in.
  resourceLibDragData: { data: TableNode[]; name: string } | null = null;

  setResourceLibDragData = action(
    (drag: { data: TableNode[]; name: string } | null) => {
      this.resourceLibDragData = drag;
    }
  );

  /**
   * Whether the Resource Library panel is open. In the store rather than
   * Table View's own state so the Build stage's canvas and table open the SAME
   * panel from their own entry points, and it stays open across the switch.
   */
  resourceLibPanelOpen = false;

  setResourceLibPanelOpen = action((open: boolean) => {
    this.resourceLibPanelOpen = open;
  });

  /**
   * Whether Build's Actions panel is open — the commands the solution's
   * action nodes run, and its scripts and config files. In the store for the
   * same reason as `resourceLibPanelOpen`: the Build bar opens it over either
   * view, and the old `/actions` route opens it on arrival.
   */
  actionsPanelOpen = false;

  setActionsPanelOpen = action((open: boolean) => {
    this.actionsPanelOpen = open;
    if (open) {
      this.explainAgentId = null;
      this.healthPanelOpen = false;
      this.suggestAgentsPanelOpen = false;
    }
  });

  /**
   * Build's Health panel — the rule-based checks in
   * `stores/utils/solution-health.ts`. One side panel at a time: opening it
   * closes Actions, and the other way round.
   */
  healthPanelOpen = false;

  setHealthPanelOpen = action((open: boolean) => {
    this.healthPanelOpen = open;
    if (open) {
      this.explainAgentId = null;
      this.actionsPanelOpen = false;
      this.suggestAgentsPanelOpen = false;
    }
  });

  /**
   * The canvas's "Explain this agent" panel: the agent it's open for, or null.
   * One side panel at a time.
   */
  explainAgentId: string | null = null;

  setExplainAgentId = action((agentId: string | null) => {
    this.explainAgentId = agentId;
    if (agentId) {
      this.actionsPanelOpen = false;
      this.healthPanelOpen = false;
      this.suggestAgentsPanelOpen = false;
    }
  });

  /**
   * The canvas's "Suggest agents" panel — groups for a solution's ungrouped
   * nodes, from `stores/utils/suggest-agents.ts`. One side panel at a time.
   */
  suggestAgentsPanelOpen = false;

  setSuggestAgentsPanelOpen = action((open: boolean) => {
    this.suggestAgentsPanelOpen = open;
    if (open) {
      this.explainAgentId = null;
      this.actionsPanelOpen = false;
      this.healthPanelOpen = false;
    }
  });

  /**
   * Create the agents a designer accepted from "Suggest agents": one group
   * per agent, through the CSV-import grouping path (`applyImportedGrouping`),
   * so it is one undoable step, persisted like any grouping. Nodes already in
   * a group are left where they are. Returns how many agents were created.
   */
  applySuggestedAgents = action(
    (agents: { name: string; rowIds: string[] }[]): number => {
      // PSTUDIO-4632: never mid-publish — the graft would unlock the table.
      if (!this.canWriteRows) return 0;
      const meta: ImportedGroupMeta[] = agents
        .map((agent, i) => ({
          groupId: uuidv4(),
          groupName: agent.name.trim() || UNTITLED_SUGGESTED_AGENT,
          // Use-case agents start collapsed on the canvas; the table matches.
          collapsed: true,
          groupColor: SUGGESTED_AGENT_COLORS[i % SUGGESTED_AGENT_COLORS.length],
          // `rowMap`, not `getRowById`: that scans every row, and a big
          // solution's suggestion runs to thousands of members.
          members: agent.rowIds
            .map((id) => this.rowMap.get(id))
            .filter(
              (row): row is TableNode & { nodeNumber: string } =>
                !!row &&
                isCanonicalNodeNumber(row.nodeNumber) &&
                !isGroupRow(row) &&
                !row.settings?.groupId
            )
            .map((row, order) => ({
              nodeNumber: row.nodeNumber,
              order,
              // PSTUDIO-4637: by id — a repeated node number would otherwise
              // resolve to a row inside another agent.
              rowId: row.id,
            })),
        }))
        .filter((m) => m.members.length >= 2);
      if (!meta.length) return 0;
      // What was actually grafted: a group whose members are no longer in
      // the table is skipped, so the panel must not report it as created.
      return this._graftGroupsInPlace(meta);
    }
  );

  /**
   * Publish was pressed with health errors: the Health panel opens with a
   * "Publish anyway" choice instead of publishing straight away.
   */
  healthPublishGate = false;

  setHealthPublishGate = action((on: boolean) => {
    this.healthPublishGate = on;
  });

  /** Bumped by "Publish anyway"; the header's Publish watches it. */
  publishAnywayRequest = 0;

  requestPublishAnyway = action(() => {
    this.publishAnywayRequest += 1;
  });

  /**
   * Apply one of the health check's one-click fixes. Each is a fixed
   * template, not a guess, and each goes through the normal (undoable)
   * row insert/update. Returns the row it wrote, or null.
   */
  applyHealthFix = action((fix: HealthFix): string | null => {
    if (this.isViewer) return null;
    if (fix.kind === 'add-error-handler') {
      if (this.rows.some((r) => nodeKey(r.nodeNumber) === ERROR_HANDLER_NODE)) {
        return null;
      }
      const last = this.rows[this.rows.length - 1];
      if (!last) return null;
      const target = fix.errorTarget;
      // The template every production solution uses for #-500.
      return this.insertRowBelow(last.id, {
        nodeType: Tenancy_Node_Type_Enum_Ps_Enum.A,
        nodeNumber: ERROR_HANDLER_NODE,
        nodeName: 'HandleBotError',
        command: 'HandleBotError',
        parameterInput: { save_error_to: 'PLATFORM_ERROR' },
        output: ['error_type'],
        decisionVariable: 'error_type',
        variables: ['PLATFORM_ERROR'],
        whatsNext: target
          ? [`bot_crash~${target}`, `bot_timeout~${target}`, `other~${target}`]
          : [],
      } as Partial<TableRow>);
    }
    const row = this.getRowById(fix.rowId);
    if (!row) return null;
    const current = Array.isArray(row.whatsNext)
      ? row.whatsNext.map(String)
      : String(row.whatsNext ?? '')
          .split('|')
          .map((x) => x.trim())
          .filter(Boolean);
    this.updateRow({
      rowId: fix.rowId,
      updates: {
        whatsNext: [...current, `error~${fix.errorTarget}`],
      } as Partial<TableRow>,
      saveImmediately: true,
    });
    return fix.rowId;
  });

  dragSession: {
    activeRowId: string;
    selectedRowIds: string[];
    isMulti: boolean;
    groupHeight: number;
    initialTop: number;
    initialLeft: number;
    initialWidth: number;
    isSelectionLocked: boolean;
    insertionIndex: number | null;
    isOversizeDrag: boolean;
    isInvalidDrop: boolean;
  } | null = null;

  get isSelectionLocked(): boolean {
    return this.dragSession?.isSelectionLocked ?? false;
  }

  get isOversizeDrag(): boolean {
    return this.dragSession?.isOversizeDrag ?? false;
  }

  // True while the active drag's source row is a group header — drives the
  // insertion-line visibility for solo group drags (PSTUDIO-3160 C).
  get isGroupDrag(): boolean {
    const activeId = this.dragSession?.activeRowId;
    if (!activeId) return false;
    return (
      this.getRowById(activeId)?.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.G
    );
  }

  startDragSession = action(
    (options: {
      activeRowId: string;
      selectedRowIds: string[];
      groupHeight: number;
      initialTop?: number;
      initialLeft?: number;
      initialWidth?: number;
      viewportHeight?: number;
    }) => {
      const {
        activeRowId,
        selectedRowIds,
        groupHeight,
        initialTop = 0,
        initialLeft = 0,
        initialWidth = 0,
        viewportHeight = 0,
      } = options;

      const isMulti = selectedRowIds.length > 1;
      const isOversizeDrag =
        isMulti && viewportHeight > 0 && groupHeight >= viewportHeight * 0.85;

      this.dragSession = {
        activeRowId,
        selectedRowIds,
        isMulti,
        groupHeight,
        initialTop,
        initialLeft,
        initialWidth,
        isSelectionLocked: isMulti,
        insertionIndex: null,
        isOversizeDrag,
        isInvalidDrop: false,
      };
    }
  );

  updateInsertionIndex = action(
    (index: number | null, isInvalidDrop = false) => {
      if (this.dragSession) {
        this.dragSession.insertionIndex = index;
        this.dragSession.isInvalidDrop = isInvalidDrop;
      }
    }
  );

  /**
   * Sorts row ids ascending by their current `order`. Single source of truth
   * for keeping a group's `settings.subRows` aligned with the visual child
   * order (used by reorderRows resort and pasteRow subRows merge).
   */
  sortIdsByRowOrder = (ids: string[]): string[] =>
    ids
      .map((id) => ({ id, order: this.getRowById(id)?.order ?? 0 }))
      .sort((a, b) => a.order - b.order)
      .map((s) => s.id);

  // Apply a `{rowId -> order}` map to the in-memory rows and persist only the
  // changed rows via the orders-only mutation. Shared by `reorderRows`, the
  // new `moveRows` reorder command (execute/undo), and any other caller that
  // needs to set orders without shipping full-row payloads.
  //
  // Ship only `{id, order, solutionVersionId}` per changed row via the
  // orders-only mutation (updateProStudioNodeOrders). The previous
  // implementation shipped full rows through updateProStudioNodes because its
  // upsert has update_columns covering every column; renumbering 500 rows in
  // the drag slow path produced a ~5MB payload that Hasura rejected.
  // updateProStudioNodeOrders pins update_columns to [order] so partial
  // objects are safe and the payload stays under the body limit independent of
  // dataset size.
  _applyOrders = action((newOrderById: Map<string, number>) => {
    const updatedRows: Map<string, number> = new Map();
    // Apply all order mutations in one pass — no fuse/validation overhead
    this.rows.forEach((row, i) => {
      const newOrder = newOrderById.get(row.id);
      if (newOrder !== undefined && row.order !== newOrder) {
        set(this.rows[i], { order: newOrder });
        set(this.rowMap, row.id, { ...row, order: newOrder });
        updatedRows.set(row.id, newOrder);
      }
    });

    // PSTUDIO-3063: reordering updates per-row `order` but leaves every
    // affected group's `settings.subRows` untouched, so the group row's
    // displayed number (read from subRows[0] in draggable-row.tsx) goes stale
    // after an intra-group move. Iterating newOrderById's keys and reading
    // `settings.groupId` captures every affected group's child set (a group's
    // children can only shuffle when at least one has a new order). Routed
    // through _applyOrders so it covers reorderRows AND moveRows undo/redo.
    //
    // PSTUDIO-3611: getRowById is a linear rows.find whose computedFn memo is
    // invalidated by any rows-array structural change (e.g. pasteRow's push),
    // so calling it once per key made this loop O(M²) at 5500 rows. One O(M)
    // pass builds a local index instead.
    const rowById = new Map(this.rows.map((row) => [row.id, row]));
    const affectedGroupIds = new Set<string>();
    for (const rowId of newOrderById.keys()) {
      const row = rowById.get(rowId);
      const gid = row?.settings?.groupId;
      if (gid) affectedGroupIds.add(gid);
    }

    // Resort each affected group's `settings.subRows` to match the new order.
    // The group row carries a `settings` change (not just `order`), so it can't
    // ride the orders-only mutation below — persist it via updateRow's full-row
    // save path. Only the group row(s) ship as full rows; the reordered child
    // rows still go through the lightweight orders-only path. Both save paths
    // are DebouncedQueue(0), so there is no sync-vs-async race between them.
    if (affectedGroupIds.size > 0) {
      emitOnce(() => {
        for (const groupId of affectedGroupIds) {
          const group = rowById.get(groupId);
          if (!group) continue;
          const currentSubRows: string[] = group.settings?.subRows ?? [];
          const resorted = this.sortIdsByRowOrder(currentSubRows);

          const alreadyOrdered =
            currentSubRows.length === resorted.length &&
            currentSubRows.every((id, i) => id === resorted[i]);
          if (alreadyOrdered) continue;

          this.updateRow({
            rowId: groupId,
            updates: {
              settings: { ...(group.settings ?? {}), subRows: resorted },
            },
            skipValidationRules: true,
            saveImmediately: true,
          });
        }
      });
    }

    const orderUpdates: RowOrderUpdate[] = [];
    updatedRows.forEach((order, id) => {
      orderUpdates.push({
        id,
        order,
        solutionVersionId: this.solutionVersionId,
      });
    });
    this.saveRowOrdersToDatabase(orderUpdates);
  });

  reorderRows = action((newOrderById: Map<string, number>) => {
    this._applyOrders(newOrderById);
  });

  endDragSession = action(() => {
    this.dragSession = null;
  });

  // PSTUDIO-2154: register the net order change from a drag-reorder as a single
  // undo/redo command. `beforeOrders` is captured by table.tsx's handleDragEnd
  // before the (group-aware) drag logic runs; this reads the current orders as
  // the "after" state. Both directions go through `_applyOrders`, which also
  // resyncs each affected group's `settings.subRows`. Reveals the dragged row
  // after undo/redo. No-op when nothing actually moved.
  recordReorderCommand = action(
    (beforeOrders: Map<string, number>, focusRowId?: string) => {
      const afterOrders = new Map<string, number>(
        this.rows.map((row): [string, number] => [row.id, row.order])
      );
      const changed = [...afterOrders].some(
        ([id, order]) => beforeOrders.get(id) !== order
      );
      if (!changed) return;

      if (this.validationMode === ValidationMode.Manual) {
        this.hasUnvalidatedChangesInManualMode = true;
      }

      this.pushCommandWithoutExecute({
        execute: () => this._applyOrders(afterOrders),
        undo: () => this._applyOrders(beforeOrders),
        description: `Reorder rows`,
        focusTarget: focusRowId ? { rowId: focusRowId } : undefined,
        // PSTUDIO-3845: _applyOrders persists via the orders-only mutation
        // (saveRowOrdersToDatabase), which does NOT run validateAndSync — so in
        // Auto mode recompileAfterHistoryChange must force the recompile itself
        // after this command's undo/redo, else validation results go stale.
        recompilesViaSavePath: false,
      });
    }
  );

  private _draftVersion?: string;

  get draftVersion(): string | undefined {
    return this._draftVersion;
  }

  set draftVersion(newDraftVersion: string) {
    if (newDraftVersion !== this._draftVersion) {
      this._draftVersion = newDraftVersion;
    }
  }

  private _botId: string = '';

  _validationMessage?: string;

  get botId() {
    return this._botId;
  }

  set botId(newBotId: string) {
    if (newBotId !== this._botId) {
      this._botId = newBotId.replaceAll('.', '-');
    }
  }

  private _solutionVersionId: string = '';

  /** The solution `_solutionVersionId` was set against — see `solutionScopeOfVersionPsId`. */
  private _solutionScope: string = '';

  get solutionVersionId() {
    return this._solutionVersionId;
  }

  get columnOrder() {
    return columnOrder;
  }

  private _richAssetsConfig = new RichAssets().createRichAssetTypeConfig();

  get richAssetsConfig() {
    return this._richAssetsConfig;
  }

  getColumnByIndex(num: number) {
    if (!columnOrder[num]) return columnOrder[0] as keyof TableNode;

    return columnOrder[num] as keyof TableNode;
  }

  /**
   * The rendered left-to-right order of the data columns (PSTUDIO-2372):
   * TanStack puts LEFT-PINNED columns first (in pin order), then the rest in
   * column-def order. Pinning is user-configurable and persisted, so this is
   * a computed over `columnPinningSettings`, not a static list. All
   * cell-selection geometry must use this order — never `columnOrder`, whose
   * legacy sequence does not match the DOM (see VISUAL_COLUMN_ORDER).
   */
  get visualColumnOrder(): NodeColumns[] {
    const pinning = this.columnPinningSettings;
    // PSTUDIO-1345: hidden columns are excluded from the rendered order so
    // cell-range geometry never lands on a column with no DOM cell. Filtering
    // here makes getVisualColumnIndex/getCellsBetween/_captureCells/
    // pasteSelectedCells/presence inherit the exclusion for free.
    const visible = VISUAL_COLUMN_ORDER.filter((c) => !HIDDEN_COLUMNS.has(c));
    const isDataColumn = (c: string): c is NodeColumns =>
      visible.includes(c as NodeColumns);
    const left = (pinning.left ?? []).filter(isDataColumn);
    const right = (pinning.right ?? []).filter(isDataColumn);
    if (left.length === 0 && right.length === 0) return visible;
    const pinned = new Set<NodeColumns>([...left, ...right]);
    const center = visible.filter((c) => !pinned.has(c));
    return [...left, ...center, ...right];
  }

  getVisualColumnIndex = computedFn((columnId: NodeColumns): number => {
    return this.visualColumnOrder.indexOf(columnId);
  });

  getCellsBetween = (
    cell1: CellSelection,
    cell2: CellSelection
  ): CellSelection[] => {
    if (!cell1 || !cell2) return [];

    const cell1RowIndex = this.getSortedRowIndex(cell1.rowId);
    const cell2RowIndex = this.getSortedRowIndex(cell2.rowId);
    // Unknown rows (e.g. a coworker's presence range referencing a row this
    // client no longer has) — no rectangle to build.
    if (cell1RowIndex === -1 || cell2RowIndex === -1) return [];

    // Rectangle columns are computed in VISUAL (rendered) order so the range
    // matches what the user sees and what keyboard focus walks — the legacy
    // columnOrder disagrees with the DOM (PSTUDIO-2372).
    const cell1ColumnIndex = this.getVisualColumnIndex(cell1.columnId);
    const cell2ColumnIndex = this.getVisualColumnIndex(cell2.columnId);
    // A cell with no rendered column (e.g. the debug 'order' column) has no
    // visual position — no rectangle to build.
    if (cell1ColumnIndex === -1 || cell2ColumnIndex === -1) return [];

    // PSTUDIO-4358: slice the SAME list the indexes above came from.
    // `getSortedRowIndex` is a position among the VISIBLE rows of
    // `getAllSortedRows()` (hidden rows skipped, groups arranged), while
    // `sortedRows` is the raw `rows` order INCLUDING hidden ones. Slicing that
    // list with these indexes drifts upward by the number of hidden rows
    // (a collapsed group) above the clicked row, so Shift+Click landed on a row
    // above the one clicked. Same divergence `calculateNewRowOrder` documents.
    const selectedRows = this.getAllSortedRows()
      .filter((row) => row.hidden !== true)
      .slice(
        Math.min(cell1RowIndex, cell2RowIndex),
        Math.max(cell1RowIndex, cell2RowIndex) + 1
      );

    const columns = this.visualColumnOrder.slice(
      Math.min(cell1ColumnIndex, cell2ColumnIndex),
      Math.max(cell1ColumnIndex, cell2ColumnIndex) + 1
    );

    const result = selectedRows
      .flatMap((row) => {
        return columns.map((column) => {
          return {
            columnId: column,
            rowId: row.id,
            cellId: `${row.id}-${column}`,
          } as CellSelection;
        });
      })
      .filter(function (e) {
        return e !== undefined;
      });

    return result;
  };

  /**
   * Compact selection payload for coworker presence (PSTUDIO-2372). A
   * multi-cell range collapses to its bounding corners in VISUAL column /
   * visible-sorted row space — the receiver re-expands the rectangle via
   * getCellsBetween — and row selection travels as the exact rowId list
   * (preserves Cmd+click gaps). Returns undefined when there is nothing
   * beyond the single focused cell to share.
   */
  getPresenceSelection():
    | {
        cellRange: {
          anchor: { rowId: string; columnId: string };
          focus: { rowId: string; columnId: string };
        } | null;
        rowIds: string[];
      }
    | undefined {
    const rowIds = [...this.selectedRows.values()];

    let cellRange: {
      anchor: { rowId: string; columnId: string };
      focus: { rowId: string; columnId: string };
    } | null = null;

    if (this.selectedCells.length > 1) {
      const cells = [...this.selectedCells];
      const rowIdxs = cells
        .map((c) => this.getSortedRowIndex(c.rowId))
        .filter((i) => i >= 0);
      const colIdxs = cells
        .map((c) => this.getVisualColumnIndex(c.columnId))
        .filter((i) => i >= 0);

      if (rowIdxs.length > 0 && colIdxs.length > 0) {
        const visibleRows = this.getAllSortedRows().filter(
          (row) => row.hidden !== true
        );
        const topRow = visibleRows[Math.min(...rowIdxs)];
        const bottomRow = visibleRows[Math.max(...rowIdxs)];
        const leftColumn = this.visualColumnOrder[Math.min(...colIdxs)];
        const rightColumn = this.visualColumnOrder[Math.max(...colIdxs)];

        if (topRow && bottomRow && leftColumn && rightColumn) {
          cellRange = {
            anchor: { rowId: topRow.id, columnId: String(leftColumn) },
            focus: { rowId: bottomRow.id, columnId: String(rightColumn) },
          };
        }
      }
    }

    if (!cellRange && rowIds.length === 0) return undefined;
    return { cellRange, rowIds };
  }

  selectedStartCellId = computedFn(() => {
    return this.selectedStartCell?.cellId;
  });

  selectedStartCell?: CellSelection = undefined;

  setSelectedStartCell = action((cell: CellSelection) => {
    emitOnce(() => {
      if (cell.cellId !== this.selectedStartCell?.cellId) {
        this.selectedStartCell = cell;
      }

      if (!this.isCellSelected(cell.cellId)) {
        this.selectCell(cell);
      }
    });
  });

  setScrollToCellCallback(cb: ((el: HTMLElement) => void) | null) {
    this.scrollToCellCallback = cb;
  }

  scrollToCellElement(el: HTMLElement) {
    this.scrollToCellCallback?.(el);
  }

  setPendingScrollToCell(
    pending: { rowIndex: number; columnId: NodeColumns } | null
  ) {
    this.pendingScrollToCell = pending;
    // Only the table can scroll to a cell, so Build switches to it.
    if (pending) this.pendingBuildViewMode = 'table';
  }

  clearPendingScrollToCell() {
    this.pendingScrollToCell = null;
  }

  setPendingScrollToCellByRowId(
    pending: { rowId: string; columnId: NodeColumns } | null
  ) {
    this.pendingScrollToCellByRowId = pending;
    if (pending) this.pendingBuildViewMode = 'table';
  }

  clearPendingScrollToCellByRowId() {
    this.pendingScrollToCellByRowId = null;
  }

  setPendingGoToNodeNumber(pending: { nodeNumber: string } | null) {
    this.pendingGoToNodeNumber = pending;
    if (pending) this.pendingBuildViewMode = 'table';
  }

  /**
   * Focus a cell in the Build table from outside it (validation errors, a
   * coworker's cursor). With the table showing it scrolls there now; with
   * Build on the canvas it asks for the table, which picks the cell up once
   * it mounts. Returns what `scrollToAndFocusCellById` would.
   */
  /** Whether the Build table is mounted (it registers the scroll callback). */
  get isTableMounted(): boolean {
    return this.scrollToCellCallback !== null;
  }

  focusCellInTable = action((rowId: string, columnId: NodeColumns): boolean => {
    if (this.isTableMounted) {
      return this.scrollToAndFocusCellById(rowId, columnId);
    }
    this.setPendingScrollToCellByRowId({ rowId, columnId });
    return true;
  });

  clearPendingGoToNodeNumber() {
    this.pendingGoToNodeNumber = null;
  }

  markPendingAutoPublish(uniqueId: string, botType: BotType) {
    this.pendingAutoPublish = { uniqueId, botType };
  }

  /**
   * Consumes the marker only if it belongs to the solution CURRENTLY loaded.
   *
   * Matched against `selectedBot.uniqueId` from the XState context (the same
   * `SelectedSolution.uniqueId` field `createSolution()`'s `cb` read to set
   * the marker in the first place — `res` is `/${projectId}/${uniqueId}`,
   * the solution's raw id) rather than `tableStore.botId`. `botId` is
   * `${customerId}.${botName}`, and `botName` is the solution's human-
   * readable NAME (`ctx.solutions.find(s => s.id === uniqueId)?.name` in
   * `sync-xstate-with-route-params.ts`) — a different field with no
   * structural relationship to `uniqueId`, so a string-suffix check against
   * `botId` could never match. `uniqueId` is the one field both sides of
   * this marker actually agree on.
   *
   * Returns the consumed marker, or `null` when there is none pending or it
   * belongs to a DIFFERENT solution — left in place in that case, so a
   * later load of the right solution can still consume it.
   */
  consumePendingAutoPublish(): { uniqueId: string; botType: BotType } | null {
    const pending = this.pendingAutoPublish;
    if (!pending) return null;
    const currentUniqueId =
      proStudioService.getSnapshot().context.selectedBot?.uniqueId;
    if (currentUniqueId !== pending.uniqueId) return null;
    this.pendingAutoPublish = null;
    return pending;
  }

  /**
   * The release check + `consumePendingAutoPublish()` together — both call
   * sites in the `solutionVersionId` reaction (the no-corpus-wait branch and
   * the corpus-ready subscription callback) need exactly this pair, so it
   * lives here once rather than being repeated at each site.
   *
   * Auto-publish is built but not part of the October release
   * (`BOBCAT_UNRELEASED`, PSTUDIO-4636), so this answers `null` whatever marker
   * is pending. The wizard that sets the marker reads the same entry; two
   * readers, so removing the entry (the way to release it) fails to compile
   * at both and neither can be missed.
   */
  tryConsumePendingAutoPublish(): {
    uniqueId: string;
    botType: BotType;
  } | null {
    if (!BOBCAT_UNRELEASED.autoPublishOnCreate) {
      return null;
    }
    return this.consumePendingAutoPublish();
  }

  /**
   * Whether a create-time marker may be set (PSTUDIO-4572): used by
   * `markPendingAutoAgentGrouping`, `markPendingEmptySolution`,
   * `markPendingSkipKnowledgeCorpus`, `markPendingUseCaseAgentGrouping` and
   * `markPendingAgentDrafts`. Set with its consumer's flag off, a marker sat in
   * memory and could still fire later in the session if LaunchDarkly's answer
   * changed. Before LaunchDarkly answers, `featureFlags` is `{}` and says
   * nothing (see `detectAutoAgentGrouping`), so the marker IS set and the
   * consume side's own flag check decides at load, as it did before. Not used
   * by `markPendingAutoPublish` (its caller checks the flag) or
   * `markPendingCanvasLanding` (see `pendingCanvasLanding`).
   */
  private markerAllowed(consumerOn: boolean): boolean {
    return !Object.keys(this.featureFlags).length || consumerOn;
  }

  markPendingAutoAgentGrouping(uniqueId: string) {
    if (
      !this.markerAllowed(
        resolveBobcatAccess('fiveStageNav', this.featureFlags) === 'allowed'
      )
    ) {
      return;
    }
    this.pendingAutoAgentGrouping = uniqueId;
  }

  /**
   * Consumes the marker only if it belongs to the solution CURRENTLY loaded.
   * Same matching approach as `consumePendingAutoPublish` — see its comment.
   */
  consumePendingAutoAgentGrouping(): string | null {
    const pending = this.pendingAutoAgentGrouping;
    if (!pending) return null;
    const currentUniqueId =
      proStudioService.getSnapshot().context.selectedBot?.uniqueId;
    if (currentUniqueId !== pending) return null;
    this.pendingAutoAgentGrouping = null;
    return pending;
  }

  /** The flag check + `consumePendingAutoAgentGrouping()` together. */
  tryConsumePendingAutoAgentGrouping(): string | null {
    if (resolveBobcatAccess('fiveStageNav', this.featureFlags) !== 'allowed') {
      return null;
    }
    return this.consumePendingAutoAgentGrouping();
  }

  markPendingEmptySolution(uniqueId: string) {
    if (
      !this.markerAllowed(
        resolveBobcatAccess('fiveStageNav', this.featureFlags) === 'allowed'
      )
    ) {
      return;
    }
    this.pendingEmptySolution = uniqueId;
  }

  /** Consumed only for the solution currently loaded, behind the front door's flag. */
  tryConsumePendingEmptySolution(): string | null {
    const pending = this.pendingEmptySolution;
    if (!pending) return null;
    if (resolveBobcatAccess('fiveStageNav', this.featureFlags) !== 'allowed') {
      return null;
    }
    const currentUniqueId =
      proStudioService.getSnapshot().context.selectedBot?.uniqueId;
    if (currentUniqueId !== pending) return null;
    this.pendingEmptySolution = null;
    return pending;
  }

  /**
   * Deletes every row of a just-created blank solution, so it opens as an
   * empty canvas. Not undoable: there was never anything there to the user.
   */
  applyEmptySolution = action(() => {
    const current = this._snapshotAllRows();
    if (!current.length) return;
    this._restoreToSnapshot([], current);
    // The Knowledge Agent's RAG node is gone, so there's no corpus to wait on.
    this.cleanupSolutionVersionSubscription(true);
    // Detection ran on the backend's rows; with none left, nothing to offer.
    this.detectAutoAgentGrouping();
    this.detectUseCaseAgentNumbering();
  });

  markPendingSkipKnowledgeCorpus(uniqueId: string) {
    if (!this.markerAllowed(this.knowledgeSearchToggleEnabled)) return;
    this.pendingSkipKnowledgeCorpus = uniqueId;
  }

  /**
   * Consumes the marker only if it belongs to the solution CURRENTLY loaded.
   * Same matching approach as `consumePendingAutoPublish` — see its comment.
   */
  consumePendingSkipKnowledgeCorpus(): string | null {
    const pending = this.pendingSkipKnowledgeCorpus;
    if (!pending) return null;
    const currentUniqueId =
      proStudioService.getSnapshot().context.selectedBot?.uniqueId;
    if (currentUniqueId !== pending) return null;
    this.pendingSkipKnowledgeCorpus = null;
    return pending;
  }

  /** The flag check + `consumePendingSkipKnowledgeCorpus()` together. */
  tryConsumePendingSkipKnowledgeCorpus(): string | null {
    if (!this.knowledgeSearchToggleEnabled) return null;
    return this.consumePendingSkipKnowledgeCorpus();
  }

  /**
   * The knowledge-search skip/restore surface is on: either Bobcat flag that
   * introduced it is allowed. One definition shared by the consume path above
   * and the Supervisor save's restore step, so the two cannot disagree about
   * which flags open it. Reads `featureFlags`, which is `{}` until
   * LaunchDarkly answers, so a not-yet-loaded flag reads as off here.
   */
  get knowledgeSearchToggleEnabled(): boolean {
    return resolveBobcatAccess('fiveStageNav', this.featureFlags) === 'allowed';
  }

  /**
   * Drops the Knowledge Agent's RAG lookup (the query-writing step and
   * `GetRAGSearchResponse`) from a solution that has no knowledge base, and
   * routes everything that pointed into it to the answer step, recording the
   * change there so `restoreKnowledgeSearch` can undo it later. Onboarding
   * calls this with `undoable: false` — it's part of creating the solution,
   * not an edit the user made; the Agents dock's "skip search" action and
   * the Publish/Deploy prompts pass `undoable: true` (one undo step).
   * Returns whether anything changed. See
   * `stores/utils/knowledge-search-toggle.ts`.
   */
  applySkipKnowledgeCorpus = action(
    async ({
      undoable = false,
    }: { undoable?: boolean } = {}): Promise<boolean> => {
      const current = this._snapshotAllRows();
      const target = skipKnowledgeSearchRows(
        current as unknown as KnowledgeRow[]
      ) as unknown as TableRow[] | null;
      if (!target) return false;
      if (undoable) {
        this._recordGroupMutation('Skip knowledge search', () =>
          this._restoreToSnapshot(target, current)
        );
      } else {
        this._restoreToSnapshot(target, current);
      }
      // Nothing is left to wait on: stop waiting for a corpus that was never
      // going to arrive, so Publish frees up.
      this.cleanupSolutionVersionSubscription(true);
      return true;
    }
  );

  /**
   * Records when knowledge files were uploaded, on the RAG search still
   * waiting for its corpus, so a setup that never finishes can be told apart
   * from one in progress (see `stores/utils/knowledge-corpus-setup.ts`).
   * Bookkeeping only — no undo step, no validation.
   */
  markKnowledgeUpload = action(() => {
    const marked = markKnowledgeUploadRows(this.rows, new Date());
    marked.forEach((row) => {
      const live = this.rows.find((r) => r.id === row.id);
      if (live) live.settings = row.settings;
    });
    if (marked.length) {
      this.saveRowsToDatabase(
        marked.map((row) => toJS(row)),
        { skipValidation: true }
      );
    }
  });

  /**
   * Puts a skipped knowledge search back (see `applySkipKnowledgeCorpus`),
   * so the backend has a RAG node to fill `live_corpus_id` on once a
   * knowledge base is added. One undo step, and all-or-nothing: the row
   * contents are saved first, and only once they're in does the new row
   * order go out — a failed save puts the table back and records no undo
   * step. Returns what couldn't be put back, for the caller to show (with
   * `failed` when nothing was saved); `null` when there was nothing to
   * restore.
   */
  restoreKnowledgeSearch = action(
    async (): Promise<{
      restoredNodeNumbers: string[];
      notes: string[];
      failed?: boolean;
    } | null> => {
      const current = this._snapshotAllRows();
      const result = restoreKnowledgeSearchRows(
        current as unknown as KnowledgeRow[],
        (fields, template) =>
          ({
            ...defaultRowData,
            ...fields,
            id: uuidv4(),
            solutionVersionId:
              (template.solutionVersionId as string) || this.solutionVersionId,
          }) as unknown as KnowledgeRow
      );
      if (!result) return null;
      const target = result.rows as unknown as TableRow[];
      if (result.restoredNodeNumbers.length || !isEqual(target, current)) {
        const checkpoint = this.history.checkpoint();
        let saved: Promise<{ error?: unknown }> | null = null;
        this._recordGroupMutation('Restore knowledge search', () => {
          saved = this._restoreToSnapshotContentFirst(target, current);
        });
        const outcome = saved ? await saved : { error: undefined };
        if (outcome.error) {
          runInAction(() => {
            this._replaceRowsInMemory(current);
            this.history.rollbackTo(checkpoint);
          });
          return {
            restoredNodeNumbers: [],
            notes: [
              "Knowledge search couldn't be turned back on, so nothing was changed. Save again to retry.",
            ],
            failed: true,
          };
        }
      }
      return {
        restoredNodeNumbers: result.restoredNodeNumbers,
        notes: result.notes,
      };
    }
  );

  /**
   * After a knowledge base is added to a solution already loaded: waits for
   * the backend to fill the RAG node's `live_corpus_id`, then reloads the
   * rows once. The load-time path (the solutionVersionId reaction) does the
   * same for a freshly created solution; this covers the Agents dock.
   */
  watchForLiveCorpusId = action(() => {
    const { solutionVersionId } = this;
    if (!solutionVersionId || this.solutionVersionSubscriptionUnsubscribe) {
      return;
    }
    this.isWaitingForBackendData = true;
    this.isWaitingForLiveCorpusIdOnly = true;
    const stopWaiting = () =>
      runInAction(() => {
        this.isWaitingForBackendData = false;
        this.isWaitingForLiveCorpusIdOnly = false;
      });
    // PSTUDIO-4638: the first complete push (or an error) ends the watch, and
    // the subscription is closed right then — not after the reload is awaited,
    // when a push landing in between would reload and toast a second time.
    // `subscribe` can deliver before it returns, i.e. before `handle` is set,
    // so the close is finished below once the handle exists.
    // `stored` is the slot's value as MobX holds it (it wraps the function),
    // so the slot is freed only while it is still this watch's.
    let done = false;
    let handle: (() => void) | null = null;
    let stored: unknown = null;
    const closeSubscription = () => {
      done = true;
      if (!handle) {
        return;
      }
      handle();
      runInAction(() => {
        if (this.solutionVersionSubscriptionUnsubscribe === stored) {
          this.solutionVersionSubscriptionUnsubscribe = null;
        }
      });
    };
    try {
      const { subscribe } = urqlGql.solutionVersionSubscription({
        solutionVersionId,
      });
      const { unsubscribe } = subscribe((subData) => {
        if (done) {
          return;
        }
        if (subData.error) {
          console.error('live_corpus_id subscription error:', subData.error);
          closeSubscription();
          stopWaiting();
          return;
        }
        const versionData = subData.data?.tenancy_solution_versions_ps_by_pk;
        if (
          !versionData ||
          hasRowWithEmptyLiveCorpusId(versionData.solution_version_nodes_ps)
        ) {
          return;
        }
        closeSubscription();
        runInAction(async () => {
          let applied: boolean;
          try {
            applied = await this.processVersionData(versionData, {
              forSolutionVersionId: solutionVersionId,
            });
          } catch (error) {
            // The subscription is already closed, so nothing else would end
            // the wait: Publish would stay gated until a reload.
            console.error('Could not apply the live_corpus_id reload:', error);
            // Not if the user has moved on: those flags are the new visit's.
            if (this.solutionVersionId === solutionVersionId) {
              stopWaiting();
            }
            return;
          }
          // PSTUDIO-3953: dropped because the store moved on — the visit that
          // replaced this one owns the waiting flags now.
          if (!applied) {
            return;
          }
          sendUserMessage({
            type: 'success',
            text: 'The knowledge base is ready. You can now publish this solution.',
            autoClose: 3000,
          });
          // Only the flags: the slot was freed above, and what is in it now
          // belongs to someone else.
          stopWaiting();
        });
      });
      handle = unsubscribe;
      if (done) {
        unsubscribe();
      } else {
        this.solutionVersionSubscriptionUnsubscribe = unsubscribe;
        stored = this.solutionVersionSubscriptionUnsubscribe;
      }
    } catch (error) {
      console.error('Could not watch for live_corpus_id:', error);
      stopWaiting();
    }
  });

  /** A RAG lookup is waiting on a corpus that was never set up. */
  get knowledgeSearchUnprovisioned(): boolean {
    return hasUnprovisionedKnowledgeSearch(this.rows);
  }

  /** The knowledge search was skipped and can be put back. */
  get knowledgeSearchRestorable(): boolean {
    return canRestoreKnowledgeSearch(this.rows as unknown as KnowledgeRow[]);
  }

  markPendingUseCaseAgentGrouping(uniqueId: string) {
    if (
      !this.markerAllowed(
        resolveBobcatAccess('fiveStageNav', this.featureFlags) === 'allowed'
      )
    ) {
      return;
    }
    this.pendingUseCaseAgentGrouping = uniqueId;
  }

  /**
   * Consumes the marker only if it belongs to the solution CURRENTLY loaded.
   * Same matching approach as `consumePendingAutoPublish` — see its comment.
   */
  consumePendingUseCaseAgentGrouping(): string | null {
    const pending = this.pendingUseCaseAgentGrouping;
    if (!pending) return null;
    const currentUniqueId =
      proStudioService.getSnapshot().context.selectedBot?.uniqueId;
    if (currentUniqueId !== pending) return null;
    this.pendingUseCaseAgentGrouping = null;
    return pending;
  }

  /** The flag check + `consumePendingUseCaseAgentGrouping()` together. */
  tryConsumePendingUseCaseAgentGrouping(): string | null {
    if (resolveBobcatAccess('fiveStageNav', this.featureFlags) !== 'allowed') {
      return null;
    }
    return this.consumePendingUseCaseAgentGrouping();
  }

  markPendingCanvasLanding(uniqueId: string) {
    this.pendingCanvasLanding = uniqueId;
  }

  /**
   * Consumes the marker only if it belongs to the solution CURRENTLY loaded.
   * See `pendingCanvasLanding`'s doc comment for why this is not itself
   * flag-gated.
   */
  consumePendingCanvasLanding(): string | null {
    const pending = this.pendingCanvasLanding;
    if (!pending) return null;
    const currentUniqueId =
      proStudioService.getSnapshot().context.selectedBot?.uniqueId;
    if (currentUniqueId !== pending) return null;
    this.pendingCanvasLanding = null;
    return pending;
  }

  markPendingAgentDrafts(
    uniqueId: string,
    brief: string,
    tasks: PendingAgentTask[]
  ) {
    if (
      !this.markerAllowed(
        resolveBobcatAccess('fiveStageNav', this.featureFlags) === 'allowed'
      )
    ) {
      return;
    }
    this.pendingAgentDrafts = tasks.length ? { uniqueId, brief, tasks } : null;
  }

  /** The flag check + the marker, for the solution CURRENTLY loaded only. */
  tryConsumePendingAgentDrafts() {
    const pending = this.pendingAgentDrafts;
    if (!pending) return null;
    if (resolveBobcatAccess('fiveStageNav', this.featureFlags) !== 'allowed') {
      return null;
    }
    const currentUniqueId =
      proStudioService.getSnapshot().context.selectedBot?.uniqueId;
    if (currentUniqueId !== pending.uniqueId) return null;
    this.pendingAgentDrafts = null;
    return pending;
  }

  // Returns whether the target row was FOUND — i.e. whether the move was
  // scheduled, not whether focus has landed (that is deferred through a
  // setTimeout plus two animation frames). No caller reads it today: the panel
  // decides "is the selection about to leave?" up front via
  // `getVerticalNeighborRow`, which answers the same question without a side
  // effect. Kept because it costs nothing and the answer is genuinely useful to
  // a caller whose next step depends on the move; do NOT read it as "focus has
  // moved" (PSTUDIO-1433).
  scrollToAndFocusCellById = action(
    (rowId: string, columnId: NodeColumns): boolean => {
      const sortedRows = this.getVisibleRows();
      const rowIndex = sortedRows.findIndex((r) => r.id === rowId);
      if (rowIndex === -1) return false;
      this.scrollToAndFocusCell(rowIndex, columnId);
      return true;
    }
  );

  // PSTUDIO-2821: Google-Sheets-parity vertical move after an in-edit Enter /
  // Shift+Enter commit. The CALLER does the commit (setCellCloseIntent('commit')
  // + blur → handleCellBlur → commitCellEditing), which also clears the editing*
  // fields — so the caller MUST snapshot editingRowId/editingColumnId BEFORE the
  // blur and pass them in here. This action only MOVES focus/selection to the
  // same-column cell one VISIBLE row below ('down') or above ('up'); it does NOT
  // commit. Neighbour is resolved against getVisibleRows() (the search-filtered,
  // virtualized order — NOT getSortedRowIndex) so it lands the next VISIBLE row.
  // Edge (no neighbour: last row + down, first row + up): no-op — the commit has
  // already happened, so this is "commit and stay" (no wrap), matching Sheets.
  // The landing cell is selected + is-selection-start and does NOT auto-open
  // (scrollToAndFocusCellById selects + focuses the <td>, never toggleCellInput).
  //
  // RETURNS whether a neighbour existed and the move was scheduled. The panel's
  // Enter does not consume it — it asks `getVerticalNeighborRow` BEFORE
  // committing instead, because it needs the answer while deciding what to
  // commit, and because "scheduled" is not "focus has landed": the move goes
  // through a setTimeout plus two animation frames, so the flag would still be
  // wrong for the window that matters (PSTUDIO-1433 — Enter on the last visible
  // row wiped the JSON the user had just committed).
  commitAndMoveVertically = action(
    (
      rowId: string,
      columnId: NodeColumns,
      direction: 'down' | 'up'
    ): boolean => {
      if (!rowId || !columnId) return false;
      const neighbor = this.getVerticalNeighborRow(rowId, direction);
      if (!neighbor) return false;
      return this.scrollToAndFocusCellById(neighbor.id, columnId);
    }
  );

  /**
   * The row one VISIBLE step below/above `rowId`, or undefined on the edge.
   * Shared by `commitAndMoveVertically` and by callers that must know BEFORE
   * committing whether the selection is about to leave the current cell — the
   * Expanded Cell View's Enter keeps its editing flag set when it isn't
   * (PSTUDIO-1433), and reading it afterwards would mean flipping the flag off
   * and on again within the tick.
   */
  getVerticalNeighborRow(rowId: string, direction: 'down' | 'up') {
    const rows = this.getVisibleRows();
    const currentIndex = rows.findIndex((r) => r.id === rowId);
    if (currentIndex === -1) return undefined;
    return direction === 'down'
      ? rows[currentIndex + 1]
      : rows[currentIndex - 1];
  }

  /**
   * Switches to the page containing the row, selects the cell, scrolls to it and focuses it.
   * Used by ValidationPopover (click on error) and Table (pendingScrollToCell after navigate).
   * Pass `selectRow: true` to also add the row to `selectedRows` (highlight).
   */
  scrollToAndFocusCell = action(
    (
      rowIndex: number,
      columnId: NodeColumns,
      options?: { selectRow?: boolean }
    ) => {
      const sortedRows = this.getVisibleRows();
      const row = sortedRows[rowIndex];
      if (!row) return;
      const rowId = row.id;
      const cellId = `${rowId}-${columnId}`;

      // If the caller just cleared the search query, the virtualizer's `count`
      // is still stale on this tick — scrollToIndex would clamp to the old
      // filtered range and silently no-op. Defer to the next macrotask so
      // React commits the new row count before we ask the virtualizer to
      // scroll, then wait two animation frames for the row to mount before
      // focusing.
      setTimeout(() => {
        this.scrollToVirtualIndex?.(rowIndex);

        this.deselectAllCells();
        this.deselectAllRows();
        this.clearSelectedStartCell();
        // Cell navigation (ValidationPopover click-on-error) selects the single
        // target cell — that's the intended landing spot. Row navigation
        // (Go to Node, `selectRow`) must NOT: a single selected cell paints the
        // purple 2px cell frame that keyboard Shift+Space row selection never
        // leaves. The row highlight is applied below instead (PSTUDIO-3663).
        if (!options?.selectRow) {
          this.selectCell({ cellId, rowId, columnId });
        }

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const cell = document.querySelector(
              `td[data-row-id="${cssEscape(rowId)}"][data-cell-id="${cssEscape(String(columnId))}"]`
            );
            if (cell instanceof HTMLElement) {
              this.scrollToCellElement(cell);
              // A focused cell also draws a frame, so for the row path we blur
              // instead of focusing — matching Shift+Space, which blurs the
              // active cell (selectFocusedRowByKeyboard handler).
              if (!options?.selectRow) {
                cell.focus();
              }
            }
            if (options?.selectRow) {
              // Land in the SAME visible state as keyboard Shift+Space
              // (selectFocusedRowByKeyboard): clear any lingering cell focus,
              // highlight the whole row, and set the anchor so a follow-up
              // Shift+Up/Down extends the range. Result is identical to
              // Shift+Space — no single-cell frame (PSTUDIO-3663).
              if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
              }
              this.selectRow(rowId);
              const anchorIndex = this.getSortedRowIndex(rowId);
              if (anchorIndex !== -1) {
                this.setRowSelectionAnchor(anchorIndex);
              }
            }
          });
        });
      }, 0);
    }
  );

  private trackFocusedCell = (e: FocusEvent) => {
    // Retained as a focus-tracking hook for future use; previously updated
    // page-relative cell focus state for the legacy paginated table.
    void e;
  };

  // The slice fed to useReactTable. As a computed getter, MobX memoizes it;
  // the same array reference is returned until virtualWindowStart/End or the
  // underlying rows change. This prevents useReactTable from rebuilding its
  // row model on every scroll tick.
  get windowedRows(): TableRow[] {
    return this.getVisibleRows().slice(
      this.virtualWindowStart,
      this.virtualWindowEnd
    );
  }

  getVisibleRows(): TableRow[] {
    const rows = this.getAllSortedRows().filter((r) => r.hidden !== true);
    if (this.searchQuery) {
      if (this.searchResultIds.size === 0) return [];
      return rows.filter((r) => this.searchResultIds.has(r.id));
    }
    return rows;
  }

  private isCellValueEmpty(value: unknown): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (
      Array.isArray(value) &&
      (value.length === 0 ||
        value.every(
          (v) =>
            v === null ||
            v === undefined ||
            (typeof v === 'string' && v.trim() === '')
        ))
    ) {
      return true;
    }
    return false;
  }

  private findDataBoundary(
    currentIndex: number,
    axis: { length: number; step: 1 | -1; isEmpty: (index: number) => boolean }
  ): number {
    const { length, step, isEmpty } = axis;
    const lastIndex = length - 1;
    const boundaryIndex = step === 1 ? lastIndex : 0;

    if (currentIndex === boundaryIndex) return currentIndex;

    if (isEmpty(currentIndex)) {
      for (let i = currentIndex + step; i >= 0 && i <= lastIndex; i += step) {
        if (!isEmpty(i)) return i;
      }
      return boundaryIndex;
    }

    const nextIndex = currentIndex + step;
    if (nextIndex < 0 || nextIndex > lastIndex) return currentIndex;

    if (isEmpty(nextIndex)) {
      for (let i = nextIndex + step; i >= 0 && i <= lastIndex; i += step) {
        if (!isEmpty(i)) return i;
      }
      return boundaryIndex;
    }

    for (let i = nextIndex + step; i >= 0 && i <= lastIndex; i += step) {
      if (isEmpty(i)) return i - step;
    }
    return boundaryIndex;
  }

  getCtrlArrowTargetIndex(
    currentRowIndex: number,
    columnId: keyof TableNode,
    direction: 'up' | 'down'
  ): number {
    const rows = this.getVisibleRows();
    if (currentRowIndex < 0 || currentRowIndex >= rows.length) {
      return currentRowIndex;
    }
    return this.findDataBoundary(currentRowIndex, {
      length: rows.length,
      step: direction === 'down' ? 1 : -1,
      isEmpty: (i) => this.isCellValueEmpty(rows[i][columnId]),
    });
  }

  getCtrlArrowTargetColumn(
    row: TableRow,
    currentColumnIndex: number,
    navigation: { direction: 'left' | 'right'; visibleColumns: string[] }
  ): number {
    const { direction, visibleColumns } = navigation;
    if (currentColumnIndex < 0 || currentColumnIndex >= visibleColumns.length) {
      return currentColumnIndex;
    }
    return this.findDataBoundary(currentColumnIndex, {
      length: visibleColumns.length,
      step: direction === 'right' ? 1 : -1,
      isEmpty: (i) =>
        this.isCellValueEmpty(row[visibleColumns[i] as keyof TableNode]),
    });
  }

  clearSelectedStartCell = action(() => {
    if (this.selectedStartCell === undefined) return;
    this.selectedStartCell = undefined;
  });

  isSelectedStartCell = computedFn((cellId: string): boolean => {
    return cellId === this.selectedStartCell?.cellId;
  });

  updateRangeSelection = (cell: CellSelection) => {
    const { selectedStartCell } = this;

    if (selectedStartCell === undefined) {
      return;
    }

    const selectedCellsInRange = tableStore.getCellsBetween(
      selectedStartCell,
      cell
    );

    const startIndex = this.selectedCells.findIndex(
      (c) => c.cellId === tableStore.selectedStartCell?.cellId
    );
    const prevSelectedCells = this.selectedCells.slice(0, startIndex);
    const newCellSelection = selectedCellsInRange.filter(
      (c) => c.cellId !== tableStore.selectedStartCell?.cellId
    );

    this.selectedCells.replace([
      ...prevSelectedCells,
      selectedStartCell,
      ...newCellSelection,
    ]);
  };

  getLastColumn() {
    return columnOrder[columnOrder.length - 1] as keyof TableNode;
  }

  set solutionVersionId(newSolutionVersionId: string) {
    // if (!newSolutionVersionId) {
    //   throw Error('a solution version id is required!');
    // }

    if (newSolutionVersionId === this._solutionVersionId) {
      return;
    }

    // PSTUDIO-3953: drain the save queues BEFORE the identity moves when the
    // SOLUTION changes. Both queues stamp `this.solutionVersionId` at flush
    // time (PSTUDIO-3669), so a flush that happens after this assignment would
    // commit the previous solution's queued rows into the new one — the
    // reported corruption shape (nodes appearing emptied / belonging to another
    // solution). Flushing here runs the handlers synchronously, while
    // `_solutionVersionId` still names the solution those rows were edited in.
    //
    // Deliberately NOT `cancel()`: dropping the queue would silently discard
    // the user's last edits. And deliberately scoped to a solution change — a
    // version-only change (a publish bump) must keep re-homing, which is the
    // behaviour PSTUDIO-3669 added on purpose.
    //
    // The empty case is the same defect with a different ending: leaving the
    // builder clears the identity, and `saveBulkRowsToDatabase`'s fail-safe
    // then drops the batch outright (it refuses to upsert against an empty
    // version). Flushing here commits those rows to the solution they were
    // edited in instead of discarding them.
    // PSTUDIO-3953: which of the two identity changes this is decides the
    // handling, and they are opposites:
    //   - same scope, different version (a publish bumps `v3` -> `v4`, or one
    //     SolutionVersion uuid to the next): the queued rows belong in the NEW
    //     version, so PSTUDIO-3669's flush-time re-home is correct and is kept;
    //   - different scope (the user switched solutions): re-homing would write
    //     one solution's rows into another, so the queue is drained first.
    //
    // The scope is CAPTURED here and compared against the one captured on the
    // previous write, never derived from `_solutionVersionId` at this point:
    // the Agent Manager v2 key is a bare uuid that encodes no solution, and
    // `this.botId` has already moved to the new solution by the time this runs,
    // so deriving both sides now would make every v2 solution switch look like
    // a version bump. See `solutionScopeOfVersionPsId`.
    const newSolutionScope = solutionScopeOfVersionPsId(
      newSolutionVersionId,
      this.botId
    );

    const leavingCurrentSolution =
      !newSolutionVersionId || newSolutionScope !== this._solutionScope;

    if (this._solutionVersionId && leavingCurrentSolution) {
      // The queue handler runs `validateAndSync` after the save unless the
      // batch opted out, and that validation reads customerId/botName/version
      // from the XState context — which the effect driving this setter has
      // ALREADY moved to the new solution — while uploading `this.sortedRows`,
      // which the reaction is about to clear. It would post an empty bot.csv
      // against the solution the user just opened. The rows themselves still
      // commit correctly (they are stamped synchronously, below); it is only
      // the trailing validation that has nowhere sensible to run, so drop it.
      // The version being left is re-validated whenever it is opened again.
      saveBatchNeedsValidation = false;
      saveRowsToDatabaseQueue.flush();
      saveRowOrdersToDatabaseQueue.flush();
    }

    this._solutionVersionId = newSolutionVersionId;
    this._solutionScope = newSolutionScope;

    // PSTUDIO-4103: the deferred-delete track is drained too — but AFTER the
    // assignment above, which is the opposite of the two queues and is the
    // whole point. The queues must commit while the store still names the
    // solution their rows were edited in. This track must not: its mutation is
    // already pinned to `deletingFor`, so it lands correctly whenever it runs,
    // and everything ELSE in that deferred body — the local `rows.replace`, the
    // group reconciliation, the starter row, the trailing `validateAndSync` —
    // belongs to a table that is about to be replaced. Draining before the
    // assignment made the body's own identity check pass and re-ran its
    // trailing `validateAndSync`, which reads customerId/botName/version from
    // the XState context — already moved to the new solution — and would post
    // the leaving solution's rows against the one just opened. That is the same
    // shape `saveBatchNeedsValidation = false` suppresses for the queues above,
    // but this is a SEPARATE call the flag never reaches: `stillOnThatVersion`
    // is the only thing that stops it, and only the order lets it say no. The reason to drain at all is that the idle callback
    // may not fire before the tab is closed, and then the delete is lost.
    // PSTUDIO-4342: draining takes the delete off the idle scheduler; it does
    // not put it on the wire — it still waits its turn behind the node writes
    // issued before it (_nodeWriteTail), and a tab closed inside that wait
    // loses it. The beforeunload guard counts it (`hasPendingNodeSaves`) on
    // the Workflows page only.
    if (leavingCurrentSolution) {
      this._flushPendingDeletes();
    }
  }

  /**
   * PSTUDIO-3953: does server data fetched for `forSolutionVersionId` still
   * belong to the version this store points at?
   *
   * The read path is asynchronous end to end — a query or a subscription
   * callback resolves long after it was issued, and the store may have moved to
   * another version (a publish bump) or another solution entirely (a tab switch)
   * in between. Applying such a payload writes one version's nodes over
   * another's; when the payload is empty it blanks the table outright.
   *
   * Only `undefined` means "the caller has no identity to assert". An EMPTY
   * STRING is a real store state (outside the builder) and is compared like any
   * other value — treating it as unasserted would let a future caller passing
   * `?? ''` silently lose the guard.
   *
   * Every production caller passes an identity today; the unasserted branch
   * exists for a caller rebuilding the current table from purely local data.
   */
  private belongsToCurrentVersion(forSolutionVersionId?: string): boolean {
    if (forSolutionVersionId === undefined) {
      return true;
    }
    if (forSolutionVersionId === this._solutionVersionId) {
      return true;
    }
    console.warn(
      '[tableStore] Dropping version data that no longer belongs to the loaded solution version:',
      {
        fetchedFor: forSolutionVersionId,
        storeIsOn: this._solutionVersionId,
      }
    );
    return false;
  }

  setErrors = action((errors: WorkflowError[]) => {
    this.errors = errors;
  });

  setPifErrors = action((errors: WorkflowError[]) => {
    this.pifErrors = errors;
  });

  /** PSTUDIO-4120 — see the `validationFailure` field for what the states mean. */
  setValidationFailure = action((reason: ValidationFailureReason | null) => {
    this.validationFailure = reason;
  });

  /**
   * Remove validation errors for the given row IDs.
   * Used when rows are deleted so the error list stays in sync, especially in manual validation mode.
   *
   * Addressed ID-FIRST, like every error source since PSTUDIO-3991 — and this
   * is not a nicety. The cleanup used to be keyed on the deleted row's NODE
   * NUMBER alone, which worked only by accident: an error on a numberless row
   * carried the raw `''` / `0` from the cell, those values landed in the number
   * set, and the row's errors went with it. PSTUDIO-3990 replaced them with
   * `nodeNumber: null` (a title must never name a number that does not exist),
   * and a null matches nothing — so the error SURVIVED its own row until the
   * next `validateAndSync` (in Manual mode until the user hits Validate) and
   * rendered as a "Solution error", with neither a number nor a row to click.
   * A group header is the same case by design: it holds `nodeNumber: null`, so
   * its id is its only address.
   *
   * The number-based branch stays for errors that carry NO id — a backend
   * diagnostic that reported only `node_num`.
   */
  removeErrorsForRowIds = action((rowIds: string[]) => {
    if (rowIds.length === 0) return;
    const rowIdsToRemove = new Set(rowIds);
    const nodeNumbersToRemove = new Set<string>();
    rowIds.forEach((id) => {
      const row = this.rows.find((r) => r.id === id);
      if (row?.nodeNumber != null) {
        nodeNumbersToRemove.add(row.nodeNumber);
      }
    });

    const belongsToDeletedRow = (err: WorkflowError): boolean => {
      if (err.nodeId != null && err.nodeId !== '') {
        return rowIdsToRemove.has(err.nodeId);
      }
      return err.nodeNumber != null && nodeNumbersToRemove.has(err.nodeNumber);
    };

    // Assign only when something was actually removed. Both lists are read by
    // `effectiveErrors`/`effectivePifErrors` and the eight per-cell indexes,
    // every one of which rebuilds on a new array identity — and this action
    // runs from the idle callback of EVERY delete, including the ones that
    // touch no error at all. Re-assigning an equal list there re-derived the
    // whole set for nothing (the write-minimality rule in
    // `.claude/rules/react-apps.md`: a MobX write fires even when the value is
    // equal).
    const nextErrors = this.errors.filter((err) => !belongsToDeletedRow(err));
    if (nextErrors.length !== this.errors.length) {
      this.errors = nextErrors;
    }

    const nextPifErrors = this.pifErrors.filter(
      (err) => !belongsToDeletedRow(err)
    );
    if (nextPifErrors.length !== this.pifErrors.length) {
      this.pifErrors = nextPifErrors;
    }
  });

  /**
   * In manual validation mode, clear the validation error for a specific cell
   * when the user edits that cell (e.g. on blur after data change).
   */
  clearCellValidationError = action(
    (rowId: string, columnId: keyof typeof NodeColumns) => {
      if (this.validationMode !== ValidationMode.Manual) {
        return;
      }
      const sortedRowIndex = this.getSortedRowIndex(rowId);
      if (sortedRowIndex === -1) {
        return;
      }
      this.errors = this.errors.filter(
        (err) => !(err.colId === columnId && err.rowIndex === sortedRowIndex)
      );
    }
  );

  // Per-cell error indexes. `errors`/`pifErrors` are reassigned wholesale by
  // setErrors/setPifErrors/removeErrorsForRowIds/clearCellValidationError, so
  // these computeds rebuild only when the error list actually changes and the
  // resulting Map/Set is shared across every cell — replacing the per-cell
  // `errors.find(...)` linear scans (~1250 cells × every commit). Plain
  // `@computed get` (NOT computedFn, which caches one entry per arg forever).
  // Keyed via cellErrorKey() — the same helper builds the lookup key in
  // cell.tsx, so the two sides can't drift; null-nodeNumber errors never match
  // a cell so they're skipped; first-match wins (only set when the key is
  // absent) to preserve the prior `.find()` semantics.
  // PSTUDIO-4000 (description point 2) + PSTUDIO-3990: BOTH node-number error
  // classes - a duplicate and a missing/invalid number - are DERIVED from the
  // current rows rather than waited for from a recompile, in one pass.
  //
  // `errors`/`pifErrors` are only ever filled by validateAndSync, so any
  // entrance that writes `rows` without triggering one — an orders-only save,
  // a RAW paste, a series of undo/redo, a Manual-mode short-circuit — left the
  // duplicate invisible until the user hit Publish. Deriving it here fixes
  // every entrance by construction, the same argument that already makes
  // `canPublish` entrance-agnostic (.claude/rules/cross-cutting-invariants.md).
  //
  // `deriveNodeNumberErrors` first STRIPS its own previous output out of the
  // passed-in list and then re-adds it, so a recompile that also reported the
  // problem cannot double-count it: the derived pass is the single source for
  // both error classes.
  get effectiveErrors(): WorkflowError[] {
    return deriveNodeNumberErrors(
      this.rowsForValidation,
      this.errors,
      this.nodeNumberErrorContext
    );
  }

  get effectivePifErrors(): WorkflowError[] {
    return deriveNodeNumberErrors(
      this.rowsForValidation,
      this.pifErrors,
      this.nodeNumberErrorContext
    );
  }

  /**
   * The row set the node-number checks walk: the publish gate
   * (`findNodeNumberProblem` in `canPublish`), the derived per-cell errors
   * (`deriveNodeNumberErrors` above) and `validateAndSync`'s JSON-field errors
   * (`buildJsonFieldErrors`). Two deliberate differences from `this.rows`,
   * both PSTUDIO-3990.
   *
   * ONE traversal is deliberately NOT on this set: `buildWorkflowErrors` in
   * `validateAndSync` walks `[...this.sortedRows].map(toJS)`, because that same
   * array is what `resolveErrorRowIndex` searches to address SERVER
   * diagnostics — swapping it would move the compiler errors' addresses. The
   * visible consequence is bounded and self-closing: a soft-deleted row can
   * leave a node-number error in `this.errors`, which `canPublish` reads, so
   * publish stays disabled until the same idle callback that compacts the array
   * runs `removeErrorsForRowIds`. That direction is fail-CLOSED — the opposite
   * of the stale-marker case above, and the safe one to be wrong in.
   *
   * (a) It INCLUDES `pendingRowsToRender`. Inside ONE method the JSON-field
   *     pass already walked `[...rows, ...pendingRowsToRender]` while the
   *     node-number pass walked `rows` only. That sleeps today (the population
   *     path is commented out — see `startingNodes.slice(50)`), but re-enabling
   *     chunked loading would make a blank number in the tail raise a JSON
   *     error while staying invisible to BOTH the publish gate and the cell
   *     highlight. One set for all three is what stops the two halves drifting
   *     again; the row-position note in `deriveNodeNumberErrors` already holds
   *     for a tail row, since its errors address the row by `nodeId` and print
   *     no row number.
   *
   * (b) It EXCLUDES rows marked for deletion — but only while they are STILL
   *     the rows that delete hid. A mark alone is not enough, and neither is
   *     `hidden` alone:
   *
   *     - `hidden` alone is wrong because a collapsed group hides rows too, and
   *       a row hidden inside a group still ships to the backend, so it must
   *       keep gating (`pendingDeletionRowIds` exists for exactly that split).
   *     - the mark alone is wrong because it outlives the delete. It is cleared
   *       in the idle callback that compacts the array, and `rows` can be
   *       REPLACED WHOLESALE before that runs — a coworker merge, a cross-tab
   *       reload, a version load, an undo restoring a snapshot. In a background
   *       tab `requestIdleCallback` can be deferred a long time. A row that
   *       came back that way is a live row again, but a stale mark kept
   *       subtracting it: a blank `Node #` in it would neither block publishing
   *       nor highlight — the gate failing OPEN, which is the dangerous
   *       direction.
   *
   *     Requiring both makes that self-correcting wherever the replacement
   *     happened, instead of depending on every such path remembering to clear
   *     the set: a returned row is visible, so it gates again on its own.
   *
   * Returns `this.rows` BY REFERENCE in the common case (no tail, nothing being
   * deleted), so the gate — read on every primary-nav render — allocates
   * nothing and adds no dependency beyond the two sizes it checks.
   */
  get rowsForValidation(): TableRow[] {
    const hasTail = this.pendingRowsToRender.length > 0;
    const pendingDeletionCount = this.pendingDeletionRowIds.size;
    if (!hasTail && pendingDeletionCount === 0) return this.rows;

    const all = hasTail
      ? [...this.rows, ...this.pendingRowsToRender]
      : this.rows.slice();

    return pendingDeletionCount === 0
      ? all
      : all.filter(
          (row) => !isClaimedByDelete(row, this.pendingDeletionRowIds)
        );
  }

  /** The resolver handed to `deriveNodeNumberErrors`. It must be injected
   *  rather than read off the `..` barrel inside the derivation: that runs from
   *  a computed, early enough that the barrel's `tableStore` binding is still
   *  undefined (PSTUDIO-4000).
   *
   *  Deliberately NOT also a row-number resolver. Handing one in makes the
   *  derivation depend on `getAllSortedRows()`, so every `order` write — a
   *  drag-reorder changes nothing else — re-sorts the table and re-derives
   *  errors for no change in the result (measured ~0.008 ms → ~8 ms per `order`
   *  write at 5,000 rows). The popover resolves the row number it prints from
   *  `nodeId` when it renders. */
  /*  A FIELD, not a getter. `makeAutoObservable` turns a getter into a
   *  `computed`, and this one reads no observable at all — it only closes over
   *  `this`. Inside a reactive context that computed is evaluated once and can
   *  never be invalidated; outside one (`validateAndSync` is an action) it
   *  allocates a NEW object on every read, twice per validation, and any
   *  `reaction` watching it would fire forever on the fresh identity alone. It
   *  is a constant, so it is stored as one — and annotated `false` in
   *  `makeAutoObservable` so MobX leaves the resolver function alone. */
  readonly nodeNumberErrorContext = {
    getColumnIndex: (colId: NodeColumns) => this.getColumnIndex(colId),
  };

  get errorsByCell(): Map<string, WorkflowError> {
    const map = new Map<string, WorkflowError>();
    for (const err of this.effectiveErrors) {
      if (err.nodeNumber == null) continue;
      const key = cellErrorKey(err.nodeNumber, err.colId);
      if (!map.has(key)) map.set(key, err);
    }
    return map;
  }

  get pifErrorsByCell(): Map<string, WorkflowError> {
    const map = new Map<string, WorkflowError>();
    for (const err of this.effectivePifErrors) {
      if (err.nodeNumber == null) continue;
      const key = cellErrorKey(err.nodeNumber, err.colId);
      if (!map.has(key)) map.set(key, err);
    }
    return map;
  }

  // Per-cell error indexes keyed by the stable node id (== the table row id;
  // verified against the agent-validator diagnostic's data.id). Unlike the
  // number-keyed indexes above, these address a cell even when its node number
  // is blank — the content-error case in PSTUDIO-3991 (and the blank-number
  // node shared with PSTUDIO-3990). cell.tsx looks these up first, by row id,
  // and only falls back to the number-keyed index.
  get errorsByCellId(): Map<string, WorkflowError> {
    const map = new Map<string, WorkflowError>();
    for (const err of this.effectiveErrors) {
      if (err.nodeId == null || err.nodeId === '') continue;
      const key = cellErrorKey(err.nodeId, err.colId);
      if (!map.has(key)) map.set(key, err);
    }
    return map;
  }

  get pifErrorsByCellId(): Map<string, WorkflowError> {
    const map = new Map<string, WorkflowError>();
    for (const err of this.effectivePifErrors) {
      if (err.nodeId == null || err.nodeId === '') continue;
      const key = cellErrorKey(err.nodeId, err.colId);
      if (!map.has(key)) map.set(key, err);
    }
    return map;
  }

  get errorRowNumbers(): Set<string> {
    const rowNumbers = new Set<string>();
    for (const err of this.effectiveErrors) {
      if (err.nodeNumber != null) rowNumbers.add(err.nodeNumber);
    }
    return rowNumbers;
  }

  get pifErrorRowNumbers(): Set<string> {
    const rowNumbers = new Set<string>();
    for (const err of this.effectivePifErrors) {
      if (err.nodeNumber != null) rowNumbers.add(err.nodeNumber);
    }
    return rowNumbers;
  }

  // Row-level error indexes keyed by node id (== the table row id), the id-first
  // counterpart of errorRowNumbers/pifErrorRowNumbers so a row whose node number
  // is blank still gets its row + index-cell error styling — symmetric with the
  // cell-level errorsByCellId (PSTUDIO-3991).
  get errorRowIds(): Set<string> {
    const ids = new Set<string>();
    for (const err of this.effectiveErrors) {
      if (err.nodeId != null && err.nodeId !== '') ids.add(err.nodeId);
    }
    return ids;
  }

  get pifErrorRowIds(): Set<string> {
    const ids = new Set<string>();
    for (const err of this.effectivePifErrors) {
      if (err.nodeId != null && err.nodeId !== '') ids.add(err.nodeId);
    }
    return ids;
  }

  isShiftKeyBeingPressed: boolean = false;

  selectCellViaKeyboardNavigation(shiftKey: boolean) {
    const maybeCellSelected = document.activeElement;

    // console.log('selectCellViaKeyboardNavigation', maybeCellSelected);

    if (maybeCellSelected instanceof HTMLTableCellElement) {
      const { rowId, cellId } = maybeCellSelected.dataset;

      if (rowId && cellId) {
        const hasCell = tableStore.hasCell(rowId, cellId);

        if (hasCell) {
          emitOnce(() => {
            if (shiftKey) {
              tableStore.selectCell({
                cellId: `${rowId}-${cellId}`,
                rowId,
                columnId: cellId as NodeColumns,
              });

              tableStore.updateRangeSelection({
                cellId: `${rowId}-${cellId}`,
                rowId,
                columnId: cellId as NodeColumns,
              });
            } else {
              tableStore.deselectAllCells();

              tableStore.setSelectedStartCell({
                cellId: `${rowId}-${cellId}`,
                rowId,
                columnId: cellId as NodeColumns,
              });

              tableStore.selectCell({
                cellId: `${rowId}-${cellId}`,
                rowId,
                columnId: cellId as NodeColumns,
              });
            }
          });
        }
      }
    }
  }

  toggleCellViaKeyboardNavigation(event: KeyboardEvent) {
    const maybeCellSelected = document.activeElement;

    if (maybeCellSelected instanceof HTMLTableCellElement) {
      const { rowId, cellId } = maybeCellSelected.dataset;

      if (rowId && cellId) {
        const hasCell = tableStore.hasCell(rowId, cellId);

        if (hasCell) {
          event.stopPropagation();
          event.preventDefault();

          tableStore.toggleCellInput(
            maybeCellSelected,
            rowId,
            cellId as keyof TableRow
          );
        }
      }
    }
  }

  toggleCellInput(
    cellRef: HTMLTableCellElement,
    rowId: string,
    cellId: keyof TableRow
  ) {
    // PSTUDIO-4450: the Enter hotkey reaches this without the click handlers'
    // read-only check, and a checkbox cell writes from here directly.
    if (this.isReadOnly) {
      return;
    }

    if (!cellRef) {
      // Defensive: cellRef is typed non-null, so this should be unreachable.
      // Warn instead of throwing — an uncaught throw inside an event handler
      // would take down nothing useful and hide the real problem.
      console.warn('toggleCellInput called without a cellRef', {
        rowId,
        cellId,
      });
      return;
    }
    // Inactive cells render only a <span> (no <input>/<select>/<textarea>), so
    // we can no longer decide what to do by querying the cell DOM. Branch on
    // the column's edit kind instead (single source of truth in
    // column-edit-kind.ts). Exhaustive switch: adding a ColumnEditKind without
    // handling it here is a compile error (`never` check in the default).
    const kind = getColumnEditKind(cellId);

    switch (kind) {
      case 'readonly':
        return;

      case 'checkbox': {
        // Checkboxes stay always-mounted and toggle on a single activation.
        const current = tableStore.getValue(rowId, cellId as keyof TableNode);
        tableStore.updateRow({
          rowId,
          updates: {
            [cellId]: !current,
          },
          skipValidationRules: false,
          saveImmediately: true,
        });
        return;
      }

      case 'behaviors': {
        // The behaviors multi-select stays always-mounted (design-system
        // ps-select behind a SelectPlaceholder). Open it directly — either the
        // live ps-select's .show() or, when still showing the placeholder, by
        // triggering the placeholder's click handler.
        const psSelect = cellRef.querySelector('ps-select') as {
          show?: () => void;
        } | null;
        const selectPlaceholder = cellRef.querySelector(
          '#select-placeholder'
        ) as HTMLElement | null;
        if (psSelect?.show) {
          tableStore.isCellBeingEdited = true;
          psSelect.show();
        } else if (selectPlaceholder) {
          tableStore.isCellBeingEdited = true;
          selectPlaceholder.click();
        } else {
          // Neither control is present (most likely a mount-timing race).
          // Surface it instead of leaving a dead click with no diagnostic.
          console.warn(
            'toggleCellInput: behaviors cell has no openable control',
            { rowId, cellId }
          );
        }
        return;
      }

      case 'select':
        // Native <select> columns (nodeType, richAssetType, command) are
        // always rendered in edit mode and open on direct click — nothing to
        // mount.
        return;

      case 'number':
      case 'text':
        // Clear any leaked close-intent from a prior cell so it can't carry
        // into this freshly opened one.
        tableStore.cellCloseIntent = null;
        // Lazily mount the control for this cell. pendingEditCellId then
        // drives the focus layout-effect in cell.tsx once it commits.
        tableStore.isCellBeingEdited = true;
        tableStore.editingCellId = `${rowId}-${cellId}`;
        // PSTUDIO-2821: commitCellEditing writes the draft using editingRowId /
        // editingColumnId — NOT editingCellId. The legacy EditableTableCell*
        // components used to set these on focus, but cell.tsx now renders the
        // native <input>/<textarea> directly and only this method opens them, so
        // they MUST be set here. Without them commitCellEditing's
        // `if (rowId && columnId && raw != null)` guard is false and a Tab/blur
        // commit silently drops the typed value (the draft never reaches the
        // store). Mount the control AND record which row/column it edits.
        tableStore.editingRowId = rowId;
        tableStore.editingColumnId = cellId;
        tableStore.pendingEditCellId = `${rowId}-${cellId}`;
        return;

      default: {
        // Compile-time exhaustiveness — a new ColumnEditKind must be handled.
        const unhandled: never = kind;
        return unhandled;
      }
    }
  }

  startCellEditingWithChar(char: string) {
    if (this.selectedCells.length !== 1) return;

    const selectedCell = this.selectedCells[0];
    const { rowId, columnId } = selectedCell;

    if (this.isCellDisabled(rowId, columnId as keyof TableRow)) return;

    const cellElement = document.activeElement;
    if (!(cellElement instanceof HTMLTableCellElement)) return;

    // Only text/textarea/number/array columns support type-to-edit. Selects,
    // checkboxes, the behaviors multi-select, and read-only columns are skipped
    // (matching the prior behavior, which bailed on ps-select/ps-checkbox).
    const kind = getColumnEditKind(columnId);
    if (kind !== 'text' && kind !== 'number') return;

    if (kind === 'number' && !/^[\d.-]$/.test(char)) {
      return;
    }

    // PSTUDIO-3813: seed the typed character into the DRAFT + the uncontrolled
    // control's DOM value ONLY — never into the store. The previous code wrote
    // the first character to the store via `updateRow` (outside `recordRowEdit`),
    // which polluted the undo baseline: the later commit recorded that SEED as
    // its "before" state, so an Undo reverted to the stale first character (the
    // reporter's `Test 13` -> `T`) instead of the cell's true prior value, the
    // fragment ALSO survived Escape/cancel (a draft discard never touched the
    // store) and it persisted across a reload. Seeding only the draft — the exact
    // path the array / `conversionIsEmpty` columns already used, which is why
    // those columns were never affected — keeps the store at its prior value
    // until the real recorded commit, so undo / redo / cancel all resolve to the
    // true original for EVERY column and entry gesture (type-to-edit, F2).
    this.pendingInitialChar = char;

    // PSTUDIO-1433: a JSON-capable column (richAssetContent / parameterInput /
    // analyticsEvent) is edited EXCLUSIVELY in the Expanded Cell View panel's
    // JSON editor — cell.tsx deliberately renders a read-only view for it in
    // EVERY state (isViewWhileSelected), so no inline <input>/<textarea> ever
    // mounts. The rAF loop below hunts for exactly that control, so on these
    // columns it burned MAX_INJECT_ATTEMPTS frames and then dropped the typed
    // character silently. Hand the character to the panel instead: address it to
    // THIS cell and return BEFORE the rAF loop. The panel's autofocus effect
    // applies it (resolveInitialCharSeed) only AFTER takeFocusSnapshot() has
    // recorded the pre-edit baseline, so the PSTUDIO-3813 contract above holds on
    // this path too — undo restores the cell's true prior value, not the typed
    // character. Non-JSON columns keep the inline rAF injection unchanged.
    if (isJsonCapableColumn(columnId)) {
      this.pendingInitialCharCellId = `${rowId}-${columnId}`;
      this.toggleCellInput(cellElement, rowId, columnId as keyof TableRow);
      return;
    }
    this.pendingInitialCharCellId = null;

    // Mount the input for this cell; the focus layout-effect in cell.tsx (keyed
    // on pendingEditCellId) focuses it after the control commits, and the rAF
    // loop below injects the typed char once it exists.
    this.toggleCellInput(cellElement, rowId, columnId as keyof TableRow);

    // The control is lazily mounted, so the first frame after toggleCellInput
    // may run before React commits it — retry for a few frames instead of
    // silently dropping the typed character on a slow commit.
    const MAX_INJECT_ATTEMPTS = 5;
    const injectTypedChar = (attempt: number) => {
      // A wrappable column replaces the selected cell's <input> with a
      // textarea once the edit commits. Writing the character into that
      // input and clearing pendingInitialChar drops it when the textarea
      // mounts with the stored value. Wait for the textarea.
      const preferTextarea =
        isWrappableTextColumn(columnId) && !isJsonCapableColumn(columnId);
      const input = (
        preferTextarea
          ? cellElement.querySelector('textarea')
          : cellElement.querySelector('input') ||
            cellElement.querySelector('textarea')
      ) as HTMLInputElement | HTMLTextAreaElement | null;
      if (!input) {
        if (attempt < MAX_INJECT_ATTEMPTS) {
          requestAnimationFrame(() => injectTypedChar(attempt + 1));
        } else {
          this.pendingInitialChar = null;
        }
        return;
      }
      // Inject the char via the native value setter + a bubbling `input` event so
      // React's onInput fires and routes it into `draftCellValue` — the same
      // mechanism the array columns already used, now applied to EVERY column so
      // none seeds the store. A number <input> rejects setSelectionRange, so
      // guard it (matches the prior code).
      input.focus();
      const proto =
        input instanceof HTMLTextAreaElement
          ? HTMLTextAreaElement.prototype
          : HTMLInputElement.prototype;
      const nativeSetter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
      if (nativeSetter) {
        nativeSetter.call(input, char);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        if (input instanceof HTMLTextAreaElement || input.type !== 'number') {
          input.setSelectionRange(char.length, char.length);
        }
      }
      this.pendingInitialChar = null;
    };
    requestAnimationFrame(() => injectTypedChar(0));
  }

  clipboardDetails: {
    rowId: string;
    cellId: keyof TableRow;
    valueAtTimeOfCopy: string;
    /**
     * PSTUDIO-3968: true when this in-app copy could NOT be mirrored to the OS
     * clipboard. It makes the in-memory buffer authoritative even against an
     * EMPTY system read — guard rail 7 keeps an internal Ctrl+C → Ctrl+V working
     * while the OS clipboard is blocked. Without this flag, refusing the buffer
     * on an empty read (which is what stops a stale value from resurrecting)
     * would also break that legitimate case.
     */
    systemWriteFailed: boolean;
  } = {
    rowId: '',
    cellId: NodeColumns.message,
    valueAtTimeOfCopy: '',
    systemWriteFailed: false,
  };

  setColumnSize(columnId: keyof ColumnsWidths, width: number) {
    this._columnWidths[columnId] = width;

    // PSTUDIO-4450: with no synced store to take them, view-settings writes
    // are dropped here and in the setters below — never sent to Hasura.
    if (this.liveEditingSource) {
      this.liveViewSettings?.updateViewSettings({
        columnWidths: toJS(this._columnWidths),
      });
      return;
    }

    urqlGql
      .updateColumnsWidth({
        solutionVersionId: this.solutionVersionId,
        columnWidths: this._columnWidths,
      })
      .toPromise()
      .then((result) => {
        if (result.error) {
          // sendToast({
          //   header: 'Column sizes saving Error',
          //   type: 'error',
          //   text: result.error.message,
          //   toastId: 'saved',
          //   closeButton: true,
          // });
        } else {
          // PSTUDIO-4342: a column-width save repairs no node write, so it must
          // not take away an unrepaired "Autosave Error".
          this._reconcileNodeWriteToasts();
          // sendToast({
          //   type: 'success',
          //   header: 'Column sizes saving',
          //   text: 'Saved to DB',
          //   autoClose: 2000,
          //   toastId: 'saved',
          //   pauseOnHover: false,
          //   pauseOnFocusLoss: false,
          // });
        }
      })
      .catch((err) => {
        sendUserMessage({
          type: 'error',
          text: 'Error updating column size: ' + err.message,
          autoClose: 3000,
        });
      });
  }

  getColumnSize(columnId: keyof ColumnsWidths) {
    return this._columnWidths[columnId] ?? columnDefaultSizes.width;
  }

  set columnsSizes(data: ColumnsWidths | null) {
    if (!data) {
      return;
    }

    this._columnWidths = {
      ...defaultColumnWidths,
      ...data,
    };
  }

  get columnsSizes() {
    return this._columnWidths;
  }

  set columnPinningSettings(data: ColumnPinningSettings | null) {
    if (!data) {
      return;
    }

    this._columnPinningSettings = {
      ...defaultColumnPinningSettings,
      ...data,
    };
  }

  get columnPinningSettings(): ColumnPinningSettings {
    return this._columnPinningSettings ?? defaultColumnPinningSettings;
  }

  // Apply a pinning snapshot to the store and persist it. No undo/redo history
  // side effect — shared by the live setColumnPinningSettings path and the
  // pin/unpin command (PSTUDIO-2154).
  private _applyColumnPinning = action((settings: ColumnPinningSettings) => {
    this._columnPinningSettings = {
      ...defaultColumnPinningSettings,
      ...settings,
    };

    if (this.liveEditingSource) {
      this.liveViewSettings?.updateViewSettings({
        columnPinningSettings: toJS(this._columnPinningSettings),
      });
      return;
    }

    urqlGql
      .updateColumnPinningSettings({
        solutionVersionId: this.solutionVersionId,
        columnPinningSettings: this._columnPinningSettings,
      })
      .toPromise()
      .then((result) => {
        if (result.error) {
          // Error handling - silent fail similar to column widths
        } else {
          // PSTUDIO-4342: see setColumnSize.
          this._reconcileNodeWriteToasts();
        }
      })
      .catch((err) => {
        sendUserMessage({
          type: 'error',
          text: 'Error updating column pinning settings: ' + err.message,
          autoClose: 3000,
        });
      });
  });

  setColumnPinningSettings(
    updaterOrValue:
      | ColumnPinningSettings
      | ((old: ColumnPinningSettings) => ColumnPinningSettings)
  ) {
    // PSTUDIO-2154: snapshot before/after and register a single undo/redo
    // command so Cmd+Z can revert a pin/unpin. The TanStack updater reads the
    // current (unmutated) pinning, so capture `before` first.
    const before = toJS(this._columnPinningSettings);
    const newValue =
      typeof updaterOrValue === 'function'
        ? updaterOrValue(this._columnPinningSettings)
        : updaterOrValue;
    const after: ColumnPinningSettings = {
      ...defaultColumnPinningSettings,
      ...newValue,
    };

    this._applyColumnPinning(after);

    if (isEqual(before, after)) return;
    this.pushCommandWithoutExecute({
      execute: () => this._applyColumnPinning(after),
      undo: () => this._applyColumnPinning(before),
      description: 'Pin columns',
    });
  }

  set columnWrapSettings(data: ColumnWrapSettings | null) {
    if (!data) return;
    this._columnWrapSettings = resolveColumnWrapSettings(data);
  }

  get columnWrapSettings(): ColumnWrapSettings {
    return this._columnWrapSettings ?? defaultColumnWrapSettings;
  }

  // Persist a wrap snapshot; no undo side effect (shared by the live setter
  // and the undo/redo command), mirroring _applyColumnPinning (PSTUDIO-2870).
  private _applyColumnWrap = action((settings: ColumnWrapSettings) => {
    this._columnWrapSettings = resolveColumnWrapSettings(settings);
    if (this.liveEditingSource) {
      this.liveViewSettings?.updateViewSettings({
        // resolveColumnWrapSettings keeps booleans only, so no undefined
        // value reaches the JSON field.
        columnWrapSettings: toJS(this._columnWrapSettings) as Record<
          string,
          boolean
        >,
      });
      return;
    }
    urqlGql
      .updateColumnWrapSettings({
        solutionVersionId: this.solutionVersionId,
        columnWrapSettings: this._columnWrapSettings,
      })
      .toPromise()
      .then((result) => {
        // PSTUDIO-4342: see setColumnSize.
        if (!result.error) this._reconcileNodeWriteToasts();
      })
      .catch((err) => {
        sendUserMessage({
          type: 'error',
          text: 'Error updating text wrap settings: ' + err.message,
          autoClose: 3000,
        });
      });
  });

  setColumnWrap(columnId: string, enabled: boolean) {
    const before = toJS(this._columnWrapSettings);
    const after = toggleColumnWrap(before, columnId, enabled);
    if (isEqual(before, after)) return;
    this._applyColumnWrap(after);
    this.pushCommandWithoutExecute({
      execute: () => this._applyColumnWrap(after),
      undo: () => this._applyColumnWrap(before),
      description: 'Toggle text wrap',
    });
  }

  // PSTUDIO-2154: column sort state. Lives in the store (not in <Table/> local
  // React state as before) so undo/redo commands can drive it like pinning.
  // Fed to useReactTable as `state.sorting`; `onSortingChange` routes back to
  // `setSorting`. Not persisted to the DB — undo only restores the in-session
  // sort; a page refresh clears it regardless. Reset on solution-version switch
  // via clearTable.
  sortingState: SortingState = [];

  // Apply a sort snapshot with no history side effect — shared by the live
  // setSorting path and the sort command's execute/undo.
  private _applySorting = action((sorting: SortingState) => {
    this.sortingState = sorting;
  });

  setSorting(
    updaterOrValue: SortingState | ((old: SortingState) => SortingState)
  ) {
    const before = toJS(this.sortingState);
    const after =
      typeof updaterOrValue === 'function'
        ? updaterOrValue(this.sortingState)
        : updaterOrValue;

    this._applySorting(after);

    if (isEqual(before, after)) return;
    this.pushCommandWithoutExecute({
      execute: () => this._applySorting(after),
      undo: () => this._applySorting(before),
      description: 'Sort columns',
    });
  }

  orderIncrementV2 = 1000;

  constructor() {
    // Remove makeAutoObservable and manually create observables

    // NOT `this.rows`. Fuse keeps the array it is given BY REFERENCE as its
    // document list (`setCollection(docs) { this._docs = docs }`) and mutates
    // it in place: `add()` pushes onto it, `removeAt()` splices out of it.
    // Handing it the observable the table renders from aliases the index onto
    // the live rows, so every `fuse.add(row)` — this store makes ~30 of those,
    // and `mergeVersionData` is built entirely on them — appends a SECOND copy
    // of the row just inserted. A teammate's subscription push arriving before
    // any full load (which re-points the index via `setCollection`) then walks
    // an N-row table to 2N. The index gets its own array; the store keeps the
    // two in step through the add/remove pairs it already makes.
    this.fuse = new Fuse([] as TableRow[], {
      keys: [
        {
          name: NodeColumns.nodeNumber,
          weight: 3,
        },
        {
          name: NodeColumns.nodeName,
          weight: 2.7,
        },
        // Group rows expose their user-facing label via settings.groupName;
        // PSTUDIO-2349 asks for "Ability to search for groups". Weighted
        // alongside nodeName so a group is as findable as a regular node.
        {
          name: 'groupName',
          weight: 2.7,
          getFn: (item) => item.settings?.groupName ?? '',
        },
        {
          name: NodeColumns.richAssetContent,
          weight: 1.8,
          getFn: (item) => JSON.stringify(item.richAssetContent),
        },

        {
          name: NodeColumns.parameterInput,
          weight: 1.8,
          getFn: (item) => JSON.stringify(item.parameterInput),
        },
        NodeColumns.nodeType,
        NodeColumns.message,
        NodeColumns.richAssetType,
        NodeColumns.behaviors,
        NodeColumns.nextNodes,
        NodeColumns.cssClassname,
        NodeColumns.command,
        NodeColumns.description,
        NodeColumns.whatsNext,
        NodeColumns.variables,
      ],
      // PSTUDIO-3052: exact (non-fuzzy) matching. Behaviour lives in
      // node-search.ts so it can be unit-tested without loading this store.
      ...NODE_SEARCH_FUSE_MATCH_OPTIONS,
    });

    makeAutoObservable(this, {
      // PSTUDIO-3723: the command-history stacks now live in `this.history`
      // (UndoRedoManager, self-observable); re-exposed as undoStack/redoStack
      // getters below for backward-compat (tests reset/read them).
      // currentState: observable,
      isValidating: observable,
      hasUnvalidatedChangesInManualMode: observable,
      // PSTUDIO-4120. `makeAutoObservable` would infer this anyway — the
      // annotation is here for symmetry with the two fields above, which state
      // the same intent explicitly. What it does buy is a place to say WHY the
      // field must stay observable: `canPublish` (a computed) and the validation
      // popover (an always-mounted observer) both read it.
      validationFailure: observable,
      sortedRows: computed,
      // PSTUDIO-4083: a plain predicate, NOT an action. `makeAutoObservable`
      // would annotate it `autoAction`, and it is called once PER ROW inside
      // three filters — an action wrapper (spy check + startBatch/endBatch) on
      // every row of a 5 000-row table, for a function that only reads a Set.
      isRowPendingDeletion: false,
      nodeCounts: computed,
      setRows: action,
      selectedStartCell: observable,
      validateAndSync: action,
      hasPendingRowsToRender: observable,
      goToNode: action,
      // @ts-ignore — a `when` disposer handle, never rendered. Observable it
      // would fire the table's top-level observer on every solution load.
      _cancelAutoGroupRetry: false,
      // @ts-ignore
      _inflightSavePromise: false,
      // @ts-ignore — bookkeeping for `templateUploadsSettled`; nothing renders it.
      _templateUploads: false,
      // PSTUDIO-3990: a constructor constant carrying one resolver function —
      // see the field. Observability would buy nothing and cost the identity
      // stability the derivation callers rely on.
      nodeNumberErrorContext: false,
      // PSTUDIO-4450: the collab store is its own MobX object; hold the
      // reference, never a deep observable copy of it.
      liveViewSettings: observable.ref,
      liveWriter: observable.ref,
      // A constant function, not state.
      convertCellText: false,
      // PSTUDIO-4449: kept unwrapped so its `.cancel()` survives — an action
      // wrapper is a plain function and drops lodash's methods.
      debouncedValidateAndSync: false,
      // @ts-ignore private field: a timer handle, never rendered.
      liveLocalValidation: false,
      // PSTUDIO-3968: per-keystroke paste plumbing, never rendered — keeping it
      // observable would fire the table's top-level observer on every Ctrl+V.
      pendingKeyboardPaste: false,
      // PSTUDIO-1433: the same reasoning on the SCROLL path — this latch is
      // written on every scroll event while a menu is open and nothing renders
      // from it, so observability would buy a table-wide re-render per event.
      contextMenuCloseRequested: false,
      // @ts-ignore — bookkeeping for "did our OS mirror land", read only when
      // resolving a range paste; nothing renders it.
      cellClipboardWriteFailed: false,
      // @ts-ignore — mirror bookkeeping for the range paste; nothing renders it.
      cellClipboardMirrorPending: false,
      // @ts-ignore
      cellClipboardTsv: false,
      // @ts-ignore
      cellClipboardEpoch: false,
      // @ts-ignore — timer handle + the retire helper: plumbing, never rendered.
      keyboardPasteFallbackTimer: false,
      // @ts-ignore
      consumeKeyboardPasteHandover: false,
      // @ts-ignore
      clipboardPasteListener: false,
      handleTableClipboardPaste: false,
      armKeyboardPasteHandover: false,
      // @ts-ignore
      _inflightOrderSavePromise: false,
      // @ts-ignore — PSTUDIO-4342: write ordering plumbing, never rendered.
      _nodeWriteTail: false,
      // @ts-ignore
      _nodeWriteProgressListeners: false,
      // @ts-ignore
      _failedNodeSaves: false,
      // @ts-ignore
      _failedNodeOrderSaves: false,
      _noteNodeWriteProgress: false,
      _waitForPreviousNodeWrite: false,
      _chainNodeWrite: false,
      _inNodeWriteOrder: false,
      _isLiveRow: false,
      _unrepairedFailures: false,
      _unrepairedSaves: false,
      _dropRefusedWrites: false,
      _requeueFailedNodeSaves: false,
      _idsInFlight: false,
      _refusedLiveRowIds: false,
      _reportRefusedWritesLost: false,
      _showSaveRefusal: false,
      _tooLargeRows: false,
      _serverKnownIds: false,
      _refusalVisit: false,
      _lost: false,
      _toastSeq: false,
      _tooLargeShown: false,
      _raiseNodeWriteToast: false,
      _showTooLarge: false,
      _reconcileNodeWriteToasts: false,
      _tooLargeText: false,
      _reportLateRefusal: false,
      _sendNodeDeleteChunks: false,
      _dropQueuedNodeWrites: false,
      _deleteOneNode: false,
      // A plain getter: it reads non-observable maps, so a cached computed
      // would answer from stale state.
      hasUnsavedWork: false,
      // @ts-ignore — PSTUDIO-3929: transient in-flight save id maps, not observable
      _inflightSaveBatches: false,
      // @ts-ignore
      _inflightOrderSaveBatches: false,
      // @ts-ignore — PSTUDIO-4103: same for the deferred-delete track.
      _pendingDeletes: false,
      _failedNodeDeletes: false,
      // @ts-ignore
      _validationModePerBot: false,
      // @ts-ignore — Project Bobcat: the deferred-open timer handle. Nothing
      // renders it, and `makeAutoObservable` would otherwise make it observable
      // despite the comment on its declaration saying it is not.
      pendingPanelOpenTimer: false,
      // @ts-ignore — PSTUDIO-2821: transient edit-buffer fields, not observable
      draftCellValue: false,
      // @ts-ignore
      cellCloseIntent: false,
      writeCellValue: action,
      commitCellEditing: action,
      cancelCellEditing: action,
      setDraftCellValue: action,
      setCellCloseIntent: action,
    });

    document.addEventListener('focusin', this.trackFocusedCell);

    hotkeys('*', { keyup: true, keydown: true }, function (event, _handler) {
      if (
        event.type === 'keydown' &&
        hotkeys.cmd &&
        !tableStore.isMetaKeyPressed
      ) {
        tableStore.setMetaKeyState(true);
      }

      if (event.type === 'keyup' && tableStore.isMetaKeyPressed) {
        tableStore.setMetaKeyState(false);
      }

      if (
        hotkeys.getPressedKeyString().includes('tab') &&
        hotkeys.getPressedKeyString().includes('⇧')
      ) {
        tableStore.isShiftKeyBeingPressed = false;
      } else if (
        event.type === 'keydown' &&
        tableStore.isShiftKeyBeingPressed === false &&
        hotkeys.shift
      ) {
        tableStore.isShiftKeyBeingPressed = true;
      }

      if (
        event.type === 'keyup' &&
        tableStore.isShiftKeyBeingPressed === true &&
        !hotkeys.shift
      ) {
        tableStore.isShiftKeyBeingPressed = false;
      }
    });

    hotkeys('escape', (_event) => {
      // event.preventDefault();
      // event.stopPropagation();

      emitOnce(() => {
        tableStore.deselectAllRows();

        tableStore.clearRowClipboard();
        tableStore.clearCellClipboard();

        if (
          document.activeElement &&
          'blur' in document.activeElement &&
          typeof document.activeElement.blur === 'function'
        ) {
          if (
            // document.activeElement instanceof HTMLTableCellElement
            tableStore.isCellBeingEdited === false
          ) {
            runInAction(() => {
              this.deselectAllCells();

              this.clearSelectedStartCell();
            });

            document.activeElement?.blur();
          }
        }
      });

      // if (this.selectedNodes || this.selectedEdges) {
      //   this.deleteElements({
      //     nodes: this.selectedNodes || [],
      //     edges: this.selectedEdges || [],
      //   });
      // }
    });

    hotkeys('backspace,delete', (_event) => {
      if (tableStore.isCellBeingEdited) return;
      if (tableStore.isReadOnly) return;
      // event.preventDefault();
      // event.stopPropagation();

      console.log('clear cell');

      if (
        !tableStore.isCellBeingEdited &&
        tableStore.selectedCells.length === 1
      ) {
        const maybeCellSelected = document.activeElement;
        if (
          maybeCellSelected instanceof HTMLTableCellElement &&
          maybeCellSelected.classList?.contains('is-selected')
        ) {
          const { rowId, cellId } = maybeCellSelected.dataset;

          if (rowId && cellId) {
            const hasCell = tableStore.hasCell(rowId, cellId);
            if (!hasCell) return;

            const typedCellId = cellId as keyof TableRow;

            // Snapshot the value before clearing so the clear is undoable
            // (PSTUDIO-2154).
            const before = {
              [typedCellId]: toJS(tableStore.getValue(rowId, typedCellId)),
            } as Partial<TableRow>;

            tableStore.updateRow({
              rowId,
              updates: {
                [typedCellId]: getDefaultValueForColumn(typedCellId),
              },
              skipValidationRules: false,
              saveImmediately: true,
            });

            tableStore.commitCellEdit({
              rowId,
              columnId: typedCellId,
              before,
            });
          }
        }
      }

      // if (this.selectedNodes || this.selectedEdges) {
      //   this.deleteElements({
      //     nodes: this.selectedNodes || [],
      //     edges: this.selectedEdges || [],
      //   });
      // }
    });

    hotkeys('command+v,ctrl+v', (event) => {
      if (!tableStore.isEditing()) return;
      if (tableStore.isCellBeingEdited) return;

      // In-app multi/single cell clipboard present → fill paste.
      //
      // PSTUDIO-3968 (round 3): this branch ALSO hands over to the native `paste`
      // event instead of cancelling the keydown. Cancelling suppressed the event —
      // the only permission-free view of the system clipboard — so once a cell had
      // been copied in-app, every later Ctrl+V replayed that STALE value: copying a
      // URL in another tab changed nothing, and pressing Escape appeared to "fix"
      // it only because `clearCellClipboard()` drops this branch's precondition.
      // `handleTableClipboardPaste` now compares the event's TSV with our mirrored
      // grid and pastes whichever is genuinely the last copy (guard rail 8).
      if (tableStore.hasCellClipboard && tableStore.selectedCells.length > 0) {
        tableStore.armKeyboardPasteHandover({ kind: 'cells' });
        return;
      }

      const selectedElement = document.activeElement as HTMLElement;
      const isChildOfTable = !!selectedElement?.closest('#ps-editor-table');
      const { rowId, cellId } = selectedElement?.dataset ?? {};

      // Single focused cell. If the clipboard holds a node bundle, REPLACE this
      // cell's row with the node(s) (cross-tab / cross-solution — PSTUDIO-3337);
      // otherwise a single-cell text paste.
      //
      // PSTUDIO-3968: this branch deliberately does NOT `preventDefault()`. The
      // permission-free way to read the clipboard is the browser's own `paste`
      // event (`ClipboardEvent.clipboardData`), and cancelling the keydown would
      // stop that event from ever being emitted. So we let the default run and
      // `handleTableClipboardPaste` (registered below) does the work with the
      // event's payload; the marker below keeps the two paths mutually exclusive.
      if (isChildOfTable && rowId && cellId) {
        tableStore.armKeyboardPasteHandover({
          kind: 'cell',
          cellId: cellId as keyof TableRow,
          rowId,
        });
        return;
      }

      // Row paste — REPLACES the selection (single row, or the whole multi-row
      // selection). Trigger on the SELECTION (not the in-memory clipboard) so a
      // cross-tab / Google-Sheets paste can hydrate from the system clipboard
      // first (PSTUDIO-3337).
      //
      // PSTUDIO-3968: this branch ALSO hands over to the native `paste` event.
      // `pasteRowsFromSystemOrMemory` → `refreshRowClipboardUnlessCut` →
      // `hydrateRowClipboardFromSystem` reads the OS clipboard through the SAME
      // permission-gated API, so keeping `preventDefault()` here left the identical
      // defect in place for a row paste (a cross-tab / Google-Sheets node paste
      // simply never arrived, and without even a toast). A same-tab CUT stays
      // in-memory and needs no clipboard at all, so it keeps the synchronous path.
      if (tableStore.selectedRows.size > 0) {
        if (tableStore.isClipboardCut) {
          event.preventDefault();
          event.stopPropagation();
          void tableStore.pasteRowsFromSystemOrMemory();
          return;
        }
        tableStore.armKeyboardPasteHandover({ kind: 'rows' });
      }
    });

    // PSTUDIO-3968 — the native clipboard path for a focused-cell paste.
    //
    // `ClipboardEvent.clipboardData` is readable WITHOUT the `clipboard-read`
    // permission, which is the whole point: `navigator.clipboard.readText()`
    // rejects in a real browser that never granted it, and the old code turned
    // that rejection into an empty write that CLEARED the cell. Capture phase, so
    // it runs before anything downstream can cancel the event. Registered here
    // next to the hotkeys because this store is a module-level singleton with the
    // same lifetime as those registrations (`hotkeys(...)` above is likewise never
    // unbound); `disposeClipboardPasteListener` exists for tests/teardown.
    this.clipboardPasteListener = (event: ClipboardEvent) =>
      tableStore.handleTableClipboardPaste(event);
    document.addEventListener('paste', this.clipboardPasteListener, true);

    hotkeys('command+c,ctrl+c', (event) => {
      if (tableStore.isCellBeingEdited) return;

      // Multi-cell copy — a non-empty cell selection wins (Google Sheets).
      if (tableStore.selectedCells.length > 0) {
        event.preventDefault();
        event.stopPropagation();
        tableStore.copySelectedCells();
        return;
      }

      const selectedElement = document.activeElement as HTMLElement;
      const isChildOfTable = !!selectedElement?.closest('#ps-editor-table');
      const { rowId, cellId } = selectedElement?.dataset ?? {};

      // Single focused-but-unselected cell (legacy path).
      if (isChildOfTable && rowId && cellId) {
        event.preventDefault();
        event.stopPropagation();
        this.handleCopy({ cellId: cellId as keyof TableRow, rowId });
        return;
      }

      // Row copy — selected rows (Google-Sheets style). Row selection does
      // not focus the table, so this keys off selectedRows, not focus.
      if (tableStore.selectedRows.size > 0) {
        event.preventDefault();
        event.stopPropagation();
        tableStore.copySelectedRows();
      }
    });

    hotkeys('command+x,ctrl+x', (event) => {
      if (!tableStore.isEditing()) return;
      if (tableStore.isCellBeingEdited) return;

      if (tableStore.selectedCells.length > 0) {
        event.preventDefault();
        event.stopPropagation();
        tableStore.cutSelectedCells();
        return;
      }

      const selectedElement = document.activeElement as HTMLElement;
      const isChildOfTable = !!selectedElement?.closest('#ps-editor-table');
      const { rowId, cellId } = selectedElement?.dataset ?? {};

      if (isChildOfTable && rowId && cellId) {
        if (tableStore.isCellDisabled(rowId, cellId as keyof TableRow)) return;
        event.preventDefault();
        event.stopPropagation();
        this.handleCut({ cellId: cellId as keyof TableRow, rowId });
        return;
      }

      if (tableStore.selectedRows.size > 0) {
        event.preventDefault();
        event.stopPropagation();
        tableStore.cutSelectedRows();
      }
    });

    // PSTUDIO-2154: undo/redo. The comma-separated combos cover macOS
    // (Cmd+Z / Cmd+Shift+Z) and Windows (Ctrl+Z / Ctrl+Shift+Z / Ctrl+Y) in a
    // single registration each. The global hotkeys.filter already blocks these
    // while a TEXTAREA/INPUT cell editor is focused, so native text-undo wins
    // mid-edit; the isCellBeingEdited guard additionally covers web-component
    // editors (e.g. ps-select) that aren't form elements.
    hotkeys('command+z,ctrl+z', (event) => {
      if (tableStore.isReadOnly) return;
      if (tableStore.isCellBeingEdited) return;
      event.preventDefault();
      event.stopPropagation();
      if (this.canUndo) {
        this.undo();
      }
    });

    hotkeys('command+shift+z,ctrl+shift+z,ctrl+y', (event) => {
      if (tableStore.isReadOnly) return;
      if (tableStore.isCellBeingEdited) return;
      event.preventDefault();
      event.stopPropagation();
      if (this.canRedo) {
        this.redo();
      }
    });

    // PSTUDIO-3602: toggle "Auto-renumber pasted nodes" (Smart ⇄ Raw). Gated by
    // the module-level hotkeys.filter (suppressed inside inputs/modals, same as
    // undo/redo — press Escape out of a cell first). preventDefault so the
    // browser's own Ctrl+Shift+S (e.g. "save page as") never steals it.
    hotkeys('command+shift+s,ctrl+shift+s', (event) => {
      if (tableStore.isReadOnly) return;
      event.preventDefault();
      event.stopPropagation();
      this.toggleAutoRenumberOnPaste();
    });

    hotkeys('left', (event) => {
      // event.preventDefault();
      // event.stopPropagation();

      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (!tableStore.isCellBeingEdited) {
        tableStore.selectCellViaKeyboardNavigation(event.shiftKey);
      }
    });

    hotkeys('right', (event) => {
      // event.preventDefault();
      // event.stopPropagation();
      // console.log('right-arrow');

      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (!tableStore.isCellBeingEdited) {
        tableStore.selectCellViaKeyboardNavigation(event.shiftKey);
      }
    });

    hotkeys('up', (event) => {
      console.log('up-arrow');
      if (
        !tableStore.isCellBeingEdited &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey
      ) {
        tableStore.selectCellViaKeyboardNavigation(event.shiftKey);
      }
    });

    hotkeys('down', (event) => {
      if (
        !tableStore.isCellBeingEdited &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey
      ) {
        tableStore.selectCellViaKeyboardNavigation(event.shiftKey);
      }
    });

    // Shift+Arrow range extension (PSTUDIO-2372). Registered as explicit
    // shift+ combos because hotkeys-js modifier matching is strict: the plain
    // 'up'/'down'/'left'/'right' handlers above never fire while Shift is held.
    // ORDER MATTERS: hotkeys-js treats the LAST '+'-segment as the main key
    // and everything before it as modifiers — 'shift+down' works, 'down+shift'
    // registers under the Shift keycode with a broken mods list and never
    // fires (the pre-2372 'down+shift' registration was dead code).
    hotkeys('shift+up,shift+down,shift+left,shift+right', (event) => {
      if (tableStore.isCellBeingEdited) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      // ROW MODE — row and cell selection are mutually exclusive (behavior
      // contract guard rail 2), so a non-empty selectedRows means Shift+Arrow
      // extends the row range instead of the cell range.
      if (tableStore.selectedRows.size > 0) {
        // Focus is usually <body> in row mode — block the default page scroll.
        event.preventDefault();
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
          tableStore.extendRowSelectionByKeyboard(
            event.key === 'ArrowUp' ? 'up' : 'down'
          );
        }
        // Shift+Left/Right in row mode: deliberate no-op (Sheets stays in
        // row mode).
        return;
      }

      // CELL MODE — table-nav (React root listener) already moved focus
      // before this document-level handler fires.
      tableStore.selectCellViaKeyboardNavigation(true);
    });

    hotkeys('command+down,ctrl+down', (event) => {
      if (tableStore.isCellBeingEdited) return;

      const { activeElement } = document;
      if (!(activeElement instanceof HTMLTableCellElement)) return;

      const { rowId, cellId } = activeElement.dataset;
      if (!rowId || !cellId) return;

      event.preventDefault();
      event.stopPropagation();

      const rows = this.getVisibleRows();
      const currentIndex = rows.findIndex((r) => r.id === rowId);
      if (currentIndex === -1) return;

      const targetIndex = this.getCtrlArrowTargetIndex(
        currentIndex,
        cellId as keyof TableNode,
        'down'
      );
      if (targetIndex === currentIndex) return;

      this.scrollToAndFocusCell(targetIndex, cellId as NodeColumns);
    });

    hotkeys('command+up,ctrl+up', (event) => {
      if (tableStore.isCellBeingEdited) return;

      const { activeElement } = document;
      if (!(activeElement instanceof HTMLTableCellElement)) return;

      const { rowId, cellId } = activeElement.dataset;
      if (!rowId || !cellId) return;

      event.preventDefault();
      event.stopPropagation();

      const rows = this.getVisibleRows();
      const currentIndex = rows.findIndex((r) => r.id === rowId);
      if (currentIndex === -1) return;

      const targetIndex = this.getCtrlArrowTargetIndex(
        currentIndex,
        cellId as keyof TableNode,
        'up'
      );
      if (targetIndex === currentIndex) return;

      this.scrollToAndFocusCell(targetIndex, cellId as NodeColumns);
    });

    hotkeys('command+right,ctrl+right', (event) => {
      if (tableStore.isCellBeingEdited) return;

      const { activeElement } = document;
      if (!(activeElement instanceof HTMLTableCellElement)) return;

      const { rowId, cellId } = activeElement.dataset;
      if (!rowId || !cellId) return;

      event.preventDefault();
      event.stopPropagation();

      const visibleColumns = getDataColumnIds(activeElement);
      if (!visibleColumns) return;

      const currentColIndex = visibleColumns.indexOf(cellId);
      if (currentColIndex === -1) return;

      const row = tableStore.getRowById(rowId);
      if (!row) return;

      const targetColIndex = tableStore.getCtrlArrowTargetColumn(
        row,
        currentColIndex,
        { direction: 'right', visibleColumns }
      );
      if (targetColIndex === currentColIndex) return;

      const targetColumnId = visibleColumns[targetColIndex] as NodeColumns;
      const rows = tableStore.getVisibleRows();
      const rowIndex = rows.findIndex((r) => r.id === rowId);
      if (rowIndex === -1) return;

      tableStore.scrollToAndFocusCell(rowIndex, targetColumnId);
    });

    hotkeys('command+left,ctrl+left', (event) => {
      if (tableStore.isCellBeingEdited) return;

      const { activeElement } = document;
      if (!(activeElement instanceof HTMLTableCellElement)) return;

      const { rowId, cellId } = activeElement.dataset;
      if (!rowId || !cellId) return;

      event.preventDefault();
      event.stopPropagation();

      const visibleColumns = getDataColumnIds(activeElement);
      if (!visibleColumns) return;

      const currentColIndex = visibleColumns.indexOf(cellId);
      if (currentColIndex === -1) return;

      const row = tableStore.getRowById(rowId);
      if (!row) return;

      const targetColIndex = tableStore.getCtrlArrowTargetColumn(
        row,
        currentColIndex,
        { direction: 'left', visibleColumns }
      );
      if (targetColIndex === currentColIndex) return;

      const targetColumnId = visibleColumns[targetColIndex] as NodeColumns;
      const rows = tableStore.getVisibleRows();
      const rowIndex = rows.findIndex((r) => r.id === rowId);
      if (rowIndex === -1) return;

      tableStore.scrollToAndFocusCell(rowIndex, targetColumnId);
    });

    // Alt+arrow used to page-jump; with the virtualized table there are no
    // pages, so the hotkeys are no-ops. Native PgUp/PgDn/Home/End still
    // work via the scroller.

    hotkeys('enter', (event) => {
      if (!tableStore.isCellBeingEdited) {
        const selectPlaceholder = (event.target as HTMLElement)?.querySelector(
          '#select-placeholder'
        );
        // Trigger a click on the select’s visual placeholder to open the actual select component.
        if (selectPlaceholder && selectPlaceholder instanceof HTMLElement) {
          tableStore.isCellBeingEdited = true;
          selectPlaceholder.click();
        }

        tableStore.toggleCellViaKeyboardNavigation(event);
      }
    });

    hotkeys('space', (event) => {
      if (!tableStore.isCellBeingEdited) {
        tableStore.toggleCellViaKeyboardNavigation(event);
      }
    });

    // PSTUDIO-1964: Shift+Space selects the whole row containing the focused
    // cell, from ANY cell in that row (Google-Sheets parity). Registered as
    // 'shift+space' — strict hotkeys-js matching means the plain 'space' handler
    // above never fires while Shift is held (ORDER MATTERS: space is the main
    // key, same rule as the shift+arrow combos). A follow-up Shift+Up/Down then
    // extends the row range via the existing shift+arrow handler (row mode).
    // Blur the cell so row mode is clean (focus on <body>, behavior contract
    // §1/§2a) and table-nav doesn't also move a cell focus.
    hotkeys('shift+space', (event) => {
      if (tableStore.isCellBeingEdited) return;
      const activeEl = document.activeElement;
      if (!(activeEl instanceof HTMLElement)) return;
      const rowId = activeEl
        .closest('tr[data-rowid]')
        ?.getAttribute('data-rowid');
      if (!rowId) return;
      event.preventDefault();
      event.stopPropagation();
      tableStore.selectFocusedRowByKeyboard(rowId);
      activeEl.blur();
    });

    // Go to Node shortcut (Cmd+G / Ctrl+G).
    // Mirrors the right-click "Go to node" menu item: only acts when a search
    // filter is active, navigating to the currently focused/selected row.
    hotkeys('command+g,ctrl+g', (event) => {
      // Prevent browser's default "Find Next" behavior
      event.preventDefault();
      event.stopPropagation();

      if (tableStore.isCellBeingEdited) return;
      if (!tableStore.searchQuery) return;

      const focusedRowId =
        tableStore.selectedStartCell?.rowId ??
        [...tableStore.selectedRows.values()][0];
      if (!focusedRowId) return;

      const row = tableStore.getRowById(focusedRowId);
      const goTo = nodeNumberToGoTo(row?.nodeNumber);
      if (goTo) tableStore.goToNode(goTo);
    });

    // Select all rows (Cmd+A / Ctrl+A), Google-Sheets style. Only hijacks the
    // browser's select-all when the table is the user's context: focus is in
    // it, OR there is already an active row/cell selection. Row selection does
    // not focus the table, so the selection check is what makes a follow-up
    // Cmd+A after a row click work.
    hotkeys('command+a,ctrl+a', (event) => {
      if (tableStore.isCellBeingEdited) return;

      const selectedElement = document.activeElement as HTMLElement;
      const isChildOfTable = !!selectedElement?.closest('#ps-editor-table');
      const hasTableSelection =
        tableStore.selectedRows.size > 0 || tableStore.selectedCells.length > 0;
      if (!isChildOfTable && !hasTableSelection) return;

      event.preventDefault();
      event.stopPropagation();
      tableStore.selectAllRows();
    });

    this.defaultRows.replace([
      {
        [NodeColumns.id]: uuidv4(),
        [NodeColumns.nodeNumber]: '1',
        [NodeColumns.nodeType]: Tenancy_Node_Type_Enum_Ps_Enum.D,
        [NodeColumns.nodeName]: '',
        [NodeColumns.message]: '',
        [NodeColumns.nextNodes]: [],
        [NodeColumns.answerRequired]: false,
        [NodeColumns.order]: 1000,
        [NodeColumns.solutionVersionId]: this.solutionVersionId,
        [NodeColumns.behaviors]: [''],
        [NodeColumns.richAssetType]: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.None,
      },
      {
        [NodeColumns.id]: uuidv4(),
        [NodeColumns.nodeNumber]: '2',
        [NodeColumns.nodeType]: Tenancy_Node_Type_Enum_Ps_Enum.D,
        [NodeColumns.nodeName]: '',
        [NodeColumns.message]: '',
        [NodeColumns.nextNodes]: [],
        [NodeColumns.answerRequired]: false,
        [NodeColumns.order]: 2000,
        [NodeColumns.intent]: 'out_of_scope',
        [NodeColumns.solutionVersionId]: this.solutionVersionId,
        [NodeColumns.behaviors]: [''],
        [NodeColumns.richAssetType]: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.None,
      },
    ]);
  }

  search(searchQuery: string) {
    // PSTUDIO-4023: filtering rebuilds the rendered set — `getVisibleRows()`
    // drops every row the query excludes — so a menu open on one of them would
    // be unmounted while its `ps-dropdown` is still projected into the popover
    // portal (the `removeChild` crash). Clearing the box rebuilds the set the
    // same way, so this sits ahead of BOTH branches.
    //
    // This covers PROGRAMMATIC callers (`goToNode`). The typed search does NOT
    // rely on it: `table.tsx`'s `onInput` re-runs the filtered row model
    // synchronously and only debounces its way here 200ms later, so it raises
    // the signal itself, on the tick that actually drops the rows.
    this.requestCloseAllContextMenus();
    if (!searchQuery || searchQuery.trim() === '') {
      this.searchQuery = '';
      // this.searchResults = [];
      this.searchResultIds.clear();
      return this.rows;
    }

    this.searchQuery = searchQuery;
    const searchResults = this.fuse.search(searchQuery);

    this.searchResultIds = new Set(
      searchResults.flatMap((result) => {
        const { item } = result;
        // If a group row matches (e.g. by groupName), surface ALL its
        // subRows too so the user sees the full group expanded.
        if (item.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.G) {
          return [item.id, ...(item.settings?.subRows ?? [])];
        }
        // If a subRow matches, surface its parent group so the row is
        // visible even when the group is collapsed.
        const groupId = item.settings?.groupId;
        if (groupId) return [groupId, item.id];
        return [item.id];
      })
    );

    return searchResults;
  }

  get needsServerValidation() {
    // Bot-compiler (server-side) is intentionally skipped in 'off' mode —
    // don't block canPublish/canServerSync on a check that will never fire.
    if (!shouldRunBotCompiler(readAgentCompilerMode(this.featureFlags))) {
      return false;
    }
    if (this.lastServerValidation === undefined) {
      return true;
    }
    if (
      this.lastServerValidationMessages &&
      this.lastServerValidationMessages.length > 0
    ) {
      return true;
    }
    return false;
  }

  get needsServerSync() {
    // if (this.lastServerSyncHash !== this.nodeHash) return true;
    return true;
  }

  get canServerSync() {
    if (this.solutionVersionId === undefined) return false;
    if (this.needsServerValidation) return false;

    return true;
  }

  /**
   * PSTUDIO-4632: rows can be written now — an editor, not mid-publish, the
   * table in `editing`. Every write surface outside the grid (Ask AI Apply,
   * canvas editing, Create's agent build) asks this first: a write that goes
   * ahead mid-publish ends in `insertRowBelow`, whose `setState` unlocks the
   * table the publish locked. Computed (`makeAutoObservable`), so observers
   * re-render when a publish starts and ends.
   */
  get canWriteRows(): boolean {
    return (
      !this.isViewer &&
      !this.solutionVersionPublishingInProgress &&
      this.currentState === 'editing'
    );
  }

  get canPublish() {
    if (this.solutionVersionPublishingInProgress) return false;
    if (this.needsServerValidation) return false;
    if (
      this.validationMode === ValidationMode.Manual &&
      this.hasUnvalidatedChangesInManualMode
    ) {
      return false;
    }
    if (this.solutionVersionId === undefined) return false;
    // PSTUDIO-4120: refuse while the last pass produced no verdict. Without
    // this, `errors` / `pifErrors` being empty means either "checked, clean" or
    // "never actually checked", and the gate below cannot tell them apart — the
    // exact reading QA saw as a clean badge over an unpublishable solution.
    // Deliberately BEFORE the two list checks, because the dangerous case is
    // precisely the one where both lists are empty.
    if (this.validationFailure !== null) return false;
    // Gate publish on whichever validator produced errors.
    // In OFF: bot-compiler is not invoked — only PIF errors block publish.
    // In ON: both block. In BOT_ONLY: only `errors` matters (PIF not invoked).
    if (this.errors.length > 0 || this.pifErrors.length > 0) return false;
    // PSTUDIO-4000: a duplicate node number makes the published solution
    // unusable, and it can reach `rows` without ever refreshing `errors`/
    // `pifErrors` (an orders-only save, a RAW paste, a series of undo/redo that
    // left validation stale). Read the CURRENT rows directly so publishing is
    // reactively blocked no matter which entrance produced the duplicate — the
    // pre-publish guard in PublishModal is the second line of defence.
    //
    // PSTUDIO-3990: a blank (or 0 / fractional) node number is just as
    // unpublishable, and until then it was gated only by ACCIDENT — two blank
    // cells collided under `Number('') === 0` and were reported as a duplicate
    // of a number nobody typed. With that coercion gone the duplicate check no
    // longer sees them, so the gate names both conditions, in ONE pass over the
    // rows rather than two.
    //
    // `rowsForValidation`, not `rows`: it drops the rows a delete has already
    // claimed but not yet spliced out (PSTUDIO-3990 — otherwise clearing a
    // number and then deleting the row left Publish disabled until the next
    // idle callback, in Manual mode until the user hit Validate) and adds the
    // not-yet-rendered tail. A row hidden by a COLLAPSED group is deliberately
    // still in there.
    if (findNodeNumberProblem(this.rowsForValidation) !== null) return false;

    return true;
  }

  /** In manual mode: true when Validate button should be enabled (has changes to validate or never validated). */
  get canValidateInManualMode() {
    if (this.validationMode !== ValidationMode.Manual) return false;
    // PSTUDIO-4120: a standing failure is precisely the state a user needs to
    // re-run OUT of, and it can arrive with no unvalidated edit behind it — the
    // forced pass PublishModal issues can end in `threw` while the table is
    // untouched. Without this the two gates deadlock: Publish refused by the
    // flag, Validate greyed out because "nothing changed", and the only way
    // forward is to edit an arbitrary cell.
    return (
      this.hasUnvalidatedChangesInManualMode ||
      this.lastServerValidation === undefined ||
      this.validationFailure !== null
    );
  }

  /** Call after script uploads to enable the Validate button in manual mode. */
  markDraftFilesChanged = action(() => {
    if (this.validationMode === ValidationMode.Manual) {
      this.hasUnvalidatedChangesInManualMode = true;
    }
  });

  // Command history methods — thin delegators to the UndoRedoManager
  // (PSTUDIO-3723). Kept on tableStore so the ~20 command call sites are
  // unchanged.
  /** PSTUDIO-4451: inside a composite gesture (`_recordGroupMutation`). */
  private _liveGestureDepth = 0;

  private _endLiveUndoStep() {
    if (this._liveGestureDepth === 0) this.liveViewSettings?.breakUndoGroup();
  }

  executeCommand(command: Command) {
    // PSTUDIO-4451: under live editing the collab store keeps the history —
    // the gesture still applies, and its end closes the store's undo step.
    if (this.liveEditingSource) {
      command.execute();
      this._endLiveUndoStep();
      return;
    }
    this.history.execute(command);
  }

  // Register a command whose effect has ALREADY been applied to the store
  // (e.g. live cell edits committed on blur, drag-reorders applied while the
  // user dragged) — skips the initial execute() so we don't redundantly
  // re-apply and re-save the change that just happened. undo()/redo() still
  // drive the command symmetrically.
  pushCommandWithoutExecute(command: Command) {
    // PSTUDIO-4451: see executeCommand. Every gesture records its command
    // when it finishes, which is exactly where the store's undo step ends —
    // otherwise two edits inside its capture window would undo together.
    if (this.liveEditingSource) {
      this._endLiveUndoStep();
      return;
    }
    this.history.push(command);
  }

  // Wipe undo/redo history. Called when the loaded solution version changes so
  // commands can never reference rows that belong to a different dataset.
  clearHistory() {
    this.history.clear();
  }

  // Delete nodes from the DB for undo/redo restore paths. urql mutation sources
  // are lazy — without subscribing (.toPromise()) the request is never
  // dispatched, so a bare `urqlGql.deleteProStudioNodes(...)` is a no-op. This
  // subscribes and surfaces failures instead of swallowing them, so an undo
  // that fails to persist is at least visible in the console (PSTUDIO-2154).
  //
  // PSTUDIO-4121: and the delete has to LAND. Four things stood between this
  // method and that, all of them silent, and the first is what makes the second
  // readable:
  //
  // (1) A still-pending UPSERT for the same row re-creates it. Both save queues
  //     run at delay 0, so the insert's own write is typically one macrotask
  //     behind an undo issued from the toolbar — and the order mutation INSERTs
  //     the id before resolving the conflict, so either queue is enough. The
  //     same race `_dissolveEmptyGroup` has purged since PSTUDIO-3159; doing it
  //     here covers all of this method's call sites at once. A write already IN
  //     FLIGHT cannot be purged, so it is AWAITED instead: `allSettled`, because
  //     a failed upsert is a reason to delete anyway, not a reason to give up.
  // (2) THE WAIT IS WHAT MAKES (4) NECESSARY, and it was missing from the first
  //     cut of this fix. Awaiting a write opens a window — one full round trip —
  //     in which the row can legitimately come BACK: a redo re-runs the insert's
  //     `execute`, and a coworker merge's Adds phase re-adds a row the server
  //     snapshot still carries. A delete decided before that window and sent
  //     after it lands last and kills a row the grid is showing: the mirror
  //     image of the defect this ticket fixes. So the row set is re-derived
  //     AFTER the wait, and a delete with nothing left to remove is not sent at
  //     all. The same re-derivation is what restores the guarantee PSTUDIO-3159
  //     had from `_dissolveEmptyGroup` purging and deleting in ONE tick.
  // (3) `affected_rows` came back and nothing read it, so a delete matching
  //     NOTHING resolved exactly like a success — the class PSTUDIO-4110
  //     describes on the deferred track (that branch is unmerged here, so this
  //     judgement is inline and should move onto `classifyNodeDelete` when the
  //     two meet). A miss is reported only for the rows the purge does NOT
  //     explain, and that has to be a SET, not a count: both queues run at delay
  //     0, so an ordinary Cmd+Z purges the insert's own write and a count-based
  //     qualifier (`purgedFromQueues === 0`) silenced the report on exactly the
  //     gesture it was written for — measured, and the first cut shipped it.
  //     `remove()` also counts ENTRIES, so one row sitting in both queues read
  //     as two. A missing count stays its own case — `unknown`, warned about
  //     rather than read as zero.
  // (4) The track was invisible to `hasPendingNodeSaves` / `waitForPendingSaves`,
  //     so publish could compile while the delete was in flight and a cross-tab
  //     reload could snapshot a row on its way out. It is registered now and it
  //     REJECTS on failure: the rows are gone from memory and alive in the
  //     database, so a gate that passes there is decorative.
  //
  //     Its flush is a NO-OP, and that is a LIMITATION rather than a guarantee.
  //     There is no idle callback to run ahead of the browser, but the request
  //     is not necessarily on the wire either: while (1) is awaiting a write,
  //     nothing can force it out, so the `solutionVersionId` setter's drain
  //     cannot save this delete from a tab that closes inside that window.
  //     Forcing it would re-open the race (2) exists to close.
  //
  //     Two more bounds, stated rather than implied: the wait has no timeout, so
  //     a request that never settles keeps the gate raised (the same shape
  //     `_runDeferredDelete` records for its own track); and the gate is only
  //     raised while the promise lives, so a publish attempted AFTER a failed
  //     delete finds nothing to refuse. `waitForPendingSaves` re-issues the
  //     failed delete instead, which is what makes the publish modal's "try
  //     again" a truthful instruction.
  private _deleteNodesInDb(nodeIds: string[]) {
    if (nodeIds.length === 0) return;
    // PSTUDIO-4451: the document has no save queue to purge and nothing in
    // flight to wait for — rows still absent from the table leave it now.
    if (this.liveEditingSource) {
      const alive = new Set(this.rows.map((row) => row.id));
      this.liveWriter?.deleteRows(nodeIds.filter((id) => !alive.has(id)));
      return;
    }
    // PSTUDIO-4103: pin the identity here too. The mutation argument was
    // already read synchronously, but the broadcast below sits inside `.then()`
    // — i.e. after the round trip — so it announced under whatever version the
    // store had moved to: the tab that needed the reload filtered the message
    // out, and a tab that did not need it woke up. Same one-line fix as
    // `_deleteRowsInternal` and the two `saveBulk*` tails.
    const deletingFor = this.solutionVersionId;

    // `id IN $nodeIds` collapses a repeat, so every count below is over the SET
    // — a caller passing the same id twice must not read as a short delete.
    const wanted = [...new Set(nodeIds)];

    // (1a) Drop queued writes for these rows before the delete is sent, and
    // record WHICH rows that covered. The set, not the count, is what lets (3)
    // tell a legitimately absent row from a delete that went nowhere.
    const purgedIds = this._dropQueuedNodeWrites(wanted);
    // PSTUDIO-4342: every caller has already taken these rows out of `rows`,
    // so a refusal of theirs has nothing left to repair.
    this._reconcileNodeWriteToasts();

    // (1b) …and wait out the ones already on the wire. Keyed by batch, so this
    // wait itself is for the writes that name these rows; the send is then
    // ordered behind EVERY node write issued before it (PSTUDIO-4342,
    // _nodeWriteTail), so an unrelated write still ahead of it now does delay
    // the delete — the price of a delete never landing before a write that
    // would re-create its row.
    const writesCovering = (batches: Map<Promise<unknown>, Set<string>>) =>
      [...batches.entries()]
        .filter(([, rowIds]) => wanted.some((id) => rowIds.has(id)))
        .map(([promise]) => promise);
    const inFlightWrites = [
      ...writesCovering(this._inflightSaveBatches),
      ...writesCovering(this._inflightOrderSaveBatches),
    ];

    // The ids this delete has not removed yet. A refusal records exactly these
    // for the re-issue in `waitForPendingSaves`, so a chunk that did land is not
    // deleted twice and then reported as a short delete.
    let notYetDeleted = new Set(wanted);

    const deletion = Promise.allSettled(inFlightWrites)
      .then(() =>
        this._inNodeWriteOrder(async () => {
          // (2) Re-derive the target AFTER the wait — now also after every
          // write ahead of it in the order. `rows` is the probe rather than
          // `rowMap`, because a redo puts the row back into both and a merge
          // into `rows` first — and because a caller may delete an id the map
          // never indexed.
          const alive = new Set(this.rows.map((row) => row.id));
          const stillAbsent = wanted.filter((id) => !alive.has(id));
          if (stillAbsent.length === 0) return null;
          notYetDeleted = new Set(stillAbsent);

          // PSTUDIO-4342: chunked by the same body-size ceiling as the saves;
          // a refusal throws, so it reaches the gate (see
          // `_sendNodeDeleteChunks`).
          const affected = await this._sendNodeDeleteChunks(
            stillAbsent,
            deletingFor,
            notYetDeleted
          );
          return { affected, stillAbsent };
        })
      )
      .then((outcome) => {
        // Nothing was sent because nothing is missing any more: the row came
        // back while we waited, and whoever brought it back owns announcing it.
        // That also retires any earlier failure for these ids — there is no
        // zombie left to repair once the row is alive in both places.
        if (!outcome) {
          wanted.forEach((id) => this._failedNodeDeletes.delete(id));
          return;
        }
        const { affected, stillAbsent } = outcome;
        // What a truthful answer must at least be: the rows whose absence the
        // purge does NOT already account for.
        const unexplained = stillAbsent.filter(
          (id) => !purgedIds.has(id)
        ).length;

        if (affected === undefined) {
          console.warn(
            '[TableStore] node delete: the response carried no affected_rows, so the effect is UNKNOWN:',
            { nodeIds: stillAbsent, solutionVersionId: deletingFor }
          );
        } else if (affected < unexplained) {
          console.error(
            '[TableStore] the node delete removed fewer rows than it named:',
            {
              requested: stillAbsent.length,
              explainedByQueuePurge: stillAbsent.length - unexplained,
              affected,
              nodeIds: stillAbsent,
              sentUnder: deletingFor,
            }
          );
        }

        // Whatever this delete accounted for is no longer outstanding.
        stillAbsent.forEach((id) => this._failedNodeDeletes.delete(id));
        broadcastNodesChanged(deletingFor);
      })
      .catch((error) => {
        console.error(
          '[TableStore] undo/redo node delete failed to persist:',
          error
        );
        // The same signal the deferred track raises for the same state
        // (`_runDeferredDelete`): the rows left the grid and are alive in the
        // database, and until now the only way to find that out was to publish.
        // Its OWN toastId, not the deferred track's 'delete-failed': these
        // toasts do not auto-close, and a shared id would let the second
        // failure's text replace the first's (_raiseNodeWriteToast).
        this._raiseNodeWriteToast({
          header: nodeWriteToastText('delete_error_header'),
          type: 'error',
          text: error instanceof Error ? error.message : String(error),
          toastId: 'undo-delete-failed',
        });
        // Remember it BEYOND the promise: `.finally` below drops the entry, so
        // without this a publish attempted after the failure would find an
        // empty gate and compile over the rows that are still there.
        notYetDeleted.forEach((id) =>
          this._failedNodeDeletes.set(id, deletingFor)
        );
        throw error;
      });

    // (4) The gate holds the REJECTING promise; callers keep their
    // fire-and-forget contract through the resolve-only view below, which is
    // also what stops a failed delete from raising an unhandled rejection.
    this._pendingDeletes.set(deletion, () => undefined);
    void deletion
      .catch(() => undefined)
      .finally(() => {
        this._pendingDeletes.delete(deletion);
      });
  }

  /**
   * PSTUDIO-4121: ids whose DB delete FAILED, kept beyond the promise that
   * carried the failure.
   *
   * `_pendingDeletes` is emptied when its promise settles, failure included, so
   * it can only refuse a publish that races the delete. A publish a second
   * later found a clean gate and compiled a solution still holding rows the
   * grid had removed — while the modal's own toast was telling the user to try
   * publishing again. `waitForPendingSaves` re-issues these instead, so the
   * retry repairs the state rather than shipping it; a row that comes back
   * (undo of the undo, a merge) is dropped from the set by the re-issue itself,
   * which finds nothing absent to delete.
   *
   * PSTUDIO-4342: valued by the solution version the delete was sent under,
   * because `id IN $nodeIds AND solutionVersionId = $sv` re-issued under
   * another version matches nothing and would read as repaired. The re-issue
   * takes only the entries of the version on screen; the rest wait for it.
   * The deferred delete (`_deleteRowsInternal`) records here too.
   */
  private _failedNodeDeletes = new Map<string, string>();

  /**
   * PSTUDIO-4342: sends a node delete chunked by SAVE_REQUEST_BYTE_LIMIT (~2,600
   * ids in one request already cross the measured edge), in order, one chunk
   * after another — the one loop both bulk-delete tracks share. Each chunk that
   * lands leaves `notYetDeleted`; the first refused one (`nodeWriteRefusal`, so
   * a 200 carrying `errors` counts) THROWS and stops the rest, leaving in
   * `notYetDeleted` exactly what the caller must record as not deleted.
   * Resolves with the summed `affected_rows`, or `undefined` once any response
   * lacked it — `affected_rows` is an aggregate per request.
   */
  private async _sendNodeDeleteChunks(
    ids: string[],
    solutionVersionId: string,
    notYetDeleted: Set<string>
  ): Promise<number | undefined> {
    let affected: number | undefined = 0;
    for (const chunk of chunkBySerializedBytes(
      ids,
      SAVE_REQUEST_BYTE_LIMIT,
      Number.POSITIVE_INFINITY
    )) {
      const result = await settleMutation(
        urqlGql.deleteProStudioNodes({ nodeIds: chunk, solutionVersionId })
      );
      this._noteNodeWriteProgress();
      const refusal = nodeWriteRefusal(result);
      if (refusal) throw refusal;
      const removed =
        result.data?.delete_tenancy_solution_version_nodes_ps?.affected_rows;
      affected =
        affected === undefined || removed === undefined
          ? undefined
          : affected + removed;
      chunk.forEach((id) => {
        notYetDeleted.delete(id);
        // Gone from the server: an undo that re-inserts it is an insert.
        this._serverKnownIds.delete(id);
      });
    }
    return affected;
  }

  /**
   * PSTUDIO-4342: drops queued writes of rows about to be deleted — flushed
   * after the delete, they would re-create the rows — and returns the ids that
   * were queued. Shared by every delete path that names rows.
   */
  private _dropQueuedNodeWrites(ids: string[]): Set<string> {
    const purged = new Set<string>();
    for (const queue of [saveRowsToDatabaseQueue, saveRowOrdersToDatabaseQueue])
      for (const id of queue.pendingIds()) if (ids.includes(id)) purged.add(id);
    saveRowsToDatabaseQueue.remove(ids);
    saveRowOrdersToDatabaseQueue.remove(ids);
    return purged;
  }

  /**
   * PSTUDIO-4342: the single-row delete both `deleteRow` and the replace-paste
   * send. A write of the row still sitting in a queue is dropped first —
   * flushed after the delete, it would re-create the row — and the delete is
   * ordered behind every node write issued before it (_nodeWriteTail); its
   * answer re-arms that order's bound like any other node write's. It is
   * registered with the pending-writes gate for as long as it waits its turn
   * and is on the wire, so publish waits for it and beforeunload counts it. It
   * can only DELAY the gate, never fail it: these deletes still do not read
   * their result (PSTUDIO-4111), so what is registered settles either way.
   */
  private _deleteOneNode(nodeId: string, solutionVersionId: string): void {
    this._dropQueuedNodeWrites([nodeId]);
    // Both callers have already taken the row out of `rows`.
    this._reconcileNodeWriteToasts();
    const deletion = this._inNodeWriteOrder(async () => {
      const result = await settleMutation(
        urqlGql.deleteProStudioNode({ nodeId, solutionVersionId })
      );
      this._noteNodeWriteProgress();
      // Gone from the server: an undo that re-inserts it is an insert.
      if (!nodeWriteRefusal(result)) this._serverKnownIds.delete(nodeId);
      return result;
    });
    const tracked = deletion.then(
      () => undefined,
      () => undefined
    );
    this._pendingDeletes.set(tracked, () => undefined);
    void tracked.then(() => this._pendingDeletes.delete(tracked));
  }

  get isMetaKeyPressed() {
    return this._isMetaKeyPressed;
  }

  // inputBeingEdited;

  setMetaKeyState(newState: boolean) {
    this._isMetaKeyPressed = newState;
  }

  getRowById = computedFn((rowId: string) => {
    return this.rows.find((row) => row.id === rowId) as TableRow;
  });

  getRowIndexById = computedFn((rowId: string) => {
    return this.sortedRows.findIndex((row) => row.id === rowId);
  });

  getRowByIndex = computedFn((rowIndex: number) => {
    return this.sortedRows[rowIndex];
  });

  /**
   * Finds a row by its node number.
   * Searches all non-hidden rows (ignoring search filter) so navigation can find
   * rows outside the current search results.
   * @param nodeNumber The node number to search for
   * @returns The row and its index in the unfiltered sorted rows, or null if not found
   */
  findRowByNodeNumber = (
    nodeNumber: string
  ): { row: TableRow; rowIndex: number } | null => {
    // Use unfiltered rows so we can navigate to nodes outside the current search
    const allRows = this.getAllSortedRows().filter((r) => r.hidden !== true);
    const rowIndex = allRows.findIndex((row) => row.nodeNumber === nodeNumber);

    if (rowIndex === -1) {
      return null;
    }

    return { row: allRows[rowIndex], rowIndex };
  };

  /**
   * Navigates to a specific node by its node number.
   * Clears any active search filter, changes the page if necessary,
   * then scrolls to and highlights the cell.
   * @param nodeNumber The node number to navigate to
   * @returns true if the node was found and navigation succeeded, false otherwise
   */
  goToNode = (nodeNumber: string): boolean => {
    const result = this.findRowByNodeNumber(nodeNumber);

    if (!result) {
      return false;
    }

    // Clear search query so the target row is visible and focusable.
    // PSTUDIO-4023: through `search('')`, not a raw field write — the setter is
    // the funnel that closes any open context menu before the rendered set is
    // rebuilt, and a direct assignment here would step around it.
    if (this.searchQuery) {
      this.search('');
    }

    const { rowIndex } = result;

    this.scrollToAndFocusCell(rowIndex, NodeColumns.nodeNumber, {
      selectRow: true,
    });

    return true;
  };

  getColumnIndex = computedFn((columnId: NodeColumns) => {
    return columnOrder.indexOf(columnId);
  });

  get getAllColumns() {
    return columnOrder as [keyof TableRow];
  }

  /**
   * Single-cell text paste.
   *
   * PSTUDIO-3968: the payload is resolved by `resolveCellPasteText`, which
   * prefers the native `ClipboardEvent`'s own data (`eventText` — the only source
   * that works without `clipboard-read`) and, crucially, NEVER coerces an
   * unreadable clipboard into an empty string. Previously a rejected
   * `readText()` fell back to `clipboardDetails.valueAtTimeOfCopy || ''`, so an
   * EXTERNAL paste in a browser that never granted `clipboard-read` wrote `''`
   * and CLEARED the focused cell. Now an unresolvable clipboard writes nothing,
   * and a blocked one surfaces one toast (symmetric to the write-side
   * `notifyClipboardWriteFailed` — KEYBOARD_CLIPBOARD_BEHAVIOR.md §7).
   *
   * `eventText`/`eventHtml` are omitted by programmatic callers (the cell context
   * menu's Paste), which keeps the Async Clipboard read as their path.
   */
  handlePaste = async ({
    cellId,
    rowId,
    eventText,
    eventHtml,
  }: {
    rowId: string;
    cellId: keyof TableRow;
    eventText?: string | null;
    eventHtml?: string | null;
  }) => {
    const hasCell = tableStore.hasCell(rowId, cellId);

    // @todo - add more error handling here
    if (!hasCell) return;
    if (tableStore.isPasteBlockedByNodeType(rowId, cellId)) return;

    // Only reach for the Async Clipboard API when the event did not already hand
    // us the text — the read is permission-gated and, in a real browser without
    // the paste confirmation, yields nothing (it resolves EMPTY rather than
    // rejecting, which is precisely why an empty read must not be trusted as
    // "the clipboard really is empty" — see the resolver's trust rule).
    let systemRead: SystemClipboardRead | undefined;
    if (!(typeof eventText === 'string' && eventText.length > 0)) {
      try {
        systemRead = {
          ok: true,
          value: await window.navigator.clipboard.readText(),
        };
      } catch (error) {
        console.warn('Failed to access clipboard:', error);
        systemRead = { ok: false };
      }
    }

    const resolved = resolveCellPasteText({
      eventText,
      eventHtml,
      systemRead,
      inMemory: this.clipboardDetails.valueAtTimeOfCopy,
      systemWriteFailed: this.clipboardDetails.systemWriteFailed,
      // No event fields at all → the programmatic context-menu Paste, whose empty
      // read cannot be distinguished from a blocked one (guard rail 7, review #5).
      programmaticPaste: eventText === undefined && eventHtml === undefined,
    });

    if (resolved.kind === 'unavailable') {
      // A blocked clipboard is worth telling the user about once; a genuinely
      // empty one is a silent no-op. Neither may touch the cell.
      if (resolved.reason === 'denied') this.notifyClipboardReadFailed();
      return;
    }

    // Re-validate AFTER the await. The async read can wait on the browser's paste
    // confirmation for an arbitrarily long time, during which the row may be gone
    // or the user may have opened THIS cell's editor — writing under an open
    // editor would be clobbered by its own commit and lose the pasted value.
    if (!tableStore.hasCell(rowId, cellId)) return;
    if (tableStore.isPasteBlockedByNodeType(rowId, cellId)) return;
    if (tableStore.isCellBeingEdited) return;

    const data = convertTextToColumnValue(
      resolved.value,
      cellId as keyof TableNode
    );
    // PSTUDIO-3963: route through the recorded `writeCellValue` choke-point (not a
    // raw `updateRow`) so a keyboard paste into a cell is captured as ONE undo/redo
    // command — same flags (`skipValidationRules:false, saveImmediately:true`) plus
    // the stale-validation-error clear. Without this the paste bypassed history and
    // Undo/Redo skipped the pasted cell content (KEYBOARD_CLIPBOARD_BEHAVIOR.md #16).
    tableStore.writeCellValue(rowId, cellId as string, data);
  };

  /**
   * Serialize a single cell's value to the canonical clipboard string (arrays
   * joined on their column delimiter, JSON columns ordered, scalars stringified,
   * missing/empty → ''). Shared by the single-cell `handleCopy` and the
   * multi-cell clipboard (PSTUDIO-3504).
   */
  private serializeCellValue(rowId: string, columnId: keyof TableRow): string {
    const row = this.rows.find((r) => r.id === rowId);
    const value = row?.[columnId];

    if (value === null || value === undefined) return '';
    if (Array.isArray(value))
      return value.join(getArrayJoinDelimiter(columnId));
    if (typeof value === 'object') {
      if (this.isJsonColumn(rowId, columnId as keyof TableNode)) {
        return getOrderedJson({
          value,
          rowId,
          // @ts-ignore — columnId is keyof TableRow; getOrderedJson takes a NodeColumns
          columnId,
          stringify: true,
        });
      }
      return JSON.stringify(value);
    }
    return value.toString();
  }

  handleCopy = async ({
    cellId,
    rowId,
  }: {
    rowId: string;
    cellId: keyof TableRow;
  }) => {
    const valueAtTimeOfCopy = this.serializeCellValue(rowId, cellId);

    this.clipboardDetails = {
      rowId,
      cellId,
      valueAtTimeOfCopy,
      systemWriteFailed: false,
    };

    // PSTUDIO-3968: record whether the OS mirror actually landed. A later paste
    // uses this to decide if the in-memory buffer may outrank an EMPTY system
    // read (guard rails 7 + 17). Still fire-and-forget — a blocked write must
    // never fail the copy.
    const onWriteFailed = (err: unknown) => {
      // `clipboardDetails` IS observable (unlike the range twin
      // `cellClipboardWriteFailed`), and this runs from a promise catch — i.e.
      // outside the action that started it — so the write needs its own action
      // boundary, the same way the post-await writes in this file do.
      runInAction(() => {
        this.clipboardDetails.systemWriteFailed = true;
      });
      this.notifyClipboardWriteFailed(err);
    };

    try {
      globalThis.navigator?.clipboard
        ?.writeText(valueAtTimeOfCopy)
        ?.catch(onWriteFailed);
    } catch (err) {
      onWriteFailed(err);
    }
  };

  // Internal state to manage row copy and cut
  private rowClipboard?: TableRow[] = [];
  private isRowCut = false;
  private showCopyIndicator = false;

  // Gets the number of rows in the clipboard
  public get clipboardSize(): number {
    return this.rowClipboard?.length ?? 0;
  }

  /**
   * Expands a list of ids to include all subRows of any group rows in the set.
   * Used by copy/cut so that a group row is always bundled together with its children.
   */
  private expandIdsWithGroupBundles(ids: string[]): string[] {
    const bundleIds = new Set<string>();
    ids.forEach((id) => {
      bundleIds.add(id);
      const row = this.getRowById(id);
      if (row?.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.G) {
        (row.settings?.subRows ?? []).forEach((subId: string) =>
          bundleIds.add(subId)
        );
      }
    });
    return [...bundleIds];
  }

  /**
   * Copies one or more existing items to the internal clipboard.
   * @param ids[] The ID of the item to copy.
   * @returns `true` if the item was found and copied, `false` otherwise.
   */
  public copyRows(ids: string[]): boolean {
    this.clearRowClipboard();
    // PSTUDIO-3337: node and cell clipboards are mutually exclusive ("last copy
    // wins") — a row copy clears any active cell-clipboard marching-ants + bundle.
    this.clearCellClipboard();

    let success = false;
    for (const id of this.expandIdsWithGroupBundles(ids)) {
      const rowToCopy = this.getRowById(id);
      if (rowToCopy) {
        this.rowClipboard?.push({ ...rowToCopy });
        this.isRowCut = false;
        this.showCopyIndicator = true;
        success = true;
      }
    }
    if (success) this.writeRowBundleToSystemClipboard(this.isRowCut);
    return success;
  }

  /**
   * Cuts one or more existing items from the array and places it on the internal clipboard.
   * @param ids[] The IDs of the items to cut.
   * @returns `true` if the item was found and cut, `false` otherwise.
   */
  public cutRows(ids: string[]): boolean {
    this.clearRowClipboard();
    // PSTUDIO-3337: node/cell clipboards are mutually exclusive — see copyRows.
    this.clearCellClipboard();

    let success = false;
    for (const id of this.expandIdsWithGroupBundles(ids)) {
      const rowToCut = this.getRowById(id);
      if (rowToCut) {
        this.rowClipboard?.push({ ...rowToCut });
        this.isRowCut = true;
        this.showCopyIndicator = true;
        success = true;
      }
    }
    if (success) this.writeRowBundleToSystemClipboard(this.isRowCut);
    return success;
  }

  public clearRowClipboard = action(() => {
    if (this.rowClipboard?.length === 0) return;
    this.rowClipboard = [];
  });

  /**
   * PSTUDIO-3337: mirror the row bundle onto the SYSTEM clipboard so it can be
   * pasted in another tab / solution (and into Google Sheets). text/plain = a
   * flat TSV (Sheets-friendly); text/html = a <table> carrying the full-fidelity
   * JSON envelope in a data-* attribute (Pro Studio round-trips the exact nodes).
   * Fire-and-forget — a denied/absent clipboard must never break the in-memory
   * copy (mirrors handleCopy's non-awaited writeText).
   */
  private writeRowBundleToSystemClipboard(isCut: boolean): void {
    const rows = toJS(this.rowClipboard ?? []);
    if (!rows.length) return;

    const grid = buildRowsTsvGrid(
      rows,
      VISIBLE_TSV_EXPORT_COLUMNS,
      (row, col) => this.serializeCellValue(row.id, col as keyof TableRow)
    );
    const tsv = gridToTsv(grid);
    const envelopeJson = serializeNodeEnvelope(rows, {
      isCut,
      sourceSolutionVersionId: this.solutionVersionId,
    });
    const html = buildNodeClipboardHtml(grid, envelopeJson);

    try {
      const { clipboard } = globalThis.navigator ?? {};
      if (clipboard?.write && typeof ClipboardItem !== 'undefined') {
        void clipboard
          .write([
            new ClipboardItem({
              'text/plain': new Blob([tsv], { type: 'text/plain' }),
              'text/html': new Blob([html], { type: 'text/html' }),
            }),
          ])
          .catch((err) => this.notifyClipboardWriteFailed(err));
      } else if (clipboard?.writeText) {
        // No rich write() → at least put the TSV on the clipboard.
        void clipboard
          .writeText(tsv)
          .catch((err) => this.notifyClipboardWriteFailed(err));
      }
    } catch (err) {
      this.notifyClipboardWriteFailed(err);
    }
  }

  /**
   * PSTUDIO-3968 — the READ-side twin of `notifyClipboardWriteFailed`.
   *
   * A blocked `clipboard-read` used to be swallowed in a `console.warn` while the
   * paste wrote an empty string over the cell. The paste is now a no-op, so the
   * failure needs a voice — otherwise the user presses Ctrl+V and nothing at all
   * happens, with no clue that the browser is withholding the clipboard. One
   * deduped toast, same shape/`toastId` discipline as the write side.
   *
   * The header deliberately differs from the write-side toast: the pre-existing
   * `clipboard/copy-failure-toast` e2e locates that one by its
   * 'Clipboard unavailable' text, so sharing the header would make the two
   * indistinguishable (and could trip Playwright strict mode if both were shown).
   *
   * Reachability note: a browser that never granted `clipboard-read` RESOLVES the
   * read with an empty string rather than rejecting it, so this toast fires on a
   * genuine rejection (blocked API, non-secure context, an in-page stub). The
   * everyday no-permission path is served by the `paste` event and needs no toast.
   */
  private notifyClipboardReadFailed(): void {
    sendToast({
      header: 'Paste unavailable',
      type: 'error',
      text: 'Pro Studio could not read your system clipboard, so nothing was pasted — the cell was left unchanged. Allow clipboard access for this site (or use the paste option your browser offers) and try again.',
      toastId: 'clipboard-read-failed',
      closeButton: true,
    });
  }

  /**
   * PSTUDIO-3612: a denied/unavailable system clipboard used to fail silently
   * (console.warn at best, an unhandled rejection at worst), so "copy from
   * Pro Studio to Google Sheets" looked unsupported when the OS write was
   * simply blocked. Surface it via the shared toast (deduped by toastId). The
   * in-memory copy still succeeds — this is feedback, not a copy failure.
   * Its text is hardcoded English and is NOT translated yet (pre-existing;
   * the node-write toasts are, under `node_writes.toast`).
   */
  private notifyClipboardWriteFailed(err: unknown): void {
    console.warn('Failed to write to the system clipboard', err);
    sendToast({
      header: 'Clipboard unavailable',
      type: 'error',
      text: 'Copied inside Pro Studio, but the system clipboard was blocked — pasting into other apps (e.g. Google Sheets) will not work until clipboard access is allowed.',
      toastId: 'clipboard-write-failed',
      closeButton: true,
    });
  }

  /**
   * PSTUDIO-3337: when THIS tab has no in-memory bundle (e.g. the rows were
   * copied in another tab), rebuild it from the SYSTEM clipboard. Prefers our
   * full-fidelity HTML envelope; otherwise imports a plain TSV paste (Google
   * Sheets). A cross-tab paste is ALWAYS a copy — the source tab owns any
   * cut/deletion — so isRowCut is forced false. Returns true on a usable bundle.
   *
   * Intentionally an async arrow field, NOT wrapped in `action`: MobX must not
   * `action`-wrap an async fn (only its synchronous prefix would run in the
   * action), so the post-await state writes are wrapped in `runInAction` instead.
   */
  public hydrateRowClipboardFromSystem = async (payload?: {
    eventText?: string | null;
    eventHtml?: string | null;
  }): Promise<boolean> => {
    // PSTUDIO-3968: the async read stays FIRST here and the native `paste` event's
    // `clipboardData` is the FALLBACK — deliberately the opposite order from the
    // single-cell path.
    //
    // Why: the row path's fixtures replace `clipboard.read()` while leaving the
    // real OS clipboard untouched (that is how `clipboard/cross-tab-node-paste`
    // delivers a captured node envelope). Preferring the event there picks up
    // whatever unrelated TSV an earlier test left on the real clipboard and pastes
    // THAT instead of the envelope. Reading first also preserves guard rail 8's
    // "last copy wins" semantics byte for byte.
    //
    // The fallback is what fixes the defect: when the read yields nothing — which
    // is what a browser that never granted `clipboard-read` does, it resolves
    // EMPTY rather than rejecting — the event payload still carries the content, so
    // a cross-tab / Google-Sheets ROW paste lands instead of silently doing
    // nothing.
    const read = await this.readSystemClipboard();
    const readYieldedNothing = !read.html?.length && !read.text?.length;
    const html =
      readYieldedNothing && payload?.eventHtml ? payload.eventHtml : read.html;
    const text =
      readYieldedNothing && payload?.eventText ? payload.eventText : read.text;

    const envelope =
      parseNodeEnvelopeFromHtml(html) ?? parseNodeEnvelopeFromText(text);
    if (envelope && envelope.rows.length) {
      runInAction(() => {
        this.rowClipboard = envelope.rows.map((r) => ({ ...r }));
        this.isRowCut = false; // cross-tab paste is a copy
      });
      return true;
    }

    if (text && text.trim()) {
      const rows = this.buildRowsFromTsv(text);
      if (rows.length) {
        runInAction(() => {
          this.rowClipboard = rows;
          this.isRowCut = false;
        });
        return true;
      }
    }
    return false;
  };

  /** Read the system clipboard, preferring read() (to reach text/html for full
   *  fidelity) with a readText() fallback. Never throws. */
  private async readSystemClipboard(): Promise<{
    html?: string;
    text?: string;
  }> {
    const { clipboard } = window.navigator;
    try {
      if (clipboard?.read) {
        const items = await clipboard.read();
        let html: string | undefined;
        let text: string | undefined;
        for (const item of items) {
          if (item.types.includes('text/html')) {
            html = await (await item.getType('text/html')).text();
          }
          if (item.types.includes('text/plain')) {
            text = await (await item.getType('text/plain')).text();
          }
        }
        if (html || text) return { html, text };
      }
    } catch (err) {
      console.warn('clipboard.read failed; falling back to readText', err);
    }
    try {
      if (clipboard?.readText) return { text: await clipboard.readText() };
    } catch (err) {
      console.warn('clipboard.readText failed', err);
    }
    return {};
  }

  /** Turn a pasted TSV (e.g. from Google Sheets) into fresh, defaulted rows homed
   *  to the current solution. Orders are (re)assigned by pasteRow; node numbers are
   *  preserved when free / renumbered on collision (PSTUDIO-3574, guard rail 11). */
  private buildRowsFromTsv(tsv: string): TableRow[] {
    const { cellMaps } = parseTsvToCellMaps(tsv, TSV_EXPORT_COLUMNS);
    // Row construction is the pure, unit-tested `buildNodeRowsFromCellMaps`; the
    // store only injects its column list, canonical converters, id factory and
    // target solution.
    const rows = buildNodeRowsFromCellMaps(cellMaps, {
      allColumns: columnOrder as NodeColumnKey[],
      getDefault: (col) => getDefaultValueForColumn(col as keyof TableNode),
      convert: (text, col) =>
        convertTextToColumnValue(text, col as keyof TableNode),
      solutionVersionId: this.solutionVersionId,
      makeId: uuidv4,
    });
    // PSTUDIO-4348: a row built from text is not "complete" the way a node
    // envelope is — a Google-Sheets row can name a Rich Asset Type and omit the
    // behaviors it depends on. The pasted type therefore adjusts the row like a
    // dropdown change, and Command is dropped where the grid shows it cleared
    // (the converter now keeps the text). Each applies only to a column the TSV
    // actually carried: Command and the type are dropped where the row's node
    // type clears them, the type is adjusted where it leaves it live. Answer
    // Required follows a type other than none, as `updateRow`'s schema rule makes
    // it for a dropdown change, unless the sheet carried its own value.
    rows.forEach((row, i) => {
      const carried = cellMaps[i] ?? {};
      const cleared = (column: NodeColumns) =>
        isClearedForNodeType(row.nodeType, column);
      if (NodeColumns.command in carried && cleared(NodeColumns.command)) {
        row.command = getDefaultValueForColumn(NodeColumns.command) as string;
      }
      if (
        NodeColumns.richAssetType in carried &&
        cleared(NodeColumns.richAssetType)
      ) {
        row.richAssetType = getDefaultValueForColumn(
          NodeColumns.richAssetType
        ) as typeof row.richAssetType;
      } else if (NodeColumns.richAssetType in carried) {
        Object.assign(
          row,
          planAssetTypeChange({
            newType: row.richAssetType,
            behaviors: row.behaviors,
            richAssetContent: (row as unknown as Record<string, unknown>)
              .richAssetContent,
            richAssetsConfig: this.richAssetsConfig,
          }).updates
        );
        if (
          !(NodeColumns.answerRequired in carried) &&
          row.richAssetType !== Tenancy_Rich_Asset_Type_Enum_Ps_Enum.None
        ) {
          row.answerRequired = true;
        }
      }
    });
    return rows;
  }

  /**
   * SYSTEM-FIRST clipboard refresh shared by EVERY paste entry point (keyboard
   * AND context menu) so "last copy wins" across tabs / solutions: the OS
   * clipboard holds the MOST RECENT copy made in ANY tab, so we pull it into the
   * in-memory bundle before pasting. Without it a stale in-memory bundle left by
   * an EARLIER cross-tab paste would win over a NEWER copy made in another tab
   * (copy 3 nodes → paste; copy 2 nodes → paste would wrongly re-paste the first
   * 3). Same-tab copy already keeps both in sync, so it's a no-op there; also a
   * no-op when the system holds nothing pasteable (leaves the in-memory bundle).
   *
   * EXCEPTION — a same-tab CUT is a MOVE: only the in-memory bundle can represent
   * it (the source rows must be deleted, and the system hydrate forces
   * isRowCut=false, turning a move into a copy). So for a cut we DON'T refresh —
   * the in-memory cut bundle is authoritative.
   */
  private refreshRowClipboardUnlessCut = async (payload?: {
    eventText?: string | null;
    eventHtml?: string | null;
  }): Promise<void> => {
    if (!this.isClipboardCut) {
      await this.hydrateRowClipboardFromSystem(payload);
    }
  };

  /**
   * PSTUDIO-3337: keyboard paste entry — SYSTEM-FIRST so "last copy wins" across
   * tabs / solutions (see `refreshRowClipboardUnlessCut`).
   */
  public pasteRowsFromSystemOrMemory = async (payload?: {
    eventText?: string | null;
    eventHtml?: string | null;
  }): Promise<boolean> => {
    await this.refreshRowClipboardUnlessCut(payload);
    if (!this.canPasteRow()) return false;
    return this.pasteRowsReplacingSelection();
  };

  /**
   * PSTUDIO-3547: context-menu paste entry (Paste / Paste above / Paste below) —
   * SYSTEM-FIRST too, symmetric with the keyboard path, so "last copy wins" works
   * from the right-click menu across tabs / solutions. The menu items used to call
   * `pasteRow` directly against the in-memory bundle, so a SECOND cross-tab paste
   * re-pasted the STALE first bundle. Gated on `canPasteRowAt` (the same predicate
   * the menu's disabled state uses) so a paste `pasteRow` would reject (a node
   * group inside another group) never looks like a silent no-op.
   */
  public pasteRowFromSystemOrMemory = async (
    targetId: string,
    pastePosition: 'above' | 'below' | 'replace'
  ): Promise<boolean> => {
    await this.refreshRowClipboardUnlessCut();
    if (!this.canPasteRowAt(targetId, pastePosition)) return false;
    return this.pasteRow(targetId, pastePosition);
  };

  /**
   * PSTUDIO-4229: context-menu paste over a MULTI-ROW selection. The third paste
   * entry point, SYSTEM-first like the other two (guard rail 10 in
   * `KEYBOARD_CLIPBOARD_BEHAVIOR.md`: every paste path refreshes the in-memory
   * bundle from the OS clipboard first, so "last copy wins" across tabs /
   * solutions holds from the menu as well as from the keyboard).
   *
   * Takes the rows EXPLICITLY rather than reading `selectedRows`: this runs after
   * the menu closed and after the `await` above, so the live selection is not the
   * one the user right-clicked on.
   *
   * The group-in-group gate is NOT repeated here: it lives on the shared
   * `pasteRowsReplacingRows`, which judges the ANCHOR — the top-most VISIBLE row
   * of the snapshot, i.e. the row the paste actually lands on — never the row
   * the menu was opened on. The two can differ: a continuous selection may cross
   * a node group's boundary (`isSelectionContinuous` judges adjacency in visual
   * order, so a group member and the top-level row under it are neighbours), and
   * `resolvePasteGroupId` answers per ROW. Gating on the clicked row let a bundle
   * containing a group header through on a top-level clicked row, only for
   * `pasteRow` to reject it on an in-group anchor — after the non-anchor rows had
   * already been deleted inside `_recordGroupMutation`, which rolls back the
   * history but not the data. Keeping that guard in ONE place is what stops the
   * two entrances from drifting apart again: this path had it while `Cmd/Ctrl+V`
   * did not, which is the same destructive dead end reached by the other door
   * (rail 0 — a guard stated for one entrance binds them all).
   *
   * @param targetRowIds the snapshot of the selection the menu was opened over.
   */
  public pasteRowsReplacingRowsFromSystemOrMemory = async (
    targetRowIds: string[]
  ): Promise<boolean> => {
    await this.refreshRowClipboardUnlessCut();
    return this.pasteRowsReplacingRows(targetRowIds);
  };

  /**
   * PSTUDIO-4229: the row context menu's primary "Paste", as ONE store
   * entrance — which rows the gesture replaces is decided HERE, not in the
   * component.
   *
   * The decision (a continuous multi-row selection containing the clicked row
   * is the target; anything else is the clicked row alone) lives in the pure
   * `resolveRowPasteTargets`, and the two system-first paths below are the
   * pre-existing ones. Keeping the branch in `row-context-menu.tsx` meant the
   * only thing a unit test could reach was a hand-written COPY of it: reverting
   * the component alone left the whole unit suite green, because no test
   * imported the component at all. Now the menu item is a snapshot plus one
   * call, so `table-store-context-menu-paste.test.ts` exercises the real path
   * and `row-context-menu-paste.test.tsx` pins that the item still calls it.
   *
   * The caller passes the selection SNAPSHOT it took before `close()`: this
   * method awaits a clipboard refresh, so `selectedRows` read after that point
   * is whatever the selection has become, not what the user right-clicked on.
   */
  public pasteFromRowContextMenu = async (
    clickedRowId: string,
    selectedRowIds: string[],
    isSelectionContinuous: boolean
  ): Promise<boolean> => {
    const targets = resolveRowPasteTargets({
      clickedRowId,
      selectedRowIds,
      isSelectionContinuous,
    });

    return targets.mode === 'selection'
      ? this.pasteRowsReplacingRowsFromSystemOrMemory(targets.rowIds)
      : this.pasteRowFromSystemOrMemory(clickedRowId, 'replace');
  };

  /**
   * Read ONLY the `text/html` flavour from the system clipboard, WITHOUT ever
   * touching `readText()`. Used by `pasteAtFocusedCell` to sniff for OUR node
   * envelope: reading `readText()` here would consume the single-cell paste's
   * clipboard read (and tests that stub `readText` as a one-shot), so we must
   * not. Returns undefined when `read()` is unavailable or exposes no html.
   */
  private async readClipboardHtml(): Promise<string | undefined> {
    const { clipboard } = window.navigator;
    try {
      if (clipboard?.read) {
        const items = await clipboard.read();
        for (const item of items) {
          if (item.types.includes('text/html')) {
            return await (await item.getType('text/html')).text();
          }
        }
      }
    } catch (err) {
      console.warn('clipboard.read (html sniff) failed', err);
    }
    return undefined;
  }

  /**
   * PSTUDIO-3968 — the handover record between the `command+v` hotkey and the
   * native `paste` event.
   *
   * The hotkey arms it and does NOT cancel the keydown (cancelling would suppress
   * the very `paste` event whose `clipboardData` needs no permission). Exactly one
   * of the two paths may then act: whoever gets there first stamps `consumedBy`
   * inside a single synchronous block, and the other sees the stamp and stands
   * down. `kind` distinguishes a focused-cell paste from a row-selection paste —
   * and (round 3) a cell-RANGE fill. All three depend on clipboard content, so
   * all three hand over.
   *
   * NOTE — this must be checked by BOTH sides. An earlier version only had the
   * timer check it while the listener blindly cleared it, which made the exclusion
   * one-directional: if the macrotask ran before the event was dispatched (a
   * virtualized clipboard over RDP/Citrix, a clipboard-manager extension), a single
   * Ctrl+V pasted twice and produced two undo steps.
   *
   * Non-observable (see `makeAutoObservable`): per-keystroke plumbing, not
   * rendered state, so it must not tick the table's top-level `observer`.
   */
  public pendingKeyboardPaste:
    | ({ consumedBy: 'event' | 'fallback' | null } & (
        | { kind: 'cell'; rowId: string; cellId: keyof TableRow }
        | { kind: 'cells' }
        | { kind: 'rows' }
      ))
    | null = null;

  /**
   * Handle for the pending fallback timer, so it can be CANCELLED rather than
   * left to expire harmlessly. Without it, key auto-repeat piles up one live
   * timer per keypress and `disposeClipboardPasteListener` cannot really tear the
   * mechanism down (a timer that outlives a test still mutates this singleton).
   */
  private keyboardPasteFallbackTimer: ReturnType<typeof setTimeout> | null =
    null;

  // Assigned only after `makeAutoObservable` has run, so it carries an explicit
  // `= undefined` initializer: with `useDefineForClassFields: true` a
  // declared-only `?:` field is defined too, but spelling the initializer out
  // keeps the annotation and the field's existence obviously in sync.
  private clipboardPasteListener:
    | ((event: ClipboardEvent) => void)
    | undefined = undefined;

  /**
   * PSTUDIO-3968 — arm the handover and schedule the Async-Clipboard fallback.
   *
   * The fallback exists for the case where no `paste` event ever arrives (a
   * synthetic/injected keydown, or an engine that does not dispatch `paste` at a
   * non-editable focus target).
   *
   * It is DELAYED, not a bare macrotask. A `setTimeout(…, 0)` loses the race on a
   * real machine: Chrome serves `paste` from the OS clipboard, which costs an
   * inter-process round trip of a few milliseconds, so the macrotask fires FIRST,
   * stamps `consumedBy: 'fallback'`, pastes the in-memory buffer, and the arriving
   * event then stands down — i.e. the whole permission-free path was dead outside
   * automated tests, where the clipboard is in-process and the event is instant.
   * That is exactly the reported "it still pastes the old one, unless I hit Escape
   * first" (Escape drops the cell-range branch, so the next keypress lands on the
   * focused-cell path, whose fallback reads `readText()` and therefore appears to
   * work). The delay must comfortably exceed that round trip while staying
   * invisible when it is genuinely needed.
   */
  public armKeyboardPasteHandover = (
    target:
      | { kind: 'cell'; rowId: string; cellId: keyof TableRow }
      | { kind: 'cells' }
      | { kind: 'rows' }
  ): void => {
    // A previous keypress must never leave a live timer behind: two arms in quick
    // succession (key auto-repeat) would otherwise both fire.
    if (this.keyboardPasteFallbackTimer !== null) {
      clearTimeout(this.keyboardPasteFallbackTimer);
      this.keyboardPasteFallbackTimer = null;
    }

    const pending = { ...target, consumedBy: null } as NonNullable<
      typeof this.pendingKeyboardPaste
    >;
    this.pendingKeyboardPaste = pending;

    this.keyboardPasteFallbackTimer = setTimeout(() => {
      tableStore.keyboardPasteFallbackTimer = null;
      if (tableStore.pendingKeyboardPaste !== pending) return;
      if (pending.consumedBy) return; // the paste event already handled it
      pending.consumedBy = 'fallback';
      // PSTUDIO-3968 review #4: RETIRE the record, exactly as the event path does
      // via `consumeKeyboardPasteHandover`. Left un-nulled, a spent 'fallback'
      // record survives until the next arm and makes the listener stand down for
      // every paste in that window (`consumedBy !== null`) — including a legitimate
      // browser Edit ▸ Paste.
      tableStore.pendingKeyboardPaste = null;
      if (pending.kind === 'cell') {
        void tableStore.pasteAtFocusedCell({
          rowId: pending.rowId,
          cellId: pending.cellId,
        });
        return;
      }
      // 250 ms is long enough for the world to change: the user can open a cell
      // editor in that window. `pasteAtFocusedCell` re-checks its own target after
      // its await, but these two synchronous paths had no such guard — and a paste
      // landing UNDER an open editor is immediately overwritten by that editor's
      // own commit, the "correct in memory, lost on reload" class.
      //
      // Deliberately NOT gated on focus: `pasteSelectedCells` /
      // `pasteRowsFromSystemOrMemory` act on the SELECTION, which is what the user
      // aimed at, and a row selection legitimately leaves focus off the cells.
      if (tableStore.isCellBeingEdited) return;

      // No `paste` event arrived for a range fill; the in-app grid is all we can
      // reach without the `clipboard-read` permission, so replay it (the pre-3968
      // behaviour) rather than dropping the keypress.
      if (pending.kind === 'cells') {
        tableStore.pasteSelectedCells();
        return;
      }
      void tableStore.pasteRowsFromSystemOrMemory();
    }, KEYBOARD_PASTE_FALLBACK_MS);
  };

  /**
   * Is focus currently on something inside the editor table?
   *
   * The single anchor both clipboard paths use, so the keydown fallback and the
   * `paste` listener can never disagree about "is this paste ours".
   */
  public isFocusInsideEditorTable = (): boolean =>
    !!(document.activeElement as HTMLElement | null)?.closest(
      '#ps-editor-table'
    );

  /**
   * Claim the handover for the `paste` event and RETIRE the record.
   *
   * Retiring is the load-bearing half. The record used to survive its keypress
   * with `consumedBy: 'event'` while the listener only rejected `'fallback'`, so
   * for the rest of the session ANY paste — into a search box, a modal field,
   * anywhere on the page — matched the stale `kind` and replayed a row/range paste
   * into the table while swallowing the user's real paste. Cancelling the timer at
   * the same time keeps auto-repeat from stacking live timers.
   */
  private consumeKeyboardPasteHandover = (
    pending: NonNullable<typeof this.pendingKeyboardPaste>
  ): void => {
    pending.consumedBy = 'event';
    if (this.pendingKeyboardPaste === pending) {
      this.pendingKeyboardPaste = null;
    }
    if (this.keyboardPasteFallbackTimer !== null) {
      clearTimeout(this.keyboardPasteFallbackTimer);
      this.keyboardPasteFallbackTimer = null;
    }
  };

  /**
   * PSTUDIO-3968 — handle a real `paste` ClipboardEvent aimed at the table.
   *
   * Bails out for anything that is NOT "a data cell is focused and not being
   * edited", so the browser's own paste keeps working everywhere it already did:
   * an OPEN cell editor (native paste into the live `<input>`/`<textarea>` — the
   * one external-clipboard case that never broke), the expanded-cell panel, search
   * boxes, modals. This mirrors `hotkeys.filter`'s INPUT/TEXTAREA/SELECT/
   * contentEditable carve-out, because the same reasoning applies.
   *
   * The load-bearing guard is the DOM anchor, not the tag check: a paste inside a
   * web component retargets `event.target` to the host, and a host never carries
   * `data-row-id`/`data-cell-id`, so shadow-DOM editors bail out here.
   */
  public handleTableClipboardPaste = (event: ClipboardEvent): void => {
    // A synthetic ClipboardEvent from any script on the origin must not be able to
    // write into a cell; only a real user paste may.
    if (!event.isTrusted) return;
    if (!this.isEditing()) return;
    if (this.isCellBeingEdited) return;

    // `event.target` is RETARGETED to the host for a paste inside a web component,
    // so a native <input> living in a design-system shadow root (`ps-search`,
    // `ps-input`) would report the HOST's tag and slip past a plain tag check.
    // `composedPath()[0]` is the real innermost target, so check both.
    const target = event.target as HTMLElement | null;
    const innermost = (event.composedPath?.()[0] ?? target) as
      | HTMLElement
      | null
      | undefined;
    const isTextEntry = (node: HTMLElement | null | undefined): boolean => {
      const tagName = node?.tagName?.toUpperCase();
      return (
        tagName === 'INPUT' ||
        tagName === 'TEXTAREA' ||
        tagName === 'SELECT' ||
        node?.isContentEditable === true
      );
    };
    if (isTextEntry(target) || isTextEntry(innermost)) return;

    const active = document.activeElement as HTMLElement | null;
    const insideTable = this.isFocusInsideEditorTable();

    const pending = this.pendingKeyboardPaste;
    // A SPENT record means this keypress was already served — by the fallback that
    // beat us to it. Standing down here is what keeps one keypress at one paste and
    // one undo step (rail 16); without it the focused-cell branch below would paste
    // a second time. A record is retired on claim, so `null` legitimately means
    // "no handover", i.e. a native paste (browser context menu) may proceed.
    if (pending && pending.consumedBy !== null) return;

    // Only a handover armed by THIS keypress may act. Freshness is the real guard:
    // the record is armed exclusively by the `command+v` hotkey (which `hotkeys
    // .filter` already keeps out of text fields) and RETIRED the moment it is
    // claimed, so a live record means "the user just pressed Ctrl+V in the table".
    //
    // Freshness, not focus, because a row paste legitimately happens while focus is
    // NOT on a data cell — selecting rows leaves it on the row's own control — so
    // anchoring these branches to `#ps-editor-table` would break the very row paste
    // this ticket fixes. Pastes aimed at a text field are already excluded above,
    // and each branch additionally requires the matching selection to exist.
    const claimable = pending?.consumedBy === null;

    const eventText = event.clipboardData?.getData('text/plain') ?? null;
    const eventHtml = event.clipboardData?.getData('text/html') ?? null;

    // A cell-RANGE fill hands over so the system clipboard can win over a stale
    // in-app buffer (guard rail 8). Our own copy mirrors itself as TSV, so an
    // event whose text equals that mirror IS our copy and is replayed from memory
    // — full fidelity, and it keeps a same-tab cut a MOVE.
    if (
      claimable &&
      pending?.kind === 'cells' &&
      this.selectedCells.length > 0
    ) {
      this.consumeKeyboardPasteHandover(pending);
      event.preventDefault();
      event.stopPropagation();
      const source = resolveCellRangePasteSource({
        eventText,
        inMemoryTsv: this.cellClipboardTsv,
        systemWriteFailed:
          this.cellClipboardWriteFailed || this.cellClipboardMirrorPending,
      });
      // PSTUDIO-3968 review #1/#2/#7: an unresolvable clipboard (image/file copy,
      // blank payload) must write NOTHING — replaying the stale in-app grid or
      // tiling a default over the selection is silent data loss. Surface the same
      // one toast the single-cell path does so a BLOCKED clipboard is not a silent
      // no-op, which also makes the guarded feedback reachable from the keyboard
      // even while a stale cell copy is still armed.
      if (source.kind === 'unavailable') {
        this.notifyClipboardReadFailed();
        return;
      }
      this.pasteSelectedCells(
        source.kind === 'event' ? source.grid : undefined
      );
      return;
    }

    // A row-selection paste hands over too (its hydrate reads the same
    // permission-gated clipboard), so honour that intent when it is armed.
    if (claimable && pending?.kind === 'rows' && this.selectedRows.size > 0) {
      this.consumeKeyboardPasteHandover(pending);
      event.preventDefault();
      event.stopPropagation();
      void this.pasteRowsFromSystemOrMemory({ eventText, eventHtml });
      return;
    }

    // A focused-cell paste requires a FRESH `cell` handover armed by this very
    // keypress — NOT merely `pending === null`.
    //
    // PSTUDIO-3968 review #3: key auto-repeat fires two keydowns before the first
    // paste event. arm #2 replaces arm #1, paste event #1 claims arm #2 (a range
    // fill), and paste event #2 then arrives with `pendingKeyboardPaste === null`.
    // The old "null means a browser context-menu paste, let it through" rule turned
    // that stray event into a DIFFERENT operation — `pasteAtFocusedCell`, which
    // replaces the row when a node envelope is on the clipboard. Gating on a live
    // `cell` record drops the stray event as a no-op. (The browser's own Edit ▸
    // Paste onto a non-editing cell is a rare path the keyboard Ctrl+V covers; not
    // worth a data-loss window — noted in KEYBOARD_CLIPBOARD_BEHAVIOR.md rail 17.)
    if (!claimable || pending?.kind !== 'cell') return;
    if (!insideTable || !active) return;
    const { rowId, cellId } = active.dataset ?? {};
    if (!rowId || !cellId) return;

    this.consumeKeyboardPasteHandover(pending);

    event.preventDefault();
    event.stopPropagation();

    void this.pasteAtFocusedCell({
      rowId,
      cellId: cellId as keyof TableRow,
      eventText,
      eventHtml,
    });
  };

  /**
   * Detach the `paste` listener AND drop any armed handover (tests / teardown; the
   * store itself is a singleton). Cancelling the timer matters as much as removing
   * the listener: a pending fallback would otherwise wake up after teardown and
   * mutate this singleton, leaking one test's paste into the next.
   */
  public disposeClipboardPasteListener(): void {
    if (this.keyboardPasteFallbackTimer !== null) {
      clearTimeout(this.keyboardPasteFallbackTimer);
      this.keyboardPasteFallbackTimer = null;
    }
    this.pendingKeyboardPaste = null;
    if (!this.clipboardPasteListener) return;
    document.removeEventListener('paste', this.clipboardPasteListener, true);
    this.clipboardPasteListener = undefined;
  }

  /**
   * PSTUDIO-3337: keyboard paste onto a FOCUSED cell (Cmd/Ctrl+V while a data
   * cell is selected). If the clipboard's `text/html` holds OUR node bundle (the
   * last copy was nodes — same tab, another tab, or another solution), REPLACE the
   * focused cell's ROW with the node(s) (matching the context menu's primary
   * "Paste" = replace and the row-selection path), so selecting ANY cell and
   * pressing paste drops the copied nodes onto that row. Otherwise fall back to
   * the single-cell text paste.
   *
   * The node sniff never consumes `readText()` — it prefers the event's own
   * `text/html` and otherwise uses `clipboard.read()` — so the single-cell
   * `handlePaste` path keeps its own read intact (a plain value still pastes into
   * the one cell, unchanged). Our copy writer always puts the envelope in
   * `text/html`, so a real node bundle is still detected; anything else falls
   * through to `handlePaste`.
   */
  public pasteAtFocusedCell = async ({
    rowId,
    cellId,
    eventText,
    eventHtml,
  }: {
    rowId: string;
    cellId: keyof TableRow;
    eventText?: string | null;
    eventHtml?: string | null;
  }): Promise<void> => {
    // PSTUDIO-3968: prefer the `text/html` a native ClipboardEvent already
    // carries — `readClipboardHtml()` (i.e. `clipboard.read()`) is
    // permission-gated and rejects in a browser that never granted
    // clipboard-read, which is exactly how the external paste path used to die.
    //
    // An EMPTY/absent `text/html` on the event is NOT evidence that there is no
    // node bundle — it only means the EVENT carried none — so the async read
    // stays a real fallback. Treating `''` as authoritative broke the cross-tab
    // node paste (`clipboard/cross-tab-node-paste-into-cell`, whose fixture stubs
    // `clipboard.read()` with the captured envelope): the sniff was skipped and a
    // node paste silently degraded into a plain-text paste.
    //
    // It is tempting to also skip the read when the event delivered plain TEXT (its
    // `clipboardData` is a full snapshot, so a missing `text/html` looks
    // authoritative). Do NOT: the node fixtures stub `clipboard.read()` while the
    // REAL clipboard still holds an unrelated TSV from an earlier copy, so the
    // event's text is present while the envelope lives only behind the stub —
    // skipping the read there degrades a cross-tab NODE paste into a plain-text
    // paste (`clipboard/cross-tab-node-paste-into-cell`). The read already fails
    // soft, and the text paste below uses the event payload regardless, so nothing
    // depends on its success.
    const html =
      eventHtml && eventHtml.length > 0
        ? eventHtml
        : await this.readClipboardHtml();
    const envelope = parseNodeEnvelopeFromHtml(html);
    if (envelope && envelope.rows.length) {
      runInAction(() => {
        this.rowClipboard = envelope.rows.map((r) => ({ ...r }));
        this.isRowCut = false; // a cross-tab / re-homed paste is always a copy
      });
      this.pasteRow(rowId, 'replace');
      return;
    }
    // Not our node bundle → single-cell text paste. `handlePaste` prefers the
    // event's own text and only falls back to `readText()` when there is none, so
    // a one-shot readText stub is never consumed by the sniff.
    await this.handlePaste({ cellId, rowId, eventText, eventHtml });
  };

  public canPasteRow() {
    // console.log('canPasteRow', this.rowClipboard?.length);
    if (this.rowClipboard?.length === 0) return false;

    return true;
  }

  /** True when the clipboard bundle contains a group-header row. */
  private clipboardHasGroupRow(): boolean {
    return (this.rowClipboard ?? []).some((row) => isGroupRow(row));
  }

  /**
   * Group id that a paste at (targetId, pastePosition) would land in;
   * undefined = top level. Single source of truth shared by `pasteRow` (the
   * actual membership) and `canPasteRowAt` (the menu's disabled state) so the
   * two can never drift (PSTUDIO-3202 / PSTUDIO-3160 C).
   *
   *  • plain / sub-row target → the target's own `settings.groupId`.
   *  • EXPANDED group header, 'below' → inside that group (its id).
   *  • COLLAPSED group header, 'below' → top level (lands after the bundle).
   */
  private resolvePasteGroupId(
    targetId: string,
    pastePosition: 'above' | 'below' | 'replace'
  ): string | null | undefined {
    const targetRow = this.getRowById(targetId);
    const targetIsGroupHeader = !!targetRow && isGroupRow(targetRow);
    let groupId = targetRow?.settings?.groupId;
    if (
      targetIsGroupHeader &&
      pastePosition === 'below' &&
      targetRow?.settings?.groupExpanded
    ) {
      groupId = targetId;
    }
    return groupId;
  }

  /**
   * `canPasteRow` plus the group-in-group guard for a concrete (target,
   * position). The Paste context-menu items disable on this so a paste that
   * `pasteRow` would reject (a node group can't live inside another group)
   * never looks like a silent no-op — it mirrors the drag path's red line
   * (PSTUDIO-3202 / PSTUDIO-3160 C).
   */
  public canPasteRowAt(
    targetId: string,
    pastePosition: 'above' | 'below' | 'replace'
  ): boolean {
    if (!this.canPasteRow()) return false;
    if (
      this.clipboardHasGroupRow() &&
      this.resolvePasteGroupId(targetId, pastePosition)
    ) {
      return false;
    }
    return true;
  }

  public needToReorder(num1: number, num2: number): boolean {
    return Math.abs(num1 - num2) <= 5;
  }

  // PSTUDIO-2821: transient edit-buffer setters (non-reactive fields).
  setDraftCellValue(raw: string) {
    this.draftCellValue = raw;
    // PSTUDIO-3624: mirror into the observable the panel reads. Equality-guarded
    // so a keystroke that doesn't change the value (e.g. an IME no-op) fires no
    // reaction (feedback_mobx_incremental_update_churn / PSTUDIO-3625).
    if (this.liveCellDraft !== raw) {
      this.liveCellDraft = raw;
    }
  }
  setCellCloseIntent(intent: 'commit' | 'cancel' | null) {
    this.cellCloseIntent = intent;
  }

  // PSTUDIO-2154/2821: single commit choke point for a native cell value —
  // wrap the row update in one undo/redo command and clear the cell's stale
  // validation error. Used by the draft commit path (commitCellEditing) and the
  // write-through columns that commit on change (checkbox in cell.tsx).
  writeCellValue(rowId: string, columnId: string, value: unknown) {
    // PSTUDIO-4348: a Rich Asset Type write is never a bare column write — the
    // type drives Disable Free Text / NLU Disabled / the default content. A
    // pasted cell (keyboard or cell menu) reaches this choke point with a string
    // type and lands on the path the dropdown and the panel call directly. It
    // records its own undo step, so it must not run inside the `recordRowEdit`
    // below.
    if (columnId === NodeColumns.richAssetType && typeof value === 'string') {
      this.commitAssetTypeChange(rowId, value);
      return;
    }
    this.recordRowEdit(
      rowId,
      `Edit ${columnId}`,
      () => {
        this.updateRow({
          rowId,
          updates: { [columnId]: value } as Partial<TableRow>,
          skipValidationRules: false,
          saveImmediately: true,
        });
        this.clearCellValidationError(
          rowId,
          columnId as keyof typeof NodeColumns
        );
      },
      columnId as NodeColumns
    );
  }

  // Change a row's Rich Asset Type the way the dropdown does (PSTUDIO-3422 /
  // PSTUDIO-3624): auto-adjust behaviors + nluDisabled, seed the default content
  // while the content is empty, clear the stale errors those writes resolve — all
  // as ONE undo/redo command. The cell dropdown and the Expanded Cell View panel
  // reach it through `commitAssetTypeChange` in `cell.tsx`; a pasted cell reaches
  // it through `writeCellValue` (PSTUDIO-4348). What it writes is decided by the
  // pure `planAssetTypeChange`, which the cell-range paste shares.
  commitAssetTypeChange(rowId: string, newType: string) {
    const plan = planAssetTypeChange({
      newType,
      behaviors: this.getValue(rowId, NodeColumns.behaviors) as
        | string[]
        | null
        | undefined,
      richAssetContent: this.getValue(rowId, NodeColumns.richAssetContent),
      richAssetsConfig: this.richAssetsConfig,
    });
    this.recordRowEdit(
      rowId,
      'Change asset type',
      () => {
        this.updateRow({
          rowId,
          updates: plan.updates as Partial<TableRow>,
          skipValidationRules: false,
          saveImmediately: true,
        });
        this._clearAssetTypeErrors(rowId, plan.adjusted);
      },
      NodeColumns.richAssetType
    );
  }

  // The stale validation errors a Rich Asset Type change resolves: the type's
  // own, plus those of the dependent cells the change actually adjusted. ONE
  // definition for the dropdown / panel / pasted cell and the cell-range paste.
  private _clearAssetTypeErrors(
    rowId: string,
    adjusted: AssetTypeChangePlan['adjusted']
  ) {
    this.clearCellValidationError(rowId, NodeColumns.richAssetType);
    if (adjusted.behaviors) {
      this.clearCellValidationError(rowId, NodeColumns.behaviors);
    }
    if (adjusted.nluDisabled) {
      this.clearCellValidationError(rowId, NodeColumns.nluDisabled);
    }
    if (adjusted.richAssetContent) {
      this.clearCellValidationError(rowId, NodeColumns.richAssetContent);
    }
  }

  // PSTUDIO-2821: leave edit mode without committing or discarding — the
  // bookkeeping half shared by commit/cancel. Clears the legacy edit flags plus
  // the new draft + intent buffers.
  private _clearCellEditingState() {
    this.isCellBeingEdited = false;
    this.editingCellId = null;
    this.editingRowId = null;
    this.editingColumnId = null;
    this.pendingEditCellId = null;
    this.draftCellValue = null;
    // PSTUDIO-3624: drop the panel's live mirror so it falls back to the
    // committed selectedCellValue() the moment the edit commits/cancels.
    this.liveCellDraft = null;
    this.cellCloseIntent = null;
    // PSTUDIO-1433: an un-applied panel seed dies with the edit it belonged to, so
    // a character whose panel never opened can never fire into a later edit
    // (resolveInitialCharSeed requires the address to match, so clearing it alone
    // disarms the seed). Only the ADDRESS is cleared here: `pendingInitialChar`
    // itself is the inline rAF-injection path's state and is left untouched, so
    // the non-JSON type-to-edit flow is unchanged. Safe for a live panel seed —
    // the panel reads AND clears both in its autofocus effect, which runs before
    // focus reaches its editor, hence before the onFocusCapture commit that
    // funnels here.
    this.pendingInitialCharCellId = null;
  }

  // PSTUDIO-2821: commit the active cell's draft to the row, then close.
  // Idempotent — a no-op when nothing is being edited or the value is unchanged.
  // Every explicit commit path routes here: Tab, click into another cell, and
  // global-search focus (via stopCellEditing).
  commitCellEditing() {
    const rowId = this.editingRowId;
    const columnId = this.editingColumnId;
    const raw = this.draftCellValue;
    if (rowId && columnId && raw != null) {
      const stored = this.getValue(rowId, columnId as keyof TableNode);
      if (!nativeCellRawEquals(columnId, raw, stored)) {
        this.writeCellValue(rowId, columnId, parseNativeCellRaw(columnId, raw));
        // PSTUDIO-1433: the inline editor just wrote the very cell the panel
        // holds a session on — a JSON cell auto-opens the panel, so both can be
        // live at once. The session's `before` snapshot predates this write, so
        // resuming it would record a history step that reverts to a value the
        // user already moved past. Ending it makes the panel's next focus open
        // a FRESH session, re-baselined against what is actually stored now.
        // (`cancelCellEditing` already does this; a commit is the other half.)
        if (this.isPanelEditRegistered(rowId, columnId)) this.endPanelEdit();
      }
    }
    this._clearCellEditingState();
  }

  /**
   * PSTUDIO-1433: register/clear the Expanded Cell View panel's open edit.
   *
   * `beginPanelEdit` is idempotent for the same cell, so the panel can call it
   * from every draft write without churn. `endPanelEdit` is what makes a
   * session INVALID: the panel checks its registration before writing, so a
   * store-side teardown (undo/redo, a coworker merge that replaced the row, a
   * delete) cancels the pending flush instead of letting it overwrite whatever
   * happened in between.
   */
  beginPanelEdit(rowId: string, columnId: string) {
    if (
      this.panelEditingRowId === rowId &&
      this.panelEditingColumnId === columnId
    ) {
      return;
    }
    this.panelEditingRowId = rowId;
    this.panelEditingColumnId = columnId;
  }

  endPanelEdit() {
    if (this.panelEditingRowId === null && this.panelEditingColumnId === null) {
      return;
    }
    this.panelEditingRowId = null;
    this.panelEditingColumnId = null;
  }

  /** Is this exact cell still the registered panel edit? */
  isPanelEditRegistered(rowId: string, columnId: string): boolean {
    return (
      this.panelEditingRowId === rowId && this.panelEditingColumnId === columnId
    );
  }

  // PSTUDIO-2821: discard the active cell's draft and close. The uncontrolled
  // control re-reads the stored value on its next render, so the visible value
  // reverts automatically.
  cancelCellEditing() {
    this._clearCellEditingState();
    // PSTUDIO-1433: an undo/redo (the caller of this via `hasActiveCellEdit`)
    // must also invalidate the panel's open session — otherwise the panel's
    // deferred flush lands AFTER the history command and writes the pre-undo
    // draft back, silently reverting the undo and pushing a bogus step.
    this.endPanelEdit();
    // PSTUDIO-2821: Esc FULLY closes the cell. Unlike a commit (Tab / click /
    // Enter all move focus to another cell, which becomes the new
    // selectedStartCell and unmounts this cell's lazy <input>), Esc does NOT
    // move focus — so without this the cancelled cell stays selectedStartCell
    // and `showControl = isActive || isSelectedStartCell` keeps its <input>
    // mounted, leaving the cell visually "open". Clearing the start-cell drops
    // it back to the plain <span> view.
    this.clearSelectedStartCell();
  }

  public stopCellEditing = action(() => {
    emitOnce(() => {
      // PSTUDIO-2821: commit any in-progress draft before tearing down, so
      // focusing the global search (the one caller) saves the user's typing
      // instead of dropping it. commitCellEditing also clears the edit flags.
      this.commitCellEditing();
      this.setKeyboardNav(false);
      this.deselectAllCells();
      this.clearSelectedStartCell();
    });
  });

  public isRowCopiedOrCut = computedFn((rowId: string) => {
    if (
      this.showCopyIndicator &&
      this.rowClipboard?.find((row) => row.id === rowId)
    ) {
      return true;
    }

    return false;
  });

  /**
   * True while the clipboard holds a CUT bundle (vs a copy). Drives the
   * greyed-out cut-row styling (PSTUDIO-3021); a copy shows only the
   * marching-ants border. Guarded by `clipboardSize` so a stale `isRowCut`
   * after `clearRowClipboard` never reports cut.
   */
  public get isClipboardCut(): boolean {
    return this.clipboardSize > 0 && this.isRowCut;
  }

  // Multi-cell clipboard (PSTUDIO-3504), parallel to the row clipboard above.
  // `cellClipboard` is the serialized 2D grid (row-major); `cellClipboardSource`
  // are the source cells (for the marching-ants indicator and cut move-clear).
  private cellClipboard: string[][] = [];
  private cellClipboardSource: CellSelection[] = [];
  private isCellCut = false;
  private showCellCopyIndicator = false;

  // PSTUDIO-3968: did THIS range's mirror onto the OS clipboard land? A range
  // paste compares the native paste event's TSV against our mirror to decide who
  // holds the last copy, and that comparison is only meaningful if the mirror
  // exists. Deliberately separate from `clipboardDetails.systemWriteFailed`, which
  // tracks the unrelated single-cell copy and could describe an older operation.
  private cellClipboardWriteFailed = false;

  /**
   * The mirror is still IN FLIGHT (`writeText` neither resolved nor rejected).
   *
   * `_captureCells` writes fire-and-forget, so a fast Ctrl+X → Ctrl+V can paste
   * before the OS clipboard has been updated. The event would then carry the
   * PREVIOUS clipboard content, the comparison would call our own copy "external",
   * and the cut would silently stop being a MOVE (source never cleared). While the
   * write is pending we cannot compare against anything, so memory wins.
   */
  private cellClipboardMirrorPending = false;

  /**
   * Monotonic id of the CURRENT cell copy. Bumped on every capture and on every
   * clear, so a `writeText` promise that settles LATE can tell whether it still
   * describes the live copy. Without it, two quick Ctrl+C in a row let the first
   * write's `.then()`/`.catch()` flip the SECOND copy's mirror flags (the second
   * write may not have reached the OS clipboard yet), and a paste in that window
   * reads the stale grid — a real, if narrow, correctness bug over a slow /
   * virtualized clipboard broker (RDP/Citrix).
   */
  private cellClipboardEpoch = 0;

  /**
   * The exact TSV we handed to the OS clipboard for the current range.
   *
   * Cached rather than recomputed per keypress: `gridToTsv` already ran at copy
   * time, this is the single source of truth for "what our mirror looks like", and
   * re-deriving it on every Ctrl+V costs a full pass over the grid (measured ~9 ms
   * for 5000×12, two thirds of it a pointless `toJS` clone).
   */
  private cellClipboardTsv = '';

  public get hasCellClipboard(): boolean {
    return this.cellClipboard.length > 0;
  }

  /** True while the cell clipboard holds a CUT range (drives greyed styling). */
  public get isCellClipboardCut(): boolean {
    return this.hasCellClipboard && this.isCellCut;
  }

  /** Whether a given cellId is in the active copied/cut range (marching ants). */
  public isCellCopiedOrCut = computedFn((cellId: string) => {
    return (
      this.showCellCopyIndicator &&
      this.cellClipboardSource.some((c) => c.cellId === cellId)
    );
  });

  public clearCellClipboard = action(() => {
    if (
      this.cellClipboard.length === 0 &&
      this.cellClipboardSource.length === 0
    ) {
      return;
    }
    this.cellClipboard = [];
    this.cellClipboardSource = [];
    this.isCellCut = false;
    this.showCellCopyIndicator = false;
    // PSTUDIO-3968: the cached mirror describes a range that no longer exists —
    // leaving it would let a later event match a dead copy and paste from memory.
    this.cellClipboardTsv = '';
    this.cellClipboardMirrorPending = false;
    this.cellClipboardWriteFailed = false;
    // Retire any in-flight write of the cleared copy so its late `.then()`/
    // `.catch()` cannot resurrect mirror flags for a copy that no longer exists.
    this.cellClipboardEpoch++;
  });

  /** Copy the current cell selection (Cmd/Ctrl+C). */
  public copySelectedCells(): boolean {
    return this._captureCells(false);
  }

  /** Cut the current cell selection (Cmd/Ctrl+X). */
  public cutSelectedCells(): boolean {
    return this._captureCells(true);
  }

  /**
   * Serialize the bounding rectangle of `selectedCells` into `cellClipboard`
   * (row-major, dense — Cmd+click non-contiguous selections collapse to their
   * bounding box, mirroring Google Sheets). Also writes a best-effort TSV to the
   * system clipboard for external interop. Returns false when nothing is
   * selected.
   */
  private _captureCells(isCut: boolean): boolean {
    this.clearCellClipboard();

    const cells = [...this.selectedCells];
    if (cells.length === 0) return false;

    // PSTUDIO-3337: node and cell clipboards are mutually exclusive ("last copy
    // wins") — a cell copy/cut clears any active node marching-ants (rowClipboard
    // + showCopyIndicator) and the in-memory node bundle, so a subsequent row
    // paste doesn't paste the stale nodes and the node highlight disappears.
    this.clearRowClipboard();
    this.showCopyIndicator = false;

    const visibleRows = this.getAllSortedRows().filter(
      (r) => r.hidden !== true
    );
    const rowIdxs = cells
      .map((c) => this.getSortedRowIndex(c.rowId))
      .filter((i) => i >= 0);
    // VISUAL column space (PSTUDIO-2372) — the clipboard grid must mirror the
    // rendered left-to-right order the selection rectangle was built in, or a
    // visually-adjacent range exports with phantom columns in between.
    const colIdxs = cells
      .map((c) => this.getVisualColumnIndex(c.columnId))
      .filter((i) => i >= 0);
    if (rowIdxs.length === 0 || colIdxs.length === 0) return false;

    const top = Math.min(...rowIdxs);
    const bottom = Math.max(...rowIdxs);
    const left = Math.min(...colIdxs);
    const right = Math.max(...colIdxs);

    const grid: string[][] = [];
    const source: CellSelection[] = [];
    for (let r = top; r <= bottom; r++) {
      // `r` is always a valid visibleRows index: getSortedRowIndex and visibleRows
      // both derive from getAllSortedRows().filter(r => r.hidden !== true), so every
      // index returned by getSortedRowIndex is in-bounds. The `if (row)` guard below
      // is purely defensive — the dense grid still pushes '' for the (unreachable) miss.
      const row = visibleRows[r];
      const gridRow: string[] = [];
      for (let c = left; c <= right; c++) {
        const columnId = this.visualColumnOrder[c] as keyof TableRow;
        gridRow.push(row ? this.serializeCellValue(row.id, columnId) : '');
        if (row) {
          source.push({
            cellId: `${row.id}-${columnId}`,
            rowId: row.id,
            columnId: columnId as NodeColumns,
          });
        }
      }
      grid.push(gridRow);
    }

    this.cellClipboard = grid;
    this.cellClipboardSource = source;
    this.isCellCut = isCut;
    this.showCellCopyIndicator = true;
    this.cellClipboardWriteFailed = false;

    // PSTUDIO-3968: serialize ONCE through `gridToTsv` and cache it — the paste
    // side compares the event's text against this exact string to recognise our
    // own copy, so it must not re-derive (cost) or diverge (correctness).
    const tsv = gridToTsv(grid);
    this.cellClipboardTsv = tsv;
    this.cellClipboardMirrorPending = true;
    // Capture THIS copy's epoch so a late-settling write cannot touch a newer one.
    const epoch = ++this.cellClipboardEpoch;
    const isCurrent = () => this.cellClipboardEpoch === epoch;

    const onWriteFailed = (err: unknown) => {
      if (!isCurrent()) return;
      this.cellClipboardWriteFailed = true;
      this.cellClipboardMirrorPending = false;
      this.notifyClipboardWriteFailed(err);
    };

    try {
      const write = globalThis.navigator?.clipboard?.writeText(tsv);
      if (write) {
        write
          .then(() => {
            if (!isCurrent()) return;
            this.cellClipboardMirrorPending = false;
          })
          .catch(onWriteFailed);
      } else {
        // No Async Clipboard API at all — there is no mirror to wait for.
        this.cellClipboardMirrorPending = false;
      }
    } catch (err) {
      onWriteFailed(err);
    }

    return true;
  }

  /**
   * Paste the cell clipboard into the current selection with Google-Sheets fill
   * semantics (PSTUDIO-3504):
   *   - single-cell target → expand the grid from the anchor (clipped to table);
   *   - multi-cell target → fill the selection rectangle exactly, tiling a
   *     smaller clipboard and clipping a larger one (never overflow).
   * Cut clears the (non-target) source cells afterwards (move) — only those whose
   * value was actually written to a target (PSTUDIO-4358). Copy keeps the
   * clipboard for repeat pastes. The whole paste is one atomic undo step.
   *
   * PSTUDIO-3968: `externalGrid` lets the native `paste` event feed a grid that
   * came from the SYSTEM clipboard (an external app, another tab) through this
   * exact fill plan. Such a paste is never a MOVE: the cut bookkeeping
   * (`isCellCut` / `cellClipboardSource`) describes OUR range, and clearing those
   * source cells because an unrelated external paste happened would destroy data.
   */
  public pasteSelectedCells = action((externalGrid?: string[][]): boolean => {
    const grid = externalGrid ?? this.cellClipboard;
    if (grid.length === 0) return false;

    // A cut is a move only when we are replaying our OWN captured range.
    const isMove = externalGrid === undefined && this.isCellCut;

    const cells = [...this.selectedCells];
    if (cells.length === 0) return false;

    const visibleRows = this.getAllSortedRows().filter(
      (r) => r.hidden !== true
    );

    let anchorRowIndex: number;
    let anchorColIndex: number;
    let targetRows: number;
    let targetCols: number;

    // VISUAL column space throughout (PSTUDIO-2372) — must match the space the
    // rectangle and the captured grid were built in.
    if (cells.length === 1) {
      anchorRowIndex = this.getSortedRowIndex(cells[0].rowId);
      anchorColIndex = this.getVisualColumnIndex(cells[0].columnId);
      targetRows = 1;
      targetCols = 1;
    } else {
      const rowIdxs = cells
        .map((c) => this.getSortedRowIndex(c.rowId))
        .filter((i) => i >= 0);
      const colIdxs = cells
        .map((c) => this.getVisualColumnIndex(c.columnId))
        .filter((i) => i >= 0);
      if (rowIdxs.length === 0 || colIdxs.length === 0) return false;
      anchorRowIndex = Math.min(...rowIdxs);
      anchorColIndex = Math.min(...colIdxs);
      targetRows = Math.max(...rowIdxs) - anchorRowIndex + 1;
      targetCols = Math.max(...colIdxs) - anchorColIndex + 1;
    }
    if (anchorRowIndex < 0 || anchorColIndex < 0) return false;

    const plan = computeCellFillPlan({
      grid,
      anchorRowIndex,
      anchorColIndex,
      targetRows,
      targetCols,
      maxRows: visibleRows.length,
      maxCols: this.visualColumnOrder.length,
    });

    // Group writes per row; skip disabled/missing cells.
    const writesByRow = new Map<string, Partial<TableRow>>();
    const targetCellIds = new Set<string>();
    // PSTUDIO-4358: which clipboard cell (`row:col` offset in `grid`) each target
    // cell takes its value from — the same modulo `computeCellFillPlan` applies.
    // A MOVE clears only the sources whose offset some surviving target received.
    const gridCols = grid[0]?.length ?? 0;
    const gridOffsetByTargetCell = new Map<string, string>();
    for (const { rowIndex, colIndex, value } of plan) {
      const row = visibleRows[rowIndex];
      if (!row) continue;
      // PSTUDIO-4358: a group header is a visible row, so the plan counts it,
      // but its cells hold the GROUP's name and message, not a node's. The grid
      // value aimed at it is written nowhere (its slot is not shifted onto the
      // next row), so the gate below keeps that value's cut source.
      if (isGroupRow(row)) continue;
      const columnId = this.visualColumnOrder[colIndex] as keyof TableRow;
      if (this.isCellDisabled(row.id, columnId)) continue;
      if (!this.hasCell(row.id, columnId)) continue;
      const updates = writesByRow.get(row.id) ?? {};
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (updates as any)[columnId] = convertTextToColumnValue(
        value,
        columnId as keyof TableNode
      );
      writesByRow.set(row.id, updates);
      const targetCellId = `${row.id}-${columnId}`;
      targetCellIds.add(targetCellId);
      gridOffsetByTargetCell.set(
        targetCellId,
        `${(rowIndex - anchorRowIndex) % grid.length}:${
          (colIndex - anchorColIndex) % gridCols
        }`
      );
    }

    // PSTUDIO-4348: Command / Rich Asset Type are written only where the row will
    // show them live AFTER this fill — the same fill can carry a new Node Type, so
    // the row's current type is not the answer. A skipped cell leaves its target
    // alone; for a MOVE the whole paste is refused instead, because the source is
    // cleared whatever the target did and a skipped target would lose the value —
    // unless the skipped cell carries nothing (the column's default), which loses
    // nothing and so does not count against the move.
    let skippedByNodeType = false;
    writesByRow.forEach((updates, rowId) => {
      const pasted = updates as Record<string, unknown>;
      const gated = NODE_TYPE_GATED_PASTE_COLUMNS.filter(
        (column) => column in pasted
      );
      if (gated.length === 0) return;
      const nodeType = (pasted[NodeColumns.nodeType] ??
        this.getRowById(rowId)?.nodeType) as
        | Tenancy_Node_Type_Enum_Ps_Enum
        | undefined;
      gated.forEach((column) => {
        if (!isClearedForNodeType(nodeType, column)) return;
        if (pasted[column] !== getDefaultValueForColumn(column)) {
          skippedByNodeType = true;
        }
        delete pasted[column];
        targetCellIds.delete(`${rowId}-${column}`);
      });
      if (Object.keys(pasted).length === 0) writesByRow.delete(rowId);
    });
    if (isMove && skippedByNodeType) return false;
    if (writesByRow.size === 0) return false;

    // PSTUDIO-4348: a pasted Rich Asset Type changes the type like the dropdown
    // does. The plan is made against the values the row will HOLD after this fill
    // — what the same range pastes into behaviors / content wins over what the row
    // holds now — and its constraints (Disable Free Text, NLU Disabled) are
    // applied on top of the pasted behaviors, as a dropdown change applies them
    // on top of the current ones. Content the range pastes is never seeded over.
    const adjustedByRow = new Map<string, AssetTypeChangePlan['adjusted']>();
    writesByRow.forEach((updates, rowId) => {
      if (!(NodeColumns.richAssetType in updates)) return;
      const pasted = updates as Record<string, unknown>;
      const heldNow = (column: NodeColumns) =>
        column in pasted ? pasted[column] : this.getValue(rowId, column);
      const typePlan = planAssetTypeChange({
        newType: pasted[NodeColumns.richAssetType] as string,
        behaviors: heldNow(NodeColumns.behaviors) as
          | string[]
          | null
          | undefined,
        richAssetContent: heldNow(NodeColumns.richAssetContent),
        richAssetsConfig: this.richAssetsConfig,
      });
      Object.assign(updates, typePlan.updates);
      adjustedByRow.set(rowId, typePlan.adjusted);
    });

    // PSTUDIO-4358: a paste never creates rows and is clipped to the target
    // rectangle, so part of a cut range can land nowhere — at the table bottom,
    // the right edge, or past a smaller explicit selection. Clearing every source
    // cell erased those values with no copy left. A source cell is cleared only
    // when its grid offset reached a target that is still written after the
    // node-type gate above. `_captureCells` builds the source row-major over the
    // same rectangle as the grid, so source `i` is grid cell
    // `(floor(i / gridCols), i % gridCols)`; when the two sizes disagree the
    // mapping is unknown and NO source is cleared (the move degrades to a copy).
    const deliveredGridOffsets = new Set<string>();
    targetCellIds.forEach((cellId) => {
      const offset = gridOffsetByTargetCell.get(cellId);
      if (offset !== undefined) deliveredGridOffsets.add(offset);
    });
    const sourceMatchesGrid =
      gridCols > 0 &&
      this.cellClipboardSource.length === grid.length * gridCols;
    const cutSources =
      isMove && sourceMatchesGrid
        ? this.cellClipboardSource.filter((_, i) =>
            deliveredGridOffsets.has(
              `${Math.floor(i / gridCols)}:${i % gridCols}`
            )
          )
        : [];

    // One atomic undo/redo command for the whole fill (+ cut clear).
    this._recordGroupMutation('Paste cells', () => {
      writesByRow.forEach((updates, rowId) => {
        this.updateRow({
          rowId,
          updates,
          skipValidationRules: false,
          saveImmediately: true,
        });
        const adjusted = adjustedByRow.get(rowId);
        if (adjusted) this._clearAssetTypeErrors(rowId, adjusted);
      });

      if (isMove) {
        cutSources.forEach((src) => {
          if (targetCellIds.has(src.cellId)) return; // overlap → keep pasted value
          if (this.isCellDisabled(src.rowId, src.columnId as keyof TableRow)) {
            return;
          }
          this.updateRow({
            rowId: src.rowId,
            updates: {
              [src.columnId]: getDefaultValueForColumn(src.columnId),
            } as Partial<TableRow>,
            skipValidationRules: false,
            saveImmediately: true,
          });
        });
      }
    });

    if (isMove) {
      this.clearCellClipboard();
    } else if (externalGrid === undefined && this.showCellCopyIndicator) {
      // PSTUDIO-3968: a COPY is visually consumed by its paste — the marching ants
      // stop after Ctrl+C → Ctrl+V instead of lingering until Escape. The grid
      // itself is deliberately KEPT, so pasting the same value into further cells
      // still works; only the indicator is cleared. Guarded by the `if` because the
      // table is a top-level `observer` and even an equal write re-renders it.
      //
      // An EXTERNAL paste leaves the indicator alone: our range was not consumed,
      // and hiding it there would leave a pending CUT armed but invisible — the
      // user would have no clue a later matching paste can still MOVE the source.
      this.showCellCopyIndicator = false;
    }
    return true;
  });

  /** Copy the currently selected rows to the clipboard (Cmd/Ctrl+C). */
  public copySelectedRows(): boolean {
    return this.copyRows([...this.selectedRows.values()]);
  }

  /** Cut the currently selected rows to the clipboard (Cmd/Ctrl+X). */
  public cutSelectedRows(): boolean {
    return this.cutRows([...this.selectedRows.values()]);
  }

  /**
   * PSTUDIO-3337: keyboard paste ONTO a ROW selection (Cmd/Ctrl+V) — REPLACES
   * the selection (matching the context menu's primary "Paste" = replace, and the
   * focused-cell path), instead of the old "below the bottom-most selected row"
   * behaviour. Applies to BOTH copy and cut: a cut paste onto a selection is the
   * same "Paste"/replace as in the context menu — the cut node replaces the
   * target and is removed from its origin, so the table ends with one less node
   * (confirmed behaviour, consistent in and out of node groups — Slack thread
   * 2026-06-10). `pasteRow` handles the cut vs copy specifics internally.
   *
   *  • SINGLE row selected → that row is replaced by the pasted node(s); the
   *    bundle lands on its slot. Uses `pasteRow`'s own symmetric undo directly.
   *  • MULTIPLE rows selected → the WHOLE selection is replaced: the non-anchor
   *    rows are deleted and the bundle lands on the TOP-MOST selected row's slot.
   *    Both mutations are collapsed into ONE atomic undo/redo command via
   *    `_recordGroupMutation` (its snapshot swap keeps the DB in sync too —
   *    PSTUDIO-3167), so a single Cmd+Z reverts the whole replace.
   *
   * Returns false when there is nothing to paste or no visible row is selected.
   * `pasteRow` owns the group-in-group guard + toast backstop (PSTUDIO-3202), so
   * callers need no extra check.
   */
  pasteRowsReplacingSelection = action((): boolean =>
    this.pasteRowsReplacingRows([...this.selectedRows.values()])
  );

  /**
   * Whether `rowId` sits inside a CONTINUOUS multi-row selection — the state in
   * which the row context menu and the drag wiring act on the whole selection
   * rather than on the clicked row (PSTUDIO-4229). The rule itself lives in
   * `isPartOfContinuousMultiSelection`; this reader exists so the two components
   * do not each restate it, and it is O(1) — no copy of the selection set per
   * rendered row.
   */
  public isRowInContinuousMultiSelection(rowId: string): boolean {
    return isPartOfContinuousMultiSelection({
      isRowSelected: this.isSelectedRow(rowId),
      selectedCount: this.selectedRows.size,
      isSelectionContinuous: this.isSelectionContinuous,
    });
  }

  /**
   * The rows a replace-paste would act on, in VISUAL order — `[0]` is the row
   * the bundle lands on and the rest are the rows that die with it.
   *
   * One implementation for two readers on purpose (PSTUDIO-4229): the gate in
   * `pasteRowsReplacingRowsFromSystemOrMemory` and the mutation in
   * `pasteRowsReplacingRows` must agree on WHICH row the paste targets, or the
   * gate judges a row the paste never touches.
   */
  private replacePasteTargetsInVisualOrder(rowIds: string[]): TableRow[] {
    const targetIds = new Set(rowIds);
    return this.getAllSortedRows().filter(
      (row) => row.hidden !== true && targetIds.has(row.id)
    );
  }

  /**
   * The row a replace-paste over `rowIds` would paste ONTO — the top-most
   * VISIBLE one — or `null` when the snapshot holds no visible row (in which
   * case `pasteRowsReplacingRows` mutates nothing either).
   */
  public resolveReplacePasteAnchorRowId(rowIds: string[]): string | null {
    return this.replacePasteTargetsInVisualOrder(rowIds)[0]?.id ?? null;
  }

  /**
   * The same replace, driven by an EXPLICIT list of rows instead of the live
   * `selectedRows` — everything documented on `pasteRowsReplacingSelection`
   * above applies unchanged; that method is now a one-line wrapper over this one.
   *
   * PSTUDIO-4229: the row context menu needs this shape. Its Paste item runs
   * AFTER the menu has closed and after an `await` on the system-clipboard
   * refresh, so a method that reads `selectedRows` itself would read whatever the
   * selection has become by then — the caller snapshots the ids before closing
   * the menu and hands them over here.
   *
   * @param rowIds rows to replace, in any order: the anchor is resolved from the
   *   VISUAL order below, exactly as the keyboard path has always done.
   */
  pasteRowsReplacingRows = action((rowIds: string[]): boolean => {
    if (!this.canPasteRow()) return false;
    if (rowIds.length === 0) return false;

    const selectedVisible = this.replacePasteTargetsInVisualOrder(rowIds);
    if (selectedVisible.length === 0) return false;

    const anchorId = selectedVisible[0].id;
    // The group-in-group gate belongs HERE, on the shared implementation, not
    // on one entrance: `Cmd/Ctrl+V` and the row menu's Paste are ONE gesture
    // (rail 0), so a guard written for either binds both. Judged on the ANCHOR
    // — the row the paste actually lands on — because the body below deletes
    // the non-anchor rows FIRST and `_recordGroupMutation` rolls back the
    // history, never the data: a gate that let this through would leave the
    // rows gone with nothing pasted, recoverable only by Cmd+Z.
    if (!this.canPasteRowAt(anchorId, 'replace')) return false;
    const otherIds = selectedVisible.slice(1).map((row) => row.id);

    // Single selection → native atomic pasteRow undo.
    if (otherIds.length === 0) return this.pasteRow(anchorId, 'replace');

    // Multi selection → delete the non-anchor selected rows + replace the
    // anchor, collapsed into ONE atomic undo/redo command.
    // Pass `otherIds` as the rows dying WITH this paste. PSTUDIO-4083 inverted
    // what that means: they used to count as FREE (preserve-when-free,
    // PSTUDIO-3574), and now they KEEP owning their numbers until the paste
    // completes, so the clones are numbered as children of the row above the
    // slot. `_deleteRowsInternal` has already hidden and marked them, which is
    // why `pasteRow` has to readmit them explicitly.
    let ok = false;
    this._recordGroupMutation('Paste (replace selection)', () => {
      this.deleteRows(otherIds);
      ok = this.pasteRow(anchorId, 'replace', otherIds);
    });
    return ok;
  });

  /**
   * Pastes the item from the clipboard relative to a target item.
   * @param targetId The ID of the target item.
   * @param pastePosition The position relative to the target ('above', 'below', or 'replace').
   * @returns `true` if the paste was successful, `false` otherwise.
   */
  public pasteRow = action(
    (
      targetId: string,
      pastePosition: 'above' | 'below' | 'replace',
      /** PSTUDIO-4083 — ids of the rows dying WITH this paste, handed over by
       *  `pasteRowsReplacingRows` (the non-anchor targets it has already
       *  passed to `deleteRows`). They KEEP owning their numbers for the whole
       *  allocation and keep bounding the slot; a row marked by an EARLIER
       *  gesture is the opposite case and owns nothing. Named `extraFreeIds`
       *  until PSTUDIO-4083, when the meaning inverted. */
      dyingRowIds?: string[]
    ): boolean => {
      if (!this.rowClipboard?.length) {
        console.warn('Clipboard is empty. Cannot paste.');
        return false;
      }

      const targetIndex = this.sortedRows.findIndex(
        (item) => item.id === targetId
      );

      const targetIndexOrder =
        this.sortedRows.find((item) => item.id === targetId)?.order || 0;

      if (targetIndex === -1 && pastePosition !== 'below') {
        console.warn('Target item not found. Cannot paste.');
        return false;
      }

      // PSTUDIO-2154: capture the pre-paste state so the operation can be wrapped
      // in a symmetric undo/redo command below. `beforeOrders` covers every row
      // that exists now (cheap — just numbers); `removedTargetSnapshot` is the
      // full row that a 'replace' paste deletes.
      const beforeOrders = new Map<string, number>(
        this.rows.map((row): [string, number] => [row.id, row.order])
      );
      const removedTargetSnapshot =
        pastePosition === 'replace'
          ? (() => {
              const found = this.getRowById(targetId);
              return found ? (toJS(found) as TableRow) : undefined;
            })()
          : undefined;

      // // Check if the item being pasted is replacing the target item itself
      // if (
      //   pastePosition === 'replace' &&
      //   this.isRowCut &&
      //   this.rowClipboard?.map((item) => item.id === targetId)
      // ) {
      //   console.warn(
      //     "Cannot cut and paste an item back into its original spot with 'replace'."
      //   );
      //   return false;
      // }

      // const itemsToPaste = this.rowClipboard.slice();

      // PSTUDIO-3160 (A): resolve the paste slot's group context. A group
      // HEADER target carries no groupId of its own, but pasting 'below' it
      // lands visually inside the group (expanded) — the pasted rows must
      // join it, mirroring the drag rule (upper-neighbour decides membership). For a COLLAPSED
      // header, "below" means after the whole bundle — anchoring at the
      // header would interleave pasted rows with the hidden subRows.
      const targetRow = this.getRowById(targetId);
      const targetIsGroupHeader = !!targetRow && isGroupRow(targetRow);
      const targetIsCollapsedGroupHeader =
        targetIsGroupHeader && !targetRow?.settings?.groupExpanded;
      // Canonical membership pointer (groupId), not settings.subRows — the
      // two can drift and groupId is the source of truth (PSTUDIO-3158).
      const collapsedChildIds = new Set<string>(
        targetIsCollapsedGroupHeader
          ? this.rows
              // PSTUDIO-4083: a member being deleted is not the anchor the
              // paste lands after — `lastChildId` below drives BOTH `baseOrder`
              // (where the rows land) and `numberingAnchorId` (what they are
              // numbered from), and a row that is about to vanish is neither.
              // This set is also the `visibleIds` handed to
              // `resolveNumberingContext`, whose contract puts this exclusion
              // here: see its doc.
              .filter(
                (r) =>
                  r.settings?.groupId === targetId &&
                  !this.isRowPendingDeletion(r)
              )
              .map((r) => r.id)
          : []
      );

      // Resolved membership (shared with canPasteRowAt so the Paste menu's
      // disabled state and the actual reject never drift — PSTUDIO-3202).
      const groupId = this.resolvePasteGroupId(targetId, pastePosition);
      let baseOrder =
        pastePosition === 'replace'
          ? targetIndexOrder
          : this.calculateNewRowOrder(targetId, pastePosition);

      // A COLLAPSED header pasted 'below' anchors after the hidden bundle (not
      // at the header) so pasted rows don't interleave with its subRows; the
      // group context itself (resolved above) stays top-level for this case.
      // PSTUDIO-3992: the NUMBERING has to follow the same anchor — the rows
      // land after the last member, so they must be numbered from it. Keeping
      // the header as the numbering anchor produced a clone ordered below the
      // members but numbered as if it sat above them.
      let numberingAnchorId = targetId;
      if (
        targetIsCollapsedGroupHeader &&
        pastePosition === 'below' &&
        collapsedChildIds.size > 0
      ) {
        const lastChildId = this.sortIdsByRowOrder([...collapsedChildIds]).at(
          -1
        )!;
        baseOrder = this.calculateNewRowOrder(lastChildId, 'below');
        numberingAnchorId = lastChildId;
      }

      // PSTUDIO-3160 (C): a node group can never live inside another group.
      // Reject the whole paste when the clipboard carries a group row and
      // the resolved slot is inside a group — silently stripping the pasted
      // group's membership used to render it inside the target group's
      // border (visual nesting) while the data said "top-level".
      // PSTUDIO-3202: use the same `clipboardHasGroupRow()` helper that
      // `canPasteRowAt` (the menu's disabled gate) uses, so the menu and this
      // store-level reject share one case-insensitive source of truth and can
      // never drift.
      if (this.clipboardHasGroupRow() && groupId) {
        console.warn('Cannot paste a node group inside another node group.');
        // PSTUDIO-3202: the Paste menu items now DISABLE for this case (they
        // bind `canPasteRowAt`). This reject + toast stays as the data-integrity
        // backstop for any non-menu paste path, mirroring the drag path's
        // red-line feedback so a rejected paste is never silent.
        sendToast({
          header: 'Cannot paste here',
          type: 'warning',
          text: 'A node group cannot be pasted inside another node group.',
          toastId: 'group-in-group-paste',
          closeButton: true,
        });
        return false;
      }

      // PSTUDIO-3574: pasted COPIES keep their source node numbers when every
      // number is free in the target table; on ANY collision the whole bundle
      // is renumbered — since PSTUDIO-3992 from the insertion point (see the
      // context resolved below), not from the table-wide maximum — and the
      // intra-bundle number references are rewritten in the third pass below so
      // the pasted subgraph stays self-contained. Cut never renumbers, so the
      // plan is only computed for the copy branch.
      const occupiedNumbers = new Set<number>();
      // PSTUDIO-4083: kept apart from the set above on purpose — a referenced
      // number blocks a collision but may not anchor Fallback's `max + 1`.
      const referencedNumbers = new Set<number>();
      // PSTUDIO-4083 — the rows dying WITH this paste: the `'replace'` target,
      // plus a multi-row replace's remaining selection, which
      // `pasteRowsReplacingRows` has already handed to `deleteRows` (they
      // are `hidden` and marked, but still in `this.rows`).
      //
      // They KEEP OWNING their numbers for the whole allocation, which is the
      // inverse of what PSTUDIO-3992 did here. Edan's sign-off of 2026-09-08
      // (Confluence 5744001026) states pasted nodes take NET NEW numbers, "to be
      // considered the 'children' of the node immediately above" — never the
      // number that just died, because a node number carries meaning in
      // reporting and recycling one makes historical data read wrong. Keeping
      // the dying row's number is exactly what makes it the slot's `upper`, so
      // the allocator refuses Sequential Fill and expands off the row ABOVE:
      // `1 · 2 · 3 · 4 · 5` replacing 4 gives 30, replacing 4+5 gives 30 · 31.
      //
      // A row inside the soft-delete window from an EARLIER gesture is the
      // opposite case and must still own nothing — its number is already gone,
      // and it is not a neighbour the next number may be derived from. That is
      // the distinction the two sets encode, and it is why this walks `rows`
      // with an explicit predicate instead of reading `rowsForNumbering`, which
      // cannot tell the two apart.
      const dyingIds = new Set<string>(dyingRowIds ?? []);
      if (pastePosition === 'replace') dyingIds.add(targetId);
      if (!this.isRowCut) {
        this.rows.forEach((row) => {
          if (this.isRowPendingDeletion(row) && !dyingIds.has(row.id)) return;
          // PSTUDIO-3992: a group header is not SUPPOSED to carry a number, but
          // legacy rows do, and a number something holds is taken no matter who
          // holds it. Skipping headers here was harmless while the renumber ran
          // off the table-wide maximum (it always landed above everything); the
          // slot-local allocator will hand the header's own number to a node
          // sitting right next to it, producing exactly the duplicate-node-number
          // error that PSTUDIO-3914 removed the auto-repair for.
          const num = toValidNodeNumber(row.nodeNumber);
          if (num !== undefined) occupiedNumbers.add(num);
          // PSTUDIO-4083: and every number this row POINTS AT. A delete does not
          // rewrite the rows that referenced the deleted node, so its number
          // survives in the data even though the row is gone — and handing it to
          // a clone would make the clone silently inherit those edges. Same
          // three fields, same patterns, as the paste-time rewriter; the insert
          // side gets this through `numberingNumbersOf`.
          collectReferencedNodeNumbers(row, referencedNumbers);
        });
      }
      // PSTUDIO-3992: the renumber branch now allocates numbers LOCAL to the
      // paste slot, through the same (reference, upper) context the insert path
      // resolves — so a number taken in an unrelated section of the solution no
      // longer drags the paste to `getMaxNodeNumber() + 1`. A 'replace' paste
      // numbers the slot ABOVE its dying target, which is why the position is
      // resolved as 'above'. Cut is always a verbatim move, so it needs no
      // context: the whole number-assignment pass below sits under
      // `if (!this.isRowCut)`, so a cut row's number is never rewritten. (The
      // plan itself is still computed for a cut — it just goes unused — so this
      // null context is what keeps it from resolving neighbours pointlessly.)
      const { reference, upper } = this.isRowCut
        ? { reference: null, upper: null }
        : this.resolveNumberingContext(
            numberingAnchorId,
            pastePosition === 'replace' ? 'above' : pastePosition,
            {
              // PSTUDIO-4083: a `'replace'` used to drop its dying rows from the
              // neighbour lookup so the clone could take the number that just
              // died (§7 as approved 2026-08-25). The sign-off reverses that, so
              // the dying row stays the neighbour BELOW the slot and the clones
              // are numbered as children of the row ABOVE it — and the option
              // that did the dropping is gone, having lost its last caller.
              //
              // A collapsed group's members are `hidden`, but they are real rows
              // the paste lands next to — they must act as neighbours. The rows
              // dying with this paste join them for the same reason: a
              // multi-row replace has already hidden them, and without this they
              // would drop out of the sorted view and stop bounding the slot.
              visibleIds: new Set([...collapsedChildIds, ...dyingIds]),
            }
          );
      const numberPlan = computePasteNumberPlan({
        bundle: (this.rowClipboard ?? []).map((row) => ({
          nodeNumber: row.nodeNumber,
          isGroupHeader: isGroupRow(row),
        })),
        occupiedNumbers,
        referencedNumbers,
        reference,
        upper,
        // PSTUDIO-4083: a smart replace takes net new numbers even when the
        // bundle's own numbers are free — Edan's rule of 2026-09-08 states it
        // without a condition. A cut is a move, so it is never a "replace" for
        // numbering purposes (its numbers travel with the rows).
        isReplace: pastePosition === 'replace' && !this.isRowCut,
        // Cut is always a move (numbers verbatim, never renumbered), so only a
        // COPY paste consults the toggle. Raw (OFF) preserves verbatim even on a
        // duplicate — the paste always lands and the normal duplicate-number
        // validation flags it; only Smart (ON) renumbers (PSTUDIO-3602).
        autoRenumber: this.isRowCut ? true : this.autoRenumberOnPaste,
      });

      // Snapshot original clipboard so we can read original id, groupId, and
      // subRows for each duplicate AFTER the first pass has overwritten them.
      const clipboardSnapshot = toJS(this.rowClipboard);
      // Track old → new id mapping for the bundle so that pasted group rows can
      // re-link to their newly-generated subRow ids and vice versa.
      const oldIdToNew = new Map<string, string>();

      // PSTUDIO-3992: walks the NON-GROUP rows of the bundle in paste order,
      // indexing into numberPlan.numbers.
      let numberSlot = 0;

      // Tracks subRow ids that a bundled group referenced but that were not in
      // the clipboard bundle (stale/dangling refs). .filter((id): id is string => !!id)
      // below drops them silently; we surface the drop after produce() so it never
      // reaches the DB unnoticed (PSTUDIO-3158).
      let droppedSubRowCount = 0;

      const itemsToPaste = produce(toJS(this.rowClipboard), (duplicates) => {
        duplicates.forEach((duplicate, index) => {
          const originalId = clipboardSnapshot[index].id;
          if (!this.isRowCut) {
            duplicate.id = uuidv4();
            oldIdToNew.set(originalId, duplicate.id);
            if (isGroupRow(duplicate)) {
              // PSTUDIO-3574: a group header is a structural separator, not a
              // node — it must carry NO node number, preserving createGroup's
              // invariant and staying consistent with the numbering exclusion
              // applied everywhere else (getMaxNodeNumber and
              // computePasteNumberPlan both skip group rows). Were it run
              // through the renumber arithmetic below, it would receive a real
              // node number. Keeping the header unnumbered is now the ONLY
              // guarantee: PSTUDIO-3914 removed the dedup-on-load pass that used
              // to delete a stray-numbered header (taking the whole group with
              // it), so nothing downstream corrects a spurious number — it would
              // simply sit there as a duplicate-node-number validation error.
              // Children keep the full
              // preserve-when-free / renumber+rewrite treatment — only the
              // header is exempt.
              duplicate.nodeNumber = null;
            } else {
              const preservedNumber = toPreservedNodeNumber(
                clipboardSnapshot[index].nodeNumber
              );
              // PSTUDIO-3992: a renumbered row takes the next number the plan
              // allocated for the insertion point. The slot counter walks
              // non-group rows only (headers stay unnumbered), so the run is
              // contiguous — the old whole-bundle index left a gap wherever a
              // header sat. One source of truth: the plan computes the numbers,
              // this pass only assigns them.
              //
              // Branch on the MODE, never on `preservedNumber !== undefined`:
              // 'preserve' returns an EMPTY `numbers` array, so a row whose
              // source number is unusable (a cleared Node # cell, or a CSV
              // import where sanitizeNodeNumber yields 0) would read past its
              // end and land `undefined` — which drops the key from the saved
              // payload entirely. Preserving verbatim means keeping "no number"
              // as the store's own `null`.
              // The `?? null` on the renumber side mirrors the preserve side:
              // `nodeNumber` is not statically typed here, so a short `numbers`
              // array would silently assign `undefined` and drop the key from
              // the saved payload. The plan allocates exactly one number per
              // non-group row, so this is unreachable today — it stays as the
              // same structural guarantee both branches make.
              const plannedNumber =
                numberPlan.mode === 'preserve'
                  ? preservedNumber
                  : numberPlan.numbers[numberSlot];
              duplicate.nodeNumber =
                plannedNumber === undefined ? null : String(plannedNumber);
              numberSlot++;
            }
          } else {
            // For cut, ids don't change — populate identity mapping so the
            // bundle re-link pass below still resolves correctly.
            oldIdToNew.set(originalId, originalId);
          }

          // PSTUDIO-3337: pasted rows always belong to the CURRENT solution.
          // saveBulkRowsToDatabase upserts row.solutionVersionId verbatim, so a
          // cross-solution paste that kept the source id would save to the wrong
          // solution (or trip the NOT-NULL/FK constraint). No-op same-solution.
          duplicate.solutionVersionId = this.solutionVersionId;

          // PSTUDIO-4081: the bundle is foreign data — a cross-tab envelope
          // carries whatever shape the SOURCE row held, including the raw draft
          // string an open panel edit leaves in an array column (PSTUDIO-3882).
          // The persist boundary coerces what reaches the database, but the row
          // lands in THIS model first, and the model has other readers:
          // `joinDelimitedField` died on such a value and took `validateAndSync`
          // down with it, so the solution silently stopped producing PIF errors.
          // Normalize here so every reader sees the column's own contract.
          //
          // Only the affected COLUMNS are written back. Assigning a whole
          // `{ ...draft }` would also copy every other key the envelope carried,
          // and a clipboard payload can hold an own `__proto__` (JSON.parse
          // keeps it) — which immer's set trap turns into
          // `Object.setPrototypeOf() cannot be used on an Immer draft`, i.e. a
          // prepared payload would break paste outright.
          applyArrayColumnFixes(duplicate as Record<string, unknown>);

          duplicate.intent = this.isRowCut ? duplicate.intent : undefined;

          duplicate.settings = {
            ...duplicate.settings,
            groupId: groupId,
          };

          if (pastePosition === 'above' && duplicates.length > 1) {
            duplicate.order = baseOrder - (duplicates.length - index) * 10;
          } else {
            // For 'below' and 'replace', use positive offsets
            duplicate.order = baseOrder + index * 10;
          }
        });

        // Second pass — re-link bundled group rows to their fresh subRow ids
        // and ensure subRows that belong to a bundled group point to the new
        // group id (instead of inheriting the paste target's groupId).
        duplicates.forEach((duplicate, index) => {
          const original = clipboardSnapshot[index];

          if (duplicate.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.G) {
            const oldSubRows: string[] = original.settings?.subRows ?? [];
            const newSubRows = oldSubRows
              .map((oldSubId) => oldIdToNew.get(oldSubId))
              .filter((id): id is string => !!id);
            // PSTUDIO-3332 (S3): count drops only against subRows that were
            // ACTUALLY bundled into the clipboard. A phantom id (never a real
            // row) is excluded from the clipboard by copyRows, so counting it
            // here fired a spurious "Paste Error" toast even though the paste
            // fully succeeded.
            const clipboardRowIds = new Set(clipboardSnapshot.map((r) => r.id));
            const expectedSubRowCount = oldSubRows.filter((id) =>
              clipboardRowIds.has(id)
            ).length;
            if (newSubRows.length !== expectedSubRowCount) {
              droppedSubRowCount += expectedSubRowCount - newSubRows.length;
            }
            duplicate.settings = {
              ...duplicate.settings,
              subRows: newSubRows,
              // A group row must never inherit another group's id.
              groupId: undefined,
            };
          } else {
            const originalGroupId = original.settings?.groupId;
            if (originalGroupId && oldIdToNew.has(originalGroupId)) {
              // The parent group is being pasted alongside — re-link to the
              // newly created group id rather than the paste target's groupId.
              duplicate.settings = {
                ...duplicate.settings,
                groupId: oldIdToNew.get(originalGroupId)!,
              };
            }
          }
        });

        // Third pass (PSTUDIO-3574) — when the bundle was renumbered, remap
        // every intra-bundle node-number reference (nextNodes, whatsNext
        // value~N pairs, rich-asset option dest) through the same old→new
        // plan, mirroring the oldIdToNew group relink above. Token-level
        // only (byte-identity with the validator/compiler, PSTUDIO-3007);
        // references to numbers outside the bundle are untouched.
        if (!this.isRowCut && numberPlan.numberMap.size > 0) {
          duplicates.forEach((duplicate) => {
            rewriteNodeNumberReferences(duplicate, numberPlan.numberMap);
          });
        }

        return duplicates;
      });

      // Defensive backstop (PSTUDIO-3202): after the PSTUDIO-3332 (S3) fix,
      // `expectedSubRowCount` counts only subRows that are actually in the
      // clipboard bundle, and every such id is in `oldIdToNew` by construction
      // — so `droppedSubRowCount` is always 0 in normal flow and this branch
      // should not fire. It is kept (rather than removed) so a future
      // regression in the two-pass re-link surfaces honestly, with wording that
      // reflects what really happened: the rest of the bundle WAS pasted.
      if (droppedSubRowCount > 0) {
        console.error(
          `[TableStore] pasteRow: ${droppedSubRowCount} subRow reference(s) ` +
            `were missing from the clipboard bundle and were dropped.`
        );
        sendToast({
          header: 'Some nodes were skipped',
          type: 'warning',
          text: 'Some grouped nodes were missing from the copied bundle and were skipped; the remaining nodes were pasted.',
          toastId: 'paste-error',
          closeButton: true,
        });
      }

      // Determine insertion position in sorted order
      let sortedInsertIndex = targetIndex;
      let shouldRemoveTarget = false;

      switch (pastePosition) {
        case 'above':
          sortedInsertIndex = targetIndex;
          break;
        case 'below':
          sortedInsertIndex =
            targetIndex !== -1 ? targetIndex + 1 : this.sortedRows.length;
          break;
        case 'replace':
          sortedInsertIndex = targetIndex;
          shouldRemoveTarget = true;
          break;
      }

      // PSTUDIO-3160 (A): the renumber pass derives final orders from the
      // splice INDEX, so a collapsed-header 'below' paste must also shift
      // its insertion index past the last (hidden) child.
      if (
        pastePosition === 'below' &&
        targetIsCollapsedGroupHeader &&
        collapsedChildIds.size > 0
      ) {
        let lastChildIndex = targetIndex;
        for (let i = targetIndex + 1; i < this.sortedRows.length; i++) {
          if (collapsedChildIds.has(this.sortedRows[i].id)) {
            lastChildIndex = i;
          }
        }
        sortedInsertIndex = lastChildIndex + 1;
      }

      let result = [...this.sortedRows];
      const cutRowIds = this.isRowCut ? itemsToPaste.map((r) => r.id) : [];

      if (this.isRowCut) {
        result = result.filter((r) => !cutRowIds.includes(r.id));

        const cutRowsBeforeTarget = this.sortedRows
          .slice(0, sortedInsertIndex)
          .filter((r) => cutRowIds.includes(r.id)).length;
        sortedInsertIndex -= cutRowsBeforeTarget;
      }

      if (shouldRemoveTarget && targetIndex !== -1) {
        const targetRowInResult = result.findIndex((r) => r.id === targetId);
        if (targetRowInResult !== -1) {
          const targetRowToDelete = result[targetRowInResult];
          result.splice(targetRowInResult, 1);

          if (targetRowInResult < sortedInsertIndex) {
            sortedInsertIndex--;
          }

          const targetRowInRows = this.rows.findIndex((r) => r.id === targetId);
          if (targetRowInRows !== -1) {
            // PSTUDIO-4083: the target's errors go with it. The per-cell error
            // indexes fall back to a NODE-NUMBER key when the row id misses
            // (`getCellErrorMessage`), so a diagnostic left behind addresses a
            // row that no longer exists: it keeps publish blocked, and it lands
            // — red border, `aria-invalid` and all — on whatever row carries
            // that number next. Under RAW paste that can be the clone itself,
            // which pastes the source number verbatim; under SMART the clone
            // takes a net new number and some later row inherits the problem.
            // `_deleteRowsInternal`, the undo/redo delete command and
            // `ungroupRows` all call this before the row leaves `rows`;
            // `restorePaste`'s `ensureAbsent` does NOT (see its own comment).
            //
            // BEFORE the splice below, and that ordering is the fix, not a
            // style choice: `removeErrorsForRowIds` resolves the row's NUMBER
            // through `this.rows.find(...)` to catch a diagnostic the backend
            // addressed by `node_num` with no `nodeId` (see its docblock). Run
            // after the row is gone, that lookup misses, the number set stays
            // empty, and exactly that class of error survives to be inherited
            // by the clone. The bulk delete (`_deleteRowsInternal`) and the
            // undo/redo delete command both call it before the row leaves
            // `rows` for the same reason.
            this.removeErrorsForRowIds([targetId]);
            this.rows.splice(targetRowInRows, 1);
            // PSTUDIO-4083: the trio (rows + rowMap + fuse) moves together
            // everywhere else; this path had been dropping the row from two of
            // the three. `getValue`/`getCellValue` read from `rowMap`, so the
            // replaced row went on answering with its own `nodeNumber` after it
            // had left the table.
            this.rowMap.delete(targetId);
            this.fuse.remove((doc) => doc.id === targetId);
          }
          if (targetRowToDelete) {
            if (this.liveEditingSource) {
              // PSTUDIO-4451
              this.liveWriter?.deleteRows([targetRowToDelete.id]);
            } else {
              // PSTUDIO-4342: ordered, gated and stripped of its queued write
              // (`_deleteOneNode`). Its result is still not read (PSTUDIO-4111).
              this._deleteOneNode(targetRowToDelete.id, this.solutionVersionId);
            }
          }
        }
      }

      result.splice(sortedInsertIndex, 0, ...itemsToPaste);

      if (!this.isRowCut) {
        this.rows.push(...itemsToPaste);
        itemsToPaste.forEach((row) => {
          set(this.rowMap, row.id, row);
          this.fuse.add(row);
        });
      }

      // Renumber the whole table through the orders-only mutation
      // (updateProStudioNodeOrders), exactly like reorderRows and the paste
      // undo/redo path (restorePaste) below. The previous loop persisted each
      // changed order via updateRow(saveImmediately: true), which ships the
      // FULL row — pasting a group into a large workspace renumbers every row
      // below the insertion point, so the entire table (every column,
      // multi-KB JSON fields included) was re-uploaded and the request blew
      // past Hasura's body limits. _applyOrders ships compact
      // {id, order, solutionVersionId} tuples instead (chunked) and keeps the
      // affected groups' settings.subRows in sync.
      //
      // PSTUDIO-3611: gap insertion. The pasted rows already carry orders
      // inside the target slot's gap (baseOrder ± index*10 via
      // calculateNewRowOrder). When those orders keep `result` strictly
      // ascending, nothing else moved — apply orders for the pasted rows only
      // (for a copy the row objects already hold the value, so the map's real
      // job is telling _applyOrders which groups to resync; for a cut it
      // performs the actual move). The previous unconditional full-table
      // renumber shipped ~M order tuples through the DebouncedQueue and
      // ~M/500 SEQUENTIAL Hasura mutations on every paste — seconds of
      // wall-clock at 5500 rows. The dense renumber is kept as the fallback
      // for an exhausted gap (e.g. repeated pastes into the same slot) and
      // for any legacy duplicate-order data, exactly like reorderRows and the
      // paste undo/redo path (restorePaste).
      const isStrictlyOrdered = result.every(
        (row, i) => i === 0 || result[i - 1].order < row.order
      );
      const newOrderById = new Map<string, number>();
      if (isStrictlyOrdered) {
        itemsToPaste.forEach((row) => newOrderById.set(row.id, row.order));
      } else {
        for (let i = 0; i < result.length; i++) {
          newOrderById.set(result[i].id, this.orderIncrementV2 * (i + 1));
        }
      }
      this._applyOrders(newOrderById);

      if (!this.isRowCut) {
        // Persist the newly-cloned rows AFTER renumbering so each snapshot
        // already carries its final order. The full-row write and the
        // orders-only tuple for a pasted row then agree on `order`, so the
        // two DebouncedQueue(0) flushes can land in either sequence without a
        // same-row race (the orders-only upsert may even arrive first — it
        // inserts {id, order, solutionVersionId} and the full-row upsert
        // fills in the rest).
        // Validation for the paste is run explicitly at the end of this method
        // (see validateAndSync below), so this bookkeeping save must not arm the
        // deferred saveBatchNeedsValidation flag — keeps validation single-pass
        // and deterministic for both copy and cut (PSTUDIO-3158).
        this.saveRowsToDatabase(
          itemsToPaste
            .map((row) => this.getRowById(row.id))
            .filter((row): row is TableRow => !!row)
            .map((row) => toJS(row)),
          { skipValidation: true }
        );
      }

      if (groupId) {
        // Exclude rows that are part of a pasted group bundle (the bundled
        // group itself + its own subRows) so they don't get nested into the
        // paste target's group.
        const bundledGroupSubRowIds = new Set<string>();
        itemsToPaste.forEach((item) => {
          if (item.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.G) {
            (item.settings?.subRows ?? []).forEach((id: string) =>
              bundledGroupSubRowIds.add(id)
            );
          }
        });
        const newIds = itemsToPaste
          .filter(
            (item) =>
              item.nodeType !== Tenancy_Node_Type_Enum_Ps_Enum.G &&
              !bundledGroupSubRowIds.has(item.id)
          )
          .map((item) => item.id);

        if (newIds.length > 0) {
          const groupSettings = this.getValue(groupId, NodeColumns.settings);

          // PSTUDIO-3160 (A): merge by visual order, not by appending — the
          // group label and counter read subRows[0]/length, so the array
          // must mirror what the user sees. Orders are final here (the
          // renumber emitOnce above already ran).
          // PSTUDIO-3332 (S1/S4): rebuild from CANONICAL members (rows whose
          // groupId points here, which now includes the freshly pasted rows)
          // instead of merging into the un-sanitized cache — a pre-existing
          // phantom id is dropped, so the header count never inflates. Set-dedupe
          // also guards a CUT row pasted back into its own group (already a member).
          const mergedSubRows = this.sortIdsByRowOrder([
            ...new Set([...this.getGroupMembers(groupId), ...newIds]),
          ]);

          this.updateRow({
            rowId: groupId,
            updates: {
              settings: {
                ...groupSettings,
                subRows: mergedSubRows,
              },
            },
            skipValidationRules: true,
            saveImmediately: true,
          });
        }
      }

      // When cutting a subRow OUT of its original group:
      //   1) Persist its new settings (target's groupId, or undefined) — the
      //      produce block only updated the in-flight clone; for cut, the
      //      original row stays in `this.rows`, so updateRow is the only way
      //      to make the new groupId stick.
      //   2) Drop its id from the SOURCE group's settings.subRows so we don't
      //      leave a phantom reference.
      // Only applies on cut (copy spawns a fresh row — old group unaffected).
      // Bundled-group cuts are handled by the second-pass relink above; here
      // we only fix lone subRows leaving their parent.
      if (this.isRowCut) {
        const cutOrphans = new Map<string, Set<string>>();
        itemsToPaste.forEach((duplicate, index) => {
          const original = clipboardSnapshot[index];
          const originalGroupId = original.settings?.groupId;
          const newGroupId = duplicate.settings?.groupId;
          if (originalGroupId === newGroupId) return;

          // Skip rows whose original parent group is also in the cut bundle —
          // it will be re-created on paste and gets fresh subRows in the
          // bundle-relink pass; touching it here would double-modify.
          if (originalGroupId && oldIdToNew.has(originalGroupId)) return;

          // 1) Persist the new groupId on the cut row itself.
          this.updateRow({
            rowId: original.id,
            updates: {
              settings: {
                ...this.getValue(original.id, NodeColumns.settings),
                groupId: newGroupId,
              },
            },
            skipValidationRules: true,
            saveImmediately: true,
          });

          // 2) Queue removal from the source group's subRows.
          if (originalGroupId) {
            const bucket = cutOrphans.get(originalGroupId) ?? new Set<string>();
            bucket.add(original.id);
            cutOrphans.set(originalGroupId, bucket);
          }
        });

        cutOrphans.forEach((orphanIds, sourceGroupId) => {
          const sourceSettings = this.getValue(
            sourceGroupId,
            NodeColumns.settings
          );
          if (!sourceSettings) return;
          this.updateRow({
            rowId: sourceGroupId,
            updates: {
              settings: {
                ...sourceSettings,
                subRows: (sourceSettings.subRows ?? []).filter(
                  (id: string) => !orphanIds.has(id)
                ),
              },
            },
            skipValidationRules: true,
            saveImmediately: true,
          });
        });
      }

      // Reorder this.rows to match sorted order so getRowIndex returns visual index
      // Only for copy operations - for cut, rows are already in this.rows
      if (!this.isRowCut) {
        const sorted = [...this.sortedRows];
        this.rows.replace(sorted);
        this.fuse.setCollection(sorted);
      }

      this.selectedRows.clear();
      itemsToPaste.forEach((row) => this.selectedRows.add(row.id));

      this.showCopyIndicator = false;

      // PSTUDIO-3551: pasting NODES is a row action — reset any stale CELL copy
      // indicator (marching-ants + bundle) and cell selection left over from an
      // earlier cell copy/cut. `clearCellClipboard` otherwise runs only from
      // copyRows/cutRows, which a cross-tab node paste (hydrate → pasteRow)
      // bypasses, so without this the source cell keeps its ants after the nodes
      // land (node & cell indicators are mutually exclusive — §3).
      this.clearCellClipboard();
      this.deselectAllCells();

      if (this.isRowCut) {
        this.isRowCut = false;
        this.clearRowClipboard();
      }

      // PSTUDIO-2154: register a symmetric undo/redo command. We don't replay
      // pasteRow on redo (the clipboard/cut state is gone by then); instead the
      // command restores the affected rows to the captured after-state (redo)
      // or before-state (undo). `insertedRows` are rows newly created by a copy
      // paste (absent before, present now); `removedRows` is the replace target
      // that was deleted (populated for any 'replace' paste, cut or copy). A
      // cut paste with 'above'/'below' only changes orders, so both lists are
      // empty there and only `_applyOrders` runs.
      const pastedIds = itemsToPaste.map((r) => r.id);
      const insertedRows = pastedIds
        .filter((id) => !beforeOrders.has(id))
        .map((id) => this.getRowById(id))
        .filter((r): r is TableRow => !!r)
        .map((r) => toJS(r) as TableRow);
      const removedRows =
        removedTargetSnapshot && !this.getRowById(removedTargetSnapshot.id)
          ? [removedTargetSnapshot]
          : [];
      const afterOrders = new Map<string, number>(
        this.rows.map((row): [string, number] => [row.id, row.order])
      );

      const restorePaste = (opts: {
        ensurePresent: TableRow[];
        ensureAbsent: TableRow[];
        orders: Map<string, number>;
      }) => {
        // Remove rows that shouldn't exist in this direction (+ DB delete).
        opts.ensureAbsent.forEach((row) => {
          const existing = this.rows.find((r) => r.id === row.id);
          if (existing) {
            this.rows.remove(existing);
            this.rowMap.delete(row.id);
            this.fuse.remove((doc) => doc.id === row.id);
          }
        });
        this._deleteNodesInDb(opts.ensureAbsent.map((r) => r.id));
        // Re-create rows that should exist in this direction (+ DB upsert).
        opts.ensurePresent.forEach((row) => {
          if (!this.rows.find((r) => r.id === row.id)) {
            this.rows.push({ ...row });
            set(this.rowMap, row.id, { ...row });
            this.fuse.add({ ...row });
          }
        });
        if (opts.ensurePresent.length) {
          this.saveRowsToDatabase(opts.ensurePresent.map((r) => ({ ...r })));
        }
        // Restore orders, then keep this.rows sorted so visual index matches.
        this._applyOrders(opts.orders);
        const sorted = [...this.sortedRows];
        this.rows.replace(sorted);
        this.fuse.setCollection(sorted);
        if (this.validationMode === ValidationMode.Manual) {
          this.hasUnvalidatedChangesInManualMode = true;
        }
      };

      this.pushCommandWithoutExecute({
        execute: () =>
          restorePaste({
            ensurePresent: insertedRows,
            ensureAbsent: removedRows,
            orders: afterOrders,
          }),
        undo: () =>
          restorePaste({
            ensurePresent: removedRows,
            ensureAbsent: insertedRows,
            orders: beforeOrders,
          }),
        description: `Paste ${itemsToPaste.length} row(s)`,
        focusTarget: pastedIds[0] ? { rowId: pastedIds[0] } : undefined,
        // PSTUDIO-3845: restorePaste recompiles ONLY in the leg that re-creates
        // rows via the full-row save (`ensurePresent.length` → saveRowsToDatabase
        // → validateAndSync). The other leg only removes rows (_deleteNodesInDb,
        // no recompile) and re-applies orders (orders-only, no recompile), so it
        // must force the recompile itself. redo drives `ensurePresent:
        // insertedRows`; undo drives `ensurePresent: removedRows`. A cut+paste
        // MOVE ('above'/'below') has BOTH lists empty → both legs are
        // validation-free → force on both; copy-paste's undo (removes the pasted
        // rows, empty ensurePresent) is likewise validation-free and gated here.
        recompilesViaSavePath: (direction) =>
          (direction === 'redo' ? insertedRows.length : removedRows.length) > 0,
      });

      // PSTUDIO-3158: single-pass validation after paste — catches duplicate
      // node numbers that neither the orders-only renumber nor the membership
      // updates would surface (both run with skipValidation/skipValidationRules:
      // true), mirroring deleteRows/ungroup.
      // PSTUDIO-3574: in a multi-row replace (`dyingRowIds` non-empty), the dying
      // selection is still in this.rows (hidden — physical removal is deferred to
      // deleteRows' idle callback), so validating NOW would upload a compiler CSV
      // carrying both the hidden originals and the clones. Since PSTUDIO-4083 a
      // SMART paste renumbers there (the dying rows hold their numbers, so the
      // bundle is never free), but a RAW one still pastes the source numbers
      // verbatim — so the duplicate this defers past is reachable through the
      // toggle, not through preserve-when-free. That
      // delete path runs its own validateAndSync after the removal, with the clones
      // already in place — defer to it and keep validation single-pass.
      if (!dyingRowIds?.length) {
        this.validateAndSync();
      }

      // PSTUDIO-3848: bring the whole pasted block into view, same as a
      // context-menu insert — the rows are already selected above (parity with
      // insertRowsSmart), so without this a paste above/below near a fold lands
      // off-screen.
      this._revealInsertedRange(pastedIds);

      return true;
    }
  );

  // PSTUDIO-3914: hydration used to collapse rows sharing a nodeNumber and
  // DELETE the losers from the database (deduplicateNodesByNumber, aimed at the
  // historical copyNodesToNewVersion double-run bug). Its predicate was only
  // "same nodeNumber", which cannot tell a machine-made clone from a duplicate
  // the user just typed — and a duplicate number is a LEGAL intermediate state:
  // the duplicate-node-number validation flags it and expects the user to fix
  // it, and a RAW paste deliberately lands colliding numbers verbatim
  // (paste-node-numbering.ts, KEYBOARD_CLIPBOARD_BEHAVIOR.md guard rail 11).
  // So any reload inside that window destroyed content, silently and with no
  // undo. Hydration is a read path: it may renumber orders, never delete rows.
  // Legacy clones now surface as ordinary validation errors instead.
  normalizeInitialRows = action(
    (
      nodesToProcess: TableRow[],
      // PSTUDIO-4103: `enqueueOrderFixes: false` makes this a PURE normalizer —
      // same repaired, order-sorted rows out, no write. It exists for the one
      // caller that normalizes a version the user does NOT have open (the
      // solution download's grouped CSV): the order tuples enqueued below are
      // re-homed onto `this.solutionVersionId` at flush time (PSTUDIO-3669,
      // saveBulkRowOrdersToDatabase), so repairing ANOTHER version's orders
      // upserts that version's node ids into the current one. Every caller that
      // normalizes the CURRENT version leaves this alone: there the repair is
      // the point (PSTUDIO-3669/3914 — see the notes above and in the loop).
      //
      // PSTUDIO-4168: the OTHER half — a repair enqueued for the version on
      // screen that is still in the queue when a publish bump moves the store —
      // is caught at flush time instead, by the identity the tuple carries. See
      // saveBulkRowOrdersToDatabase.
      options?: { enqueueOrderFixes?: boolean }
    ): TableRow[] => {
      let nodes = nodesToProcess;
      nodes = nodes.sort((a, b) => a.order - b.order);

      const nodesToSave = [];

      let hasChanges = false;
      let prevOrder = 0;

      for (let i = 0; i < nodes.length; i++) {
        const item = nodes[i];

        // PSTUDIO-4453: every row the store holds carries its node number as a
        // canonical string, whatever the source sent — Hasura's `numeric` may
        // arrive as a JSON number. A representation, not a repair: not saved.
        const nodeNumber = toNodeNumberCell(item.nodeNumber);
        if (item.nodeNumber !== nodeNumber) item.nodeNumber = nodeNumber;

        const currOrder = item.order || 0;

        // Only renumber on actual corruption: missing/non-integer order, or an
        // order that collides with or inverts the previous row. A small (but
        // positive) gap is not corruption — the integer space has room and
        // rebalancing on load just to widen the gap forces a full UpdateProStudioNodes
        // mutation on every refresh of a large solution, which exceeds Hasura's
        // request body limit. The Math.max guard keeps the assignment strictly
        // monotonic so the renumber never re-introduces the collision it just
        // fixed — which applies to EVERY order collision now that PSTUDIO-3914
        // removed the dedup pass (no row is ever dropped before this loop, so
        // rows sharing an order reach it whatever their node number).
        //
        // Note (PSTUDIO-2220): an earlier `<=5` proximity heuristic here flagged
        // legitimate group bundles (group row + subRows sit on adjacent integers)
        // as corruption. main removed that heuristic entirely, so the false
        // positive — and the group-aware guard that worked around it — are both
        // gone; the duplicate / non-integer / inversion checks still apply.
        if (
          item.order === undefined ||
          !Number.isInteger(currOrder) ||
          currOrder <= prevOrder
        ) {
          item.order = Math.max((i + 1) * this.orderIncrementV2, prevOrder + 1);
          hasChanges = true;

          nodesToSave.push(item);
        }

        prevOrder = item.order || 0;
      }

      // PSTUDIO-4103: the repair of `order` above ALWAYS happens — it is what
      // the returned rows are for. Only the persistence of it is optional, so
      // the two variants return byte-identical rows.
      if (
        hasChanges &&
        nodesToSave.length &&
        options?.enqueueOrderFixes !== false
      ) {
        console.log('has changes - saving new rows', nodesToSave.length);
        // Ship orders-only — normalizeInitialRows only mutates `order`, never
        // any other field. Routing through saveRowsToDatabase would send full
        // rows via the wide-column upsert, which exceeds Hasura's body limit
        // for large solutions and was the source of the on-refresh megamutation.
        this.saveRowOrdersToDatabase(
          nodesToSave.map((n) => ({
            id: n.id,
            order: n.order,
            solutionVersionId: this.solutionVersionId,
          }))
        );
      }

      const sortedStartingNodes = nodes.sort(
        (a, b) => (a.order || 0) - (b.order || 0)
      );

      return sortedStartingNodes;
    }
  );

  normalizeRowOrder = action(() => {
    const rows = [
      ...this.rows.slice().sort((a, b) => (a.order || 0) - (b.order || 0)),
    ];

    const orderUpdates: RowOrderUpdate[] = [];
    emitOnce(() => {
      for (let i = 0; i < rows.length; i++) {
        const item = rows[i];
        const newOrder = (i + 1) * this.orderIncrementV2;
        if (item.order === newOrder) continue;
        // Mutate the row in place — bypass updateRow() because it routes
        // through the full-row save queue. Full-row saves of every row would
        // produce a multi-MB payload that Hasura rejects on large solutions.
        const rowIndex = this.rows.findIndex((r) => r.id === item.id);
        if (rowIndex === -1) continue;
        set(this.rows[rowIndex], { order: newOrder });
        set(this.rowMap, item.id, { ...item, order: newOrder });
        orderUpdates.push({
          id: item.id,
          order: newOrder,
          solutionVersionId: this.solutionVersionId,
        });
      }
    });
    this.saveRowOrdersToDatabase(orderUpdates);
  });

  handleCut = async ({
    cellId,
    rowId,
  }: {
    rowId: string;
    cellId: keyof TableRow;
  }) => {
    // Snapshot the value before clearing so cut is undoable (PSTUDIO-2154).
    const before = {
      [cellId]: toJS(this.getValue(rowId, cellId)),
    } as Partial<TableRow>;
    this.handleCopy({ cellId, rowId });
    const defaultValueForCell = getDefaultValueForColumn(cellId);
    this.updateRow({
      rowId,
      updates: {
        [cellId]: defaultValueForCell,
      },
      skipValidationRules: false,
      saveImmediately: true,
    });
    this.commitCellEdit({ rowId, columnId: cellId, before });
  };

  duplicateRow = action((id: string) => {
    const existingRow = this.getRowById(id);

    if (!existingRow) {
      throw Error('row trying to be duplicated does not exist!');
    }

    const newRow = produce(toJS(existingRow), (duplicate) => {
      // PSTUDIO-4081: the clone is `pasteRow`'s sibling — it copies the source
      // row verbatim, so a raw draft string in an array column travels into a
      // row that will OUTLIVE the edit that produced it (new id, nothing to
      // heal it). Same targeted write, same reason.
      applyArrayColumnFixes(duplicate as Record<string, unknown>);
      duplicate.intent = undefined;
      duplicate.id = uuidv4();
      duplicate.order = this.calculateNewRowOrder(id, 'below');
      // PSTUDIO-3992: the clone is dropped directly BELOW its source, so it is
      // numbered from that slot — the same context an insert or a paste there
      // resolves. `getMaxNodeNumber() + 1` sent it to the far end of the
      // solution instead, which is the defect this ticket fixed for paste.
      const { reference, upper } = this.resolveNumberingContext(id, 'below');
      const duplicateNumbers = this.getNumberingNumbers();
      const [duplicateNumber] = assignNodeNumberStrings(
        reference,
        upper,
        1,
        duplicateNumbers.own,
        duplicateNumbers.referenced
      );
      duplicate.nodeNumber = duplicateNumber;

      return duplicate;
    });

    // PSTUDIO-2154: wrap duplicate in an undo/redo command. undo removes the
    // clone locally and from the DB; redo re-creates it.
    const command: Command = {
      execute: () => {
        this.rows.push(newRow);
        set(this.rowMap, newRow.id, newRow);
        this.fuse.add(newRow);

        if (this.validationMode === ValidationMode.Manual) {
          this.hasUnvalidatedChangesInManualMode = true;
        }

        this.saveRowsToDatabase([newRow]);

        this.checkNearbyRowOrder(newRow.id);
      },
      undo: () => {
        const targetRow = this.rows.find((row) => row.id === newRow.id);
        if (targetRow) {
          this.rows.remove(targetRow);
          this.rowMap.delete(newRow.id);
          this.fuse.remove((doc) => doc.id === newRow.id);
        }

        if (this.validationMode === ValidationMode.Manual) {
          this.hasUnvalidatedChangesInManualMode = true;
        }

        this._deleteNodesInDb([newRow.id]);
      },
      description: `Duplicate row ${id}`,
      focusTarget: { rowId: newRow.id },
    };

    this.executeCommand(command);

    // this.snapshot("duplicate node");
  });

  // ---------------------------------------------------------------------------
  // Workflows table node grouping (PSTUDIO-2220 / PSTUDIO-2349)
  // ---------------------------------------------------------------------------
  // The group feature is UI-only:
  //   * Group rows are stored in `tenancy.solution_version_nodes_ps` with
  //     `nodeType='g'`; their member ids live in `settings.subRows` and a
  //     user-facing label in `settings.groupName`.
  //   * SubRows are normal rows (`nodeType='a' | 'd'`) carrying
  //     `settings.groupId` pointing at the parent group row.
  //   * Nesting is forbidden: see `canBeGrouped` below. UI guards in
  //     `draggable-row.tsx` use the same predicate via `canGroupSelection`.
  //   * Persistence is the regular `UpdateProStudioNodes` upsert — `settings`
  //     is in the mutation's `update_columns`, so any change to the bag
  //     (groupId, subRows, groupExpanded, groupName) is written atomically
  //     with the row's other fields. No grouping-specific GraphQL.
  //   * The Bot Compiler / agent-compiler MUST never see group rows or
  //     `settings`: payload is filtered through `excludeGroupNodes` and
  //     `settings` is omitted from `columnOrder`. See PSTUDIO-2220 comment
  //     by Faisal Ahmad for the reasoning.
  // ---------------------------------------------------------------------------

  canBeGrouped = (id: string): boolean => {
    const row = this.getRowById(id);
    if (!row) return false;
    if (row.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.G) return false;
    if (row.settings?.groupId) return false;
    return true;
  };

  // True when the current selection can be turned into a group: at least 1
  // row (a single-node group is legal), none of which is a group or already in
  // a group.
  get canGroupSelection(): boolean {
    const ids = [...this.selectedRows.values()];
    return ids.length >= 1 && ids.every((id) => this.canBeGrouped(id));
  }

  // True when every group row is currently expanded (or no groups exist).
  // Used by the "Expand / Collapse all node groups" header button to derive
  // its icon and click-target directly from store state — no shadow useState.
  get allGroupsExpanded(): boolean {
    const groups = this.rows.filter(
      (r) => r.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.G
    );
    return (
      groups.length === 0 || groups.every((g) => !!g.settings?.groupExpanded)
    );
  }

  // The chevron reads the persisted `settings.groupExpanded`, but each subRow's
  // `hidden` flag is transient — it's stripped before save (saveBulkRowsToDatabase)
  // and toggleRowGroupVisibility only flips it in memory. On load nothing rebuilt
  // it, so a collapsed group reloaded with its rows visible while the chevron
  // still showed "collapsed" (PSTUDIO-2220 follow-up). Mirror the toggle here:
  // a subRow is hidden iff its parent group is collapsed. Pure in-memory; the
  // `hidden` flag is never persisted, so this triggers no save.
  // PSTUDIO-3625: `onlyIfChanged` makes the incremental cross-tab merge path
  // write-minimal. processVersionData (full rebuild) calls this WITHOUT the
  // option and keeps its original unconditional behavior — churn is irrelevant
  // there because it replaces the whole rows array anyway. mergeVersionData
  // passes `{ onlyIfChanged: true }` so a hidden member of a still-collapsed
  // group is not re-`set` (a no-net-change true→true write still fires the
  // Table's per-row `hidden` observer and re-renders the row).
  private applyGroupCollapseVisibility(
    rows: TableRow[],
    options?: { onlyIfChanged?: boolean }
  ): void {
    const onlyIfChanged = options?.onlyIfChanged === true;
    const collapsedGroupIds = new Set(
      rows
        .filter(
          (r) =>
            r.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.G &&
            r.settings?.groupExpanded === false
        )
        .map((r) => r.id)
    );
    // With onlyIfChanged we still need to walk every row: a row that WAS hidden
    // under a now-expanded group must be flipped back to false. Without it we
    // preserve the original early-return fast path.
    if (!onlyIfChanged && collapsedGroupIds.size === 0) return;

    // PSTUDIO-4023: this is the OTHER writer of `hidden` — the one the group
    // toggles do not go through. It runs on a CSV import's grouping pass, on
    // `ungroupRows`, on the cross-tab merge's post-reconcile (a coworker
    // collapsing a group hides MY subRows with no gesture of mine at all) and
    // on a full version reload. Every one of those can pull a row out of the
    // rendered set while a context menu is projected into the popover portal.
    // Asked BEFORE the writes, and only when a row is actually about to become
    // hidden, so a pass that changes nothing raises no signal.
    if (
      rows.some((row) => {
        const groupId = row.settings?.groupId;
        return !!groupId && collapsedGroupIds.has(groupId) && !row.hidden;
      })
    ) {
      this.requestCloseAllContextMenus();
    }

    for (const row of rows) {
      const groupId = row.settings?.groupId;
      const shouldHide = !!(groupId && collapsedGroupIds.has(groupId));
      if (onlyIfChanged) {
        // Compute desired hidden and set-if-changed — folds the merge path's
        // old unconditional false-reset into one conditional write so a row
        // whose visibility did not change emits ZERO mobx mutations.
        const desired = shouldHide ? true : false;
        if (row.hidden !== desired) {
          set(row, 'hidden', desired);
        }
      } else if (shouldHide) {
        row.hidden = true;
      }
    }
  }

  /**
   * PSTUDIO-3332 (S2): before swapping a freshly-loaded row set into the store,
   * rewrite each group header's `settings.subRows` to its canonical members
   * (rows in the SAME set whose groupId points at it, in order). Discards any
   * server/client drift so the count badge and label load correct. Mutates the
   * incoming header settings in place and returns the same array.
   */
  reconcileIncomingGroupSubRows(rows: TableRow[]): TableRow[] {
    const orderOf = new Map(rows.map((r) => [r.id, r.order ?? 0]));
    rows.forEach((row) => {
      if (!isGroupRow(row)) return;
      const members = rows
        .filter((r) => r.settings?.groupId === row.id)
        .map((r) => r.id)
        .sort((x, y) => (orderOf.get(x) ?? 0) - (orderOf.get(y) ?? 0));
      if (!isEqual(members, row.settings?.subRows ?? [])) {
        row.settings = { ...(row.settings ?? {}), subRows: members };
      }
    });
    return rows;
  }

  // PSTUDIO-2154 — atomic grouping commands.
  //
  // groupRows / ungroupRows / addRowToGroup are composed of lower-level
  // mutators: insertRowAbove / insertRowBelow / deleteRow each register their
  // OWN undo command, while the child `settings.groupId` and header
  // `settings.subRows` changes route through raw updateRow and register none.
  // So a single user-level group/ungroup left a PARTIAL command on the stack —
  // undoing it restored only the header insert/delete and orphaned the children
  // (header present but children no longer pointing at it, or vice-versa). The
  // reported symptom: ungroup → undo produced a broken group with the wrong
  // nodes and broken UI.
  //
  // Fix: run the existing body unchanged (so the live effect + efficient DB
  // writes are identical to before), discard whatever inner commands it pushed,
  // and record ONE atomic command that swaps a full before/after row snapshot.

  private _snapshotAllRows(): TableRow[] {
    return toJS(this.rows);
  }

  // Make the in-memory table + DB match `target`, given the table currently
  // holds `current`. In-memory state is replaced wholesale with rows + rowMap +
  // fuse kept in lock-step (the same trio deleteRows maintains). DB writes are
  // diffed so an undo/redo only touches what changed: rows that vanished are
  // hard-deleted, rows that differ only in `order` ride the lightweight
  // orders-only mutation, and the rest go through the (chunked) full-row upsert.
  private _restoreToSnapshot(target: TableRow[], current: TableRow[]) {
    const currentById = new Map(
      current.map((r) => [r.id, r] as [string, TableRow])
    );
    const targetIds = new Set(target.map((r) => r.id));

    this.rows.replace(target);
    this.rowMap.clear();
    this.rows.forEach((r) => this.rowMap.set(r.id, r));
    this.fuse.setCollection(this.rows.slice());

    const removedIds = [...currentById.keys()].filter(
      (id) => !targetIds.has(id)
    );
    if (removedIds.length) this._deleteNodesInDb(removedIds);

    const fullRowSaves: TableRow[] = [];
    const orderOnlyUpdates: RowOrderUpdate[] = [];
    this.rows.forEach((r) => {
      const prev = currentById.get(r.id);
      const next = toJS(r);
      if (prev && isEqual(toJS(prev), next)) return; // unchanged
      if (prev && isEqual({ ...toJS(prev), order: 0 }, { ...next, order: 0 })) {
        orderOnlyUpdates.push({
          id: r.id,
          order: r.order,
          solutionVersionId: this.solutionVersionId,
        });
      } else {
        fullRowSaves.push(next);
      }
    });
    if (orderOnlyUpdates.length) this.saveRowOrdersToDatabase(orderOnlyUpdates);
    if (fullRowSaves.length) this.saveRowsToDatabase(fullRowSaves);

    if (this.validationMode === ValidationMode.Manual) {
      this.hasUnvalidatedChangesInManualMode = true;
    }
    this.validateAndSync();
  }

  // In-memory half of `_restoreToSnapshot`: rows + rowMap + fuse, no writes.
  private _replaceRowsInMemory(target: TableRow[]) {
    this.rows.replace(target);
    this.rowMap.clear();
    this.rows.forEach((r) => this.rowMap.set(r.id, r));
    this.fuse.setCollection(this.rows.slice());
  }

  // `_restoreToSnapshot`, but all-or-nothing on the database: the changed row
  // contents are written (and awaited) first; only if that succeeds do the
  // deletes and order-only updates go out. Resolves with the content save's
  // error, if any — the caller rolls the in-memory rows back on failure.
  private _restoreToSnapshotContentFirst(
    target: TableRow[],
    current: TableRow[]
  ): Promise<{ error?: unknown }> {
    const currentById = new Map(
      current.map((r) => [r.id, r] as [string, TableRow])
    );
    const targetIds = new Set(target.map((r) => r.id));
    this._replaceRowsInMemory(target);

    const removedIds = [...currentById.keys()].filter(
      (id) => !targetIds.has(id)
    );
    const fullRowSaves: TableRow[] = [];
    const orderOnlyUpdates: RowOrderUpdate[] = [];
    this.rows.forEach((r) => {
      const prev = currentById.get(r.id);
      const next = toJS(r);
      if (prev && isEqual(toJS(prev), next)) return;
      if (prev && isEqual({ ...toJS(prev), order: 0 }, { ...next, order: 0 })) {
        orderOnlyUpdates.push({
          id: r.id,
          order: r.order,
          solutionVersionId: this.solutionVersionId,
        });
      } else {
        fullRowSaves.push(next);
      }
    });

    return this.saveBulkRowsToDatabase(fullRowSaves, true).then((result) => {
      if (result.error) return { error: result.error };
      runInAction(() => {
        if (removedIds.length) this._deleteNodesInDb(removedIds);
        if (orderOnlyUpdates.length) {
          this.saveRowOrdersToDatabase(orderOnlyUpdates);
        }
        if (this.validationMode === ValidationMode.Manual) {
          this.hasUnvalidatedChangesInManualMode = true;
        }
        this.validateAndSync();
      });
      return { error: undefined };
    });
  }

  // Run a composite grouping `mutate` (its live effect happens exactly as
  // before), then collapse it into a single atomic undo/redo command. Any inner
  // commands the body pushed are discarded — the one snapshot-swap command owns
  // undo/redo for the whole operation. `getFocusRowId` runs after `mutate` so
  // callers can reveal a row created during the mutation (e.g. the new group).
  private _recordGroupMutation(
    description: string,
    mutate: () => void,
    getFocusRowId?: () => string | undefined
  ) {
    const checkpoint = this.history.checkpoint();
    const before = this._snapshotAllRows();
    // PSTUDIO-4451: the inner commands this gesture is made of must not end
    // the collab store's undo step one by one — only the gesture's own push
    // below does, so the whole gesture undoes as one.
    this._liveGestureDepth += 1;
    try {
      mutate();
    } finally {
      this._liveGestureDepth -= 1;
    }
    const after = this._snapshotAllRows();

    // PSTUDIO-3159: dev-only sanity check — every composite group mutation
    // must leave the subRows/groupId graph referentially consistent.
    // PSTUDIO-3867: the ordering half of this check expects RENDER order, but
    // `after` is the raw physical snapshot (kept raw here on purpose — it feeds
    // the isEqual no-op guard and computeRowDelta below). Physical order legally
    // diverges from render order (e.g. grouping a non-contiguous multi-select
    // renumbers `order` without re-splicing this.rows), so passing `after` would
    // spuriously flag "not contiguous" on correct mutations. Hand the check the
    // arranged render order instead — same rows, canonical order.
    assertGroupInvariants(this.getAllSortedRows(), description);

    // Discard any sub-commands `mutate()` pushed and clear redo, so the whole
    // group edit is recorded below as ONE atomic command (PSTUDIO-3723:
    // identity-based rollbackTo replaces the old undoStack.splice + redoStack
    // .replace — a numeric depth desynced once the stack hit maxHistorySize).
    this.history.rollbackTo(checkpoint);

    if (isEqual(before, after)) return;

    // PSTUDIO undo-memory: store ONLY the touched-row delta in the command
    // closures, not two whole-table snapshots. `before`/`after` are transient —
    // used just above for the invariant check + no-op guard and here to diff —
    // and are released once this method returns (the closures capture `delta`,
    // never `before`/`after`). The target row set is reconstructed from the LIVE
    // table + delta at undo/redo time, which is correct because the history
    // stack is strictly LIFO (see undo-row-delta.ts). This keeps
    // `_restoreToSnapshot` behavior identical while cutting per-command memory
    // from O(table) to O(touched rows).
    const delta = computeRowDelta(before, after);

    const focusRowId = getFocusRowId?.();
    this.pushCommandWithoutExecute({
      execute: () => {
        const current = this._snapshotAllRows();
        this._restoreToSnapshot(applyRowDelta(current, delta, 'redo'), current);
      },
      undo: () => {
        const current = this._snapshotAllRows();
        this._restoreToSnapshot(applyRowDelta(current, delta, 'undo'), current);
      },
      description,
      focusTarget: focusRowId ? { rowId: focusRowId } : undefined,
    });
  }

  // PSTUDIO-3159: a group whose last member was deleted or dragged out is a
  // zombie — a header with count 0 the user can't meaningfully interact
  // with. Decision on the ticket: auto-dissolve. Membership is derived from
  // the canonical settings.groupId pointers (same source of truth as
  // moveRowGroup), so a stale subRows array can't block the dissolve.
  // Removes the header from memory (rows + rowMap + fuse — the trio
  // deleteRows maintains) and hard-deletes it in the DB.
  private _dissolveEmptyGroup = action((groupId: string) => {
    const groupRow = this.getRowById(groupId);
    if (!groupRow || !isGroupRow(groupRow)) return;
    const hasMembers = this.rows.some((r) => r.settings?.groupId === groupId);
    if (hasMembers) return;
    this.removeErrorsForRowIds([groupId]);
    this.rows.remove(groupRow);
    this.rowMap.delete(groupId);
    this.fuse.remove((doc) => doc.id === groupId);
    // PSTUDIO-3159: a still-queued write for this group would re-insert a
    // zombie header in Postgres right after the hard-delete — the drag-out
    // sequence queues a full-row UPSERT (the emptied-subRows write in
    // syncGroupMembershipForRow / the subRows-resort in _applyOrders) and, on
    // the slow-path renumber that rebuilds order from EVERY row including the
    // header, an order-only tuple in the SEPARATE order queue. Both are purged
    // by `_deleteNodesInDb` itself since PSTUDIO-4121, which is where the same
    // race reaches every other caller too; this call site kept its own copy
    // until then.
    this._deleteNodesInDb([groupId]);
  });

  // PSTUDIO-3159: single point of truth for re-inserting a previously
  // removed row into the in-memory trio (rows + rowMap + fuse) that all
  // delete/undo paths must keep in lock-step. The DB write stays at the
  // call site — some callers batch it, others save per-row.
  private _reinsertRowIntoMemory = action((row: TableRow, index: number) => {
    // A row coming back is not pending deletion any more (PSTUDIO-3990). Undo
    // normally lands after the idle callback has already cleared the marker;
    // this keeps the two in step when it lands inside the window instead.
    this.pendingDeletionRowIds.delete(row.id);
    const insertAt = Math.min(index, this.rows.length);
    this.rows.splice(insertAt, 0, row);
    set(this.rowMap, row.id, row);
    this.fuse.add(row);
  });

  groupRows = action(
    (
      ids: string[],
      options?: { groupName?: string; groupColor?: string | null }
    ) => {
      let groupId: string | undefined;
      this._recordGroupMutation(
        'Group rows',
        () => {
          const eligibleIds = ids.filter((id) => this.canBeGrouped(id));

          if (eligibleIds.length < 1) return;

          // Sort eligible ids by their current visual order so the group anchors
          // at the topmost selected row and the displayed first-nodeNumber matches
          // the visual leader. Otherwise a non-contiguous multi-select would
          // create a group above an arbitrary id with scattered subRows.
          const sortedEligibleIds = [...eligibleIds].sort((a, b) => {
            const aOrder = this.getRowById(a)?.order ?? 0;
            const bOrder = this.getRowById(b)?.order ?? 0;
            return aOrder - bOrder;
          });

          const anchorId = sortedEligibleIds[0];

          groupId = this.insertRowAbove(anchorId, {
            nodeType: Tenancy_Node_Type_Enum_Ps_Enum.G,
            // A group header is a structural separator, not a node — it must carry
            // no node number. insertRowAbove normally assigns a smart
            // adjacency-aware number; passing null here overrides it so the
            // group header carries no number (a structural separator). A stale
            // number would collide with a real node and surface as a
            // duplicate-node-number validation error the user has to clear by
            // hand — since PSTUDIO-3914 nothing deletes it on load any more.
            nodeNumber: null,
            settings: {
              subRows: sortedEligibleIds,
              groupExpanded: true,
              ...(options?.groupName ? { groupName: options.groupName } : {}),
              ...(options?.groupColor
                ? { groupColor: options.groupColor }
                : {}),
            },
          });

          emitOnce(() => {
            sortedEligibleIds.forEach((id) => {
              this.updateRow({
                rowId: id,
                updates: {
                  settings: {
                    ...this.getValue(id, NodeColumns.settings),
                    groupId,
                  },
                },
                skipValidationRules: true,
                saveImmediately: true,
              });
            });
          });

          // If the selection was non-contiguous, the new subRows are still
          // scattered across the table — pull them together so the group renders
          // as a unit.
          const anchorRow = this.getRowById(anchorId);
          if (anchorRow) {
            this.moveRowGroup(groupId, anchorRow.order);
          }

          // Grouping is a completed action — clear both row and cell selection
          // so the grouped rows don't stay purple-highlighted (PSTUDIO-3162).
          // The right-clicked row sits in selectedRows
          // (c-editor-table-tr--selected); cells are cleared too for any caller
          // that left a selected cell behind.
          this.deselectAllRows();
          this.deselectAllCells();
        },
        () => groupId
      );
    }
  );

  // ---- Project Bobcat: Supervisor + Knowledge Agent grouping ----------------
  //
  // A solution imported from the platform arrives UNGROUPED: the five `Group *`
  // CSV columns are a Pro-Studio-only round-trip, so `templates/bot.csv` carries
  // no grouping even when its structure is plainly the two-agent pipeline. The
  // members below recognise that shape on load (`detectAutoAgentGrouping`, which
  // never writes) and write the groups only when asked
  // (`applyAutoAgentGrouping`, or the create wizard's pending marker).
  //
  // BOTH node-naming conventions are recognised — the legacy
  // `Supervisor Agent Step 1…` and the AI-native `NLX - Supervisor - …` that the
  // current starter CSV emits. Which one a solution uses is detected per
  // solution; see `auto-agent-grouping.ts`, and `__bobcat__.status().dialect`
  // when a bot that plainly has both agents is not being offered grouping.
  //
  // Guards, in order — each one is a reason NOT to touch the solution:
  //  - the master `bobcat-five-stage-nav` is off (the default);
  //  - the user is a viewer, so we must not write at all;
  //  - the solution already carries ANY grouping — someone has organised it, and
  //    silently adding groups on top would be a surprise. This is also what
  //    makes it idempotent: once the groups exist, nothing is on offer;
  //  - the two-agent pattern is not recognised (`planAutoAgentGroups` returns []).
  //
  // The writer reuses `applyImportedGrouping`, the CSV-import path, so the
  // group rows are built and persisted by code the round-trip already exercises.

  /**
   * Cancels a pending "retry once the feature flags land" watcher. Held so a
   * second solution load cannot leave the first load's watcher running.
   */
  private _cancelAutoGroupRetry?: () => void;

  /**
   * True when this solution matches the Supervisor + Knowledge Agent shape and
   * carries no grouping yet — i.e. `applyAutoAgentGrouping()` would do
   * something if asked. Detection only: nothing is written to reach this.
   */
  autoAgentGroupingAvailable = false;

  /**
   * A snapshot of the rows as they would stand AFTER `applyAutoAgentGrouping`
   * has cleared the groups an earlier version of this feature created — the
   * stale headers dropped and the `groupId` those headers owned removed from
   * their members.
   *
   * Pure: it builds plain copies and touches nothing in the store, so the
   * load-time detection can ask "would the writer find anything to do?" without
   * becoming a writer itself.
   */
  private effectiveRowsForAutoGrouping = (): TableNode[] => {
    const rows = this.rows.map((r) => toJS(r));
    const staleIds = new Set(findStaleAutoGroupRows(rows).map((r) => r.id));
    if (!staleIds.size) return rows;

    return rows
      .filter((r) => !staleIds.has(r.id))
      .map((r) =>
        r.settings?.groupId && staleIds.has(String(r.settings.groupId))
          ? { ...r, settings: { ...r.settings, groupId: undefined } }
          : r
      );
  };

  /**
   * Inspect the loaded solution and record whether grouping is on offer.
   *
   * Runs on every solution load and **never writes** — that separation is the
   * entire point. The previous version applied and persisted the grouping here,
   * on any solution whose node names happened to match, for anyone with the
   * flag on.
   *
   * Same guard order as the writer, so the offer can never appear for a
   * solution the writer would refuse: flag off, viewer, no rows, already
   * grouped, or pattern not recognised.
   */
  detectAutoAgentGrouping = action(() => {
    this.autoAgentGroupingAvailable = false;

    // Flags arrive asynchronously, and `featureFlags` is `{}` rather than
    // undefined before they land, so an empty bag is indistinguishable from
    // "all off". Retry once they arrive, or the offer never appears on a hard
    // reload straight onto a solution URL.
    if (!Object.keys(this.featureFlags).length) {
      this._cancelAutoGroupRetry?.();
      this._cancelAutoGroupRetry = when(
        () => Object.keys(this.featureFlags).length > 0,
        () => this.detectAutoAgentGrouping()
      );
      return;
    }

    this._cancelAutoGroupRetry?.();
    this._cancelAutoGroupRetry = undefined;

    if (resolveBobcatAccess('fiveStageNav', this.featureFlags) !== 'allowed') {
      return;
    }
    if (this.isViewer || !this.rows.length) return;

    // Model what the WRITER would see, which is not the same as what is in the
    // store: `applyAutoAgentGrouping` clears any group left behind by an
    // earlier version of this feature BEFORE it plans. Detection has to do the
    // same or it under-reports — a solution carrying only a stale
    // "Use-case Agents" group looks "already grouped" here, so the offer never
    // appears even though `__bobcat__.group()` would happily apply it. Done on
    // a COPY: nothing below writes to the store.
    const rows = this.effectiveRowsForAutoGrouping();
    if (hasAnyGrouping(rows) || hasAgentGroups(rows)) return;
    if (!planAutoAgentGroups(rows).length) return;

    this.autoAgentGroupingAvailable = true;
    console.info(
      '[tableStore] Supervisor / Knowledge Agent grouping is available for ' +
        'this solution. Nothing has been written — run __bobcat__.group() to ' +
        'apply it.'
    );
  });

  /**
   * Apply the Supervisor / Knowledge Agent grouping. **THIS WRITES AND
   * PERSISTS** — it must only ever run from an explicit user action.
   *
   * Renamed from `maybeAutoGroupAgents` and unhooked from the load path on
   * 2026-09-05. It used to run on every solution load: for anyone with the flag
   * on, opening ANY solution whose node names happened to match wrote group
   * rows into it. Contained while one person demoed one rehearsed bot; not
   * contained once the team uses the prototype on their own solutions, because
   * it silently edits someone else's work. Detection now happens on load
   * (`detectAutoAgentGrouping`); writing happens only when asked.
   *
   * Returns the group names it created — empty when it declined — so a caller
   * reports what happened instead of assuming success.
   */
  applyAutoAgentGrouping = action((): string[] => {
    // No deferral here, deliberately. Waiting for late-arriving flags is
    // `detectAutoAgentGrouping`'s job, because it runs unprompted on load. This
    // runs because a person asked for it, and an action that silently completes
    // later — after they have moved on, possibly to another solution — is worse
    // than one that declines now and says why.
    if (!Object.keys(this.featureFlags).length) {
      console.warn(
        '[tableStore] applyAutoAgentGrouping: feature flags have not loaded ' +
          'yet; declining rather than writing. Try again in a moment.'
      );
      return [];
    }

    if (resolveBobcatAccess('fiveStageNav', this.featureFlags) !== 'allowed') {
      return [];
    }

    if (this.isViewer) return [];

    if (!this.rows.length) return [];

    // Clean up after an EARLIER version of this feature before anything else.
    // It wrote a real "Use-case Agents" group from the leftover rows; that is
    // now a display-only card, so any such group left in the database is a
    // stale artifact that renders as a section full of Decision nodes. Clearing
    // it here means a solution heals itself on the next load instead of needing
    // a manual ungroup.
    const stale = findStaleAutoGroupRows(this.rows.map((r) => toJS(r)));
    if (stale.length) {
      console.info(
        '[tableStore] clearing stale auto-groups:',
        stale.map((g) => g.settings?.groupName).join(', ')
      );
      const staleIds = new Set(stale.map((g) => g.id));
      stale.forEach((g) => this.ungroupRows(g.id));

      // `ungroupRows` clears `groupId` off the members it finds in
      // `settings.subRows`, and that array can drift from the canonical
      // pointer (PSTUDIO-3161). A member left pointing at the header we just
      // deleted still counts as grouping to `hasAnyGrouping`, which would skip
      // the planner — the same symptom, one indirection along. Sweep the ids
      // just removed, and only those.
      emitOnce(() => {
        this.rows
          .filter((r) => staleIds.has(String(r.settings?.groupId)))
          .forEach((r) => {
            this.updateRow({
              rowId: r.id,
              updates: {
                settings: { ...toJS(r.settings), groupId: undefined },
              },
              skipValidationRules: true,
              saveImmediately: true,
            });
          });
      });
    }

    // Snapshot AFTER the cleanup, not before: the stale group's members still
    // carried a `groupId` in the pre-cleanup snapshot, so `hasAnyGrouping`
    // would have seen grouping that no longer exists and skipped the planner —
    // leaving the solution ungrouped, which is the whole bug being fixed.
    const rows = this.rows.map((r) => toJS(r));

    // `hasAnyGrouping` subsumes `hasAgentGroups`; both are checked so the
    // already-auto-grouped case is explicit at the call site rather than
    // implied.
    if (hasAnyGrouping(rows) || hasAgentGroups(rows)) return [];

    const plan = planAutoAgentGroups(rows);
    if (!plan.length) return [];

    console.info(
      '[tableStore] auto-grouping agents:',
      plan.map((g) => `${g.groupName} (${g.members.length})`).join(', ')
    );

    this._graftGroupsInPlace(plan);
    // The solution now carries grouping, so the offer is stale.
    this.autoAgentGroupingAvailable = false;

    return plan.map((g) => g.groupName);
  });

  /**
   * Project Bobcat — propose real groups for onboarding-declared task stub
   * nodes (ungrouped rows carrying `settings.taskId`). See
   * `use-case-agent-numbering.ts` for the full grounding, including why
   * detection is by that marker and not by node number.
   *
   * True when at least one such stub row exists and is not already grouped.
   * Detection only — nothing is written to reach this. Deliberately NOT
   * gated on `hasAnyGrouping`, unlike `autoAgentGroupingAvailable`: a stub
   * group is independent of whatever else the solution already has grouped
   * (its Supervisor/Knowledge groups, if any), so a solution that already
   * has real agent groups can still offer this.
   */
  useCaseAgentNumberingAvailable = false;

  /** Mirrors `_cancelAutoGroupRetry` — see its comment. */
  private _cancelUseCaseAgentNumberingRetry?: () => void;

  /**
   * Inspect the loaded solution and record whether use-case stub grouping is
   * on offer. Runs on every solution load and **never writes** — same N1
   * discipline as `detectAutoAgentGrouping`, pinned by the same source-level
   * test (`auto-grouping-never-writes-on-load.test.ts`).
   */
  detectUseCaseAgentNumbering = action(() => {
    this.useCaseAgentNumberingAvailable = false;

    // Same retry-once-flags-arrive handling as `detectAutoAgentGrouping`,
    // for the same reason: `featureFlags` is `{}` before LaunchDarkly
    // answers, indistinguishable from "all off" without this.
    if (!Object.keys(this.featureFlags).length) {
      this._cancelUseCaseAgentNumberingRetry?.();
      this._cancelUseCaseAgentNumberingRetry = when(
        () => Object.keys(this.featureFlags).length > 0,
        () => this.detectUseCaseAgentNumbering()
      );
      return;
    }

    this._cancelUseCaseAgentNumberingRetry?.();
    this._cancelUseCaseAgentNumberingRetry = undefined;

    if (resolveBobcatAccess('fiveStageNav', this.featureFlags) !== 'allowed') {
      return;
    }
    if (this.isViewer || !this.rows.length) return;

    const rows = this.rows.map((r) => toJS(r));
    if (!planUseCaseAgentGroups(rows).length) return;

    this.useCaseAgentNumberingAvailable = true;
    console.info(
      '[tableStore] Use-case agent grouping is available for this ' +
        'solution. Nothing has been written — run ' +
        '__bobcat__.groupUseCaseAgents() or the controls-bar action to apply it.'
    );
  });

  /**
   * Apply the use-case stub grouping. **THIS WRITES AND PERSISTS** — only
   * ever runs from an explicit user action, same discipline as
   * `applyAutoAgentGrouping`.
   *
   * Deliberately does NOT reuse `applyImportedGrouping`/`planImportedGroups`
   * the way `applyAutoAgentGrouping` does. It was written when
   * `planImportedGroups` skipped any group of fewer than two matched members
   * (`matched.length < 2` in `csv-group-meta.ts`) and `groupRows` ignored a
   * selection of one, and every group this planner proposes is a single-member
   * group by design. That minimum was never a rule of the grouping data model
   * (`collectGroupInvariantViolations` has no member-count check), and
   * PSTUDIO-4198 removed it from both. This method still writes directly
   * with the same primitives (`insertRowAbove` + `updateRow`); routing it
   * through `applyImportedGrouping` is possible now and has not been done.
   * A use-case stub is one placeholder node with nothing to combine it with,
   * and turning it into its own real group of one is the point (it is what
   * makes canvas render it as its own card).
   *
   * Returns the group names it created — empty when it declined.
   */
  applyUseCaseAgentGrouping = action((): string[] => {
    if (!Object.keys(this.featureFlags).length) {
      console.warn(
        '[tableStore] applyUseCaseAgentGrouping: feature flags have not ' +
          'loaded yet; declining rather than writing. Try again in a moment.'
      );
      return [];
    }

    if (resolveBobcatAccess('fiveStageNav', this.featureFlags) !== 'allowed') {
      return [];
    }

    if (this.isViewer) return [];
    if (!this.rows.length) return [];

    const rows = this.rows.map((r) => toJS(r));
    const plan = planUseCaseAgentGroups(rows);
    if (!plan.length) return [];

    console.info(
      '[tableStore] grouping use-case agents:',
      plan.map((g) => g.groupName).join(', ')
    );

    const created: string[] = [];

    this._recordGroupMutation('Group use-case agents', () => {
      plan.forEach((meta) => {
        const nodeNumber = meta.members[0]?.nodeNumber;
        const member = this.rows.find((r) => r.nodeNumber === nodeNumber);
        if (!member || !this.canBeGrouped(member.id)) return;

        const groupId = this.insertRowAbove(member.id, {
          id: meta.groupId,
          nodeType: Tenancy_Node_Type_Enum_Ps_Enum.G,
          // A group header is a structural separator, not a node — see the
          // identical reasoning on `groupRows`' own insertRowAbove call.
          nodeNumber: null,
          settings: {
            subRows: [member.id],
            groupExpanded: !meta.collapsed,
            groupName: meta.groupName,
            groupColor: meta.groupColor,
          },
        });

        this.updateRow({
          rowId: member.id,
          updates: {
            settings: {
              ...this.getValue(member.id, NodeColumns.settings),
              groupId,
            },
          },
          skipValidationRules: true,
          saveImmediately: true,
        });

        created.push(meta.groupName);
      });
    });

    this.useCaseAgentNumberingAvailable = false;

    return created;
  });

  /**
   * Project Bobcat canvas: add a new agent — a group of one starting
   * Decision node — for the Build canvas's "Add agent" and for a Resource
   * Library item dropped on empty canvas. **THIS WRITES AND PERSISTS**, from
   * an explicit user action only.
   *
   * A group of one, written with the same primitives
   * `applyUseCaseAgentGrouping` uses: a card on the canvas IS a group. (This
   * predates PSTUDIO-4198, which lifted the two-member minimum of `groupRows`
   * and `planImportedGroups`; the direct write is no longer forced by it.)
   * The new node goes
   * at the end of the table, where Table View's own "add row" at the bottom
   * would put it, and is coloured like every other use-case agent.
   *
   * Returns the new ids, or null when it declined (viewer, nothing loaded).
   */
  createCanvasAgent = action(
    (
      name: string,
      /** The starting node's own fields, e.g. an Action node's type. */
      nodeConfig?: Partial<TableRow>
    ): { groupId: string; rowId: string } | null => {
      if (this.isViewer) return null;
      const last = this.rows[this.rows.length - 1];
      if (!last) return null;
      const groupName = name.trim() || 'New agent';

      let created: { groupId: string; rowId: string } | null = null;
      this._recordGroupMutation('Add agent', () => {
        const rowId = this.insertRowBelow(last.id, {
          nodeType: Tenancy_Node_Type_Enum_Ps_Enum.D,
          nodeName: `${groupName} start`,
          ...nodeConfig,
        });
        const groupId = this.insertRowAbove(rowId, {
          nodeType: Tenancy_Node_Type_Enum_Ps_Enum.G,
          // A group header is a structural separator, not a node — see the
          // identical reasoning on `groupRows`' own insertRowAbove call.
          nodeNumber: null,
          settings: {
            subRows: [rowId],
            groupExpanded: true,
            groupName,
            // The next colour the solution's agents don't use yet.
            groupColor: nextUseCaseAgentColor(
              this.rows
                .filter((r) => isGroupRow(r))
                .map((r) => r.settings?.groupColor)
            ),
          },
        });
        this.updateRow({
          rowId,
          updates: {
            settings: {
              ...this.getValue(rowId, NodeColumns.settings),
              groupId,
            },
          },
          skipValidationRules: true,
          saveImmediately: true,
        });
        created = { groupId, rowId };
      });
      return created;
    }
  );

  /**
   * Undo the auto-grouping: ungroup every group this feature created, leaving
   * the nodes themselves untouched.
   *
   * Covers both the groups this feature creates now and the ones an earlier
   * version created (`STALE_AUTO_GROUP_NAMES`).
   *
   * `ungroupRows` (NOT `deleteRowGroup`, which would delete the members) frees
   * each group's rows and removes the header. Returns the names it removed so a
   * caller can report what happened.
   *
   * Exists because `applyAutoAgentGrouping` refuses any solution that already
   * has grouping — so re-testing it otherwise means re-importing the bot.
   * Identification is by NAME; see `findAutoGroupRows` for why, and for the
   * caveat that a hand-made group with the same name is indistinguishable.
   */
  resetAutoAgentGroups = action((): string[] => {
    const removed: string[] = [];
    const removedIds = new Set<string>();
    const snapshot = this.rows.map((r) => toJS(r));

    // Stale names as well as current ones: a solution grouped by an earlier
    // version of this feature carries a group nothing creates any more, and a
    // reset that could not remove it left the operator hand-ungrouping.
    [
      ...findAutoGroupRows(snapshot),
      ...findStaleAutoGroupRows(snapshot),
    ].forEach((groupRow) => {
      removed.push(String(groupRow.settings?.groupName ?? groupRow.id));
      removedIds.add(groupRow.id);
      this.ungroupRows(groupRow.id);
    });

    if (!removedIds.size) return removed;

    // Same sweep `applyAutoAgentGrouping` does after its stale cleanup, and for
    // the same reason: `ungroupRows` only clears `groupId` off the members it
    // finds in `settings.subRows`, and that array can drift from the canonical
    // pointer (PSTUDIO-3161). A member left pointing at a header we just
    // deleted still counts as grouping to `hasAnyGrouping`, so the very next
    // `applyAutoAgentGrouping` would decline with "already grouped" —
    // `__bobcat__.regroup()` (reset then group) would silently do nothing.
    // Sweep the ids just removed, and only those.
    emitOnce(() => {
      this.rows
        .filter((r) => removedIds.has(String(r.settings?.groupId)))
        .forEach((r) => {
          this.updateRow({
            rowId: r.id,
            updates: {
              settings: { ...toJS(r.settings), groupId: undefined },
            },
            skipValidationRules: true,
            saveImmediately: true,
          });
        });
    });

    return removed;
  });

  /**
   * PSTUDIO-3443 (Option Z): after a CSV import + reload created the base
   * nodes, re-hydrate node grouping from the file's group columns.
   *
   * Matches members by Node Number, synthesizes 'g' headers whose id is the
   * verbatim `Group Id` from the CSV, writes `settings.groupId` on each
   * member, persists, and lets reconcile/visibility passes re-render the
   * groups. Wrapped in `_recordGroupMutation` for atomic undo (mirrors
   * `groupRows`).
   *
   * Verbatim id guarantee: `insertRowAbove` builds `newRow` with
   * `[NodeColumns.id]: uuidv4()` first, then spreads `...config` on top
   * (table-store.ts line ~8267). Passing `id: header.id` in the config
   * object therefore overrides the generated uuid with the verbatim Group Id
   * from the CSV — no post-insert rename is needed.
   *
   * PSTUDIO-4122: takes the version the CSV was uploaded FOR (the store's
   * own identity string, pinned at upload), and returns what it did instead of
   * reporting it itself.
   *
   * `uploadedFor` must be `tableStore.solutionVersionId` as it stood when the
   * upload was issued — `${customerId}-${botName}-${draftVersion}` — not the
   * REST UUID (`draftVersionSolutionData.id`). Those are different tables;
   * `reload-nodes.ts` already says Hasura cannot resolve the UUID as this PK.
   * Comparing the UUID here would refuse every successful same-version import.
   *
   * The remaining window this pin closes: `reload-nodes.ts` reads the LIVE
   * store field when the invoke starts. A publish bump between
   * `sendProStudioEvent('reloadNodes')` and that read fetches the NEW version
   * and still reports `applied`. Every write below is stamped with the store's
   * CURRENT version, so PSTUDIO-4168's filter would then DROP the group header
   * on the next flush — `{ error: undefined }`, indistinguishable from a save
   * that wrote. That is the "import said success, grouping was gone" half of
   * this ticket.
   *
   * The outcome is returned rather than toasted because this store has no
   * translations: the caller is `use-upload-csv.ts`, a React hook with
   * `useTranslation`. The toast raised here was an English literal on all five
   * locales.
   */
  applyImportedGrouping = action(
    (
      groupMeta: ImportedGroupMeta[],
      uploadedFor: string
    ): ImportGroupingOutcome => {
      if (!groupMeta.length) return { status: 'nothing-to-do' };

      // Required, not optional with a fallback. The obvious fallback — the
      // identity of the rows being grafted onto — is unavailable exactly when it
      // would matter: leaving a solution CLEARS `rows` (measured, PSTUDIO-4122),
      // so the fallback would find nothing and compare the store's version with
      // itself, passing vacuously for every caller that omitted the argument.
      const storeIsOn = this.solutionVersionId;

      // `!storeIsOn` is its own case: a store with no version loaded stamps
      // every write with an empty one, which `saveBulkRowsToDatabase` refuses
      // wholesale (PSTUDIO-3669) — the same silent loss, one layer down.
      if (!storeIsOn || uploadedFor !== storeIsOn) {
        console.warn(
          '[tableStore] applyImportedGrouping: refusing to graft imported groups into another version',
          { uploadedFor, storeIsOn }
        );
        return { status: 'version-changed', uploadedFor, storeIsOn };
      }

      const plan = planImportedGroups(
        groupMeta,
        this.rows.map((r) => toJS(r))
      );

      if (plan.skipped.length) {
        // A group is skipped when its members can't be matched back by Node
        // Number, or by row id for callers that name rows (PSTUDIO-4637) (e.g. the CSV was exported from a DIFFERENT solution whose
        // numbers collided on import and got renumbered) or when the
        // reconstructed structure fails the group invariants. Reported to the
        // caller (PSTUDIO-4122) so the grouping loss is visible instead of
        // silently dropped (PSTUDIO-3443).
        console.warn(
          '[tableStore] applyImportedGrouping: skipped invalid groups',
          plan.skipped
        );
      }

      if (!plan.headers.length) {
        return { status: 'applied', restored: 0, skipped: plan.skipped };
      }

      return this._graftImportedGroups(plan);
    }
  );

  /**
   * PSTUDIO-4122: the write half of `applyImportedGrouping`, split out only so
   * the identity gate above reads as one unbroken decision.
   */
  private _graftImportedGroups = action(
    (plan: ImportGroupPlan): ImportGroupingOutcome => {
      // Counted rather than taken from `plan.headers.length`: a header whose
      // first member has no row in the table is skipped by the `!anchor` guard
      // below, and reporting it as restored is exactly the false success this
      // ticket removes.
      let restored = 0;

      this._recordGroupMutation('Import grouping', () => {
        // 1) Write groupId onto matched members (mirrors groupRows' emitOnce block).
        emitOnce(() => {
          plan.memberUpdates.forEach(({ rowId, groupId }) => {
            const currentSettings = this.getValue(rowId, NodeColumns.settings);
            // PSTUDIO-3625: updateRow's guard is reference equality, and we always
            // build a fresh settings object, so skip the write when groupId is
            // already correct to avoid churning MobX observers on a re-apply.
            if (currentSettings?.groupId === groupId) return;
            this.updateRow({
              rowId,
              updates: {
                settings: {
                  ...currentSettings,
                  groupId,
                },
              },
              skipValidationRules: true,
              saveImmediately: true,
            });
          });
        });

        // 2) Insert synthesized 'g' headers above each group's first member.
        //    `insertRowAbove` spreads `...config` last, so `id: header.id`
        //    overrides the internally generated uuidv4() — verbatim id is
        //    preserved without any post-insert mutation.
        plan.headers.forEach((header) => {
          const firstMemberId = header.settings?.subRows?.[0];
          const anchor = firstMemberId
            ? this.getRowById(firstMemberId)
            : undefined;
          if (!anchor) return;

          this.insertRowAbove(anchor.id, {
            id: header.id,
            nodeType: Tenancy_Node_Type_Enum_Ps_Enum.G,
            // Must be null: a stale number collides with real nodes and (since
            // PSTUDIO-3914 dropped the dedup-on-load pass) stays as a
            // duplicate-node-number validation error until the user clears it.
            // Mirrors groupRows' explicit `nodeNumber: null`.
            nodeNumber: null,
            settings: header.settings,
          });

          // Pull scattered members together so the group renders as a unit
          // (mirrors groupRows' moveRowGroup call).
          this.moveRowGroup(header.id, anchor.order);
          restored += 1;
        });

        // 3) Canonicalise membership graph and apply collapse/expand visibility.
        //    `onlyIfChanged` (PSTUDIO-3625) avoids redundant `hidden` writes on
        //    every ungrouped row when only a collapsed group needs hiding.
        this.reconcileIncomingGroupSubRows(this.rows);
        this.applyGroupCollapseVisibility(this.rows, { onlyIfChanged: true });
      });

      return { status: 'applied', restored, skipped: plan.skipped };
    }
  );

  /**
   * Project Bobcat's in-place groupers (`applySuggestedAgents`,
   * `applyAutoAgentGrouping`) reuse the CSV-import graft. They group the
   * solution already on screen, so the version they write for is the store's
   * own — the PSTUDIO-4122 identity gate passes unless no version is loaded,
   * where it rightly refuses. Returns how many groups were actually grafted.
   */
  private _graftGroupsInPlace = (groupMeta: ImportedGroupMeta[]): number => {
    const outcome = this.applyImportedGrouping(
      groupMeta,
      this.solutionVersionId
    );
    return outcome.status === 'applied' ? outcome.restored : 0;
  };

  ungroupRows = action((parentId: string) => {
    const parentRow = this.getRowById(parentId);
    if (!parentRow || !isGroupRow(parentRow)) {
      // PSTUDIO-3159: grouping invariants are convention-only; guard every
      // group mutator consistently (moveRowGroup already did) and surface
      // the anomaly instead of corrupting state.
      console.error('[tableStore] ungroupRows: target is not a group row', {
        parentId,
      });
      return;
    }
    this._recordGroupMutation(
      'Ungroup rows',
      () => {
        const subRows = this.getValue(parentId, NodeColumns.settings)?.subRows;

        emitOnce(() => {
          if (subRows) {
            subRows.forEach((id: string) => {
              this.updateRow({
                rowId: id,
                updates: {
                  settings: {
                    ...this.getValue(id, NodeColumns.settings),
                    groupId: undefined,
                  },
                },
                skipValidationRules: true,
                saveImmediately: true,
              });
            });
          }
          this.deleteRow(parentId);

          // PSTUDIO-3687: while collapsed, applyGroupCollapseVisibility set each
          // member's transient `hidden` flag to true. Clearing groupId + deleting
          // the header above leaves those members orphaned but still hidden — they
          // vanish from the rendered order (index column skips them) until a reload
          // rebuilds visibility. Re-run the reconciliation: with groupId now
          // undefined the members are no longer under any collapsed group, so they
          // flip back to visible. onlyIfChanged keeps it write-minimal — an ungroup
          // of an already-expanded group touches no `hidden` flag (PSTUDIO-3625).
          this.applyGroupCollapseVisibility(this.rows, { onlyIfChanged: true });
        });
      },
      () => parentId
    );
  });

  deleteRowGroup = action((id: string) => {
    const subRows = this.getValue(id, NodeColumns.settings)?.subRows;

    this.deleteRows([id, ...(subRows ?? [])]);
  });

  /**
   * Appends a new row to the group and returns its id (so batch inserts can
   * chain). Returns '' and inserts nothing when `groupRowId` is not a group
   * row — a should-never-happen state (callers always pass a real group id),
   * so it is a `console.error` dev-invariant, NOT a user-facing toast. Every
   * current caller ignores the returned id (draggable-row "add" button,
   * insertRowAfter); '' is the agreed "no row inserted" sentinel (PSTUDIO-3202).
   */
  addRowToGroup = action(
    (groupRowId: string, config?: Partial<TableRow>): string => {
      const groupRow = this.getRowById(groupRowId);
      if (!groupRow || !isGroupRow(groupRow)) {
        console.error('[tableStore] addRowToGroup: target is not a group row', {
          groupRowId,
        });
        return '';
      }
      let newRowId = '';
      this._recordGroupMutation(
        'Add node to group',
        () => {
          // PSTUDIO-3333: anchor on the last CANONICAL member, never on the raw
          // subRows cache — a phantom id at subRows.at(-1) would crash
          // insertRowBelow with "Target row not found".
          const members = this.getGroupMembers(groupRowId);
          const targetRowId = members.at(-1) || groupRowId;

          emitOnce(() => {
            // Insert new row below the target
            newRowId = this.insertRowBelow(targetRowId, {
              ...config,
              settings: {
                groupId: groupRowId,
              },
            });

            // Re-read settings AFTER insertRowBelow — it may have triggered nearby
            // order/validation logic that mutated this group's settings; spreading
            // a stale snapshot would silently clobber those updates.
            const currentSettings = this.getValue(
              groupRowId,
              NodeColumns.settings
            );

            this.updateRow({
              rowId: groupRowId,
              updates: {
                settings: {
                  ...currentSettings,
                  // PSTUDIO-3332/3333: rebuild from canonical members (now
                  // including newRowId) so a pre-existing phantom never survives
                  // and the header count reflects the real children.
                  subRows: this.sortIdsByRowOrder([
                    ...new Set([...this.getGroupMembers(groupRowId), newRowId]),
                  ]),
                },
              },
              skipValidationRules: true,
              saveImmediately: true,
            });
          });
        },
        () => newRowId || undefined
      );
      return newRowId;
    }
  );

  /**
   * PSTUDIO-3332/3333: canonical group membership = rows whose `settings.groupId`
   * points at `groupId`, in visual (order) sequence. `settings.subRows` is only a
   * derived cache of this; read membership from here, never from the cache.
   */
  getGroupMembers = (groupId: string): string[] =>
    this.sortIdsByRowOrder(
      this.rows.filter((r) => r.settings?.groupId === groupId).map((r) => r.id)
    );

  /**
   * Rewrite a group header's `settings.subRows` to its canonical members,
   * discarding any phantom/stale ids. No-op when already in sync. Keeps the
   * count badge / label (which read subRows) matching the real children.
   */
  reconcileGroupSubRows = action((groupId: string): void => {
    const group = this.getRowById(groupId);
    if (!group || !isGroupRow(group)) return;
    const canonical = this.getGroupMembers(groupId);
    const current = group.settings?.subRows ?? [];
    if (isEqual(canonical, current)) return;
    this.updateRow({
      rowId: groupId,
      updates: { settings: { ...(group.settings ?? {}), subRows: canonical } },
      skipValidationRules: true,
      saveImmediately: true,
    });
  });

  // PSTUDIO-2154: rename a group header as a single undo/redo step. The name
  // lives in `settings.groupName`; recordRowEdit snapshots the row's settings
  // before/after so undo restores the prior name. No-op when unchanged.
  setGroupName = action((rowId: string, name: string) => {
    const current = this.getValue(rowId, NodeColumns.settings);
    if ((current?.groupName ?? '') === name) return;
    this.recordRowEdit(rowId, 'Rename group', () => {
      this.updateRow({
        rowId,
        updates: {
          settings: {
            ...current,
            groupName: name,
          },
        },
        skipValidationRules: true,
        saveImmediately: true,
      });
    });
  });

  // PSTUDIO-3393: color a group header as a single undo/redo step. The color
  // lives in `settings.groupColor`; recordRowEdit snapshots settings before/
  // after so undo restores the prior color. No-op when unchanged (churn guard).
  // `null` clears the color back to the neutral band.
  setGroupColor = action((rowId: string, color: string | null) => {
    const current = this.getValue(rowId, NodeColumns.settings);
    if ((current?.groupColor ?? null) === (color ?? null)) return;
    this.recordRowEdit(rowId, 'Change group color', () => {
      this.updateRow({
        rowId,
        updates: {
          settings: {
            ...current,
            groupColor: color,
          },
        },
        skipValidationRules: true,
        saveImmediately: true,
      });
    });
  });

  // PSTUDIO-3866: set/clear the transient live color preview for a group's
  // in-flight custom-color drag (see the `groupColorPreview` field). Both the
  // group header and its subrow guide lines read this so they preview the same
  // in-flight color. `writeGroupColorPreview` keys the write by groupId (so only
  // this group's readers re-render) and guards an equal-value write to a MobX
  // no-op — no re-render churn on a stationary frame. Pass `color: null` to
  // clear (scoped delete of this group's key). NOT recorded in undo and NOT
  // persisted — the value is committed once via `setGroupColor` on drag release.
  setGroupColorPreview = action((groupId: string, color: string | null) => {
    writeGroupColorPreview(this.groupColorPreview, groupId, color);
  });

  // PSTUDIO-3866: the live preview color for `groupId`, or null when no drag is
  // in flight for it. Read in `draggable-row.tsx` by the group header and its
  // subrow guide lines, each falling back to the committed `settings.groupColor`.
  // Reads a single map key → subscribes only to this group's preview.
  getGroupColorPreview(groupId: string): string | null {
    return readGroupColorPreview(this.groupColorPreview, groupId);
  }

  moveRowGroup = action((groupId: string, targetOrder: number) => {
    const groupRow = this.getRowById(groupId);
    if (!groupRow || !isGroupRow(groupRow)) {
      console.error('[tableStore] moveRowGroup: target is not a group row', {
        groupId,
      });
      return;
    }

    // Derive children from the canonical groupId pointer on each row rather
    // than from settings.subRows — if the two ever drift, the groupId pointer
    // is the source of truth for "who lives in this group right now".
    const children = this.rows
      .filter((r) => r.settings?.groupId === groupId)
      .map((r) => r.id);

    // Anchor the group +1 above targetOrder so when targetOrder collides with
    // an existing row's order, the group lands AFTER it (stable sort otherwise
    // breaks ties by insertion order, which is wrong for dnd semantics).
    // normalizeRowOrder below spreads everyone back onto orderIncrementV2.
    const groupOrder = targetOrder + 1;

    // PSTUDIO-3160 (C): never order a group inside another group's range.
    // The UI blocks invalid drops (red insertion line in handleDragMove /
    // refusal in handleDragEnd); this is the data-level backstop so no
    // caller can interleave one group's bundle into another's.
    // Each group's last child order, in one pass over the rows — not one
    // pass per group, which on a solution with thousands of rows and dozens
    // of groups (a "Suggest agents" apply moves every group in turn) cost
    // seconds per move.
    const lastChildOrderByGroup = new Map<string, number>();
    for (const r of this.rows) {
      const parent = r.settings?.groupId;
      if (!parent) continue;
      const prev = lastChildOrderByGroup.get(parent);
      if (prev === undefined || r.order > prev) {
        lastChildOrderByGroup.set(parent, r.order);
      }
    }
    for (const other of this.rows) {
      // isGroupRow (case-insensitive) over a raw `=== G` compare so an
      // imported/legacy `'G'` header is still treated as a group here
      // (PSTUDIO-3202).
      if (!isGroupRow(other)) continue;
      if (other.id === groupId) continue;
      const lastChildOrder = lastChildOrderByGroup.get(other.id);
      if (lastChildOrder === undefined) continue;
      if (groupOrder > other.order && groupOrder <= lastChildOrder) {
        // Data-level backstop: the UI red-line guard already blocks this drop.
        // Log (don't toast) for parity with the sibling guards in this file — a
        // bare `return` here would hide a regressed UI guard (PSTUDIO-3202).
        console.error(
          '[tableStore] moveRowGroup: blocked nesting inside group',
          {
            groupId,
            blockedBy: other.id,
          }
        );
        return;
      }
    }

    // Build a single atomic order map for the group + every child. Routing
    // through reorderRows (instead of N× updateRow with saveImmediately:true,
    // one per group member) collapses what was N queued DebouncedQueue items
    // + per-row validateAndSync into one bulk GraphQL upsert with
    // skipValidation=true — no race window between queue flush and a
    // subscription replay that would otherwise overwrite half the new orders
    // with stale data.
    const newOrderById = new Map<string, number>();
    newOrderById.set(groupId, groupOrder);
    children.forEach((subRowId, index) => {
      newOrderById.set(subRowId, groupOrder + (index + 1));
    });
    this.reorderRows(newOrderById);

    // Only normalize when our anchoring actually collided with an existing
    // row's order; otherwise the move is already on clean orderIncrementV2
    // multiples and a full-table reflow would be wasted GraphQL writes.
    const orderCounts = new Map<number, number>();
    for (const row of this.rows) {
      orderCounts.set(row.order, (orderCounts.get(row.order) ?? 0) + 1);
    }
    const hasDuplicates = [...orderCounts.values()].some((count) => count > 1);
    if (hasDuplicates) {
      this.normalizeRowOrder();
    }
  });

  /**
   * After a drag reorders a single regular row, reconcile its group
   * membership with where it actually landed visually.
   *
   * Rule (sosed-sverhu): the row immediately above (in sorted order)
   * decides membership.
   *  - prev = expanded group-row G → row joins G.
   *  - prev = subRow of group G (and G is expanded) → row joins G.
   *  - prev = collapsed group-row / collapsed-group subRow → row keeps its
   *    existing membership iff that membership was the same G (i.e. it was
   *    already in G); otherwise it stays ungrouped. Collapsed groups don't
   *    absorb new members (their subRow range is visually invisible).
   *  - prev = ungrouped row, or no prev → row is ungrouped.
   *
   * Applies the diff against the row's current `settings.groupId` plus the
   * `subRows` arrays on both old and new group, in a single emitOnce so
   * the GraphQL bulk save ships one batch.
   *
   * Not called for group-row drags: a moved group must not change the
   * membership of unrelated rows that the bundle happened to translate
   * across; that's the inverse contract the consolidation in handleDragEnd
   * (groupOrder + index +1) preserves.
   */
  syncGroupMembershipForRow = action((rowId: string) => {
    const row = this.getRowById(rowId);
    if (!row) return;
    if (row.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.G) return;

    const sorted = this.sortedRows;
    const idx = sorted.findIndex((r) => r.id === rowId);
    if (idx === -1) return;

    const prev = idx > 0 ? sorted[idx - 1] : undefined;
    const currentGroupId = row.settings?.groupId;

    let target: string | undefined;
    if (!prev) {
      target = undefined;
    } else if (prev.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.G) {
      if (prev.settings?.groupExpanded) {
        target = prev.id;
      } else {
        target = currentGroupId === prev.id ? prev.id : undefined;
      }
    } else if (prev.settings?.groupId) {
      const prevGroupId = prev.settings.groupId;
      const prevGroup = this.getRowById(prevGroupId);
      if (prevGroup?.settings?.groupExpanded) {
        target = prevGroupId;
      } else {
        target = currentGroupId === prevGroupId ? prevGroupId : undefined;
      }
    } else {
      target = undefined;
    }

    if (currentGroupId === target) return;

    emitOnce(() => {
      if (currentGroupId) {
        const oldGroup = this.getRowById(currentGroupId);
        if (oldGroup) {
          const filtered = (oldGroup.settings?.subRows ?? []).filter(
            (id: string) => id !== rowId
          );
          // PSTUDIO-3159: don't queue an upsert for a group that's about to
          // be dissolved (filtered empty → _dissolveEmptyGroup below hard-
          // deletes it). The queued UPSERT would race deleteProStudioNodes
          // and re-insert a zombie header in Postgres — the same hazard
          // _deleteRowsInternal guards with its early return.
          if (filtered.length > 0) {
            this.updateRow({
              rowId: currentGroupId,
              updates: {
                settings: {
                  ...(oldGroup.settings ?? {}),
                  subRows: filtered,
                },
              },
              skipValidationRules: true,
              saveImmediately: true,
            });
          }
        }
      }

      if (target) {
        const newGroup = this.getRowById(target);
        if (newGroup) {
          const existing = (newGroup.settings?.subRows ?? []).filter(
            (id: string) => id !== rowId
          );
          // Insert by visual order so subRows array mirrors the actual
          // child order in the table — keeps copy/paste, persistence, and
          // count UI in sync with what the user sees.
          const withOrders = existing
            .map((id: string) => ({
              id,
              order: this.getRowById(id)?.order ?? 0,
            }))
            .sort((a, b) => a.order - b.order);
          let insertAt = 0;
          while (
            insertAt < withOrders.length &&
            withOrders[insertAt].order < row.order
          ) {
            insertAt += 1;
          }
          const nextSubRows = withOrders.map((s) => s.id);
          nextSubRows.splice(insertAt, 0, rowId);

          this.updateRow({
            rowId: target,
            updates: {
              settings: {
                ...(newGroup.settings ?? {}),
                subRows: nextSubRows,
              },
            },
            skipValidationRules: true,
            saveImmediately: true,
          });
        }
      }

      // Use explicit null (not undefined) when leaving a group so the field
      // round-trips through JSON.stringify → GraphQL → Hasura UPSERT. An
      // `undefined` key is silently dropped from the payload and Postgres
      // keeps the prior groupId, so the subscription replays back the row
      // with its old membership — counter goes down (the group's subRows
      // array did clear), but the row keeps its --subrow class because
      // `isSubRow = !!rowSettings?.groupId` is still truthy.
      this.updateRow({
        rowId,
        updates: {
          settings: {
            ...(row.settings ?? {}),
            groupId: target ?? null,
          },
        },
        skipValidationRules: true,
        saveImmediately: true,
      });
    });

    // PSTUDIO-3159: if this row was the group's last member, the group is
    // now empty — dissolve it. Runs after emitOnce so the membership writes
    // above (old group's subRows, this row's groupId=null) are already
    // applied; _dissolveEmptyGroup re-derives emptiness from the canonical
    // groupId pointers, so a row merely MOVING between groups never
    // triggers a false dissolve.
    if (currentGroupId && currentGroupId !== target) {
      this._dissolveEmptyGroup(currentGroupId);
    }

    // Flush the debounced save queue synchronously, so the payload carrying
    // the new groupId/subRows is on its way before anything else can replace
    // it. Without the flush, the subscription replay leaks back the stale
    // settings.groupId and the row keeps its --subrow class.
    //
    // Order of the two writes of a drag (PSTUDIO-4342, corrected): the
    // `reorderRows` call that handleDragEnd makes first goes through
    // `_applyOrders` into the ORDER queue, which flushes on a 0 ms timer — so
    // this content upsert is ISSUED FIRST and the order tuples second, and
    // _nodeWriteTail keeps that order on the wire. That is harmless: the
    // tuples write only `order` (update_columns: [order]), and the row snapshot
    // sent here already carries the post-drag order.
    saveRowsToDatabaseQueue.flush();
  });

  // PSTUDIO-2154: apply one group's expanded flag plus each subRow's hidden
  // flag from an explicit snapshot, persisting both. No undo/redo history side
  // effect — shared by the live toggles and their commands. `saveImmediately`
  // mirrors the original per-toggle behavior (single toggle saves now; the bulk
  // toggle batches via saveImmediately:false).
  private _applyGroupVisibility = action(
    (snapshot: GroupVisibilitySnapshot, saveImmediately = true) => {
      const { groupRowId, groupExpanded, subRowHidden } = snapshot;
      // PSTUDIO-4023: collapsing a group removes its subRows from `tbody`, and
      // a menu open on one of them would unmount with its `ps-dropdown` still
      // projected into the popover portal — the `removeChild` crash. Every
      // group-visibility path funnels through here (the single toggle, the
      // Collapse-all bulk toggle, and both legs of their undo/redo commands),
      // so this is the one place that covers them all.
      //
      // Only when a row is actually about to DISAPPEAR: an expand adds rows
      // (nothing unmounts under a menu) and a repeated Collapse-all over
      // already-collapsed groups changes nothing — neither should raise a
      // signal that re-renders every observer reading the nonce, which is the
      // same discipline `undo()` applies with its `canUndo` guard.
      const hidesARenderedRow = [...subRowHidden.entries()].some(
        ([id, hidden]) => hidden && !this.getRowById(id)?.hidden
      );
      if (hidesARenderedRow) this.requestCloseAllContextMenus();
      emitOnce(() => {
        subRowHidden.forEach((hidden, id) => {
          this.updateRow({
            rowId: id,
            updates: { hidden },
            skipValidationRules: true,
            saveImmediately,
          });
        });

        this.updateRow({
          rowId: groupRowId,
          updates: {
            settings: {
              ...this.getValue(groupRowId, NodeColumns.settings),
              groupExpanded,
            },
          },
          skipValidationRules: true,
          saveImmediately,
        });
      });
    }
  );

  // Capture the current expanded flag + per-subRow hidden flags for a group row.
  private _snapshotGroupVisibility(
    groupRowId: string,
    subRows: string[],
    groupExpanded: boolean
  ): GroupVisibilitySnapshot {
    return {
      groupRowId,
      groupExpanded,
      subRowHidden: new Map<string, boolean>(
        subRows.map((id) => [id, !!this.getRowById(id)?.hidden])
      ),
    };
  }

  toggleRowGroupVisibility = action((groupRowId: string) => {
    if (this.liveEditingSource) {
      const sink = this.liveViewSettings;
      if (!sink) return;
      const collapsed = sink.viewSettings.collapsedGroupIds;
      sink.updateViewSettings({
        collapsedGroupIds: collapsed.includes(groupRowId)
          ? collapsed.filter((id) => id !== groupRowId)
          : [...collapsed, groupRowId],
      });
      return;
    }

    const settings = this.getValue(groupRowId, NodeColumns.settings);
    // Derive members from the canonical groupId pointer on each row rather
    // than settings.subRows. The two can drift (drag in/out update the
    // pointer but not always the array); trusting the stale array here left
    // children hidden after expand — the group looked expanded yet showed no
    // nodes, and two adjacent groups rendered combined (PSTUDIO-3161).
    // moveRowGroup already uses the pointer as the source of truth.
    const subRows: string[] = this.rows
      .filter((r) => r.settings?.groupId === groupRowId)
      .map((r) => r.id);
    const expandedBefore = !!settings?.groupExpanded;
    const expandedAfter = !expandedBefore;

    const before = this._snapshotGroupVisibility(
      groupRowId,
      subRows,
      expandedBefore
    );
    const after: GroupVisibilitySnapshot = {
      groupRowId,
      groupExpanded: expandedAfter,
      subRowHidden: new Map<string, boolean>(
        subRows.map((id) => [id, !expandedAfter])
      ),
    };

    this._applyGroupVisibility(after);

    // PSTUDIO-2154: a toggle always flips groupExpanded, so it's always a real
    // change worth recording. Reveal the group row (it stays visible even when
    // collapsed) on undo/redo.
    this.pushCommandWithoutExecute({
      execute: () => this._applyGroupVisibility(after),
      undo: () => this._applyGroupVisibility(before),
      description: expandedAfter ? 'Expand group' : 'Collapse group',
      focusTarget: { rowId: groupRowId },
    });
  });

  private _applyAllGroupsVisibility = action(
    (snapshots: GroupVisibilitySnapshot[]) => {
      emitOnce(() => {
        snapshots.forEach((s) => this._applyGroupVisibility(s, false));
      });
    }
  );

  toggleAllGroupRowsExpanded = action((newVisibility: boolean) => {
    const groupRows = this.rows.filter(
      (row) => row.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.G
    );

    if (groupRows.length === 0) return;

    if (this.liveEditingSource) {
      this.liveViewSettings?.updateViewSettings({
        collapsedGroupIds: newVisibility ? [] : groupRows.map((g) => g.id),
      });
      return;
    }

    // Derive members from the canonical groupId pointer, not the
    // drift-prone settings.subRows array — same root cause as
    // toggleRowGroupVisibility (PSTUDIO-3161). Trusting the array here
    // stranded pointer-only children hidden/visible after Collapse-all /
    // Expand-all.
    const childIdsOf = (groupRowId: string): string[] =>
      this.rows
        .filter((r) => r.settings?.groupId === groupRowId)
        .map((r) => r.id);

    const before = groupRows.map((g) =>
      this._snapshotGroupVisibility(
        g.id,
        childIdsOf(g.id),
        !!g.settings?.groupExpanded
      )
    );
    const after: GroupVisibilitySnapshot[] = groupRows.map((g) => ({
      groupRowId: g.id,
      groupExpanded: newVisibility,
      subRowHidden: new Map<string, boolean>(
        childIdsOf(g.id).map((id) => [id, !newVisibility])
      ),
    }));

    // PSTUDIO-2154: record one command for the whole bulk toggle. No-op (no
    // command) when every group is already in the target state.
    const changed = before.some((b) => b.groupExpanded !== newVisibility);

    this._applyAllGroupsVisibility(after);

    if (!changed) return;
    this.pushCommandWithoutExecute({
      execute: () => this._applyAllGroupsVisibility(after),
      undo: () => this._applyAllGroupsVisibility(before),
      description: newVisibility ? 'Expand all groups' : 'Collapse all groups',
    });
  });

  async validate({
    projectId,
    customerId,
    botName,
    draftVersion,
    validationMode,
  }: {
    projectId: string;
    customerId: string;
    botName: string;
    draftVersion: string;
    validationMode?: ValidationMode;
  }) {
    try {
      const agentCompilerMode = readAgentCompilerMode(this.featureFlags);
      // Bot Manager has no record of a solution created after the Agent Manager migration,
      // so its compiler cannot be asked about one — `validateCsv` / `validateVersion` just
      // 404, on every sync. Same gate as `upload()`. The `bot_only` kill switch therefore
      // cannot silence PIF for such a solution either, or it would be left unvalidated.
      const hasLegacyBot = Boolean(
        readLegacyBotSolutionId(proStudioService.getSnapshot().context)
      );
      const runPif = agentCompilerMode !== 'bot_only' || !hasLegacyBot;
      const runBotCompiler =
        shouldRunBotCompiler(agentCompilerMode) && hasLegacyBot;

      if (
        (validationMode === ValidationMode.Manual &&
          this.validationMode === ValidationMode.Auto) ||
        (!validationMode && this.validationMode === ValidationMode.Manual)
      ) {
        console.log('returning cached errors');
        return {
          errors: [],
          botCompilerErrors: this.errors,
          abort: () => {},
          didPerformValidation: false,
        };
      }

      const botVersion = 5;
      const uploadUrl = `${import.meta.env.FE_ADMIN_URL}/rest/bot-manager/${projectId}/compile?botVersion=${botVersion}`;

      if (this.sortedRows.length + this.pendingRowsToRender.length === 0) {
        this.pifPartialValidationService.reset();
        tableStore.setErrors([]);
        tableStore.setPifErrors([]);
        // An empty table's empty error list IS a verdict, so a stale flag from
        // an earlier pass must not survive it (PSTUDIO-4120). The raw
        // diagnostics go with it: they are what a verdict-less pass re-serves,
        // and findings about rows that no longer exist would come back the
        // moment one row is added again.
        this.originalPifCompilerDiagnostics = [];
        this.setValidationFailure(null);
        return {
          errors: [],
          botCompilerErrors: [],
          abort: () => {},
          didPerformValidation: false,
        };
      }

      const rows = [...this.sortedRows].map((item) => toJS(item));
      // Strip UI-only group rows (nodeType === 'G') before any Bot Compiler
      // payload — see PSTUDIO-2220.
      const compilerRows = excludeGroupNodes(rows);

      // The PIF payload is `compilerRows` MINUS the rows a delete has already
      // claimed. Two separate reasons, and both matter:
      //
      // - the guard below must judge THIS array. In the first version of this
      //   fix it judged `rowsForValidation` (which subtracts those rows) while
      //   the payload kept them, so a row whose `Node #` was cleared and then
      //   deleted was INVISIBLE to the guard and PRESENT in the payload. The
      //   pass went out, the compiler died on it, and — measured on
      //   agent-compiler 1.11.0 — a compiler holding an accumulated solution
      //   summary answers that by silently returning the PREVIOUS pass's
      //   findings, carrying no marker at all. `hasDegradedDiagnostic` cannot
      //   see that shape, so the guard is the ONLY thing standing between this
      //   window and the original bug. One array for both is what stops the two
      //   drifting apart again.
      // - a row on its way out has nothing to say to the compiler anyway, and
      //   subtracting it (rather than teaching the guard to see it) is what
      //   keeps PSTUDIO-3990 closed: `d052beb88a` established that "clear a
      //   number, see the error, delete the row" must not leave Publish
      //   disabled with nothing on screen to fix.
      //
      // The CSV path deliberately keeps the full `compilerRows`, but NOT because
      // `resolveErrorRowIndex` reads that array — it does not; it resolves
      // against the full `rows`, group headers included (see its own docblock,
      // and `rowsForValidation` above). The reason is one step earlier: the
      // SERVER computes `row_num` by counting the CSV payload it was given, and
      // that number is the last-resort address `resolveErrorRowIndex` falls back
      // to. Dropping a row from the CSV would shift it.
      const pifRows =
        this.pendingDeletionRowIds.size === 0
          ? compilerRows
          : compilerRows.filter(
              (row) => !isClaimedByDelete(row, this.pendingDeletionRowIds)
            );

      const testData = json2csvParser.parse(compilerRows);
      const csvFile = blobToFile(createCsvBlob(testData), 'bot.csv') as File;
      const versionId = this.solutionVersionId;
      const versionIdLegacy = versionId.replace('-', '.');

      if (compilerRows.length === 0) {
        this.pifPartialValidationService.reset();
        tableStore.setErrors([]);
        tableStore.setPifErrors([]);
        // Same reasoning as the exit above, raw diagnostics included: a solution
        // of nothing but group headers has no node to be wrong about
        // (PSTUDIO-4120).
        this.originalPifCompilerDiagnostics = [];
        this.setValidationFailure(null);
        return {
          errors: [],
          botCompilerErrors: [],
          abort: () => {},
          didPerformValidation: false,
        };
      }

      // `pifRows`, so the archive the support flow ships carries the payload the
      // pass WOULD send — NOT `compilerRows`, which still holds the rows a
      // delete has claimed. Deliberately not "what was sent": this assignment
      // runs before the guard below, so on a skipped pass it describes a payload
      // that never left. (It no longer matches the CSV path byte for byte;
      // PSTUDIO-2220's constraint is about stripping GROUP rows — verified in
      // the ticket itself, where the backend rule is about group VALUES — and
      // both sets strip those.)
      const dataForCompiling = runPif
        ? prepareRowsDataForPifCompiler(pifRows)
        : undefined;
      // TODO: NEED IT UNTIL WE SWITCH UI TO USE PIF COMPILER
      this.dataForCompiling = dataForCompiling ?? '';

      // PSTUDIO-4120: a node the PIF grammar cannot read does not produce a
      // diagnostic about itself — it kills the pass from the inside. The
      // compiler's `.trim()` on an error-recovered `nodeNumber` throws, Langium
      // turns that into an address-less diagnostic, and what comes back depends
      // on hidden compiler state: with no accumulated solution summary the whole
      // list is REPLACED by one opaque message, and with one it silently returns
      // the PREVIOUS pass's findings as if they were fresh. Both were verified
      // by running agent-compiler 1.11.0. So the pass is not sent at all, and
      // the run is recorded as one that produced no verdict.
      //
      // The blank cell is not left unreported by this: `deriveNodeNumberErrors`
      // already names it per-cell and `findNodeNumberProblem` already blocks
      // Publish — this only stops a second, useless answer overwriting the real
      // ones.
      //
      // `pifRows`, which IS the payload — see the derivation above for why
      // reading any other set reopens the bug.
      //
      // PSTUDIO-4417: a `parameterInput` the grammar cannot read (plain text on
      // an Action node, kept as typed since that ticket) kills the pass the same
      // way, and on a warm compiler it does so with no marker at all — the
      // solution-level findings simply drop out of the answer. Same guard, same
      // label. Both shapes are also named per cell by `buildJsonFieldErrors`
      // below, through the same `isCompilableParameterInput` this guard uses:
      // text that is not JSON, and valid JSON that is not an object (`123`,
      // `[1]`, `true`, PSTUDIO-4492 item 7).
      const uncompilableRow = runPif
        ? (findUncompilableNodeNumberRow(pifRows) ??
          findUncompilableParameterInputRow(pifRows))
        : null;

      // The payload emptied out while the table did NOT: every row the compiler
      // would judge is on its way out (Delete All Rows, a group delete, or a
      // select-all + Delete), and the idle callback has not compacted the array
      // yet. `compilerRows` is still non-empty, so neither empty-table exit
      // above fires.
      //
      // An empty payload is NOT evidence that the solution is clean — it is
      // evidence the question cannot be answered right now. Sending it anyway
      // returned `[]`, which read as a fresh clean verdict: the badge went
      // green, Publish opened, and a STANDING failure could be cleared without
      // fixing anything, all while `upload()` still ships those rows. So this
      // pass produces no verdict at all and leaves the previous one alone.
      const pifPayloadEmptied =
        runPif && pifRows.length === 0 && compilerRows.length > 0;

      const runPifPass =
        runPif && uncompilableRow === null && !pifPayloadEmptied;

      if (uncompilableRow) {
        // Named, not just counted: when the badge says the run did not finish,
        // this is the one line that says WHICH row stopped it — the popover
        // cannot, because no diagnostic was produced to address.
        console.log(
          '%c=====================PIF PASS SKIPPED=====================',
          'color: orange; font-size: larger; font-weight: bold',
          {
            rowId: uncompilableRow.id,
            nodeNumber: uncompilableRow.nodeNumber,
          }
        );
      }

      const [apiValidatorResult, apiCompilerErrors, pifDiagnostics] =
        await Promise.all([
          runBotCompiler
            ? validateCsv({
                url: uploadUrl,
                file: csvFile,
                versionId,
                versionIdLegacy,
              })
            : Promise.resolve<{
                errors: string[];
                botCompilerErrors: BotCompilerError[];
                networkError?: true;
              }>({ errors: [], botCompilerErrors: [] }),
          runBotCompiler
            ? validateVersion({
                projectId,
                customerId,
                botName,
                draftVersion,
              })
            : Promise.resolve([] as BotCompilerError[]),
          runPifPass
            ? this.pifPartialValidationService.validate(
                pifRows,
                this.solutionVersionId,
                prepareRowsDataForPifCompiler
              )
            : undefined,
        ]);

      // Check if solution changed during validation - prevent race condition
      const currentContext = proStudioService.getSnapshot().context;
      if (currentContext.botName !== botName) {
        return {
          errors: [],
          botCompilerErrors: [],
          abort: () => {},
          didPerformValidation: false,
        };
      }

      // PSTUDIO-4120: the compiler answered, but did it answer about the
      // SOLUTION? `hasDegradedDiagnostic` recognises the two shapes that mean
      // "the validator broke": its own `Unhandled exception occurred during
      // validation` (which REPLACED every real finding it had already made) and
      // Langium's `An error occurred during validation: …` (a wrapped check
      // exception, carrying no node address, so it can highlight nothing).
      const pifPassDegraded =
        runPifPass && hasDegradedDiagnostic(pifDiagnostics);

      // PSTUDIO-4242: a failed or timed-out Bot Compiler request comes back as
      // an empty finding list — indistinguishable from a clean pass unless
      // `networkError` is read.
      const botCompilerUnreachable =
        runBotCompiler && apiValidatorResult.networkError === true;

      // Why the PIF side produced no verdict, kept apart from the Bot Compiler
      // so that an unreachable Bot Compiler does not discard a real PIF answer.
      const pifFailure: ValidationFailureReason | null = pifPayloadEmptied
        ? this.validationFailure
        : uncompilableRow !== null
          ? 'payload-not-compilable'
          : pifPassDegraded
            ? 'compiler-degraded'
            : null;

      // A PIF reason wins: `compiler-degraded` is what resets the compiler
      // below, and it must not be masked by the Bot Compiler being down too.
      const validationFailure: ValidationFailureReason | null =
        pifFailure ?? (botCompilerUnreachable ? 'validator-unreachable' : null);

      // When the pass produced no verdict, keep serving the LAST one rather
      // than an empty list: an empty list is a clean badge over an unchecked
      // solution, i.e. the same fail-open this ticket is about, pointing the
      // other way. `validationFailure` is what tells the popover and
      // `canPublish` that these are not fresh.
      //
      // `pifPayloadEmptied` is checked separately because it can leave the flag
      // at `null` (nothing was wrong before) while still not being a verdict —
      // reading the flag alone would blank the list on that path.
      //
      // `pifFailure`, not `validationFailure`: when only the Bot Compiler is
      // unreachable, this pass's PIF answer IS a verdict (PSTUDIO-4242).
      const pifDiagnosticsForDisplay =
        !pifPayloadEmptied && pifFailure === null
          ? (pifDiagnostics ?? [])
          : this.originalPifCompilerDiagnostics;

      // TODO: NEED IT UNTIL WE SWITCH UI TO USE PIF COMPILER
      this.originalPifCompilerDiagnostics = pifDiagnosticsForDisplay;

      // Map diagnostics to the shared error shape carrying the stable node id
      // (data.id); the concrete cell address is resolved id-first downstream in
      // buildWorkflowErrors so a blank node number still highlights/navigates
      // (PSTUDIO-3991).
      const pifCompilerErrors: BotCompilerError[] = transformPifDiagnostics(
        pifDiagnosticsForDisplay.filter(({ severity }) => severity === 1)
      );

      const pifCompilerWarnings: BotCompilerError[] = transformPifDiagnostics(
        pifDiagnosticsForDisplay.filter(({ severity }) => severity === 2)
      );

      const apiValidatorErrors = apiValidatorResult.botCompilerErrors || [];

      const errorsFromApi = [
        ...(apiValidatorErrors || []),
        ...(apiCompilerErrors || []),
      ].filter(
        (item, index, self) => index === self.findIndex((t) => isEqual(t, item))
      );

      const totalErrors = [
        // ...pifCompilerErrors,
        ...errorsFromApi,
      ];

      console.log(
        '%c=====================PIF COMPILER DIAGNOSTICS=====================',
        'color: blue; font-size: larger; font-weight: bold',
        runPif
          ? {
              // What came back from THIS pass, and what the popover will
              // actually show — they differ whenever the pass produced no
              // verdict, and the WARNINGS/ERRORS banners below are built from
              // the latter.
              fresh: pifDiagnostics,
              shown: pifDiagnosticsForDisplay,
              validationFailure,
            }
          : 'not enabled'
      );

      console.log(
        '%c=====================PIF COMPILER WARNINGS=====================',
        'color: orange; font-size: larger; font-weight: bold',
        runPif ? pifCompilerWarnings : 'not enabled'
      );

      console.log(
        '%c=====================PIF COMPILER ERRORS=====================',
        'color: red; font-size: larger; font-weight: bold',
        runPif ? pifCompilerErrors : 'not enabled'
      );

      console.log(
        '%c=====================API CSV VALIDATOR ERRORS=====================',
        'color: pink; font-size: larger; font-weight: bold',
        runBotCompiler ? apiValidatorErrors : 'not enabled'
      );

      console.log(
        '%c=====================API CSV COMPILER ERRORS=====================',
        'color: pink; font-size: larger; font-weight: bold',
        runBotCompiler ? apiCompilerErrors : 'not enabled'
      );

      // ** Rich Asset Content / Parameter Input / Analytics Event Errors **
      // @TODO: temporary fix for rich asset content, parameter input, and analytics errors
      // remove it after pif-compiler is ready
      const sortedRowIndexMap = new Map(
        this.sortedRows.map((row, i) => [row.id, i])
      );

      // Extracted to a pure helper so these messages and their `nodeNumber`
      // fields are reachable from a unit test — inside this method they were
      // not, and a mutation proved every one of them could be reverted with the
      // suite still green (`json-field-errors.test.ts`).
      // The SAME row set the node-number derivation and the publish gate walk
      // (PSTUDIO-3990) — see `rowsForValidation`. It was `[...this.rows,
      // ...this.pendingRowsToRender]` here while both of those walked
      // `this.rows`, so one method held two definitions of "which rows".
      const jsonFieldErrors: WorkflowError[] = this.rowsForValidation.flatMap(
        (row) =>
          buildJsonFieldErrors(
            {
              rowId: row.id,
              richAssetType: row.richAssetType,
              richAssetContent: row.richAssetContent,
              parameterInput: row.parameterInput,
              analyticsEvent: row.analyticsEvent,
              nodeNumber: row.nodeNumber,
              nodeType: row.nodeType,
            },
            {
              // `?? null`, never `as number`: the map is built from
              // `sortedRows` (i.e. `this.rows`) while this walk also covers
              // `pendingRowsToRender`, so for a tail row the lookup really is
              // `undefined` and the cast only hid it — a NaN index into the
              // visible rows rather than "no index" (PSTUDIO-3990). The
              // popover addresses these errors by `nodeId` first anyway.
              rowIndex: sortedRowIndexMap.get(row.id) ?? null,
              getColumnIndex: (colId) => this.getColumnIndex(colId),
              // The object-shape rule is the PIF grammar's: judged only where
              // PIF runs (PSTUDIO-4492, item 7).
              enforceParameterInputObjectShape: runPif,
              isJsonAssetType: (assetType) =>
                this.isJsonAssetType(
                  assetType as Parameters<typeof this.isJsonAssetType>[0]
                ),
            }
          )
      );

      // ** End of Rich Asset Content / Parameter Input / Analytics Errors **

      const errors: WorkflowError[] = buildWorkflowErrors(
        totalErrors,
        rows,
        this.nodeNumberErrorContext
      );
      const pifErrors: WorkflowError[] = runPif
        ? buildWorkflowErrors(
            pifCompilerErrors,
            rows,
            this.nodeNumberErrorContext
          )
        : [];

      // The Bot Compiler always validates; the Agent Validator (PIF) is gated
      // by the multi-state ps-agent-compiler-errors flag. Frontend JSON-field
      // errors must reach whichever list is visible in the popover without
      // appearing twice in side-by-side mode: route them to PIF whenever PIF
      // runs (off/on), and to API only as the kill-switch fallback (bot_only).
      const allErrors =
        agentCompilerMode === 'bot_only'
          ? [...errors, ...jsonFieldErrors]
          : [...errors];
      const allPifErrors = runPif ? [...pifErrors, ...jsonFieldErrors] : [];

      console.log(
        '%c=====================UI ERRORS=====================',
        'color: lime; font-size: larger; font-weight: bold',
        { allErrors, allPifErrors, agentCompilerMode }
      );

      // PSTUDIO-4120: a pass that produced no verdict must not be able to
      // re-assert itself from a cache. `PifPartialValidationService` stores the
      // answer in `lastDiagnostics` and returns it verbatim when the next pass
      // diffs to no changed/removed node — so a degraded answer would come back
      // degraded for every subsequent pass until the user edited a row, and in
      // Auto mode there is no Validate button to press. Resetting drops that
      // cache AND the compiler's own accumulated `pifSolutionSummary`, which the
      // crash is what corrupts (`updateScriptFileNames` resets for the same
      // reason). The next pass is then a full one against a fresh compiler.
      // Only `compiler-degraded` reaches this branch needing a reset (`threw`
      // is handled in the catch below, and it is the only other value that can
      // mean the compiler itself is compromised). `payload-not-compilable` is
      // the opposite case: the pass was never SENT, so nothing in the compiler
      // was touched and its incremental cache is still valid. Resetting there
      // only discarded that cache and made the next pass a full one — measured
      // on agent-compiler 1.11.0 across three machines/runs, a full pass costs
      // 130-180 ms at 1,000 nodes and 660-780 ms at 5,000, against 11-29 ms
      // incremental — an order of magnitude either way. With a blank `Node #`
      // sitting in the table the branch fires on every debounced pass.
      if (validationFailure === 'compiler-degraded') {
        this.pifPartialValidationService.reset();
      }

      // One transaction, not three: each of these is an `action`, so left
      // separate they wake an observer reading all three fields once per write.
      // Measured on this repo's MobX build: a pass that CHANGES the verdict
      // wakes it 3 times against 1 batched, and a clean pass 2 against 1 — not
      // 0, because `setErrors`/`setPifErrors` are handed freshly built arrays
      // (`[...errors]` above), so they are never the equal-value write MobX
      // drops. Only `setValidationFailure(null)` over `null` is free.
      runInAction(() => {
        // Unreachable: keep the last Bot Compiler verdict rather than the
        // empty list the failed request stood in with.
        if (!botCompilerUnreachable) tableStore.setErrors(allErrors);
        tableStore.setPifErrors(allPifErrors);
        this.setValidationFailure(validationFailure);
      });

      // PSTUDIO-4120: `didPerformValidation` now means what its name says. A
      // pass that could not produce a verdict reports false, so Manual mode
      // keeps `hasUnvalidatedChangesInManualMode` — and therefore keeps Publish
      // disabled and the Validate button enabled — instead of recording the
      // solution as validated.
      return {
        ...apiValidatorResult,
        // `pifPayloadEmptied` is excluded explicitly: it can leave the flag at
        // `null` while still having produced no verdict, and reporting `true`
        // there would clear `hasUnvalidatedChangesInManualMode` for a pass that
        // asked the compiler nothing.
        didPerformValidation: validationFailure === null && !pifPayloadEmptied,
      };
    } catch (e) {
      console.error('Validation failed:', e);
      // PSTUDIO-4120: the previous `errors` / `pifErrors` are deliberately left
      // in place (blanking them would show a clean badge for a solution nobody
      // checked), but they are now labelled as not-a-fresh-verdict, which is
      // what makes `canPublish` refuse and the popover say so.
      //
      // Reset for the same reason as the success path above: whatever threw may
      // have left the compiler mid-parse, and its cached answer would otherwise
      // be served to the next pass.
      this.pifPartialValidationService.reset();
      // ...but the verdict is stamped only if this pass still owns the
      // solution. The success path checks exactly this before writing
      // (`currentContext.botName !== botName` above); without the same check
      // here, a pass that dies AFTER the user switched solutions labels the one
      // they just opened — Publish refused and a warning badge over a solution
      // this session never validated, the very state `clearMetadata` exists to
      // prevent.
      if (proStudioService.getSnapshot().context.botName === botName) {
        this.setValidationFailure('threw');
      }
      return {
        errors: [(e as Error).message],
        botCompilerErrors: [],
        abort: () => {},
        didPerformValidation: false,
      };
    }
  }

  async upload({
    customerId,
    botName,
    draftVersion,
    projectId,
  }: {
    customerId: string;
    botName: string;
    draftVersion: string;
    projectId: string;
  }) {
    const solutionVersionIdLegacy = `${customerId}.${botName}.${draftVersion}`;
    const botVersion = 5;
    const uploadUrl = `${
      import.meta.env.FE_ADMIN_URL
    }/rest/bot-manager/${projectId}/${solutionVersionIdLegacy}/templates?botVersion=${botVersion}`;

    // Strip UI-only group rows (nodeType === 'G') from the published CSV —
    // they must never reach the Bot Compiler (PSTUDIO-2220).
    const compilerRows = excludeGroupNodes(
      [...this.sortedRows, ...this.pendingRowsToRender].map((item) => {
        return toJS(item);
      })
    );

    const csv = json2csvParser.parse(compilerRows);

    const testData = csv;

    // Bot Manager only has a bot for solutions that predate the migration. For a v2
    // solution this endpoint 404s on every keystroke-triggered sync, and uploadCsv then
    // records the HTML error page as `lastServerSyncError`. The v2 repo holds a
    // backend-generated `templates/bot.pif` and no bot.csv, so there is nothing to push
    // here — see docs/agent-manager-v2-migration.md for the open question of whether the
    // backend recompiles it from the Hasura nodes on publish.
    if (!readLegacyBotSolutionId(proStudioService.getSnapshot().context)) {
      return;
    }

    const uploading = uploadCsv({
      url: uploadUrl,
      file: blobToFile(createCsvBlob(testData), 'bot.csv') as File,
      versionIdLegacy: this.solutionVersionId.replaceAll('-', '.'),
      versionId: this.solutionVersionId,
      // load,
      // error,
      // progress,
      // abort,
      // onChange,
    });
    this._templateUploads.add(uploading);
    try {
      return await uploading;
    } finally {
      this._templateUploads.delete(uploading);
    }
  }

  /**
   * Resolves once no template upload is in flight, including any that start
   * while it waits. Each upload ends: `uploadCsv` aborts an idle request
   * (PSTUDIO-4242). The auto-publish waits on this (PSTUDIO-4578): a publish
   * must not start while the template it publishes is still being written.
   */
  async templateUploadsSettled(): Promise<void> {
    while (this._templateUploads.size) {
      await Promise.allSettled([...this._templateUploads]);
    }
  }

  /**
   * Every node of a solution version, normalized the same way a loaded
   * solution's rows are. The current version comes from memory (including
   * rows not yet rendered); any other version is fetched. Throws on a fetch
   * transport error rather than returning a partial list.
   */
  async fetchVersionRows(solutionVersionId?: string): Promise<TableRow[]> {
    if (!solutionVersionId || solutionVersionId === this.solutionVersionId) {
      return [...this.sortedRows, ...this.pendingRowsToRender].map((r) =>
        toJS(r)
      );
    }

    const res = await urqlGql
      .getProStudioVersionData(
        { solutionVersionId },
        { requestPolicy: 'network-only' }
      )
      .toPromise();

    // Check the transport error BEFORE consuming res.data — urql can return
    // a partial payload alongside an error.
    if (res.error) {
      throw res.error;
    }

    const rawNodes =
      res.data?.tenancy_solution_versions_ps_by_pk?.solution_version_nodes_ps ??
      [];

    // PSTUDIO-4103: normalize WITHOUT enqueueing the order fixes. This reads a
    // version the user does not have open, and the order-save queue re-homes
    // every tuple onto the CURRENT `solutionVersionId` at flush time
    // (PSTUDIO-3669) — so repairing this version's `order` would upsert ITS
    // node ids into the version on screen. A read (download, Deploy's diff) may
    // sort the rows it returns, never write them.
    return this.normalizeInitialRows(rawNodes as unknown as TableRow[], {
      enqueueOrderFixes: false,
    });
  }

  /**
   * PSTUDIO-3443: build a grouped CSV string that carries node-grouping
   * metadata in 4 extra columns (base columns + Group Id/Name/Order/Collapsed),
   * enabling a lossless round-trip. Group header rows ('g') are excluded by the
   * builder — they are reconstructed on import, never emitted as CSV rows.
   *
   * Draft/current version → in-memory rows; other version → GraphQL fetch.
   * Returns `null` when the source has NO groups, so callers can skip bundling
   * a redundant file (it would just be bot.csv + empty group columns).
   *
   * This is the single producer of the grouped CSV; it is bundled into the
   * solution-download ZIP (see `downloadVersionFile`) alongside `bot.csv`.
   * Throws on a version-fetch transport error — the caller decides whether the
   * grouping is best-effort (skip) or fatal.
   */
  async buildGroupedCsvString(
    solutionVersionId?: string
  ): Promise<string | null> {
    return toGroupedCsv(await this.readRowsForCsv(solutionVersionId));
  }

  /**
   * PSTUDIO-4354: the plain workflow CSV — the same serialisation `upload()` sends
   * as `bot.csv` — for a version's download. An Agent Manager v2 archive is the
   * solution repo's branch, which carries no workflow file until publish, so the
   * download bundles this instead. Group header rows are stripped, as for the
   * compiler (PSTUDIO-2220). Returns `null` for a version with no rows. Throws on a
   * version-fetch transport error, like `buildGroupedCsvString`.
   */
  async buildPlainCsvString(
    solutionVersionId?: string
  ): Promise<string | null> {
    return toPlainCsv(await this.readRowsForCsv(solutionVersionId));
  }

  /**
   * Both download CSVs of one version from ONE read of its rows. Building them
   * separately fetched a non-current version's nodes twice, and two snapshots
   * could disagree if a write landed in between.
   */
  async buildDownloadCsvs(solutionVersionId: string): Promise<{
    workflowCsv: string | null;
    groupedCsv: string | null;
  }> {
    const rows = await this.readRowsForCsv(solutionVersionId);
    return { workflowCsv: toPlainCsv(rows), groupedCsv: toGroupedCsv(rows) };
  }

  /**
   * The rows a CSV export of `solutionVersionId` is built from: the in-memory rows
   * for the version on screen, otherwise that version's nodes fetched read-only.
   */
  private async readRowsForCsv(
    solutionVersionId?: string
  ): Promise<TableRow[]> {
    return this.fetchVersionRows(solutionVersionId);
  }

  undo() {
    if (this.liveEditingSource) {
      this._liveHistory('undo');
      return;
    }
    // PSTUDIO-3813: finalize (cancel) any in-progress cell edit BEFORE reverting,
    // so a stale/partial uncommitted draft can't be committed on the following
    // blur/click/reveal and clobber the undo. Cancel — not commit — because Undo
    // must not resurrect half-typed text; this mirrors PSTUDIO-2821's Esc=discard
    // semantics (there, Tab/Enter/click commit and Esc discards). Runs before the
    // canUndo guard so the active draft is discarded even when the stack is empty.
    if (hasActiveCellEdit(this)) this.cancelCellEditing();
    // PSTUDIO-3723: the undo/redo stack engine now lives in `this.history`
    // (UndoRedoManager) — it pops the command, drives command.undo(), and fires
    // onReveal → _revealCommandFocus. It no-ops on an empty stack, so guard the
    // recompile below on canUndo to skip a pointless validate on a no-op undo.
    if (!this.history.canUndo) return;
    // PSTUDIO-4023: a command's undo leg can remove rows from the rendered set
    // (the undo of an insert, the redo of a delete, a group snapshot swap), and
    // unmounting a row under a portal-hosted `ps-dropdown` is the `removeChild`
    // crash the scroll path already guards against. Every command reaches the
    // rows through here, so this ONE call covers the whole family — a per-
    // command call is the shape that left three entrances behind. Placed after
    // the canUndo guard: an undo that changes nothing must not raise a signal
    // that re-renders every observer reading the nonce.
    this.requestCloseAllContextMenus();
    const command = this.history.undo();
    this.recompileAfterHistoryChange(command, 'undo');
  }

  redo() {
    if (this.liveEditingSource) {
      this._liveHistory('redo');
      return;
    }
    // PSTUDIO-3813: see undo() — tear down the active cell edit before redo too.
    if (hasActiveCellEdit(this)) this.cancelCellEditing();
    // PSTUDIO-3723: stack engine in `this.history`; no-ops on an empty redo
    // stack, so guard the recompile on canRedo.
    if (!this.history.canRedo) return;
    // PSTUDIO-4023: see undo() — the redo leg reaches the same rendered-set
    // change, so it closes any open context menu first too.
    this.requestCloseAllContextMenus();
    const command = this.history.redo();
    this.recompileAfterHistoryChange(command, 'redo');
  }

  // PSTUDIO-3748 / PR #2637 review: after an undo/redo, force a real recompile
  // ONLY in Manual validation mode. In Manual, validate() short-circuits — a
  // bare validateAndSync() returns cached errors without recompiling — so a
  // stale on-screen error would linger even though the reverted/re-applied state
  // is valid; passing the explicit Manual mode bypasses that guard and clears
  // it. Intentionally immediate, NOT debouncedValidateAndSync (that helper calls
  // validateAndSync() with no mode, re-hitting the same short-circuit).
  //
  // In AUTO mode this is USUALLY a deliberate no-op: the undone/redone command's
  // own save path (updateRow{saveImmediately} → saveBulkRowsToDatabase) already
  // runs a full recompile, so a second explicit call here would just double the
  // backend round-trip and race `isValidating` into a spinner flicker.
  //
  // PSTUDIO-3845: the one exception is a command whose save path is
  // validation-free — the reorder command persists via the orders-only mutation
  // (saveRowOrdersToDatabase), which never calls validateAndSync. Such a command
  // marks itself `recompilesViaSavePath: false`, and in Auto mode we must force
  // the recompile here (a bare validateAndSync() — Auto never short-circuits, so
  // it runs a full recompile), else validation results go stale after a reorder
  // undo/redo. Commands without the flag keep the no-op fast path.
  //
  // A command whose two legs persist asymmetrically supplies a direction-aware
  // predicate instead of a static boolean; resolve it against the leg actually
  // being driven so only the validation-free leg forces the recompile (e.g.
  // insert-undo and cut+paste-move both legs — never the full-row-save legs).
  private recompileAfterHistoryChange(
    command: Command | undefined,
    direction: HistoryDirection
  ) {
    if (this.validationMode === ValidationMode.Manual) {
      this.validateAndSync(this.validationMode);
      return;
    }
    const flag = command?.recompilesViaSavePath;
    const recompilesViaSavePath =
      typeof flag === 'function' ? flag(direction) : flag;
    if (recompilesViaSavePath === false) {
      this.validateAndSync();
    }
  }

  // PSTUDIO-2154: after an undo/redo, scroll the affected cell into view and
  // select/focus it so the user sees what changed (Google-Sheets style).
  // No-ops when the row isn't currently visible — e.g. the row was removed by
  // the undo (an add being undone), or filtered out by an active search.
  private _revealCommandFocus(target?: {
    rowId: string;
    columnId?: NodeColumns;
  }) {
    if (!target) return;
    if (!this.getVisibleRows().some((r) => r.id === target.rowId)) return;
    const columnId =
      target.columnId && columnOrder.includes(target.columnId)
        ? target.columnId
        : NodeColumns.nodeNumber;
    this.scrollToAndFocusCellById(target.rowId, columnId);
  }

  get canUndo() {
    if (this.liveEditingSource) {
      return !this.isReadOnly && !!this.liveViewSettings?.canUndo;
    }
    return this.history.canUndo;
  }

  get canRedo() {
    if (this.liveEditingSource) {
      return !this.isReadOnly && !!this.liveViewSettings?.canRedo;
    }
    return this.history.canRedo;
  }

  /**
   * PSTUDIO-4451: undo/redo under live editing. The collab store reverts only
   * this user's own edits — a coworker's change in between stays — and hands
   * back where the change was, which is revealed the way a legacy command's
   * focus target is. Same teardown as the legacy path first: an open cell edit
   * is cancelled and context menus close, since rows can come and go.
   */
  private _liveHistory(direction: HistoryDirection) {
    if (hasActiveCellEdit(this)) this.cancelCellEditing();
    const store = this.liveViewSettings;
    const can = direction === 'undo' ? this.canUndo : this.canRedo;
    if (!store || !can) return;
    this.requestCloseAllContextMenus();
    const selection = direction === 'undo' ? store.undo() : store.redo();
    // PSTUDIO-3748: in Manual mode a bare validate returns cached errors, so
    // a stale error would outlive the state it was about — recompile, as the
    // legacy history does. (Auto validates through the store's 'local'
    // transaction.)
    if (this.validationMode === ValidationMode.Manual) {
      this.validateAndSync(this.validationMode);
    }
    if (!selection) return;
    // The feed brings the change into the rows on its own turn — possibly
    // behind a coworker's change still being applied. Reveal once the row is
    // there; give up quietly if it never is (an undone insert).
    const field = String(selection.field ?? '');
    const columnId = (
      field === 'cssClassName' ? NodeColumns.cssClassname : field
    ) as NodeColumns;
    when(
      () => this.getVisibleRows().some((r) => r.id === selection.nodeId),
      () => this._revealCommandFocus({ rowId: selection.nodeId, columnId }),
      { timeout: 2000, onError: () => {} }
    );
  }

  setRows(newRows: TableRow[]) {
    this.rows.replace(newRows);
    this.fuse.setCollection(newRows);
  }

  cleanupSolutionVersionSubscription = action((force = false) => {
    console.log(
      '[SolutionVersionSubscription] cleanupSolutionVersionSubscription called:',
      {
        force,
        isWaitingForBackendData: this.isWaitingForBackendData,
        hasUnsubscribe: !!this.solutionVersionSubscriptionUnsubscribe,
        solutionVersionId: this.solutionVersionId,
      }
    );
    if (this.isWaitingForBackendData && !force) {
      console.log(
        '[SolutionVersionSubscription] Skipping cleanup (waiting for backend and force=false)'
      );
      return;
    }

    if (this.solutionVersionSubscriptionUnsubscribe) {
      console.log(
        '[SolutionVersionSubscription] Cleaning up subscription',
        this.solutionVersionId
      );
      this.solutionVersionSubscriptionUnsubscribe();
      this.solutionVersionSubscriptionUnsubscribe = null;
      console.log('[SolutionVersionSubscription] Cleanup complete');
    }

    // PSTUDIO-4215: the flags used to be cleared INSIDE the branch above, so a
    // forced cleanup did nothing whenever the handle had already gone — and the
    // two are not written together: the flag is raised before the subscription
    // is opened (see the reaction), and the single handle slot is overwritten
    // by whichever fetch resolves last. A `force` means "this visit is over",
    // so the waiting state goes with it; publish is gated on exactly these two
    // (`publish-button.tsx`), which is how a stale flag kept the button behind
    // "We're still setting things up" until a reload.
    if (force) {
      this.isWaitingForBackendData = false;
      this.isWaitingForLiveCorpusIdOnly = false;
    }
  });

  processVersionData = action(
    async (
      versionData: {
        solution_version_nodes_ps?:
          | GetProStudioNodesQuery['tenancy_solution_version_nodes_ps']
          | null;
        columnWidths?: ColumnsWidths | null;
        columnPinningSettings?: Record<string, unknown> | null;
        columnWrapSettings?: Record<string, unknown> | null;
      } | null,
      options?: { skipValidation?: boolean; forSolutionVersionId?: string }
    ) => {
      // PSTUDIO-3953: a fetch is issued for one solution version and its
      // response applied whenever it lands — by then the store may point at a
      // different version (a publish bump, a solution switch). Callers pass the
      // identity the request was issued FOR; a payload that no longer belongs
      // is dropped rather than written over the live table, and the return
      // value reports which happened so a caller does not run follow-up work
      // (validation, a success toast, a subscription teardown) for data that
      // was never applied.
      //
      // EVERY production caller passes an identity — including the CSV import,
      // which reaches this through `reload-nodes`. The unasserted branch is for
      // a caller rebuilding the current table from purely local data; do not
      // read it as licence to omit the identity on a version-keyed fetch.
      if (!this.belongsToCurrentVersion(options?.forSolutionVersionId)) {
        return false;
      }

      if (!versionData) {
        return false;
      }

      const rawNodes = versionData.solution_version_nodes_ps;
      const { columnWidths, columnPinningSettings, columnWrapSettings } =
        versionData;

      this.columnsSizes = columnWidths ?? null;
      this.columnPinningSettings =
        (columnPinningSettings as ColumnPinningSettings) ?? null;
      // Mirror the columnPinningSettings load: pass the raw jsonb blob straight
      // to the setter, which no-ops on null and normalizes exactly once
      // (PSTUDIO-2870).
      this.columnWrapSettings =
        (columnWrapSettings as ColumnWrapSettings) ?? null;

      // PSTUDIO-4342: both branches below replace the rows wholesale (a re-entry,
      // the fetch after a version change, a non-silent reload, a no-nodes
      // version), so the refused-write records of the replaced rows go with
      // them, reported (see _failedNodeSaves). Before the replacement, so the
      // loss is counted against the rows it is about — and before
      // `normalizeInitialRows`, whose order repair of the NEW snapshot belongs
      // to the visit this load starts: dropped after it, the flush sent that
      // repair under the visit that was ending, and a refusal of it read as a
      // loss of edits the user never made (review round 12).
      this._dropRefusedWrites();

      let startingNodes: TableRow[] | null | undefined = null;
      if (rawNodes !== undefined && rawNodes !== null) {
        // Guard: deduplicate by UUID before normalization (should not happen in
        // normal operation, but defensive against double-subscription callbacks).
        const seenIds = new Set<string>();
        const deduplicatedRaw = (rawNodes as unknown as TableRow[]).filter(
          (n) => {
            if (seenIds.has(n.id)) {
              console.warn(
                '[processVersionData] Duplicate UUID in server response, skipping:',
                n.id
              );
              return false;
            }
            seenIds.add(n.id);
            return true;
          }
        );
        startingNodes = this.normalizeInitialRows(deduplicatedRaw);
      }

      if (startingNodes && startingNodes.length) {
        // If the user is actively editing a cell, preserve their in-progress
        // value so the subscription update does not erase what they are typing.
        // PSTUDIO-1433: the Expanded Cell View's open edit is preserved on the
        // same terms as an inline one — it sets no editing ids of its own, so
        // without this a subscription update erased what the user was typing in
        // the panel.
        const { rowId: liveEditRowId, columnId: liveEditColumnId } =
          this.activeEditCell();
        if (
          (this.isCellBeingEdited || this.panelEditingRowId != null) &&
          liveEditRowId &&
          liveEditColumnId
        ) {
          const currentValue = this.getValue(
            liveEditRowId,
            liveEditColumnId as keyof TableNode
          );
          const nodeIndex = startingNodes.findIndex(
            (n) => n.id === liveEditRowId
          );
          if (nodeIndex !== -1 && currentValue !== undefined) {
            startingNodes[nodeIndex] = {
              ...startingNodes[nodeIndex],
              [liveEditColumnId]: currentValue,
            };
          } else if (nodeIndex === -1 && this.panelEditingRowId) {
            // PSTUDIO-1433: the edited row is GONE from the incoming snapshot
            // (deleted upstream, or a different version was loaded). Unlike the
            // merge path this one does not clear the selection, so the panel
            // keeps the same rowId, its cleanup effect never fires, and the
            // registration would hang forever — leaving `hasActiveCellEdit`
            // true, which blocks every later cross-tab reload.
            this.endPanelEdit();
          }
        }

        // PSTUDIO-3332 (S2): drop any server/client drift in each group
        // header's subRows before it enters the store, so the count badge and
        // label load from canonical membership.
        this.reconcileIncomingGroupSubRows(startingNodes);

        // Rebuild transient subRow visibility from the persisted group state so
        // collapsed groups reload collapsed and the chevron matches its rows.
        this.applyGroupCollapseVisibility(startingNodes);

        this.fuse.setCollection(startingNodes);

        // PSTUDIO-4023: a full version reload replaces the whole row set, so
        // every currently rendered row unmounts — including one holding an open
        // context menu (this path also arrives unprompted, e.g. the
        // `live_corpus_id` provisioning update seconds after a solution is
        // created). Same guard the merge's remove phase and `clearTable` carry;
        // `endPanelEdit` above is the panel-session half of the same reasoning.
        this.requestCloseAllContextMenus();

        runInAction(() => {
          this.rows.replace(startingNodes);
          this.rowMap.replace(new Map(startingNodes.map((r) => [r.id, r])));
        });
        // PSTUDIO-4342: what the server holds now (see _serverKnownIds).
        for (const row of startingNodes) this._serverKnownIds.add(row.id);
        // this.rows.replace(startingNodes.slice(0, 200) || []);

        // if (startingNodes.length > 50) {
        //   this.pendingRowsToRender = startingNodes.slice(50);

        //   this.hasPendingRowsToRender = this.pendingRowsToRender.length > 0;
        // }

        // Only clear isWaitingForBackendData when not waiting for live_corpus_id;
        // otherwise cleanup(false) would run and close the subscription prematurely.
        if (!this.isWaitingForLiveCorpusIdOnly) {
          this.isWaitingForBackendData = false;
        }
        this.setState(this.isViewer ? 'viewing' : 'editing');

        // Project Bobcat: the solution is now in MobX, so this is the point at
        // which its shape can be inspected. DETECTION ONLY — this deliberately
        // does not write. Applying the grouping is an explicit user action; see
        // `applyAutoAgentGrouping`.
        this.detectAutoAgentGrouping();
        // Same discipline, for the onboarding use-case stub nodes; see
        // `applyUseCaseAgentGrouping`.
        this.detectUseCaseAgentNumbering();

        // Project Bobcat: "Create a blank solution" — the backend's standard
        // rows go, so it opens as an empty canvas. See `pendingEmptySolution`.
        if (this.tryConsumePendingEmptySolution()) {
          this.applyEmptySolution();
        }

        // Project Bobcat: a solution created with the knowledge base skipped
        // loses its Knowledge Agent's RAG lookup here, before anything groups
        // the Knowledge Agent's rows. See `pendingSkipKnowledgeCorpus`.
        if (this.tryConsumePendingSkipKnowledgeCorpus()) {
          await this.applySkipKnowledgeCorpus();
        }

        // Project Bobcat: the conversational Create Solution flow marks this
        // right after creating a solution — this is the first moment its
        // rows exist to group. MUST run before the use-case grouping below:
        // `applyAutoAgentGrouping` declines once the solution has ANY
        // grouping, so applying use-case stubs first would silently starve
        // Supervisor/Knowledge of their turn. See `pendingAutoAgentGrouping`'s
        // comment.
        if (this.tryConsumePendingAutoAgentGrouping()) {
          this.applyAutoAgentGrouping();
        }

        // Project Bobcat: the conversational Create Solution flow marks this
        // right after creating a solution — this is the first moment its
        // rows exist to group. See `pendingUseCaseAgentGrouping`'s comment.
        if (this.tryConsumePendingUseCaseAgentGrouping()) {
          this.applyUseCaseAgentGrouping();
        }

        // Project Bobcat: the use-case groups exist now, so their one-node
        // stubs can be built into real agents. Not awaited: it waits for the
        // first auto-publish to finish, and AI drafts take a while.
        const agentDrafts = this.tryConsumePendingAgentDrafts();
        if (agentDrafts) {
          void this.buildAgentsAfterCreate(agentDrafts);
        }
      } else {
        if (!this.isWaitingForLiveCorpusIdOnly) {
          this.isWaitingForBackendData = false;
        }
        // New solution has no nodes — make sure we don't keep showing
        // rows from the previous solution.
        runInAction(() => {
          this.rows.replace([]);
          this.rowMap.clear();
          this.fuse.setCollection([]);
        });
        this.setState(this.isViewer ? 'viewing' : 'editing');
      }

      await new Promise((resolve) => setTimeout(resolve, 0));
      if (!options?.skipValidation) {
        this.validateAndSync();
      }
      return true;
    }
  );

  // PSTUDIO-3625: incremental sibling of processVersionData for the silent
  // cross-tab reload path. processVersionData does `this.rows.replace(...)`,
  // which flushes the entire observable array — the top-level `observer` in
  // <Table/> re-renders every row and react-virtual resets the scroll window.
  // For a cross-tab reload that (usually) changed only a handful of nodes that
  // full churn also drops scroll position and any transient focus.
  //
  // mergeVersionData instead diffs the incoming (already-normalized) rows
  // against `this.rows` by `id` and applies the minimum mutation:
  //   - removed ids      -> spliced out of rows + rowMap + selection + fuse
  //   - fields that drift -> mutated IN PLACE on the existing observable row
  //                          (same object reference, so React does not treat
  //                          the row as new) via mobx `set`
  //   - new ids          -> inserted at the order-sorted position
  // In the pure-update case the array reference AND length are unchanged, so
  // the top-level observer does not re-render and scroll/selection survive.
  //
  // The input shape, null/empty guard, columnsSizes/columnPinningSettings
  // assignment, UUID dedup, in-progress-cell-edit guard,
  // isWaitingForBackendData/isWaitingForLiveCorpusIdOnly handling, setState,
  // and the trailing validateAndSync all MIRROR processVersionData so the two
  // paths converge on the same store state.
  mergeVersionData = action(
    async (
      versionData: {
        solution_version_nodes_ps?:
          | GetProStudioNodesQuery['tenancy_solution_version_nodes_ps']
          | null;
        columnWidths?: ColumnsWidths | null;
        columnPinningSettings?: Record<string, unknown> | null;
        columnWrapSettings?: Record<string, unknown> | null;
      } | null,
      options?: {
        skipValidation?: boolean;
        resetHistoryOnRemoteChange?: boolean;
        forSolutionVersionId?: string;
      }
    ) => {
      // PSTUDIO-3953: same identity guard as processVersionData. It matters
      // more here, not less: this path's "Removes" phase deletes every live row
      // absent from the incoming snapshot, so a late payload from another
      // version would wipe the table rather than merely replace it.
      if (!this.belongsToCurrentVersion(options?.forSolutionVersionId)) {
        return false;
      }

      if (!versionData) {
        return false;
      }

      const rawNodes = versionData.solution_version_nodes_ps;
      const { columnWidths, columnPinningSettings, columnWrapSettings } =
        versionData;

      // Guard the column writes (PSTUDIO-3625): these observables feed the
      // TanStack column model in <Table/> (columns useMemo + useReactTable
      // state). The `columnsSizes`/`columnPinningSettings` SETTERS rebuild the
      // whole underlying observable.object wholesale (`{ ...defaults, ...data }`
      // reassigned to `_columnWidths` / `_columnPinningSettings`), so calling
      // them on EVERY merge — even with a fresh object holding identical values
      // — churns every column key and forces a full-table re-render. Compute
      // what the setter WOULD store and assign only when it differs from the
      // current value, so a pure-update cross-tab reload does not touch them.
      // (null is a no-op in the setters, matching processVersionData; isEqual +
      // toJS are already imported and used throughout this store — toJS makes
      // the observable comparable to the plain incoming object.)
      if (columnWidths) {
        const nextColumnWidths = { ...defaultColumnWidths, ...columnWidths };
        if (!isEqual(toJS(this.columnsSizes), nextColumnWidths)) {
          this.columnsSizes = columnWidths;
        }
      }
      if (columnPinningSettings) {
        const nextColumnPinning = {
          ...defaultColumnPinningSettings,
          ...(columnPinningSettings as ColumnPinningSettings),
        };
        if (!isEqual(toJS(this.columnPinningSettings), nextColumnPinning)) {
          this.columnPinningSettings =
            columnPinningSettings as ColumnPinningSettings;
        }
      }
      if (columnWrapSettings) {
        // nextWrap is the resolved shape used ONLY for the isEqual comparison;
        // the setter re-normalizes, so assign the raw blob (mirrors the
        // columnPinningSettings branch above — PSTUDIO-2870).
        const nextWrap = resolveColumnWrapSettings(columnWrapSettings);
        if (!isEqual(toJS(this.columnWrapSettings), nextWrap)) {
          this.columnWrapSettings = columnWrapSettings as ColumnWrapSettings;
        }
      }

      let incoming: TableRow[] = [];
      if (rawNodes !== undefined && rawNodes !== null) {
        // Guard: deduplicate by UUID before normalization (defensive against
        // double-subscription callbacks) — same as processVersionData.
        const seenIds = new Set<string>();
        const deduplicatedRaw = (rawNodes as unknown as TableRow[]).filter(
          (n) => {
            if (seenIds.has(n.id)) {
              console.warn(
                '[mergeVersionData] Duplicate UUID in server response, skipping:',
                n.id
              );
              return false;
            }
            seenIds.add(n.id);
            return true;
          }
        );
        incoming = this.normalizeInitialRows(deduplicatedRaw);
      }

      const incomingById = new Map<string, TableRow>(
        incoming.map((r) => [r.id, r])
      );

      runInAction(() => {
        // PSTUDIO-4342: every id this snapshot carries is one the server holds
        // — including a row a coworker added, which reaches this tab through
        // the Adds below and never through a snapshot or a landed chunk of
        // ours. Read by the NEXT merge's Removes (see _serverKnownIds).
        for (const id of incomingById.keys()) this._serverKnownIds.add(id);
        // PSTUDIO-3929 (a): rows with an uncommitted local write (queued OR in
        // flight, content- and order-save tracks). Their LOCAL state is the
        // source of truth until the save lands, so a coworker push — which
        // still carries the PRE-write server state for them — must not touch
        // them in ANY phase: not delete them (Removes), not overwrite their
        // fields (Updates), not move them by a stale server order (Re-sort).
        // Computed once here and consulted by all three phases below.
        const pendingRowIds = this.pendingSaveRowIds();
        // PSTUDIO-4342: rows whose save was REFUSED and not repaired keep their
        // local fields and position (Updates, Re-sort) so the publish re-send
        // writes the user's value; in Removes, only a refused row the server
        // had KNOWN goes (a coworker deleted it) — a refused insert the server
        // never had stays. See _failedNodeSaves.
        const refusedRowIds = this._refusedLiveRowIds();
        const keepsLocalState = (id: string) =>
          pendingRowIds.has(id) || refusedRowIds.has(id);
        // Only a refused CONTENT save shields the row's fields: an order
        // refusal re-sends `{id, order}` alone, so shielding the fields for it
        // kept a coworker's edit off screen and the next full upsert of the
        // row wrote the stale value back over theirs (review round 11). Its
        // POSITION is still the user's: Updates leaves its `order` alone and
        // Re-sort reads keepsLocalState (review round 12 — applying `order`
        // with the other fields put the row back where the server had it, and
        // the publish then re-sent the server's order as the repair).
        const keepsLocalFields = (id: string) =>
          pendingRowIds.has(id) ||
          (refusedRowIds.has(id) && this._failedNodeSaves.has(id));

        // PSTUDIO-3930 (Option A): did this merge ACTUALLY change the live table
        // (a remove, an in-place field update, an add, or a reorder)? Set by the
        // phases below; a pure no-op merge leaves it false. Used only to decide
        // whether a teammate's change invalidates the undo history (see the end
        // of this action) — never to gate the merge work itself.
        let mutated = false;

        // --- 1. Removes: live ids no longer present upstream. ---
        const removedIds: string[] = [];
        for (const row of this.rows) {
          // Never remove a row with a pending local save: a just-added node
          // whose insert is still queued/in flight is absent from the server
          // snapshot, so removing it here would vanish it until the save lands
          // (the non-collision sibling of the Updates clobber).
          if (!incomingById.has(row.id) && !pendingRowIds.has(row.id)) {
            if (
              refusedRowIds.has(row.id) &&
              !this._serverKnownIds.has(row.id)
            ) {
              continue;
            }
            removedIds.push(row.id);
          }
        }
        if (removedIds.length) {
          mutated = true;
          const removedSet = new Set(removedIds);
          // A refused row a coworker deleted: re-sending it would INSERT it
          // again, so its record goes with it — a loss, and reported as one.
          const deletedByCoworker: string[] = [];
          for (const id of removedIds) {
            this._serverKnownIds.delete(id);
            if (!refusedRowIds.has(id)) continue;
            deletedByCoworker.push(id);
            this._failedNodeSaves.delete(id);
            this._failedNodeOrderSaves.delete(id);
          }
          if (deletedByCoworker.length) {
            this._reportRefusedWritesLost({ coworker: deletedByCoworker });
          }
          // PSTUDIO-1433: a REMOTE delete reaches the same state a local one
          // does, and only the local path (`_deleteRowsInternal`) tore the
          // panel session down. Leaving it registered means the panel's flush
          // later writes a row the server no longer has, and — because
          // `hasActiveCellEdit` stays true — the cross-tab reload is blocked
          // for the rest of the session. Structural writes to `rows` bypass
          // `updateRow`, which is exactly why this path needed saying out loud
          // (PSTUDIO-3929 / PSTUDIO-3930).
          if (
            this.panelEditingRowId &&
            removedSet.has(this.panelEditingRowId)
          ) {
            this.endPanelEdit();
          }
          // Same for an OPEN context menu on a row that is about to unmount:
          // its `ps-dropdown` has projected itself into the popover portal, and
          // unmounting the row under it is the `removeChild` crash the scroll
          // path already guards against. No user gesture here at all.
          this.requestCloseAllContextMenus();
          // Splice out in place — keep the array reference; a filtered replace
          // would still count as a length change (expected when rows leave).
          const survivors = this.rows.filter((r) => !removedSet.has(r.id));
          this.rows.replace(survivors);
          for (const id of removedIds) {
            this.rowMap.delete(id);
            this.selectedRows.delete(id);
            this.fuse.remove((doc) => doc.id === id);
          }
          // PSTUDIO-4342: a removed row's refusal has nothing left to repair.
          if (deletedByCoworker.length) this._reconcileNodeWriteToasts();
          if (this.selectedCells.length) {
            const keptCells = this.selectedCells.filter(
              (c) => !removedSet.has(c.rowId)
            );
            if (keptCells.length !== this.selectedCells.length) {
              this.selectedCells.replace(keptCells);
            }
          }
        }

        // --- 2. Updates: rows present in both — mutate fields IN PLACE. ---
        // The actively-edited cell keeps its local draft (mirror the guard in
        // processVersionData): skip ONLY the editing row+column, still merge
        // every other field the server changed. Mutating `this.rows[i]` via
        // mobx `set` preserves the array element's object identity — react-table
        // does not see a "new" row, so it does not re-render it.
        // PSTUDIO-1433: the panel's open edit counts too. It sets neither
        // `editingRowId`/`editingColumnId` nor (while suppressed)
        // `isCellBeingEdited`, so a merge used to overwrite the cell the user
        // was typing in — and the panel's flush then wrote the coworker's value
        // back as the user's own edit, making Undo revert THEIR change.
        const { rowId: editingRowId, columnId: editingColumnId } =
          this.activeEditCell();
        const isEditing =
          (this.isCellBeingEdited || this.panelEditingRowId != null) &&
          !!editingRowId &&
          !!editingColumnId;
        for (let i = 0; i < this.rows.length; i++) {
          const existing = this.rows[i];
          const next = incomingById.get(existing.id);
          if (!next) continue;
          // Skip the WHOLE pending row: our queued/in-flight save is the source
          // of truth for it and carries every field via a full-row upsert. A
          // coworker's edit to a DIFFERENT cell of this same row in this window
          // is therefore dropped (row-level last-write-wins), not merged — the
          // usual consequence of full-row upserts, widened only for this window.
          if (keepsLocalFields(existing.id)) continue;
          const keepsLocalOrder = keepsLocalState(existing.id);
          let changed = false;
          for (const key of Object.keys(next) as (keyof TableRow)[]) {
            if (keepsLocalOrder && key === 'order') continue;
            if (
              isEditing &&
              existing.id === editingRowId &&
              (key as string) === editingColumnId
            ) {
              continue;
            }
            if (!isEqual(existing[key], next[key])) {
              // `set` keeps `this.rows[i]` the SAME reference while making the
              // changed key observable — the whole point of this method.
              set(this.rows[i], key, next[key]);
              changed = true;
            }
          }
          if (changed) {
            mutated = true;
            // Refresh fuse for changed rows so search stays correct.
            this.fuse.remove((doc) => doc.id === existing.id);
            this.fuse.add(existing);
          }
        }

        // --- 3. Adds: incoming ids not currently present. ---
        for (const inc of incoming) {
          if (this.rowMap.has(inc.id)) continue;
          const order = inc.order ?? 0;
          // Insert at the position that keeps `this.rows` ascending by order.
          let insertAt = this.rows.length;
          for (let i = 0; i < this.rows.length; i++) {
            if ((this.rows[i].order ?? 0) > order) {
              insertAt = i;
              break;
            }
          }
          this.rows.splice(insertAt, 0, inc);
          this.rowMap.set(inc.id, inc);
          this.fuse.add(inc);
          mutated = true;
        }

        // --- 3b. A kept refused row does not keep a dangling group link. ---
        // Group membership is a relation of TWO rows: the member's
        // `settings.groupId` and the header's `subRows`. The fields of a
        // refused row are kept for the publish to re-send, but when a merge
        // has removed the header its `groupId` names, keeping that link would
        // show a member of nothing and re-send it into the database (review
        // round 11). It takes the server's link instead — `null` when the
        // server no longer has the row either.
        const presentIds = refusedRowIds.size
          ? new Set(this.rows.map((row) => row.id))
          : null;
        for (const row of presentIds ? this.rows : []) {
          if (!keepsLocalFields(row.id) || pendingRowIds.has(row.id)) continue;
          const settings = row.settings as
            | { groupId?: string | null }
            | null
            | undefined;
          const groupId = settings?.groupId;
          if (!groupId || presentIds!.has(groupId)) continue;
          const incomingSettings = incomingById.get(row.id)?.settings as
            | { groupId?: string | null }
            | null
            | undefined;
          set(row, 'settings', {
            ...settings,
            groupId: incomingSettings?.groupId ?? null,
          });
          mutated = true;
        }

        // --- 4. Re-sort in place if order drifted. ---
        // Order each LIVE row by its EFFECTIVE order: the incoming (server)
        // order normally, but a pending row's LOCAL order (PSTUDIO-3929 a) —
        // otherwise a stale incoming order would drag a just-reordered row back
        // to its pre-drag position (field guarded in Updates, but the array
        // position is decided HERE), and a pending just-added row absent from
        // incoming would sort to the front. Non-pending rows keep the exact
        // prior behavior (incoming order when present), so a pure-update merge
        // still computes the identical sequence and never touches the array.
        const effectiveOrderOf = (row: TableRow): number => {
          if (keepsLocalState(row.id)) return row.order ?? 0;
          const inc = incomingById.get(row.id);
          return inc?.order ?? row.order ?? 0;
        };
        const desiredSequence = this.rows
          .slice()
          .sort((a, b) => effectiveOrderOf(a) - effectiveOrderOf(b));
        const desiredIndex = new Map(desiredSequence.map((r, i) => [r.id, i]));
        let outOfOrder = false;
        for (let i = 1; i < this.rows.length; i++) {
          const prev = desiredIndex.get(this.rows[i - 1].id) ?? -1;
          const curr = desiredIndex.get(this.rows[i].id) ?? -1;
          if (prev > curr) {
            outOfOrder = true;
            break;
          }
        }
        if (outOfOrder) {
          this.rows.replace(desiredSequence);
          mutated = true;
        }

        // --- 5. Post-merge group reconciliation on the LIVE rows. ---
        // Same two passes processVersionData runs before its replace, but
        // WRITE-MINIMAL (PSTUDIO-3625). The old code unconditionally reset every
        // row's `hidden` to false and then let applyGroupCollapseVisibility flip
        // it back on — a true→false→true churn that re-fired the Table's per-row
        // `hidden` observer on EVERY merge, even a pure update. That reset is now
        // folded into applyGroupCollapseVisibility({ onlyIfChanged: true }):
        // it computes each row's DESIRED hidden (true iff in a collapsed group,
        // false otherwise — the same net effect as reset-then-hide) and only
        // `set`s when the current value differs, so an unchanged row emits zero
        // mutations. reconcileIncomingGroupSubRows already guards its
        // settings.subRows write with isEqual, so it too is a no-op on a
        // pure-update merge.
        const liveRows = this.rows.slice();
        this.reconcileIncomingGroupSubRows(liveRows);
        this.applyGroupCollapseVisibility(liveRows, { onlyIfChanged: true });

        // --- 5b. Re-point rowMap at the live rows. ---
        // getValue()/getCellValue() read from rowMap while getRowById()/
        // sortedRows read from rows — they must agree. The update pass mutated
        // the rows objects and the two group helpers just rewrote settings /
        // hidden on those same rows, so rebuild rowMap from the (same) rows
        // references. This replaces the map's contents but NOT the rows array,
        // so the top-level observer over rows still does not re-render.
        this.rowMap.replace(new Map(this.rows.map((r) => [r.id, r])));

        // --- 6. Waiting/state bookkeeping — mirror processVersionData. ---
        if (!this.isWaitingForLiveCorpusIdOnly) {
          this.isWaitingForBackendData = false;
        }
        this.setState(this.isViewer ? 'viewing' : 'editing');

        // --- 7. Option A (PSTUDIO-3930): invalidate undo history on a teammate
        // change that actually mutated the table. ---
        // The undo/redo commands were recorded against the PRE-merge row set;
        // once a teammate's live edit changes the live table, replaying them
        // would either no-op, overwrite the teammate's fresh value, or resurrect
        // a row they deleted (server desync). Clean slate: drop the history so
        // Undo greys out (canUndo is reactive) and the user only undoes their own
        // subsequent actions. Scoped to the coworker-subscription path via
        // `resetHistoryOnRemoteChange`; a same-user cross-tab reload
        // (reload-nodes.ts) does NOT pass the flag, so it keeps history. Gated on
        // `mutated` so an idle subscription tick (a teammate just moving their
        // cursor, no data change) never wipes the user's undo.
        if (mutated && options?.resetHistoryOnRemoteChange) {
          this.clearHistory();
        }
      });

      await new Promise((resolve) => setTimeout(resolve, 0));
      if (!options?.skipValidation) {
        this.validateAndSync();
      }
      return true;
    }
  );

  cleanupCoworkerFocusSubscription = action(() => {
    if (this.coworkerFocusSubscriptionUnsubscribe) {
      this.coworkerFocusSubscriptionUnsubscribe();
      this.coworkerFocusSubscriptionUnsubscribe = null;
    }
  });

  // State management methods
  setState(newState: TableState) {
    if (
      this.draftVersion === undefined ||
      this.draftVersion === '' ||
      this.draftVersion === null ||
      !this.solutionVersionId
    ) {
      this.currentState = 'viewing';
    } else if (
      this.liveEditingSource &&
      this.isReadOnly &&
      newState !== 'loading'
    ) {
      // PSTUDIO-4450/4451: read-only while the document refuses this user's
      // writes (or before it has synced).
      this.currentState = 'viewing';
    } else {
      this.currentState = newState;

      // not needed if we already auto-validate when the app first loads
      // if (newState === 'editing') {
      //   this.validateAndSync();
      // }
    }
  }

  isEditing = computedFn(() => {
    return this.currentState === 'editing';
  });

  isAdding = computedFn(() => {
    return this.currentState === 'adding';
  });

  get nodeCounts() {
    // Group rows are bookkeeping for the collapse UI, not real nodes. The
    // `hidden` flag flips on group collapse — that's a visual state, not a
    // deletion — so the count must include hidden children too. Filter
    // strictly on nodeType, not on visibility.
    const rows = this.sortedRows.filter(
      (row) => row.nodeType !== Tenancy_Node_Type_Enum_Ps_Enum.G
    );

    const decisionNodes = rows.filter(
      (row) => row.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.D
    ).length;
    const actionNodes = rows.filter(
      (row) => row.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.A
    ).length;
    const totalNodes = rows.length;

    return {
      decisionNodes,
      actionNodes,
      totalNodes,
    };
  }

  // Data management methods
  getValue = computedFn((rowId: string, columnId: keyof TableNode) => {
    const row = this.rowMap.get(rowId);
    return row
      ? (row[columnId] ?? getDefaultValueForColumn(columnId))
      : undefined;
  });

  getCellValue = computedFn((rowId: string, columnId: keyof TableNode) => {
    return this.getValue(rowId, columnId);
  });

  async validateAndSync(validationMode?: ValidationMode) {
    const { customerId, botName, draftVersion, project } =
      proStudioService.getSnapshot().context;

    const projectId = project?.id;

    if (!projectId) {
      // throwErrorWithId('Project ID is required');
      console.error('Project ID is required');
      return;
    }

    if (!customerId) {
      // throwErrorWithId('Customer ID is required');
      console.error('Customer ID is required');
      return;
    }

    if (!botName) {
      // throwErrorWithId('Bot Name is required');
      console.error('Bot Name is required');
      return;
    }

    if (!draftVersion) {
      // throwErrorWithId('Draft Version is required');
      console.error('Draft Version is required');
      return;
    }

    const validationId = ++this._latestValidationId;
    runInAction(() => {
      this.isValidating = true;
    });

    try {
      await this.upload({
        customerId,
        botName,
        draftVersion,
        projectId,
      });

      // Check if solution changed during upload
      const currentContext = proStudioService.getSnapshot().context;
      if (currentContext.botName !== botName) {
        console.log(
          '[TableStore] Solution changed during upload. Skipping validation.'
        );
        return;
      }

      // Sync uploaded script file names so the compiler can detect missing scripts
      const { draft } = proStudioService.getSnapshot().context;
      const scriptFileNames = buildScriptFileNames(
        draft?.files ?? [],
        this.actionNodeNames
      );
      this.pifPartialValidationService.updateScriptFileNames(scriptFileNames);

      // if (this.needsServerValidation) {
      const { errors, didPerformValidation } = await this.validate({
        projectId,
        customerId,
        botName,
        draftVersion,
        validationMode,
      });

      if (errors) {
        // console.log('onError', errors);
      }

      if (didPerformValidation) {
        runInAction(() => {
          this.hasUnvalidatedChangesInManualMode = false;
        });
      }
    } catch (error) {
      console.error('[TableStore] validateAndSync failed:', error);
      // PSTUDIO-4120: this catch sits ABOVE `validate()`, so it swallows what
      // throws BEFORE the pass ever starts — `upload()` builds the CSV from the
      // same rows (`json2csvParser.parse`), and PSTUDIO-4081 is precisely a value
      // in those rows killing the whole run. Labelling only `validate()`'s own
      // catch left this door open: the pass produced no verdict, the flag stayed
      // null, and the badge went on showing a clean tick over an unchecked
      // solution — the exact fail-open this ticket closes, one frame out.
      // Same identity check as `validate()`'s own catch: `upload()` is awaited,
      // and a solution switch during that await must not leave its failure on
      // the solution the user moved to.
      if (proStudioService.getSnapshot().context.botName === botName) {
        this.setValidationFailure('threw');
      }
    } finally {
      // Always reset isValidating, even if validation was aborted or errored —
      // unless a newer pass has started since, which owns it (PSTUDIO-4242).
      if (validationId === this._latestValidationId) {
        runInAction(() => {
          this.isValidating = false;
        });
      }
    }
  }

  // Data management methods
  updateRow = action(
    ({
      rowId,
      updates,
      skipValidationRules,
      saveImmediately,
    }: {
      rowId: string;
      updates: Partial<TableRow>;
      skipValidationRules?: boolean;
      saveImmediately?: boolean;
    }) => {
      const row = this.rows.find((r) => r.id === rowId);
      const rowIndex = this.rows.findIndex((r) => r.id === rowId);
      if (!row) return;

      const actualChanges: Partial<TableRow> = {};

      // runInAction(() => {
      objectEntries(updates).forEach(([key, value]) => {
        if (value !== row[key]) {
          if (
            key !== '__typename' &&
            key !== NodeColumns.id &&
            // throws Type 'any' is not assignable to type 'never' without this...
            key !== NodeColumns.answerRequired &&
            key !== NodeColumns.solutionVersionId
          ) {
            actualChanges[key] = value;
            set(this.rows[rowIndex], {
              [key]: value,
            });
          } else if (key === NodeColumns.answerRequired) {
            actualChanges[key] = value;

            set(this.rows[rowIndex], {
              [key]: value,
            });
          }

          if (
            skipValidationRules !== true &&
            Object.keys(actualChanges).length !== 0
          ) {
            this.applyValidationRules(rowId, key, value);
          }
        }
      });

      // `skipValidationRules: true` is the contract for "this updateRow is
      // bookkeeping, not a user-authored edit" — paste/cut shuffles, group
      // membership reconciliation, collapse/expand visibility, drag reordering
      // etc. None of those should re-arm the unvalidated-changes banner, so the
      // flag flip is gated on the same flag the per-row validator already
      // respects. Symptom that motivated the gate (PSTUDIO-3063 #1): collapsing
      // or expanding a group made the banner reappear because the children's
      // `hidden` flips and the group's `groupExpanded` flip ran through
      // updateRow and dirtied the flag.
      if (
        skipValidationRules !== true &&
        this.validationMode === ValidationMode.Manual &&
        Object.keys(actualChanges).length > 0
      ) {
        this.hasUnvalidatedChangesInManualMode = true;
      }

      if (Object.keys(actualChanges).length !== 0) {
        const updatedRow = {
          ...row,
          ...updates,
        };

        this.fuse.remove((doc) => doc.id === rowId);
        this.fuse.add(updatedRow);
        set(this.rowMap, rowId, updatedRow);

        if (saveImmediately) {
          this.saveRowsToDatabase([updatedRow], {
            skipValidation: skipValidationRules === true,
          });
        }
        // If not saving immediately, the caller will handle the save on blur
      }
    }
  );
  /* eslint-enable max-params */

  // PSTUDIO-2154: run a mutation that changes one row (dropdowns, selects,
  // checkboxes, type changes — anything that may touch several fields at once)
  // and register it as a SINGLE undo/redo command. Snapshots the row before and
  // after `fn` runs, diffs the changed keys, and pushes a command that restores
  // them. This is the hook for atomic cell commits — including the inline
  // free-text inputs in cell.tsx, which commit on explicit commit (Enter/Tab/
  // click-away) via commitCellEditing -> `writeCellValue` (it routes through
  // here). The standalone editor components
  // (editable-table-cell-input/textarea) and the formula bar use commitCellEdit
  // instead (snapshot at focus, command on blur). No-op if the row vanished or
  // nothing changed.
  /* eslint-disable max-params */
  recordRowEdit = action(
    (
      rowId: string,
      description: string,
      fn: () => void,
      // The column the user actually interacted with — revealed on undo/redo.
      // Callers MUST pass this; a change can touch several fields (e.g. an asset
      // type change also seeds richAssetContent), so we can't reliably infer the
      // user's column from the changed-key set (it would land on whatever column
      // happens to sort first, e.g. answerRequired).
      focusColumn?: NodeColumns
    ) => {
      const rowBefore = this.rows.find((r) => r.id === rowId);
      if (!rowBefore) {
        fn();
        return;
      }
      const before = toJS(rowBefore) as object as Record<string, unknown>;

      fn();

      const rowAfter = this.rows.find((r) => r.id === rowId);
      if (!rowAfter) return;
      const after = toJS(rowAfter) as object as Record<string, unknown>;

      const changedKeys = Array.from(
        new Set([...Object.keys(before), ...Object.keys(after)])
      ).filter(
        (key) =>
          key !== '__typename' &&
          key !== NodeColumns.id &&
          key !== NodeColumns.solutionVersionId &&
          !isEqual(before[key], after[key])
      );

      if (changedKeys.length === 0) return;

      const beforeVals: Record<string, unknown> = {};
      const afterVals: Record<string, unknown> = {};
      changedKeys.forEach((key) => {
        beforeVals[key] = before[key];
        afterVals[key] = after[key];
      });

      // Reveal the column the user interacted with; fall back to the first real
      // changed column (skip JSON `${col}Map` siblings and non-column keys).
      const revealColumn =
        focusColumn && columnOrder.includes(focusColumn)
          ? focusColumn
          : (changedKeys.find((key) =>
              columnOrder.includes(key as NodeColumns)
            ) as NodeColumns | undefined);

      this.pushCommandWithoutExecute({
        execute: () =>
          this.updateRow({
            rowId,
            updates: afterVals as Partial<TableRow>,
            skipValidationRules: false,
            saveImmediately: true,
          }),
        undo: () =>
          this.updateRow({
            rowId,
            updates: beforeVals as Partial<TableRow>,
            skipValidationRules: false,
            saveImmediately: true,
          }),
        description,
        focusTarget: { rowId, columnId: revealColumn },
      });
    }
  );
  /* eslint-enable max-params */

  // PSTUDIO-2154: register ONE undo/redo command per committed user cell edit.
  // Called from the cell editors' blur handler only when the value actually
  // changed since focus. The new value is already live in the store (keystrokes
  // wrote it via updateRow) and is persisted by the caller's normal save path,
  // so this only records the before/after for the affected keys and registers
  // the command — it does not re-execute on registration. `before` is the
  // snapshot captured at focus (the column value plus its JSON `${columnId}Map`
  // sibling, if any); `after` is read from the store now. undo()/redo() flip
  // between them and own the save for those directions.
  commitCellEdit = action(
    ({
      rowId,
      columnId,
      before,
    }: {
      rowId: string;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      columnId: keyof TableRow | any;
      // The affected keys captured at focus (column value plus any JSON
      // `${columnId}Map` sibling). Kept as a loose record so the cell editors
      // (whose columnId prop is `any`) can pass their snapshot directly.
      before: Record<string, unknown>;
    }) => {
      const row = this.rows.find((r) => r.id === rowId);
      if (!row) return;

      const rowRecord = row as unknown as Record<string, unknown>;
      const after: Record<string, unknown> = {};
      Object.keys(before).forEach((key) => {
        after[key] = toJS(rowRecord[key]);
      });

      // No real change for the tracked keys — nothing to record (defensive;
      // the caller already gates on the column value changing).
      if (isEqual(before, after)) return;

      const applyValues = (values: Record<string, unknown>) => {
        this.updateRow({
          rowId,
          updates: values as Partial<TableRow>,
          skipValidationRules: false,
          saveImmediately: true,
        });
        this.clearCellValidationError(
          rowId,
          columnId as keyof typeof NodeColumns
        );
      };

      this.pushCommandWithoutExecute({
        execute: () => applyValues(after),
        undo: () => applyValues(before),
        description: `Edit ${String(columnId)} on row ${rowId}`,
        focusTarget: { rowId, columnId: columnId as NodeColumns },
      });
    }
  );

  private _deleteRowsInternal(
    rowIds: string[],
    onAutoAdd?: (row: TableRow) => void
  ): Promise<void> {
    // PSTUDIO-4103: pin the identity where the work is SCHEDULED, not where it
    // runs. The DB delete below is deferred into `requestIdleCallback` and used
    // to read the mutable `this.solutionVersionId` from inside that callback —
    // so a solution switch between the gesture and the idle tick sent solution
    // A's row ids under version B: the delete matched nothing, the rows stayed
    // alive in A and came back on the next load. Same fix, same reasoning as
    // `committedFor` in saveBulkRowsToDatabase / saveBulkRowOrdersToDatabase;
    // this is the third write track of the same defect (PSTUDIO-3953).
    //
    // Pinned to the FULL id, version included, and that half is a deliberate
    // open question rather than a settled one. The mutation filters on
    // `solutionVersionId`, and a publish bump re-homes the rows onto the new
    // version (PSTUDIO-3669), so a delete parked across a bump now issues
    // against the version it was made in — where the row no longer is. The
    // setter's drain does not cover that case either: it is scoped to a
    // SOLUTION change on purpose. Before this ticket the idle callback read the
    // live id and happened to land on the bump while missing the switch; this
    // change trades one for the other, and the switch is the one the ticket is
    // about. Named as a follow-up (pin by `solutionScopeOf` and let the bump
    // re-home, mirroring the two save tracks) instead of widened into it — the
    // backend's behaviour on a bumped-then-deleted node is unverified here.
    const deletingFor = this.solutionVersionId;

    // Runs the deferred body ahead of the browser (see waitForPendingSaves).
    // Assigned synchronously inside the executor below — a Promise executor
    // runs synchronously — so it is set before this method returns. Optional
    // rather than definite-assigned: if the executor throws before the
    // assignment, `waitForPendingSaves` must skip this flush, not die on a
    // TypeError while draining the others.
    let flushDeferredWork: (() => void) | undefined;

    // The promise the GATE holds. It REJECTS when the delete fails, mirroring
    // the save tracks (`saveBulkRowsToDatabase` throws so `waitForPendingSaves`
    // rejects and publish aborts). A delete that failed left the rows alive in
    // the DB and gone from memory, so letting publish compile over it is
    // exactly the zombie-row state the error log below warns about — a gate
    // that resolves on failure is decorative. The promise callers get back is
    // the resolve-only view below, so a fire-and-forget delete still cannot
    // raise an unhandled rejection.
    const deletion = new Promise<void>((resolve, reject) => {
      const taskIdsToDelete: string[] = [];
      // Use a Set so each parent group is reconciled exactly once, even when
      // multiple of its subRows are deleted in the same call.
      const rowGroupIds = new Set<string>();

      emitOnce(() => {
        runInAction(() => {
          rowIds.forEach((id) => {
            // PSTUDIO-1433: a deleted row can still be the panel's registered
            // edit (the panel keeps a session open across a suppressed commit,
            // with no focus in the editor to blur). Drop the registration so the
            // pending flush cannot resurrect the row inside the delete window.
            if (this.panelEditingRowId === id) this.endPanelEdit();
            const targetRowIndex = this.rows.findIndex((row) => row.id === id);

            if (targetRowIndex !== -1) {
              const taskId = this.rows[targetRowIndex].settings?.taskId;
              if (taskId) {
                taskIdsToDelete.push(taskId);
              }

              const groupId = this.rows[targetRowIndex].settings?.groupId;
              if (groupId) {
                rowGroupIds.add(groupId);
              }

              this.rows[targetRowIndex].hidden = true;
              // PSTUDIO-3990: `hidden` is also how a collapsed group hides its
              // members, so the node-number checks cannot key on it. Mark the
              // row explicitly for the window between here and the idle
              // callback below, where it is unmarked and physically removed.
              this.pendingDeletionRowIds.add(id);
            }
          });
        });
      });

      // The deferred body, extracted so waitForPendingSaves can run it early.
      // Idempotent: whoever gets there first does the work, and the other call
      // (the browser's idle callback, or the flush) becomes a no-op — running
      // it twice would issue a second delete and a second rows.replace.
      let deferredWorkRan = false;
      const runDeferredWork = () => {
        if (deferredWorkRan) return;
        deferredWorkRan = true;

        // PSTUDIO-4103: everything from here to the GraphQL call must settle
        // the gate, whatever happens. `deferredWorkRan` is already true, so a
        // throw would make the browser's own idle callback a no-op and leave
        // this promise pending FOREVER — and the promise is now in
        // `_pendingDeletes`, so `hasPendingNodeSaves` would stay true for the
        // life of the page: publish parks on `waitForPendingSaves` with no
        // timeout behind a non-dismissable "Publishing in progress" toast, and
        // the cross-tab reload never fires again. Before this ticket the same
        // throw cost one lost delete; the gate is what turns it into a wedge.
        try {
          this._runDeferredDelete({
            rowIds,
            deletingFor,
            taskIdsToDelete,
            rowGroupIds,
            onAutoAdd,
            resolve,
            reject,
          });
        } catch (err) {
          console.error('deleteProStudioNodes deferred body failed', {
            rowIds,
            err,
          });
          reject(err);
        }
      };

      // Schedule first, so the handle is a `const` the flush can cancel. A
      // polyfill that runs the callback synchronously is fine: the flush is
      // assigned on the very next line, still inside this executor.
      const idleHandle = requestIdleCallback(() => runDeferredWork());
      flushDeferredWork = () => {
        if (deferredWorkRan) return;
        if (typeof cancelIdleCallback === 'function') {
          cancelIdleCallback(idleHandle);
        }
        runDeferredWork();
      };
    });

    // PSTUDIO-4103: make this track visible to the pending-saves gate, and
    // register it at the GESTURE — the window the bug lives in starts here,
    // before the idle callback has fired, so a registration made inside the
    // callback would leave the gate blind to exactly that window.
    this._pendingDeletes.set(deletion, () => flushDeferredWork?.());
    // Attaching a handler here is also what keeps a REJECTED gate from
    // surfacing as an unhandled rejection: the rejection still reaches
    // `waitForPendingSaves` through the map, exactly as `_inflightSavePromise`
    // carries the save tracks' failure while `saveBulkRowsToDatabase` swallows
    // it locally.
    void deletion
      .catch(() => undefined)
      .finally(() => {
        this._pendingDeletes.delete(deletion);
      });

    // Callers (`deleteRows`' command, `deleteRowsAsync`) never handled a
    // rejection here and must not have to start.
    return deletion.catch(() => undefined);
  }

  /**
   * PSTUDIO-4103: run every parked deferred delete NOW, ahead of the browser's
   * idle scheduler. Two callers, and deliberately NOT one contract — they want
   * opposite things from the store's identity, which is why each call site
   * carries its own reasoning:
   *
   * - `waitForPendingSaves` flushes so publish does not park on the scheduler,
   *   and flushes BEFORE the two queues because this body enqueues into them.
   * - the `solutionVersionId` setter flushes so a delete is not lost when the
   *   tab closes on it, and flushes AFTER the assignment so the body's own
   *   identity check sees the new version and skips everything local.
   *
   * Batched without `emitOnce`: `makeAutoObservable` annotates this method as
   * an action, so the whole loop is one notification (measured — draining two
   * parked deletes notifies an `autorun` over `rows` exactly once). Per-entry
   * isolated, for the reason spelled out at the catch below.
   */
  private _flushPendingDeletes(): void {
    if (this._pendingDeletes.size === 0) return;
    // Copy first: each entry removes itself when its own promise settles.
    // No `emitOnce` here: `makeAutoObservable` annotates this method as an
    // action, so MobX already batches the whole loop into one notification.
    // The try/catch is unreachable from the delete BODY — `_runDeferredDelete`
    // is itself wrapped, so a throwing body settles its own gate and never gets
    // here — but it IS reachable from the flush closure around that call, whose
    // one fallible step is `cancelIdleCallback`. Measured rather than assumed:
    // with a throwing `cancelIdleCallback` on the first entry, the catch still
    // lets the second entry issue its delete; without it neither is issued and
    // `waitForPendingSaves` rejects. One failing entry must not swallow the
    // rest of the loop, nor — at that call site — the queue flushes after it.
    // The caught entry keeps its gate open until the browser's own idle tick
    // runs the body: degraded back to the scheduler this flush exists to
    // bypass, which is the honest description of it, not lost.
    for (const flushDelete of [...this._pendingDeletes.values()]) {
      try {
        flushDelete();
      } catch (err) {
        console.error('pending delete flush failed', err);
      }
    }
  }

  /**
   * PSTUDIO-4103: the deferred half of `_deleteRowsInternal`, extracted so the
   * caller can wrap it in one try/catch that always settles the gate.
   */
  private _runDeferredDelete({
    rowIds,
    deletingFor,
    taskIdsToDelete,
    rowGroupIds,
    onAutoAdd,
    resolve,
    reject,
  }: {
    rowIds: string[];
    deletingFor: string;
    taskIdsToDelete: string[];
    rowGroupIds: Set<string>;
    onAutoAdd?: (row: TableRow) => void;
    resolve: () => void;
    reject: (reason: unknown) => void;
  }): void {
    // PSTUDIO-4103: is the table still the one this gesture happened in? Asked
    // ONCE, up front, because the answer decides three different things below
    // and asking it three times is how they drifted apart. Everything local —
    // the row removal, the group reconciliation, the starter row, the trailing
    // validation — is about THIS table and must not touch another one. Only the
    // DB mutation is unconditional: it carries `deletingFor`, so it is correct
    // whenever it runs, and skipping it is what would lose the delete.
    const stillOnThatVersion = this.solutionVersionId === deletingFor;

    if (stillOnThatVersion) {
      const rowsToKeep = this.rows.filter((row) => !rowIds.includes(row.id));
      runInAction(() => {
        if (
          this.validationMode === ValidationMode.Manual &&
          rowsToKeep.length !== this.rows.length
        ) {
          this.hasUnvalidatedChangesInManualMode = true;
        }
        this.removeErrorsForRowIds(rowIds);
        rowIds.forEach((id) => {
          this.rowMap.delete(id);
          // The soft-delete window closes here: the rows leave `rows` on the
          // next line, so the marker has nothing left to exclude.
          this.pendingDeletionRowIds.delete(id);
        });
        this.rows.replace(rowsToKeep);
        this.fuse.setCollection(rowsToKeep);
      });
      // PSTUDIO-4342: a refused save of a deleted row has nothing left to
      // repair, so its toasts must not keep saying so.
      this._reconcileNodeWriteToasts();
    } else {
      // Another table is on screen now, so nothing local may be touched — but
      // the soft-delete markers are keyed by row id and would outlive the rows
      // they name. This branch is NOT only belt and braces: the
      // `solutionVersionId` setter drains this track (`_flushPendingDeletes`)
      // inside its own action, i.e. BEFORE the identity reaction runs
      // `clearTable` (measured in review round 10). So the rows are still in
      // `rows` here, their markers are about to go, and without the line below
      // `_dropRefusedWrites` would read them as live and report a refused save
      // of a row the user DELETED as a discarded change. The user deleted it:
      // nothing is left to repair and nothing was lost.
      rowIds.forEach((id) => {
        this._failedNodeSaves.delete(id);
        this._failedNodeOrderSaves.delete(id);
      });
      runInAction(() => {
        rowIds.forEach((id) => this.pendingDeletionRowIds.delete(id));
      });
    }

    // PSTUDIO-4342: ordered behind every node write issued before it, so a save
    // of these rows that is still waiting or still sending chunks lands FIRST
    // and this delete has the last word (without it the save re-inserted the
    // rows after the delete — measured). Chunked by the same body-size ceiling
    // as the saves (`_sendNodeDeleteChunks`, shared with `_deleteNodesInDb`);
    // the first refused chunk stops the rest, and every id not yet deleted is
    // handed to `_failedNodeDeletes` so the next publish re-issues it.
    const notYetDeleted = new Set(rowIds);
    // PSTUDIO-4451: a collab-fed table deletes from the document — applied
    // locally at once, so it cannot fail the way a round trip can, and there
    // is no REST write to order, chunk or re-issue.
    const deleteRequest: Promise<unknown> = this.liveEditingSource
      ? Promise.resolve().then(() => {
          this.liveWriter?.deleteRows(rowIds);
          // The delete lands after its command was recorded; close the undo
          // step here so the next edit does not join it.
          this._endLiveUndoStep();
        })
      : (() => {
          this._dropQueuedNodeWrites(rowIds);
          return this._inNodeWriteOrder(() =>
            this._sendNodeDeleteChunks(rowIds, deletingFor, notYetDeleted)
          );
        })();
    deleteRequest
      .then(() => {
        // PSTUDIO-3610: signal sibling tabs to reload after a persisted
        // delete. A non-group delete has no other DB write (survivors keep
        // their orders and no group header is re-saved), so without this
        // the cross-tab reload never fires for ungrouped rows — the direct
        // deleteProStudioNodes above was the only write. Mirrors the
        // broadcast in _deleteNodesInDb and the save-path broadcasts.
        // PSTUDIO-4103: announce under the version the rows were
        // deleted FROM — a broadcast under the version the user has
        // since switched to is filtered out by the tab that needs it
        // and wakes a tab that does not.
        broadcastNodesChanged(deletingFor);
        resolve();
      })
      .catch((err: unknown) => {
        const error = err instanceof Error ? err : new Error(String(err));
        // A swallowed delete failure leaves "zombie" rows in the DB that
        // are gone from memory — invisible until the next reload
        // resurrects them. Surface it so the desync is diagnosable.
        console.error('deleteProStudioNodes failed', {
          rowIds,
          error: error.message,
        });
        notYetDeleted.forEach((id) =>
          this._failedNodeDeletes.set(id, deletingFor)
        );
        this._raiseNodeWriteToast({
          header: nodeWriteToastText('delete_error_header'),
          type: 'error',
          text: error.message,
          toastId: 'delete-failed',
        });
        reportStoreFailure(error, 'delete-nodes', {
          rowCount: rowIds.length,
          solutionVersionId: deletingFor,
        });
        // PSTUDIO-4103: and it must fail the GATE, not just the console.
        // The rows are gone from memory but alive in the DB, so a publish
        // that compiled over this would ship the zombie state. Symmetric
        // with saveBulkRowsToDatabase, which throws for the same reason.
        reject(error);
      });

    // Everything from here on is local bookkeeping for THIS table: task
    // deletion, the group-membership reconciliation (which upserts a
    // header and can dissolve an empty group through `_deleteNodesInDb` —
    // real writes, not the no-op an earlier version of this comment
    // claimed), the starter row, and the trailing validation. None of it
    // may run against a table this gesture does not belong to.
    if (!stillOnThatVersion) return;

    // Sync task deletions with solution data
    emitOnce(() => {
      // PSTUDIO-4451: under live editing a task is its node — deleting the
      // node is deleting the task; there is no Hasura task row to remove.
      if (!this.liveEditingSource) {
        taskIdsToDelete.forEach((taskId) => {
          deleteSolutionTask(taskId);
        });
      }

      rowGroupIds.forEach((groupId) => {
        // Skip groups whose own id is in the delete batch — they're about
        // to be removed anyway, and the trailing GraphQL upsert would
        // race the delete (and produce a phantom write that immediately
        // gets erased on the next subscription tick).
        if (rowIds.includes(groupId)) return;

        const settings = this.getValue(groupId, NodeColumns.settings);
        if (settings) {
          const remaining = (settings.subRows ?? []).filter(
            (id: string) => !rowIds.includes(id)
          );
          // PSTUDIO-3159: last member deleted → dissolve the empty group
          // instead of leaving a zombie header with count 0.
          // (Relies on the flat-group invariant — nesting is blocked by
          // canBeGrouped — so a dissolve can never empty ANOTHER group.)
          if (remaining.length === 0) {
            this._dissolveEmptyGroup(groupId);
            return;
          }
          this.updateRow({
            rowId: groupId,
            updates: {
              settings: {
                ...settings,
                subRows: remaining,
              },
            },
            skipValidationRules: false,
            saveImmediately: true,
          });
        }
      });
      // PSTUDIO-3159: dev-only — deletes must not leave dangling
      // subRows/groupId references behind.
      // PSTUDIO-3867: pass the arranged render order (not raw this.rows) so
      // the ordering half of the check doesn't false-positive on physical
      // vs render order divergence.
      assertGroupInvariants(
        this.getAllSortedRows(),
        'deleteRows reconciliation'
      );
    });

    if (this.rows.length === 0) {
      const added = this._addEmptyRowNoCommand(true);
      onAutoAdd?.(added);
    }

    this.validateAndSync();
  }

  deleteRows = action((rowIds: string[]) => {
    // PSTUDIO-4451: read-only (or not yet synced) — no local delete either,
    // so the table never shows a row the document still has.
    if (this.liveEditingSource && this.isReadOnly) return;
    // Snapshot the rows being deleted (full objects + original indices) so undo
    // can re-insert them locally and re-create them in the DB. PSTUDIO-2154.
    const snapshot = rowIds
      .map((id) => {
        const index = this.rows.findIndex((r) => r.id === id);
        return index === -1 ? null : { row: { ...this.rows[index] }, index };
      })
      .filter((x): x is { row: TableRow; index: number } => x !== null)
      .sort((a, b) => a.index - b.index);

    if (snapshot.length === 0) return;

    // PSTUDIO-3159: _deleteRowsInternal rewrites each affected parent
    // group's subRows (and dissolves a group the batch empties). Snapshot
    // those parents so undo restores their settings — or re-creates the
    // header — symmetrically with the member rows.
    const affectedParentIds = [
      ...new Set(
        snapshot
          .map(({ row }) => row.settings?.groupId)
          .filter((gid): gid is string => !!gid && !rowIds.includes(gid))
      ),
    ];
    const parentSnapshots = affectedParentIds
      .map((gid) => {
        const index = this.rows.findIndex((r) => r.id === gid);
        return index === -1
          ? null
          : { row: toJS(this.rows[index]) as TableRow, index };
      })
      .filter((x): x is { row: TableRow; index: number } => x !== null);

    // Tracks the blank starter row auto-added if the batch empties the table,
    // so undo can remove it.
    let autoAddedRow: TableRow | undefined;

    const command: Command = {
      execute: () => {
        autoAddedRow = undefined;
        this._deleteRowsInternal(rowIds, (row) => {
          autoAddedRow = row;
        });
      },
      undo: () => {
        if (autoAddedRow) {
          const stale = this.rows.find((r) => r.id === autoAddedRow!.id);
          if (stale) {
            this.rows.remove(stale);
            this.rowMap.delete(autoAddedRow.id);
            this.fuse.remove((doc) => doc.id === autoAddedRow!.id);
          }
          this._deleteNodesInDb([autoAddedRow.id]);
          autoAddedRow = undefined;
        }

        // Re-insert in ascending original-index order so each splice index
        // stays valid as the array grows back.
        snapshot.forEach(({ row, index }) => {
          this._reinsertRowIntoMemory(row, index);
        });

        if (this.validationMode === ValidationMode.Manual) {
          this.hasUnvalidatedChangesInManualMode = true;
        }

        // Re-create the deleted rows in the DB via the full-row upsert.
        this.saveRowsToDatabase(snapshot.map((s) => s.row));

        // PSTUDIO-3159: restore affected parent groups — settings for the
        // survivors, full re-insert for headers dissolved by the batch.
        parentSnapshots.forEach(({ row, index }) => {
          if (this.rowMap.has(row.id)) {
            this.updateRow({
              rowId: row.id,
              updates: { settings: row.settings },
              skipValidationRules: true,
              saveImmediately: true,
            });
          } else {
            this._reinsertRowIntoMemory(row, index);
            this.saveRowsToDatabase([row]);
          }
        });
      },
      description: `Delete ${snapshot.length} rows`,
      focusTarget: snapshot[0] ? { rowId: snapshot[0].row.id } : undefined,
    };

    this.executeCommand(command);
  });

  deleteRow = action((id: string) => {
    // PSTUDIO-4451: read-only (or not yet synced) — no local delete either,
    // so the table never shows a row the document still has.
    if (this.liveEditingSource && this.isReadOnly) return;
    const rowIndex = this.rows.findIndex((r) => r.id === id);
    if (rowIndex === -1) return;

    /**
     * this shouldn't ever happen but if the deleteRow command COULD get run
     * when zero rows exist, auto-add an empty row and exit early
     * */
    if (this.rows.length === 0) {
      this._addEmptyRowNoCommand(true);
      return;
    }

    const deletedRow = { ...this.rows[rowIndex] };
    const taskId = deletedRow.settings?.taskId;
    // PSTUDIO-3159: the bulk delete path reconciles each affected parent
    // group's settings.subRows; the single-row path did not, leaving a
    // dangling id (count badge, copy/paste and persistence all read
    // subRows). Capture the parent's pre-delete state so undo can restore
    // it symmetrically.
    const parentGroupId = deletedRow.settings?.groupId ?? undefined;
    const parentRowIndex = parentGroupId
      ? this.rows.findIndex((r) => r.id === parentGroupId)
      : -1;
    const parentBefore =
      parentRowIndex !== -1
        ? (toJS(this.rows[parentRowIndex]) as TableRow)
        : undefined;
    // True when execute() dissolved the emptied parent group, so undo knows
    // to re-create the header instead of just restoring its settings.
    let parentDissolved = false;
    // Tracks the blank starter row auto-added when deleting the last row, so
    // undo can remove it (otherwise undo would leave both it AND the restored
    // row). PSTUDIO-2154.
    let autoAddedRow: TableRow | undefined;

    const command: Command = {
      execute: () => {
        const targetRow = this.rows.find((row) => row.id === id);

        if (targetRow) {
          if (this.validationMode === ValidationMode.Manual) {
            this.hasUnvalidatedChangesInManualMode = true;
          }
          this.removeErrorsForRowIds([id]);
          this.rows.remove(targetRow);
          this.rowMap.delete(id);

          this.fuse.remove((doc) => doc.id === id);

          if (this.liveEditingSource) {
            // PSTUDIO-4451: the node, and with it any task it is.
            this.liveWriter?.deleteRows([id]);
          } else {
            // PSTUDIO-4342: ordered, gated and stripped of its queued write
            // (`_deleteOneNode`). Its result is still not read (PSTUDIO-4111).
            this._deleteOneNode(id, this.solutionVersionId);

            // Sync task deletion with solution data
            if (taskId) {
              deleteSolutionTask(taskId);
            }
          }

          // PSTUDIO-3159: mirror _deleteRowsInternal's reconciliation
          // (intentionally with skipValidationRules: true — membership
          // bookkeeping must not re-arm the manual-validation banner);
          // dissolve the group when this was its last member.
          if (parentGroupId) {
            const parent = this.getRowById(parentGroupId);
            if (parent) {
              const remaining = (parent.settings?.subRows ?? []).filter(
                (subId: string) => subId !== id
              );
              if (remaining.length === 0) {
                this._dissolveEmptyGroup(parentGroupId);
                parentDissolved = !this.rowMap.has(parentGroupId);
              } else {
                this.updateRow({
                  rowId: parentGroupId,
                  updates: {
                    settings: {
                      ...(parent.settings ?? {}),
                      subRows: remaining,
                    },
                  },
                  skipValidationRules: true,
                  saveImmediately: true,
                });
              }
            }
          }
        }

        if (this.rows.length === 0) {
          autoAddedRow = this._addEmptyRowNoCommand(true);
        }

        this.validateAndSync();

        // this.rowToDelete = null;
        // this.reorderRows();
        // this.selectedCells = this.selectedCells.filter(
        //   (cell) => cell.rowId !== id
        // );
      },
      undo: () => {
        // Remove the blank starter row that execute() auto-added when the
        // table was emptied, so undo restores the original single-row state.
        if (autoAddedRow) {
          const stale = this.rows.find((row) => row.id === autoAddedRow!.id);
          if (stale) {
            this.rows.remove(stale);
            this.rowMap.delete(autoAddedRow.id);
            this.fuse.remove((doc) => doc.id === autoAddedRow!.id);
          }
          this._deleteNodesInDb([autoAddedRow.id]);
          autoAddedRow = undefined;
        }

        this._reinsertRowIntoMemory(deletedRow, rowIndex);

        if (this.validationMode === ValidationMode.Manual) {
          this.hasUnvalidatedChangesInManualMode = true;
        }

        // Undoing a delete must re-create the row in the DB. The node was hard
        // deleted via deleteProStudioNode, so re-insert it through the full-row
        // upsert (PSTUDIO-2154).
        this.saveRowsToDatabase([deletedRow]);

        // PSTUDIO-3159: restore the parent group — either its pre-delete
        // subRows, or the whole header row if execute() dissolved it.
        if (parentBefore) {
          if (parentDissolved && !this.rowMap.has(parentBefore.id)) {
            this._reinsertRowIntoMemory(
              parentBefore,
              parentRowIndex === -1 ? this.rows.length : parentRowIndex
            );
            this.saveRowsToDatabase([parentBefore]);
            parentDissolved = false;
          } else if (this.rowMap.has(parentBefore.id)) {
            this.updateRow({
              rowId: parentBefore.id,
              updates: { settings: parentBefore.settings },
              skipValidationRules: true,
              saveImmediately: true,
            });
          }
        }
        // this.reorderRows();
      },
      description: `Delete row ${id}`,
      focusTarget: { rowId: id },
    };

    this.executeCommand(command);
  });

  // Full-row payloads are large (each row carries every column including
  // multi-KB JSON fields like settings/utterances/parameterInput), so a
  // request is capped at SAVE_REQUEST_BYTE_LIMIT bytes of serialized objects
  // (see its note for the measurement behind it) and at 100 rows. The row
  // cap is the tighter of the two only for rows under ~800 bytes.
  static readonly FULL_ROW_SAVE_BYTE_LIMIT = SAVE_REQUEST_BYTE_LIMIT;
  static readonly FULL_ROW_SAVE_ROW_LIMIT = 100;

  async saveBulkRowsToDatabase(
    rowsToSave: Partial<TableRow>[],
    skipValidation?: boolean
  ) {
    // PSTUDIO-4451: never under live editing — the guarded funnels route to
    // the document; this guards any caller that reaches here directly.
    if (this.liveEditingSource) return { error: undefined, data: {} };
    if (!rowsToSave.length) return { error: undefined, data: {} };

    // PSTUDIO-3669: fail safe — never upsert against an empty/unknown draft
    // version. An empty solutionVersionId produces an FK violation on
    // solution_version_nodes_ps. Drop this flush rather than emit a bad insert:
    // an empty version is unsaveable anyway, so this is strictly safer than the
    // pre-fix FK-violating insert (which lost the data AND crashed the editor).
    // (This specific batch is not re-enqueued — a later draft-version change
    // does not replay it.)
    //
    // PSTUDIO-3953: this used to add "the only path that empties
    // solutionVersionId mid-session is leaving the builder, which clears the
    // table regardless" as the reason dropping the batch was acceptable. That
    // path is exactly the data loss 3953 fixes: the setter now drains both
    // queues BEFORE the identity is cleared, so those rows commit to the
    // solution they were edited in. This guard remains as the last line of
    // defence for a caller that reaches here with no identity at all.
    if (!this.solutionVersionId) {
      return { error: undefined, data: {} };
    }

    // PSTUDIO-3669: re-home to the CURRENT draft version at flush time. Rows
    // captured their solutionVersionId at create time; if the draft version
    // changed since, the captured id may no longer exist → FK violation. Stamp
    // the current store id (non-empty per the guard above) so the upsert always
    // targets the live version.
    // PSTUDIO-3953: pin the identity this batch commits under. The stamp below
    // is synchronous and therefore correct, but everything AFTER the await —
    // the cross-tab broadcast and the trailing validation — used to re-read the
    // mutable store field, which by then can name a different solution (the
    // drain in the `solutionVersionId` setter makes that the normal case, not
    // an edge one). A broadcast under the wrong identity is filtered out by the
    // tab that actually needs it and wakes a tab that does not.
    const committedFor = this.solutionVersionId;

    // PSTUDIO-4081: an array-valued column can be holding a raw string. The
    // Expanded Cell View writes the typed text per keystroke and canonicalizes
    // it only on commit (PSTUDIO-3882), the grid used to render such a cell
    // BLANK, and a row copy carries whatever the row holds into the clipboard
    // envelope. That row then reaches a `[numeric!]` / `[String!]` variable as
    // a non-list, Hasura rejects the whole chunk, and every pasted row is lost
    // to an "Autosave Error" — the cross-solution paste this ticket reports.
    // Coerce here, the one boundary every writer funnels through, using the
    // same converter the PANEL's commit path uses — the path that produced the
    // draft. (The grid's own commit converter, `parseNativeCellRaw`, splits
    // differently and yields strings where this one yields numbers; the two
    // have disagreed since PSTUDIO-2821 and this change does not reconcile
    // them.)
    // PSTUDIO-4168: the content track's half of the same defect — see
    // saveBulkRowOrdersToDatabase for the full reasoning. `id` alone is the
    // primary key, so the publish clone gives the new draft fresh ids and a row
    // captured under the previous version conflicts with nothing here: the
    // upsert INSERTS it. Unlike the order track this one carries every column,
    // so what lands is a full duplicate of a real node — including its
    // `nodeNumber`, which is how a number already assigned elsewhere shows up
    // twice.
    //
    // Only rows that actually assert an identity are judged; `undefined` means
    // the caller asserts none, the same convention `belongsToCurrentVersion`
    // uses, so a partial payload is never dropped for lacking the field.
    const staleRows: string[] = [];
    const rows = rowsToSave
      .filter((row) => {
        if (
          row.solutionVersionId !== undefined &&
          row.solutionVersionId !== committedFor
        ) {
          staleRows.push(String(row.id));
          return false;
        }
        return true;
      })
      .map((row) => {
        const { hidden, ...rest } = row;
        const coerced = coerceRowArrayColumns(
          { ...rest, solutionVersionId: committedFor },
          (text, column) =>
            convertTextToColumnValue(text, column as keyof TableNode)
        );
        // PSTUDIO-4453: the row may hold a value kept as typed ("abc", "1e3")
        // for validation to flag. `numeric` rejects the WHOLE chunk on the
        // first and reads the second as 1000 — so, as the collab materializer
        // does, the column gets the canonical number or null.
        if (coerced.nodeNumber !== undefined) {
          coerced.nodeNumber = canonicalNodeNumber(coerced.nodeNumber) ?? null;
        }
        return coerced;
      });

    if (staleRows.length) {
      console.warn(
        '[tableStore] Dropping row saves enqueued for a version the store has left:',
        {
          count: staleRows.length,
          sampleIds: staleRows.slice(0, 5),
          storeIsOn: committedFor,
        }
      );
    }

    // Nothing survived the identity check — no write, and nothing to report.
    // Deliberately NOT dismissing toasts: `'saved'` is also the toastId of
    // "Autosave Error", and a flush that wrote nothing has no standing to clear
    // a failure another batch raised — that toast is the user's only sign that
    // data was lost.
    if (!rows.length) {
      return { error: undefined, data: {} };
    }

    const chunks = chunkBySerializedBytes(
      rows,
      TableStore.FULL_ROW_SAVE_BYTE_LIMIT,
      TableStore.FULL_ROW_SAVE_ROW_LIMIT
    );

    // Wrap all chunks in a single outer promise so waitForPendingSaves()
    // (which awaits _inflightSavePromise) waits for every chunk to commit
    // before publish reads the data. Sequential — don't parallelize chunks
    // against the same PK constraint or Hasura serializes them anyway.
    let lastError: { message: string } | undefined;
    // The size of each node of THIS batch refused for its size. Kept here,
    // not read back from _tooLargeRows, which a replacement may empty while
    // the batch is still in flight.
    const tooLargeInBatch: number[] = [];
    let lostAfterVisit: string[] | undefined;
    const visit = this._refusalVisit;
    // PSTUDIO-4342: sent after every node write issued before this one (see
    // _nodeWriteTail). A refused chunk records its rows and every row of the
    // chunks it stops, so a later publish re-sends them (_failedNodeSaves); a
    // chunk that lands clears its rows from that record. Refused means
    // `nodeWriteRefusal` — `checkForErrors`, not `r.error` alone. The one
    // exception is a refused chunk of ONE row past the ceiling: that row is
    // too large to save at all, so it is recorded alone, reported by name
    // (_tooLargeRows) and the rest of the batch still goes out.
    const outer = this._inNodeWriteOrder(async () => {
      for (let i = 0; i < chunks.length; i++) {
        const r = await urqlGql
          .updateProStudioNodes({ objects: chunks[i] })
          .toPromise();
        this._noteNodeWriteProgress();
        const refusal = nodeWriteRefusal(r);
        // Sent again, so whatever it was refused for before no longer stands.
        for (const row of chunks[i])
          if (row.id) this._tooLargeRows.delete(row.id);
        // The visit these rows belong to has ended (_reportLateRefusal).
        if (refusal && this._refusalVisit !== visit) {
          lostAfterVisit = chunks
            .slice(i)
            .flat()
            .flatMap((row) => (row.id ? [row.id] : []));
          lastError = refusal;
          throw refusal;
        }
        const only = chunks[i].length === 1 ? chunks[i][0] : undefined;
        // Only the authorization hook's refusal means "too large": once the
        // backend limit is raised, a big row refused for another reason (a
        // type error, say) must still read as an ordinary refusal.
        if (
          refusal &&
          only?.id &&
          /authorization hook/i.test(refusal.message)
        ) {
          const onlyBytes = textEncoder.encode(JSON.stringify(only)).length;
          if (onlyBytes > SAVE_REQUEST_BYTE_LIMIT) {
            this._failedNodeSaves.set(only.id, committedFor);
            this._tooLargeRows.set(only.id, onlyBytes);
            tooLargeInBatch.push(onlyBytes);
            continue;
          }
        }
        if (!refusal) {
          for (const row of chunks[i]) {
            if (!row.id) continue;
            this._serverKnownIds.add(row.id);
            // A full-row upsert writes `order` too, so it repairs an order
            // refusal of the same row (review round 11).
            if (this._failedNodeOrderSaves.get(row.id) === committedFor) {
              this._failedNodeOrderSaves.delete(row.id);
            }
          }
        }
        recordChunkOutcome(
          this._failedNodeSaves,
          { chunks, index: i, refusal, solutionVersionId: committedFor },
          (row) => row.id
        );
        if (refusal) {
          lastError = refusal;
          throw refusal;
        }
      }
      // The batch did NOT fully land: the publish gate must see it.
      if (tooLargeInBatch.length) {
        throw new Error('A node is too large to save');
      }
    });

    this._inflightSavePromise = outer;
    // PSTUDIO-3929 (a): record which rows this in-flight save covers, keyed by
    // THIS save's promise, so an incoming coworker merge skips them
    // (pendingSaveRowIds) until it lands — and an overlapping second save can't
    // drop this batch's rows from the pending set.
    // PSTUDIO-4168: `rows`, not `rowsToSave` — a row dropped by the identity
    // check above is not in flight, so a merge must not hold it as a pending
    // local write and skip the server's state for it.
    const inflightIds = new Set(
      rows
        .map((r) => r.id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0)
    );
    this._inflightSaveBatches.set(outer, inflightIds);

    try {
      await outer;
    } catch {
      // error already captured; fall through to the toast logic below.
    }

    // Delete THIS save's batch unconditionally (keyed by its own promise, so a
    // later overlapping save's batch is untouched). _inflightSavePromise keeps
    // its last-writer-wins semantics for waitForPendingSaves.
    this._inflightSaveBatches.delete(outer);
    if (this._inflightSavePromise === outer) {
      this._inflightSavePromise = null;
    }

    if (lostAfterVisit) {
      this._reportLateRefusal({
        lostIds: lostAfterVisit,
        error: lastError,
        track: 'autosave-nodes',
        solutionVersionId: committedFor,
      });
      return { error: lastError, data: undefined };
    }
    if (lastError) {
      // A node of an earlier refusal may have been shortened and sent again
      // in this batch: its too-large toast must stop naming it.
      this._reconcileNodeWriteToasts();
      if (tooLargeInBatch.length) this._showTooLarge();
      this._showSaveRefusal(
        nodeWriteToastText('autosave_error_header'),
        lastError.message
      );
      reportStoreFailure(lastError, 'autosave-nodes', {
        solutionVersionId: committedFor,
      });
      return { error: lastError, data: undefined };
    }

    // PSTUDIO-4342: not while a refused write of this version is unrepaired —
    // with writes ordered, the next write of the same gesture lands right after
    // the refusal and would otherwise take the error away at once.
    this._reconcileNodeWriteToasts();
    broadcastNodesChanged(committedFor);
    // Validate only while the store still holds the version this batch was
    // written to. After a solution switch the validation would upload the NEW
    // solution's (just-cleared) rows under the NEW context — see the setter.
    if (!skipValidation && this.solutionVersionId === committedFor) {
      this.validateAndSync();
    }
    // A node too large to save did not stop the rest of the batch, which
    // landed and is announced, validated and repaired above like any other —
    // but the batch is still not whole.
    if (tooLargeInBatch.length) {
      this._showTooLarge();
      const error = new Error('A node is too large to save');
      reportStoreFailure(error, 'autosave-nodes', {
        solutionVersionId: committedFor,
        tooLargeNodes: tooLargeInBatch.length,
        largestRefusedBytes: Math.max(...tooLargeInBatch),
      });
      return { error, data: undefined };
    }
    return { error: undefined, data: {} };
  }

  saveRowsToDatabase(
    rowsToSave: Partial<TableRow>[],
    options?: { skipValidation?: boolean }
  ) {
    // PSTUDIO-4451: a collab-fed table saves to the document, never Hasura.
    if (this.liveEditingSource) {
      if (!this.isReadOnly) this.liveWriter?.saveRows(rowsToSave);
      return;
    }
    if (options?.skipValidation !== true) {
      saveBatchNeedsValidation = true;
    }
    saveRowsToDatabaseQueue.add(rowsToSave);
  }

  // Soft cap on the JSON byte size of a single updateProStudioNodeOrders
  // request: the same ceiling as the full-row track (SAVE_REQUEST_BYTE_LIMIT),
  // because the measured refusal is about the size of the request body and not
  // about its operation. It used to be 512_000, which never fired: the row cap
  // below ended every chunk first. That held only while a tuple stayed short.
  // Measured 2026-10-16: a tuple with a four-digit `order` serializes to 81
  // bytes plus the length of its solutionVersionId (91 with a 10-character id,
  // 221 with a 140-character one; each further digit of `order` adds a byte).
  // The id is `<customer>-<bot>-v<N>`, and on candidate 91 real tuples took
  // 13,442 bytes (~148 each), so 500 of them are ~74 KB and with a 140-character
  // id ~111 KB — the latter over the accepted size.
  static readonly ORDER_SAVE_BYTE_LIMIT = SAVE_REQUEST_BYTE_LIMIT;

  // Row-count cap per request. Hasura's auth webhook returns "Invalid
  // response from authorization hook" once a single upsert reaches ~1000
  // rows. That is consistent with the byte ceiling above rather than a
  // separate rule: 1000 tuples of the ~148-byte size measured on candidate are
  // ~148 KB, past the measured edge of ~100.8 KB; it was not tested on its own.
  // The earlier reading of it as a per-row ownership check that times out was a
  // hypothesis, and a refusal that does not depend on the operation or the row
  // count (measured 2026-10-16) does not support it. 500 rows keeps the total
  // request count manageable (a 5000-row reorder fans out to 10 sequential
  // chunks) when tuples are short; with real identities the byte ceiling ends a
  // chunk at ~540 tuples, so the two bounds are close.
  static readonly ORDER_SAVE_ROW_LIMIT = 500;

  saveRowOrdersToDatabase(orders: RowOrderUpdate[]) {
    // PSTUDIO-4451: the document orders by position — the rows move between
    // their new neighbours, and no `order` value is written.
    if (this.liveEditingSource) {
      if (!this.isReadOnly) this.liveWriter?.moveRows(orders.map((o) => o.id));
      return;
    }
    if (!orders.length) return;
    saveRowOrdersToDatabaseQueue.add(orders);
  }

  /**
   * Sends compact order-only updates via updateProStudioNodeOrders. Chunks by
   * serialized byte size AND row count. The tuple has a fixed shape but not a
   * fixed size: it carries the solutionVersionId, so it is 91 bytes with a
   * 10-character one and 221 with a 140-character one (measured 2026-10-16,
   * four-digit `order`), and with a long identifier the byte ceiling, not the
   * row cap, ends a chunk. Chunks are dispatched sequentially under a single
   * outer promise so waitForPendingSaves() resolves only after the last write.
   */
  async saveBulkRowOrdersToDatabase(orders: RowOrderUpdate[]) {
    if (!orders.length) return;
    // PSTUDIO-4451: never under live editing — the guarded funnels route to
    // the document; this guards any caller that reaches here directly.
    if (this.liveEditingSource) return;

    // PSTUDIO-3669: same fail-safe as saveBulkRowsToDatabase — don't emit an
    // order-only upsert with an empty solutionVersionId (FK violation).
    if (!this.solutionVersionId) {
      return;
    }

    // PSTUDIO-3669: re-home order tuples to the current draft version (see
    // saveBulkRowsToDatabase). Order tuples capture this.solutionVersionId at
    // enqueue time (table-store.ts L5022/L5055), which can be stale by flush.
    // PSTUDIO-3953: pin the committed identity — same reason as the row save.
    const committedFor = this.solutionVersionId;

    // PSTUDIO-4168: re-homing a tuple the store has moved out from under does
    // not relocate it — it FABRICATES a node. `id` alone is this table's
    // primary key (`solution_version_nodes_ps_pkey`, the only constraint on it,
    // and `..._pk_columns_input` is `{ id }`), so no two rows can share an id
    // across versions and the server's publish clone necessarily mints fresh
    // ids for the new draft. A tuple carrying a previous version's id therefore
    // hits no conflict in `committedFor`: Hasura's INSERT ... ON CONFLICT DO
    // UPDATE takes the INSERT branch and writes a row holding only
    // id/order/solutionVersionId, every content column empty. That is the blank
    // phantom node this ticket reports.
    //
    // Nor could the re-home ever do what PSTUDIO-3669 wanted through THIS
    // mutation: `solutionVersionId` is not in its `update_columns` (only
    // `order` is), so on the conflict branch the value is ignored outright. It
    // is read on exactly one path — the INSERT that must never happen. Keeping
    // the stamp is still right for the rows that DO exist here, because
    // Postgres validates the proposed insert row's NOT NULL/FK before resolving
    // the conflict; what has to go is writing tuples that belong elsewhere.
    //
    // Every producer stamps `this.solutionVersionId` at enqueue time, so
    // `!== committedFor` reads exactly as "the store moved between enqueue and
    // flush". The solution-change half of that is already drained ahead of the
    // move (PSTUDIO-3953); what reaches here is the publish bump, which is
    // deliberately NOT drained. Dropping loses nothing a write could have
    // saved: the row is not in this version to update, and publish awaits
    // `waitForPendingSaves()` before it bumps, so a gesture the user actually
    // made has already committed.
    const stale: string[] = [];
    const rehomed: RowOrderUpdate[] = [];
    for (const order of orders) {
      if (order.solutionVersionId !== committedFor) {
        stale.push(order.id);
        continue;
      }
      rehomed.push({ ...order, solutionVersionId: committedFor });
    }

    if (stale.length) {
      console.warn(
        '[tableStore] Dropping order tuples enqueued for a version the store has left:',
        {
          count: stale.length,
          sampleIds: stale.slice(0, 5),
          enqueuedFor: orders.find((o) => o.solutionVersionId !== committedFor)
            ?.solutionVersionId,
          storeIsOn: committedFor,
        }
      );
    }

    // Nothing survived the identity check — no write, and no sibling tab to
    // wake. Deliberately NOT dismissing toasts: `'saved'` is also the toastId
    // of "Reorder Save Error", and a flush that wrote nothing has no standing
    // to clear a failure another batch raised.
    if (!rehomed.length) {
      return;
    }

    const chunks = chunkBySerializedBytes(
      rehomed,
      TableStore.ORDER_SAVE_BYTE_LIMIT,
      TableStore.ORDER_SAVE_ROW_LIMIT
    );

    let lastError: { message: string } | undefined;
    let lostAfterVisit: string[] | undefined;
    const visit = this._refusalVisit;
    // PSTUDIO-4342: same ordering and the same record of refused tuples as the
    // content track — see _nodeWriteTail and _failedNodeSaves.
    const outer = this._inNodeWriteOrder(async () => {
      for (let i = 0; i < chunks.length; i++) {
        const result = await urqlGql
          .updateProStudioNodeOrders({ objects: chunks[i] })
          .toPromise();
        this._noteNodeWriteProgress();
        const refusal = nodeWriteRefusal(result);
        // The visit these tuples belong to has ended (_reportLateRefusal).
        if (refusal && this._refusalVisit !== visit) {
          lostAfterVisit = chunks
            .slice(i)
            .flat()
            .map((tuple) => tuple.id);
          lastError = refusal;
          throw refusal;
        }
        // Landed: the upsert INSERTs a missing id, so the server holds every
        // one of these rows now (see _serverKnownIds).
        if (!refusal) {
          for (const tuple of chunks[i]) this._serverKnownIds.add(tuple.id);
        }
        recordChunkOutcome(
          this._failedNodeOrderSaves,
          { chunks, index: i, refusal, solutionVersionId: committedFor },
          (tuple) => tuple.id
        );
        if (refusal) {
          lastError = refusal;
          // Short-circuit so later chunks don't pile additional failures on
          // top of the same error. Prior chunks have already committed. Throw
          // (not just break) so `outer` REJECTS: _inflightOrderSavePromise then
          // rejects and waitForPendingSaves rejects, so the publish flow aborts
          // on an uncommitted reorder instead of compiling a stale order
          // (preserves the 680a2bcc91 invariant).
          throw refusal;
        }
      }
    });

    this._inflightOrderSavePromise = outer;
    // PSTUDIO-3929 (a): mirror the content-save track — record the in-flight
    // order-save row ids keyed by this save's promise so mergeVersionData skips
    // them (a stale incoming order would otherwise re-sort a reorder that
    // hasn't committed), overlap-safe.
    // PSTUDIO-4168: `rehomed`, not `orders` — a dropped stale repair is not in
    // flight, so a merge must not treat its row as pending-local and skip the
    // server's order for it.
    this._inflightOrderSaveBatches.set(
      outer,
      new Set(rehomed.map((o) => o.id))
    );
    try {
      await outer;
    } catch {
      // PSTUDIO-3669: swallow the rejection HERE so the fire-and-forget queue
      // driver (processOrderTransactions, which calls this with no .catch) sees
      // no unhandled rejection — that unhandled rejection is what the app's
      // error-boundary promiseRejectionHandler turned into a full editor crash.
      // The rejection still reaches waitForPendingSaves via
      // _inflightOrderSavePromise (captured before this await), so a publish
      // already waiting on this reorder is refused. One started after it
      // settled re-sends the refused tuples instead (_failedNodeOrderSaves).
      // Mirrors the saveBulkRowsToDatabase path.
    }

    this._inflightOrderSaveBatches.delete(outer);
    if (this._inflightOrderSavePromise === outer) {
      this._inflightOrderSavePromise = null;
    }

    if (lostAfterVisit) {
      this._reportLateRefusal({
        lostIds: lostAfterVisit,
        error: lastError,
        track: 'reorder-nodes',
        solutionVersionId: committedFor,
      });
      return;
    }
    if (lastError) {
      // Surface the failure as a toast (the crash isolation is the catch
      // above; this only informs the user). Mirrors saveBulkRowsToDatabase.
      this._showSaveRefusal(
        nodeWriteToastText('reorder_error_header'),
        lastError.message
      );
      reportStoreFailure(lastError, 'reorder-nodes', {
        solutionVersionId: committedFor,
      });
      return;
    }

    this._reconcileNodeWriteToasts();
    broadcastNodesChanged(committedFor);
  }

  /**
   * PSTUDIO-4452: the publish modal's short "snapshot" lock. Without live
   * editing the table holds view mode while saves flush and the publish is
   * accepted, since an edit in between would publish rows nobody validated.
   * Under live editing the backend flushes the document and snapshots it on
   * accept, and editing stays available for everyone connected — no lock.
   * Returns whether it locked; release with that answer, so a source that
   * flips mid-publish cannot leave the table locked or unlock a viewer.
   */
  beginPublishSnapshotLock = action((): boolean => {
    if (this.liveEditingSource) return false;
    this.setState('viewing');
    return true;
  });

  releasePublishSnapshotLock = action((locked: boolean) => {
    if (!locked) return;
    this.setState(this.isViewer ? 'viewing' : 'editing');
  });

  /**
   * Flushes any queued node saves and waits for the in-flight DB write to complete.
   * Call this before triggering publish to ensure all node changes are committed
   * to the database before the backend reads them for compilation.
   */
  async waitForPendingSaves(): Promise<void> {
    // PSTUDIO-4452: under live editing nothing is queued here — edits are in
    // the collab document. What publish needs instead is the document in sync
    // with the server, which snapshots it; a client offline too long may hold
    // edits the server does not have, so publish stops rather than ship
    // without them.
    if (this.liveEditingSource) {
      // A delete still waits for idle time before it reaches the writer;
      // run it now, or the snapshot keeps the rows the grid removed.
      this._flushPendingDeletes();
      await when(() => this.liveViewSettings?.connectionStatus === 'synced', {
        timeout: LIVE_SYNC_BEFORE_PUBLISH_MS,
      });
      return;
    }
    // PSTUDIO-4103: run any deferred delete BEFORE flushing the queues. Two
    // reasons, and the order matters for both. (1) The delete's promise only
    // settles once its GraphQL call returns, and that call is issued from a
    // `requestIdleCallback` which may not have fired — awaiting it without
    // running it would park publish on the browser's idle scheduler (in a
    // backgrounded tab, for a long time). Running it here is the same move the
    // queues' own `flush()` makes: do the deferred work now, then wait for it.
    // (2) That deferred body re-saves group headers, i.e. it ENQUEUES into the
    // two save queues — so it has to run before they are flushed, or those
    // writes miss this wait. The flush is idempotent, so the browser's own idle
    // callback afterwards is a no-op.
    this._flushPendingDeletes();

    // PSTUDIO-4121: re-issue any delete that FAILED earlier, before the gate is
    // read. The gate itself cannot carry that failure — `_pendingDeletes` drops
    // an entry when its promise settles, failure included — so a publish a
    // second after a failed delete found a clean gate and compiled a solution
    // still holding rows the grid had removed, while the modal's toast was
    // telling the user to try publishing again. Re-issuing makes that
    // instruction true: the retry either repairs the state or fails again and
    // refuses the publish through the promise registered below. A row that has
    // come back in the meantime is dropped by the re-issue itself, which finds
    // nothing absent to delete. PSTUDIO-4342: only the deletes of the version
    // on screen — `_deleteNodesInDb` sends under that version, and a delete
    // recorded for another one would match nothing there and read as repaired.
    const retry: string[] = [];
    for (const [id, failedFor] of [...this._failedNodeDeletes]) {
      if (failedFor !== this.solutionVersionId) continue;
      this._failedNodeDeletes.delete(id);
      retry.push(id);
    }
    if (retry.length) this._deleteNodesInDb(retry);

    // PSTUDIO-4342: the same for a SAVE that was refused (or never sent because
    // an earlier chunk of its batch was refused): re-enqueue it from the current
    // rows, before the flush below sends the queues. A retry that fails again
    // rejects this wait through its in-flight promise.
    this._requeueFailedNodeSaves();

    // flush() synchronously calls the queue handlers which set the inflight
    // promises before their first await — safe to read after flush. Await both
    // the content-save and the order-save tracks so publish doesn't race a
    // pending reorder commit.
    saveRowsToDatabaseQueue.flush();
    saveRowOrdersToDatabaseQueue.flush();
    // Await every in-flight outer promise across both tracks. The single-slot
    // fields are last-writer-wins (an overlapping save clobbers them), so we
    // also drain the per-promise batch maps — a Set dedups the slot promise,
    // which is always one of the batch keys while it is set.
    const pending = new Set<Promise<unknown>>();
    if (this._inflightSavePromise) pending.add(this._inflightSavePromise);
    if (this._inflightOrderSavePromise)
      pending.add(this._inflightOrderSavePromise);
    for (const p of this._inflightSaveBatches.keys()) pending.add(p);
    for (const p of this._inflightOrderSaveBatches.keys()) pending.add(p);
    // PSTUDIO-4103: and the deferred deletes, flushed above so none of them is
    // left waiting on an idle tick. PSTUDIO-4342: flushed is not yet SENT — each
    // waits its turn behind the node writes issued before it (_nodeWriteTail),
    // which this wait covers too.
    for (const p of this._pendingDeletes.keys()) pending.add(p);
    if (pending.size) {
      await Promise.all(pending);
    }
  }

  updateRowType({
    rowId,
    newRowType,
  }: {
    rowId: TableRow['id'];
    newRowType: TableRow['nodeType'];
  }) {
    if (!rowId) {
      throw Error('rowId is required!');
    }

    if (!newRowType) {
      throw Error('newRowType is required!');
    }

    const row = this.rows.find((r) => r.id === rowId);

    if (!row) {
      throw Error('Row not found');
    }

    const clearedCells = nodeTypeDependentCells[newRowType]
      .map((columnId) => ({
        [columnId]: getDefaultValueForColumn(columnId),
      }))
      .reduce((acc, curr) => ({ ...acc, ...curr }), {});

    // Dropdowns should save immediately
    this.updateRow({
      rowId,
      updates: { nodeType: newRowType, ...clearedCells },
      skipValidationRules: false,
      saveImmediately: true,
    });
  }

  getNextNodeOrder(incrementor: number = 1) {
    if (this.rows.length) {
      const maxOrder = this.rows.reduce(
        (max, row) => Math.max(max, row.order || 0),
        0
      );
      return maxOrder + this.orderIncrementV2 * incrementor;
    } else {
      return this.orderIncrementV2 * incrementor;
    }
  }

  deleteAllRows = action(() => {
    const rowsIdsToDelete = this.rows.slice().map((row) => row.id);

    this.deleteRows(rowsIdsToDelete);
  });

  checkNearbyRowOrder(rowId: string) {
    const currentRowOrder = this.getRowById(rowId).order;
    const aboveTargetOrder = this.calculateNewRowOrder(rowId, 'above');
    const belowTargetOrder = this.calculateNewRowOrder(rowId, 'below');

    if (
      this.needToReorder(currentRowOrder, aboveTargetOrder) ||
      this.needToReorder(currentRowOrder, belowTargetOrder)
    ) {
      this.normalizeRowOrder();
    }
  }

  calculateNewRowOrder(
    originalRowId?: string,
    newRowPosition: 'above' | 'below' = 'below'
  ) {
    if (!originalRowId) {
      return this.orderIncrementV2;
    }

    // Derive BOTH the index and its neighbours from the SAME sorted list.
    // Previously the index came from getSortedRowIndex() (which indexes
    // getAllSortedRows() — this.rows PLUS pendingRowsToRender, minus hidden
    // rows) while the neighbours were read from this.sortedRows (this.rows
    // only). When the two lists diverge — virtualization queueing pending
    // rows, or a collapsed group hiding rows — the index pointed past the end
    // of this.sortedRows, so `startingRowOrder` was undefined and every
    // arithmetic branch produced NaN. NaN JSON-serializes to null and the
    // `order` column is NOT NULL on Hasura, so the autosave rejected with
    // "unexpected null value for type 'numeric'" and the row (order≈0) was
    // shoved to the top. (Add node bug.)
    const sorted = this.sortedRows;
    const startingRowIndex = sorted.findIndex((r) => r.id === originalRowId);
    const startingRowOrder = sorted[startingRowIndex]?.order;

    // Target missing or order-less: append to the end of the order-space so we
    // never emit NaN/null. Belt-and-suspenders against any future divergence.
    if (startingRowIndex === -1 || startingRowOrder == null) {
      const maxOrder = sorted.reduce(
        (max, r) => Math.max(max, r.order || 0),
        0
      );
      return maxOrder + this.orderIncrementV2;
    }

    const existingAboveTargetOrder =
      sorted[startingRowIndex - 1]?.order ||
      startingRowOrder - this.orderIncrementV2;

    const existingBelowTargetOrder =
      sorted[startingRowIndex + 1]?.order ||
      startingRowOrder + this.orderIncrementV2;

    if (newRowPosition === 'above') {
      const newOrder = Math.round(
        (startingRowOrder + existingAboveTargetOrder) / 2
      );

      if (startingRowIndex === 0) {
        if (startingRowOrder - this.orderIncrementV2 > 0) {
          return Math.round(startingRowOrder - this.orderIncrementV2);
        } else {
          return Math.round(startingRowOrder - newOrder);
        }
      }

      return Math.round(newOrder);
    } else {
      const newOrder = Math.round(
        (startingRowOrder + existingBelowTargetOrder) / 2
      );

      if (startingRowIndex === sorted.length - 1) {
        return Math.round(existingBelowTargetOrder);
      }

      return Math.round(newOrder);
    }
  }

  /**
   * PSTUDIO-2316: the row a new node should land below. Unions cell- and
   * row-selection (the canonical `selectedStartCell?.rowId ?? selectedRows`
   * pattern) and, when several rows are selected, returns the BOTTOM-MOST one
   * (highest `order`) so the batch is appended under the selection, not above
   * it. Returns `undefined` when nothing is selected OR the selection is stale
   * (id no longer in `rows`), so callers fall back to append-to-end safely.
   */
  getInsertionTargetRowId(): string | undefined {
    return this._selectionBoundaryRowId('below');
  }

  /**
   * PSTUDIO-3848: the row a context-menu insert should anchor to for a
   * multi-node selection. `'below'` returns the BOTTOM-MOST selected row and
   * `'above'` the TOP-MOST, so the batch lands under / over the WHOLE
   * selection — not around whichever row the menu was triggered from. Unions
   * cell- and row-selection like `getInsertionTargetRowId`, and returns
   * `undefined` when nothing valid is selected (callers fall back to the
   * trigger row / append-to-end).
   */
  getInsertionAnchorRowId(position: 'above' | 'below'): string | undefined {
    return this._selectionBoundaryRowId(position);
  }

  /**
   * Shared boundary picker over the row-selection ∪ cell-selection union:
   * `'above'` → smallest `order` (top-most), `'below'` → largest (bottom-most).
   */
  private _selectionBoundaryRowId(
    position: 'above' | 'below'
  ): string | undefined {
    const candidateIds = new Set<string>(this.selectedRows.values());
    if (this.selectedStartCell?.rowId)
      candidateIds.add(this.selectedStartCell.rowId);

    let chosen: TableRow | undefined;
    for (const id of candidateIds) {
      const row = this.rowMap.get(id);
      // Skip hidden rows to mirror isSelectionContinuous (which judges continuity
      // over visibleSortedRows). Collapsing a group hides its members but never
      // prunes selectedRows, so a stale hidden member could otherwise win the
      // boundary and anchor the insert INSIDE the collapsed group instead of at
      // the visible selection edge (PSTUDIO-3848). Both callers fall back safely
      // when the filtered set is empty (getInsertionAnchorRowId ?? the clicked
      // row; getInsertionTargetRowId → append to end).
      if (!row || row.hidden === true) continue;
      if (!chosen) {
        chosen = row;
        continue;
      }
      const isBetter =
        position === 'above'
          ? (row.order || 0) < (chosen.order || 0)
          : (row.order || 0) > (chosen.order || 0);
      if (isBetter) chosen = row;
    }
    return chosen?.id;
  }

  addEmptyRows = action((numberOfRows: number) => {
    // PSTUDIO-2871/2872: with a selection, drop the whole batch below the
    // bottom-most selected row with adjacency-aware unique numbers and select
    // the result. insertRowsSmart computes the number block ONCE for the real
    // count (so Expansion/Family use the right base) and is group-aware.
    const targetRowId = this.getInsertionTargetRowId();
    if (targetRowId) {
      this.insertRowsSmart(targetRowId, 'below', numberOfRows);
      return;
    }

    // No selection → append at the end with smart numbers. reference = the last
    // data row, upper = null (append) → Sequential Fill / Fallback. Lock the
    // number/order base once (PSTUDIO-2555: rapid "Add 10" must not re-read
    // this.rows mid-batch and duplicate).
    const lastDataNumber = this.getAppendNumberingAnchor();
    const batchNumbers = this.getNumberingNumbers();
    const numbers = assignNodeNumberStrings(
      lastDataNumber,
      null,
      numberOfRows,
      batchNumbers.own,
      batchNumbers.referenced
    );
    const baseOrder = this.rows.reduce(
      (max, row) => Math.max(max, row.order || 0),
      0
    );
    const increment = this.orderIncrementV2;

    const runAppend = () => {
      emitOnce(() => {
        for (let i = 0; i < numberOfRows; i++) {
          this.addEmptyRow(false, {
            nodeNumber: numbers[i],
            order: baseOrder + increment * (i + 1),
          });
        }
      });
    };

    if (numberOfRows === 1) {
      runAppend();
    } else {
      // PSTUDIO-3640: collapse the batch append into ONE atomic undo step,
      // mirroring the insertRowsSmart fix. addEmptyRow's per-node commands
      // are stripped by _recordGroupMutation; one snapshot-swap owns undo/redo.
      this._recordGroupMutation(`Add ${numberOfRows} nodes`, runAppend);
    }
  });

  // PSTUDIO-2154: insert a blank row and persist it WITHOUT registering an
  // undo/redo command. Used by the delete flows' "table emptied -> add a blank
  // starter row" guard so that implicit row never lands on the undo stack (and
  // so a delete command can clean it up on undo). Returns the inserted row.
  _addEmptyRowNoCommand = action(
    (starting: boolean | undefined = false, config?: Partial<TableRow>) => {
      // PSTUDIO-3992: was `Math.max(...this.rows.map(...)) + 1` — a third copy of
      // the arithmetic that (a) counted group headers, which getMaxNodeNumber
      // deliberately excludes, (b) skipped toValidNodeNumber, and (c) spread the
      // whole row array through the call stack. Same allocator as every other
      // append now.
      const starterNumbers = this.getNumberingNumbers();
      const [starterNumber] = assignNodeNumberStrings(
        this.getAppendNumberingAnchor(),
        null,
        1,
        starterNumbers.own,
        starterNumbers.referenced
      );
      const newRow: TableRow = {
        ...defaultRowData,
        nodeNumber: starterNumber,
        message: starting ? 'Hello what is your name?' : '',
        intent: starting ? 'out_of_scope' : undefined,
        order: this.getNextNodeOrder(),
        id: uuidv4(),
        solutionVersionId: this.solutionVersionId,
        ...config,
      };

      this.rows.push(newRow);
      set(this.rowMap, newRow.id, newRow);
      this.fuse.add(newRow);

      if (this.validationMode === ValidationMode.Manual) {
        this.hasUnvalidatedChangesInManualMode = true;
      }

      this.saveRowsToDatabase([newRow]);

      return newRow;
    }
  );

  addEmptyRow = action(
    (starting: boolean | undefined = false, config?: Partial<TableRow>) => {
      // PSTUDIO-2316: a plain "Add Node" click drops the new node directly
      // below the selection (group-aware), not at the bottom. Bootstrap
      // (`starting`) and batch-internal (`config`) calls keep legacy append.
      const targetRowId = this.getInsertionTargetRowId();
      if (targetRowId && !starting && !config) {
        return this.insertRowAfter(targetRowId);
      }

      // PSTUDIO-3992: number the appended row from the END of the table, not
      // from the solution-wide maximum. `addEmptyRows` (the batch sibling on the
      // same toolbar) already did this, so "Add node" and "Add 3 nodes" used to
      // disagree whenever a far-away number existed.
      //
      // PSTUDIO-4083: skip the allocation entirely when the caller already
      // carries a number. `addEmptyRows` allocates the whole block ONCE and then
      // calls this per row with `config.nodeNumber` set, and the `...config`
      // spread below overwrites whatever we compute — so every one of those
      // passes was thrown away. That was cheap before this ticket and is not
      // now: collecting the numbers walks every row's rich asset, which measured
      // ~37 ms per pass on a 5,000-row table, i.e. ~370 ms of pure waste for one
      // "Add 10 Nodes".
      let appendedNumber = config?.nodeNumber;
      if (appendedNumber === undefined) {
        const appendNumbers = this.getNumberingNumbers();
        [appendedNumber] = assignNodeNumberStrings(
          this.getAppendNumberingAnchor(),
          null,
          1,
          appendNumbers.own,
          appendNumbers.referenced
        );
      }
      const newRow: TableRow = {
        ...defaultRowData,
        nodeNumber: appendedNumber,
        message: starting ? 'Hello what is your name?' : '',
        intent: starting ? 'out_of_scope' : undefined,
        order: this.getNextNodeOrder(),
        id: uuidv4(),
        solutionVersionId: this.solutionVersionId,
        ...config, // Apply any provided configuration
      };

      const command: Command = {
        execute: () => {
          runInAction(() => {
            this.rows.push(newRow);
            set(this.rowMap, newRow.id, newRow);
          });

          this.fuse.add(newRow);

          if (this.validationMode === ValidationMode.Manual) {
            this.hasUnvalidatedChangesInManualMode = true;
          }

          setTimeout(() => {
            if (this.tableScrollerRef !== undefined) {
              console.log(
                'this.tableScrollerRef.scrollHeight',
                this.tableScrollerRef.scrollHeight
              );
              this.tableScrollerRef.scrollTo({
                top: this.tableScrollerRef.scrollHeight,
                behavior: 'smooth',
              });
            }
          }, 0);

          this.saveRowsToDatabase([newRow]);
        },
        undo: () => {
          runInAction(() => {
            const targetRow = this.rows.find((row) => row.id === newRow.id);
            if (targetRow) {
              this.rows.remove(targetRow);
              this.rowMap.delete(newRow.id);
              this.fuse.remove((doc) => doc.id === newRow.id);
            }

            if (this.rows.length > 0) {
              // Preserve edit mode — undoing an add must NOT drop the table into
              // read-only 'viewing' (that made the node-type cell render as a
              // static label and blocked editing). PSTUDIO-2154.
              this.setState(this.isViewer ? 'viewing' : 'editing');
            }

            if (this.validationMode === ValidationMode.Manual) {
              this.hasUnvalidatedChangesInManualMode = true;
            }
          });

          // Undoing an add must remove the row from the DB too, not just the
          // local store (PSTUDIO-2154).
          this._deleteNodesInDb([newRow.id]);
        },
        description: `Add new row`,
        focusTarget: { rowId: newRow.id },
      };

      this.executeCommand(command);
    }
  );

  addResourceLibRows = action((resourceLibData: TableNode[]) => {
    // PSTUDIO-4571: the fresh ids and free node numbers
    // (`prepareResourceLibInsert`) belong to the master (`fiveStageNav`). The
    // flag is checked here and not inside `prepareResourceLibInsert` because
    // that helper is shared: `addResourceLibRowsAtRow` and
    // `addResourceLibRowsToGroup` also call it, and both are reachable only
    // with this flag on (the drop effect in table.tsx returns unless
    // `resourceLibDragDropAccess` is 'allowed'; `onResourceDrop` in
    // canvas-view.tsx returns unless `isDraggingResource`, which needs the
    // flag), while this click-to-add path is the one consumer that exists
    // without it. With the flag off the rows are built as before. `pending`
    // (flags not loaded) counts as off.
    const polished =
      resolveBobcatAccess('fiveStageNav', this.featureFlags) === 'allowed';
    const rows: TableRow[] = polished
      ? this.prepareResourceLibInsert(resourceLibData).map((config, index) => ({
          ...defaultRowData,
          ...config,
          order: this.getNextNodeOrder(index + 1),
          id: uuidv4(),
          solutionVersionId: this.solutionVersionId,
        }))
      : resourceLibData.map((config, index) => ({
          ...defaultRowData,
          ...config,
          // PSTUDIO-4453: the library's JSON carries numbers.
          nodeNumber: toNodeNumberCell(config.nodeNumber),
          order: this.getNextNodeOrder(index + 1),
          id: config.id || uuidv4(),
          solutionVersionId: this.solutionVersionId,
        }));
    if (rows.length === 0) return;

    const command: Command = {
      execute: () => {
        this.deselectAllRows();
        this.rows.push(...rows);
        rows.forEach((row) => {
          set(this.rowMap, row.id, row);
          this.fuse.add(row);
        });

        this.saveRowsToDatabase(rows);
        this.setState(this.isViewer ? 'viewing' : 'editing');
        rows.forEach(({ id }) => {
          this.selectRow(id);
        });
      },
      undo: () => {
        rows.forEach((row) => {
          const targetRow = this.rows.find((r) => r.id === row.id);
          if (targetRow) {
            this.rows.remove(targetRow);
            this.rowMap.delete(row.id);
            this.fuse.remove((doc) => doc.id === row.id);
          }
        });

        if (this.rows.length > 0) {
          this.setState(this.isViewer ? 'viewing' : 'editing');
        }

        if (this.validationMode === ValidationMode.Manual) {
          this.hasUnvalidatedChangesInManualMode = true;
        }

        // Undoing the add must remove the rows from the DB too (PSTUDIO-2154).
        this._deleteNodesInDb(rows.map((row) => row.id));
      },
      description: `Add resource lib rows`,
      focusTarget: rows[0] ? { rowId: rows[0].id } : undefined,
    };

    this.executeCommand(command);
    this.validateAndSync();
  });

  /**
   * A Resource Library item's catalogue rows, ready to insert here: no
   * catalogue `id` / `order`, no group rows, and node numbers kept only when
   * free — otherwise renumbered past the max with the item's own routing
   * rewritten. Shared by every Resource Library insert path.
   */
  private prepareResourceLibInsert(
    resourceLibData: TableNode[]
  ): Array<Omit<TableNode, 'id' | 'order'>> {
    const occupiedNumbers = new Set<number>();
    this.rows.forEach((row) => {
      if (isGroupRow(row)) return;
      const num = toValidNodeNumber(row.nodeNumber);
      if (num !== undefined) occupiedNumbers.add(num);
    });
    // The plan works in numbers; a stored cell is a canonical string (PSTUDIO-4453).
    return prepareResourceLibRows(resourceLibData, {
      occupiedNumbers,
      reference: toValidNodeNumber(this.getMaxNodeNumber()) || null,
      isGroupRow,
    }).map((row) => ({ ...row, nodeNumber: toNodeNumberCell(row.nodeNumber) }));
  }

  /**
   * The color a new resource sub-group should inherit: the target's own
   * color if it's a group header, or its owning group's color if it's a
   * member row. `null` (no inherited color) if the target is ungrouped —
   * `groupRows` then falls back to whatever a colorless group renders as,
   * same as a manually-created one.
   */
  private colorOfGroupContaining(rowId: string): string | null {
    const row = this.getRowById(rowId);
    if (!row) return null;
    const groupRow =
      row.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.G
        ? row
        : this.getRowById(row.settings?.groupId ?? '');
    return groupRow?.settings?.groupColor ?? null;
  }

  /**
   * Project Bobcat: drop a Resource Library item onto a specific row (Table
   * View drag-and-drop) instead of
   * always appending at the end like `addResourceLibRows` does for the
   * click-to-add flow.
   *
   * Confirmed with the user: a multi-node resource should land as its OWN
   * labeled group, not flat sibling rows spliced into the target's existing
   * group — visually adjacent to whatever agent it was dropped onto, since
   * literal nesting (a group row as a member of another group) isn't
   * something this data model supports. `canBeGrouped` (used by `groupRows`)
   * explicitly refuses any row that already carries a `settings.groupId` —
   * groups here are one level, by design, not an oversight — so the rows are
   * inserted PLAIN first (`insertRowBelow`, no group assignment) and only
   * grouped after, rather than reusing `insertRowAfter`'s group-inheriting
   * placement like the original version of this method did.
   *
   * A single-node resource doesn't get wrapped — `groupRows` itself no-ops
   * under 2 rows, so it lands as one plain row, same as before.
   *
   * `insertRowBelow` and `groupRows` each persist and push their own atomic
   * undo command; wrapping the whole sequence in `_recordGroupMutation`
   * collapses it into ONE undo step, matching `addResourceLibRows`' single
   * command for the append path. `validateAndSync()` at the end matches that
   * same method's validation behavior — a drop is not just a visual
   * placement, it runs the same validation an edit would.
   */
  addResourceLibRowsAtRow = action(
    (resourceLibData: TableNode[], targetRowId: string, groupName: string) => {
      // The catalogue's `id` and `order: 0` used to pass straight through
      // `insertRowBelow`'s `...config`, so a repeated drop collided on id and
      // a drop could sort to the top. Prepared rows carry neither.
      const prepared = this.prepareResourceLibInsert(resourceLibData);
      if (prepared.length === 0) return;

      let firstInsertedId = '';
      const inheritedColor = this.colorOfGroupContaining(targetRowId);

      this._recordGroupMutation(
        'Add resource lib rows',
        () => {
          const newRowIds: string[] = [];
          let anchor = targetRowId;
          prepared.forEach((config) => {
            const newRowId = this.insertRowBelow(anchor, config);
            if (!newRowId) return;
            newRowIds.push(newRowId);
            if (!firstInsertedId) firstInsertedId = newRowId;
            anchor = newRowId;
          });

          if (newRowIds.length > 1) {
            this.groupRows(newRowIds, {
              groupName,
              groupColor: inheritedColor,
            });
          }
        },
        () => firstInsertedId || undefined
      );

      if (firstInsertedId) {
        this.selectRow(firstInsertedId);
      }

      this.validateAndSync();
    }
  );

  /**
   * Project Bobcat: drop a Resource Library item onto a canvas agent card.
   * Unlike the Table View drop (`addResourceLibRowsAtRow`, which lands a
   * multi-node item as its own sub-group beside the target), a canvas drop
   * joins the agent it landed on — on the canvas a separate sub-group would
   * read as a different agent, and a single node would drop out of sight
   * into the ungrouped list. Each node goes in through `addRowToGroup`, the
   * same append the group's own "add node" button uses, all as ONE undo step,
   * then the same `validateAndSync()` every add runs.
   *
   * Returns the new row ids, in order, so the canvas can land on them.
   */
  /**
   * Project Bobcat — the Supervisor's first node (its lowest number): where a
   * built agent sends the customer back for their next question. Null when
   * the solution has no Supervisor Agent group.
   */
  supervisorEntryNumber(): number | null {
    const supervisor = this.rows.find(
      (r) =>
        isGroupRow(r) && /supervisor/i.test(String(r.settings?.groupName ?? ''))
    );
    if (!supervisor) return null;
    const numbers = this.getGroupMembers(supervisor.id)
      .map((id) => toValidNodeNumber(this.getRowById(id)?.nodeNumber))
      .filter((n): n is number => n !== undefined && n > 0);
    return numbers.length ? Math.min(...numbers) : null;
  }

  /** Record where an agent came from on its group row (`AgentSource`). */
  setAgentSource = action((groupRowId: string, source: AgentSource) => {
    const group = this.getRowById(groupRowId);
    if (!group || !isGroupRow(group) || this.isViewer) return;
    this.updateRow({
      rowId: groupRowId,
      updates: {
        settings: {
          ...this.getValue(groupRowId, NodeColumns.settings),
          agentSource: source,
        },
      },
      skipValidationRules: true,
      saveImmediately: true,
    });
  });

  /**
   * Project Bobcat — build a one-node agent out into the steps of `spec` (an
   * agent template or an AI draft). **THIS WRITES AND PERSISTS.** The agent's
   * existing node stays its first step — it carries the example phrases the
   * Supervisor routes on — and takes the spec's first step's content (not its name); the
   * other steps are added to the agent on free numbers right after it, as one
   * undoable change. Declines (false) for a viewer, or an agent that is no
   * longer a single node (someone has built it since).
   */
  fillAgentFromSpec = action(
    (groupRowId: string, spec: AgentSpec, source: AgentSource): boolean => {
      if (this.isViewer) return false;
      const group = this.getRowById(groupRowId);
      if (!group || !isGroupRow(group)) return false;
      const memberIds = this.getGroupMembers(groupRowId);
      const entry =
        memberIds.length === 1 ? this.getRowById(memberIds[0]) : null;
      if (!entry || !isNumberingNodeNumber(entry.nodeNumber)) return false;

      const extra = this.computeSmartNodeNumbers(
        entry.id,
        'below',
        specRowCount(spec) - 1
      );
      const [first, ...rest] = specToRows(spec, {
        // The spec counts in numbers; its rows come back as strings.
        numbers: [entry.nodeNumber, ...extra].map(Number),
        returnTo: this.supervisorEntryNumber(),
      });

      this._recordGroupMutation(
        `Build agent ${String(group.settings?.groupName ?? '')}`.trim(),
        () => {
          // The entry keeps its name (the task's): the Supervisor's
          // phrase-matched route is labelled with it on the canvas.
          this.updateRow({
            rowId: entry.id,
            updates: {
              message: first.message,
              answerRequired: first.answerRequired,
              nluDisabled: first.nluDisabled,
              nextNodes: first.nextNodes,
              variables: first.variables,
              richAssetType: first.richAssetType,
              richAssetContent: first.richAssetContent,
              behaviors: first.behaviors,
            },
            skipValidationRules: true,
            saveImmediately: true,
          });
          rest.forEach((row) => {
            // Never the row defaults' own id, order or version: those are
            // the insert's to set.
            const {
              id: _id,
              order: _order,
              solutionVersionId: _version,
              settings: _settings,
              ...config
            } = row;
            this.addRowToGroup(groupRowId, config);
          });
          this.setAgentSource(groupRowId, source);
        }
      );
      this.validateAndSync();
      return true;
    }
  );

  /**
   * Project Bobcat — after Talk it through creates a solution, build each
   * declared task's one-node agent into a real one: from an agent template
   * when one fits, otherwise drafted by AI. Waits until the table is
   * editable — the first auto-publish locks it, and a template's lookup
   * command has no script yet, so its nodes must not be in that first
   * publish. Every write re-checks that the same solution is still open.
   */
  buildAgentsAfterCreate = async ({
    uniqueId,
    brief,
    tasks,
  }: {
    uniqueId: string;
    brief: string;
    tasks: PendingAgentTask[];
  }): Promise<void> => {
    const stillOpen = () =>
      proStudioService.getSnapshot().context.selectedBot?.uniqueId === uniqueId;
    /** Rows can be written now: this solution, editable, not mid-publish. */
    const canWrite = () => stillOpen() && this.canWriteRows;

    /** The agent Create made for a task: the group whose one node is it. */
    const groupFor = (task: PendingAgentTask): string | null => {
      const name = task.name.trim().toLowerCase();
      const group = this.rows.find((r) => {
        if (!isGroupRow(r)) return false;
        const members = this.getGroupMembers(r.id).map((id) =>
          this.getRowById(id)
        );
        return members.some(
          (m) =>
            !!m?.settings?.taskId &&
            String(m.nodeName ?? '')
              .trim()
              .toLowerCase() === name
        );
      });
      return group?.id ?? null;
    };

    // The plan, at once: which agents come from a template, which AI drafts.
    const fromTemplate: {
      groupId: string;
      template: NonNullable<ReturnType<typeof agentTemplateById>>;
    }[] = [];
    const toDraft: { task: PendingAgentTask; groupId: string }[] = [];
    tasks.forEach((task) => {
      const groupId = groupFor(task);
      if (!groupId) return;
      const template =
        agentTemplateById(task.template) ??
        matchAgentTemplate(task.name, task.phrases);
      if (template) fromTemplate.push({ groupId, template });
      else toDraft.push({ task, groupId });
    });
    if (!fromTemplate.length && !toDraft.length) return;

    // Said straight away, on the agents themselves (a pulsing badge) and in
    // a message, so a one-node agent never looks like the finished result.
    if (canWrite()) {
      fromTemplate.forEach(({ groupId, template }) =>
        this.setAgentSource(groupId, {
          kind: 'template',
          template: template.name,
          status: 'drafting',
        })
      );
      toDraft.forEach(({ groupId }) =>
        this.setAgentSource(groupId, { kind: 'ai', status: 'drafting' })
      );
    }
    sendUserMessage({
      type: 'info',
      header: 'Building your agents',
      text: [
        fromTemplate.length &&
          `${fromTemplate.length} from ${fromTemplate.length === 1 ? 'a template' : 'templates'}`,
        toDraft.length && `drafting ${toDraft.length} with AI`,
      ]
        .filter(Boolean)
        .join(', ')
        .replace(/^./, (c) => c.toUpperCase()),
      autoClose: 6000,
    });

    // The AI drafts start now, while the rest waits.
    const commands = [...this.actionNodeNames];
    const draftRequests = toDraft.map(({ task, groupId }) => ({
      task,
      groupId,
      request: requestAgentDraft({
        name: task.name,
        phrases: task.phrases,
        brief,
        commands,
      }),
    }));
    // Not awaited one by one: a failure is handled where each is read.
    draftRequests.forEach(({ request }) => request.catch(() => undefined));

    // A command no script backs yet gets a placeholder that says so
    // (`placeholder-scripts.ts`), uploaded BEFORE its rows, so a publish
    // never sees a step without its script.
    const placed = new Set<string>();
    const placeholdersFor = async (specs: AgentSpec[]) => {
      // Built-in commands, and any script the solution already has, are
      // never replaced by a placeholder.
      const have = new Set(
        buildScriptFileNames(
          proStudioService.getSnapshot().context.draft?.files ?? [],
          this.actionNodeNames
        )
      );
      const needed = new Map<
        string,
        { decisionVariable: string; answer?: string }
      >();
      specs.forEach((spec) =>
        spec.steps.forEach((step) => {
          if (
            step.type === 'A' &&
            !have.has(step.command) &&
            !placed.has(step.command)
          ) {
            needed.set(step.command, {
              decisionVariable: step.decisionVariable,
              answer: step.stub,
            });
          }
        })
      );
      if (!needed.size || !stillOpen()) return;
      [...needed.keys()].forEach((c) => placed.add(c));
      const uploaded = await uploadPlaceholderScripts(
        [...needed].map(([command, rest]) => ({ command, ...rest }))
      );
      if (uploaded.length && stillOpen()) this.markDraftFilesChanged();
    };

    // Best after the first auto-publish has fired; but it waits on the
    // knowledge base, which can take long or never come, so only briefly.
    try {
      await when(
        () =>
          canWrite() &&
          this.pendingAutoPublish?.uniqueId !== uniqueId &&
          !this.isWaitingForBackendData,
        { timeout: 10_000 }
      );
    } catch {
      // Build as soon as rows can be written instead.
    }

    await placeholdersFor(fromTemplate.map(({ template }) => template.spec));
    try {
      await when(canWrite, { timeout: 600_000 });
      fromTemplate.forEach(({ groupId, template }) =>
        this.fillAgentFromSpec(groupId, template.spec, {
          kind: 'template',
          template: template.name,
          connect: template.spec.connect,
        })
      );
    } catch {
      console.warn('[tableStore] template agents not built: not editable.');
    }

    let drafted = 0;
    await Promise.all(
      draftRequests.map(async ({ task, groupId, request }) => {
        try {
          const result = await request;
          await placeholdersFor([result.spec]);
          await when(canWrite, { timeout: 600_000 });
          if (
            this.fillAgentFromSpec(groupId, result.spec, {
              kind: 'ai',
              status: 'done',
              connect: result.spec.connect,
              summary: result.summary,
            })
          ) {
            drafted += 1;
          }
        } catch (err) {
          console.warn('[tableStore] AI agent draft failed', task.name, err);
          if (stillOpen()) {
            this.setAgentSource(groupId, { kind: 'ai', status: 'failed' });
          }
        }
      })
    );

    if (stillOpen()) {
      const failed = toDraft.length - drafted;
      sendUserMessage({
        type: failed ? 'warning' : 'success',
        header: failed
          ? 'Some agents weren’t drafted'
          : 'Your agents are built',
        text: failed
          ? `${failed} of ${toDraft.length} couldn’t be drafted. Fill them in with Ask AI.`
          : 'Open each on Canvas to review it; steps that call your systems still need connecting.',
        autoClose: 6000,
      });
    }
  };

  addResourceLibRowsToGroup = action(
    (resourceLibData: TableNode[], groupRowId: string): string[] => {
      const groupRow = this.getRowById(groupRowId);
      if (resourceLibData.length === 0 || !groupRow || !isGroupRow(groupRow)) {
        return [];
      }

      const newRowIds: string[] = [];
      // No catalogue id/order, no group rows (groups are one level deep), and
      // free node numbers with the item's own routing rewritten to match.
      const prepared = this.prepareResourceLibInsert(resourceLibData);
      this._recordGroupMutation(
        'Add resource lib rows',
        () => {
          prepared.forEach((config) => {
            const newRowId = this.addRowToGroup(groupRowId, config);
            if (newRowId) newRowIds.push(newRowId);
          });
        },
        () => newRowIds[0]
      );

      if (newRowIds[0]) {
        this.deselectAllRows();
        this.selectRow(newRowIds[0]);
      }

      this.validateAndSync();
      return newRowIds;
    }
  );

  deleteRowsAsync(rowIds: string[]): Promise<void> {
    return this._deleteRowsInternal(rowIds);
  }

  async uploadCsvToDatabase(data: TableRow[]) {
    const currentRows = this.sortedRows.map((r) => r.id);

    let currentOrder = this.getNextNodeOrder(1);
    const rowsToAdd: TableRow[] = data.map((config) => {
      const newRow = {
        ...config,
        order: currentOrder,
        id: config.id || uuidv4(),
        solutionVersionId: this.solutionVersionId,
      };

      currentOrder += this.orderIncrementV2;
      return newRow;
    });

    const BATCH_SIZE = 500;

    emitOnce(async () => {
      await processBatch(rowsToAdd, BATCH_SIZE, (row) => {
        runInAction(async () => {
          this.rows.push(row);
          set(this.rowMap, row.id, row);
          this.fuse.add(row);
        });
      });
    });

    await this.deleteRowsAsync(currentRows);

    await this.saveBulkRowsToDatabase(rowsToAdd);
    runInAction(async () => {
      this.setState(this.isViewer ? 'viewing' : 'editing');
    });

    return true;
  }

  addTaskRows(
    updatedSolutionData?: ProStudioContext['draftVersionSolutionData']
  ) {
    const tasks = getTasksFromSolutionData(updatedSolutionData);

    if (tasks.length === 0) {
      return;
    }

    // Use syncTasksWithNodes to avoid creating duplicate task nodes
    this.syncTasksWithNodes(tasks);
  }

  insertRowAbove(targetRowId: string, config?: Partial<TableRow>) {
    const targetRow = this.rows.find((r) => r.id === targetRowId);
    if (!targetRow) {
      throw Error('Target row not found');
    }

    const targetIndex = this.rows.findIndex((r) => r.id === targetRowId);
    const newRow: TableRow = {
      ...defaultRowData,
      // PSTUDIO-2871: adjacency-aware smart number (Rule 2). `...config` overrides.
      [NodeColumns.nodeNumber]:
        config?.nodeNumber ??
        this.computeSmartNodeNumbers(targetRowId, 'above', 1)[0],
      [NodeColumns.order]: this.calculateNewRowOrder(targetRowId, 'above'),
      [NodeColumns.id]: uuidv4(),
      [NodeColumns.solutionVersionId]: this.solutionVersionId,
      ...config, // Apply any provided configuration
    };

    const command: Command = {
      execute: () => {
        this.rows.splice(targetIndex, 0, newRow);
        set(this.rowMap, newRow.id, newRow);
        this.fuse.add(newRow);
        this.saveRowsToDatabase([newRow]);
        this.checkNearbyRowOrder(newRow.id);

        if (this.validationMode === ValidationMode.Manual) {
          this.hasUnvalidatedChangesInManualMode = true;
        }
        this.setState(this.isViewer ? 'viewing' : 'editing');
      },
      undo: () => {
        const insertedRow = this.rows.find((row) => row.id === newRow.id);
        if (insertedRow) {
          this.rows.remove(insertedRow);
          this.rowMap.delete(newRow.id);
          this.fuse.remove((doc) => doc.id === newRow.id);
        }
        // Re-grid orders. (index + 1) * orderIncrementV2 — the previous
        // `row.order = index` produced order=0 on the first row, which trips
        // normalizeInitialRows' currOrder<=prevOrder check on the next load
        // and forced a follow-up megamutation. Route through the orders-only
        // path so this single undo doesn't ship full rows.
        const orderUpdates: RowOrderUpdate[] = [];
        this.rows.forEach((row, index) => {
          const newOrder = (index + 1) * this.orderIncrementV2;
          if (row.order !== newOrder) {
            row.order = newOrder;
            orderUpdates.push({
              id: row.id,
              order: newOrder,
              solutionVersionId: this.solutionVersionId,
            });
          }
        });
        this.saveRowOrdersToDatabase(orderUpdates);
        // PSTUDIO-4121: the row leaves the DATABASE too, not only `rows`.
        // Without this the undone insert came back on the next load — and with
        // a node number renumbering may have handed to another row in the
        // meantime. `addEmptyRow` and `duplicateRow` have always done this from
        // their own undo; these insert paths did not.
        //
        // Scope, because the call sites are easy to overcount: this undo runs
        // for a single insert OUTSIDE a group. An insert into a group
        // (`addRowToGroup`, `_insertIntoGroupAtomic`) and a multi-row insert
        // both wrap themselves in `_recordGroupMutation`, which rolls this
        // command off the stack, so the undo that runs there is
        // `_restoreToSnapshot` — which has always deleted in the DB.
        this._deleteNodesInDb([newRow.id]);
      },
      description: `Insert row above ${targetRowId}`,
      focusTarget: { rowId: newRow.id },
      // PSTUDIO-3845: asymmetric save path. execute/redo runs a full-row save
      // (saveRowsToDatabase → validateAndSync, recompiles), but undo re-grids
      // via the orders-only saveRowOrdersToDatabase, which never recompiles —
      // so an insert-undo in Auto would leave validation stale (the removed
      // node's errors linger). Force the recompile on the undo leg only; redo
      // already recompiles via its own save path.
      recompilesViaSavePath: (direction) => direction === 'redo',
    };

    this.executeCommand(command);

    return newRow.id;
  }

  insertRowBelow = action((targetRowId: string, config?: Partial<TableRow>) => {
    const targetRow = this.rows.find((r) => r.id === targetRowId);
    if (!targetRow) {
      throw Error('Target row not found');
    }

    const targetIndex = this.rows.findIndex((r) => r.id === targetRowId);
    const newRow: TableRow = {
      ...defaultRowData,
      // PSTUDIO-2871: adjacency-aware smart number (Rule 2). `...config` below
      // still overrides it — batch inserts and legacy callers pass an explicit
      // nodeNumber.
      [NodeColumns.nodeNumber]:
        config?.nodeNumber ??
        this.computeSmartNodeNumbers(targetRowId, 'below', 1)[0],
      [NodeColumns.order]: this.calculateNewRowOrder(targetRowId, 'below'),
      [NodeColumns.id]: uuidv4(),
      [NodeColumns.solutionVersionId]: this.solutionVersionId,
      ...config, // Apply any provided configuration
    };

    const command: Command = {
      execute: () => {
        this.rows.splice(targetIndex + 1, 0, newRow);
        set(this.rowMap, newRow.id, newRow);
        this.fuse.add(newRow);
        this.saveRowsToDatabase([newRow]);
        this.checkNearbyRowOrder(newRow.id);

        if (this.validationMode === ValidationMode.Manual) {
          this.hasUnvalidatedChangesInManualMode = true;
        }
        this.setState(this.isViewer ? 'viewing' : 'editing');
      },
      undo: () => {
        const insertedRow = this.rows.find((row) => row.id === newRow.id);
        if (insertedRow) {
          this.rows.remove(insertedRow);
          this.rowMap.delete(newRow.id);
          this.fuse.remove((doc) => doc.id === newRow.id);
        }
        // See insertRowAbove undo for rationale.
        const orderUpdates: RowOrderUpdate[] = [];
        this.rows.forEach((row, index) => {
          const newOrder = (index + 1) * this.orderIncrementV2;
          if (row.order !== newOrder) {
            row.order = newOrder;
            orderUpdates.push({
              id: row.id,
              order: newOrder,
              solutionVersionId: this.solutionVersionId,
            });
          }
        });
        this.saveRowOrdersToDatabase(orderUpdates);
        // PSTUDIO-4121: the row leaves the DATABASE too — see `insertRowAbove`
        // for the reasoning and for which entrances do NOT reach this line.
        this._deleteNodesInDb([newRow.id]);
      },
      description: `Insert row below ${targetRowId}`,
      focusTarget: { rowId: newRow.id },
      // PSTUDIO-3845: asymmetric save path — see insertRowAbove. redo full-row
      // saves (recompiles); undo re-grids orders-only (does not) → force the
      // recompile on the undo leg only.
      recompilesViaSavePath: (direction) => direction === 'redo',
    };

    this.executeCommand(command);

    return newRow.id;
  });

  /**
   * PSTUDIO-2316: insert a new node directly below `targetRowId`, preserving
   * group membership.
   *  • group header selected  → append as the group's LAST member (anchor =
   *    last subRow). Leaves subRows[0] — the group's displayed number — intact
   *    (PSTUDIO-3063).
   *  • grouped sub-row selected → join the same group, spliced into subRows
   *    right after the selected member.
   *  • ungrouped row → plain insertRowBelow.
   * Returns the new row id so batch inserts can chain.
   */
  insertRowAfter = action(
    (targetRowId: string, config?: Partial<TableRow>): string => {
      const target = this.getRowById(targetRowId);
      const isGroupHeader =
        target?.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.G;
      const groupId = isGroupHeader ? targetRowId : target?.settings?.groupId;

      if (!groupId) return this.insertRowBelow(targetRowId, config);
      if (isGroupHeader) return this.addRowToGroup(targetRowId, config);

      return this._insertIntoGroupAtomic(targetRowId, groupId, 'after', config);
    }
  );

  /**
   * PSTUDIO-3167: insert a new node into the group `groupId`, adjacent to
   * `targetRowId`, as a single atomic (undo-reversible) mutation. `direction`
   * picks the side: 'before' uses insertRowAbove + splices ahead of the anchor;
   * 'after' uses insertRowBelow + splices behind it. Mirrors addRowToGroup's
   * _recordGroupMutation wrapping so undo restores BOTH the row and the header
   * subRows in one step. The composite (insertRow* pushes its own undo command,
   * the header updateRow pushes none) is only batched by emitOnce — not made
   * atomic on the undo stack — hence the _recordGroupMutation wrap. Returns the
   * new row id, or '' if nothing was inserted — the same "no row inserted"
   * sentinel as addRowToGroup; current callers (insertRowBefore/After from the
   * row context menu) ignore the id rather than focus/splice on it (PSTUDIO-3202).
   */
  /* eslint-disable max-params */
  private _insertIntoGroupAtomic(
    targetRowId: string,
    groupId: string,
    direction: 'before' | 'after',
    config?: Partial<TableRow>
  ): string {
    /* eslint-enable max-params */
    let newRowId = '';
    this._recordGroupMutation(
      'Insert node into group',
      () => {
        emitOnce(() => {
          newRowId =
            direction === 'before'
              ? this.insertRowAbove(targetRowId, {
                  ...config,
                  settings: { groupId },
                })
              : this.insertRowBelow(targetRowId, {
                  ...config,
                  settings: { groupId },
                });
          const settings = this.getValue(groupId, NodeColumns.settings);
          const subRows = [...(settings?.subRows ?? [])];
          const at = subRows.indexOf(targetRowId);
          if (at === -1) {
            if (direction === 'before') subRows.unshift(newRowId);
            else subRows.push(newRowId);
          } else {
            subRows.splice(direction === 'before' ? at : at + 1, 0, newRowId);
          }
          this.updateRow({
            rowId: groupId,
            updates: { settings: { ...settings, subRows } },
            skipValidationRules: true,
            saveImmediately: true,
          });
        });
      },
      () => newRowId || undefined
    );
    return newRowId;
  }

  /**
   * PSTUDIO-3167: insert a new node directly above `targetRowId`, preserving
   * group membership (mirror of insertRowAfter).
   *  • grouped sub-row selected → join the same group, spliced into subRows
   *    right before the selected member (becomes subRows[0] if inserted above
   *    the first member — the displayed group number then follows the new row,
   *    which is the user's explicit intent here).
   *  • group header selected     → plain insertRowAbove (a row above the whole
   *    group, ungrouped). The header itself never shows this menu, so this is a
   *    defensive fallback for programmatic callers.
   *  • ungrouped row             → plain insertRowAbove.
   * Returns the new row id so batch inserts can chain.
   */
  insertRowBefore = action(
    (targetRowId: string, config?: Partial<TableRow>): string => {
      const target = this.getRowById(targetRowId);
      const isGroupHeader =
        target?.nodeType === Tenancy_Node_Type_Enum_Ps_Enum.G;
      const groupId = isGroupHeader ? undefined : target?.settings?.groupId;

      if (!groupId) return this.insertRowAbove(targetRowId, config);

      return this._insertIntoGroupAtomic(
        targetRowId,
        groupId,
        'before',
        config
      );
    }
  );

  /**
   * PSTUDIO-2871 / PSTUDIO-2872: insert `count` new nodes at (targetRowId,
   * position) with adjacency-aware, guaranteed-unique numbers, and select the
   * result so the user sees where they landed. Numbers are computed ONCE for the
   * whole batch (so Expansion/Family strategies use the real count), then pushed
   * row-by-row through the group-aware insert plumbing. count is clamped to 1..99.
   */
  insertRowsSmart = action(
    (
      targetRowId: string,
      position: 'above' | 'below',
      count: number
    ): string[] => {
      const n = Math.max(1, Math.min(99, Math.floor(count) || 1));
      const numbers = this.computeSmartNodeNumbers(targetRowId, position, n);
      const insertedIds: string[] = [];

      // The row-insertion loop. Chains anchors so the block stacks in order.
      const runInsert = () => {
        let anchor = targetRowId;
        for (let i = 0; i < n; i++) {
          const newId =
            position === 'above'
              ? i === 0
                ? this.insertRowBefore(anchor, { nodeNumber: numbers[i] })
                : this.insertRowAfter(anchor, { nodeNumber: numbers[i] })
              : this.insertRowAfter(anchor, { nodeNumber: numbers[i] });
          if (!newId) break; // group insert sentinel — stop chaining
          insertedIds.push(newId);
          anchor = newId; // chain so rows stack in order
        }
      };

      emitOnce(() => {
        if (n === 1) {
          // Single-node insert keeps its original, already-atomic command
          // (behaviour unchanged — PSTUDIO-3640 AC #2).
          runInsert();
        } else {
          // PSTUDIO-3640: collapse the N per-node insert commands into ONE
          // atomic undo/redo step, mirroring group mutations. One user action
          // = one Ctrl+Z. The outer _recordGroupMutation strips the inner
          // per-node commands (undoStack.splice) and pushes a single
          // snapshot-swap command; nested group inserts are stripped too.
          this._recordGroupMutation(
            `Insert ${n} nodes ${position} ${targetRowId}`,
            runInsert,
            () => insertedIds[0]
          );
        }

        // Visual feedback (PSTUDIO-2872 §3): select the freshly inserted rows.
        if (insertedIds.length > 0) {
          this.deselectAllRows();
          insertedIds.forEach((id) => this.selectRow(id));
        }
      });

      // PSTUDIO-3848: bring the WHOLE inserted block into view. Selecting the
      // rows above only highlights them — without an explicit range scroll the
      // viewport can jump (native focus return on context-menu close, or a
      // virtualizer remount when `count` changes) and cut off part of what was
      // just inserted. Every single-index scroll helper centers ONE row, so a
      // multi-node insert would still reveal only one; reveal the range instead.
      this._revealInsertedRange(insertedIds);

      return insertedIds;
    }
  );

  /**
   * PSTUDIO-3848: scroll the viewport so a freshly inserted/pasted, contiguous
   * block of rows is visible. Shared by insert (`insertRowsSmart`) and paste
   * (`pasteRow`). The rows were added THIS tick and only render on the next React
   * commit, so defer past it with a DOUBLE requestAnimationFrame — the rows must
   * be in the DOM before <Table/> can scroll to them. Bail up-front when no
   * viewport is wired up (unit tests / an unmounted <Table/>); the guard is
   * repeated inside the deferred callback in case <Table/> unmounts between
   * scheduling and the frame, so we never do a pointless getVisibleRows() pass or
   * call a stale callback. Positions are resolved against `getVisibleRows()` (the
   * search-filtered visible list) and both the first/last ids and indices are
   * handed to <Table/>: it scrolls the real DOM rows (reliable for freshly-added
   * rows, whose virtualizer measurements aren't ready) and uses the indices only
   * as a fallback when a row is virtualized out of the DOM. A row filtered out of
   * the visible set (e.g. by an active search) is skipped.
   */
  private _revealInsertedRange(insertedIds: string[]) {
    if (insertedIds.length === 0) return;
    if (!this.scrollRangeIntoView) return;
    const reveal = () => {
      if (!this.scrollRangeIntoView) return; // <Table/> unmounted before the frame
      const inserted = new Set(insertedIds);
      const visible = this.getVisibleRows();
      let firstIndex = -1;
      let lastIndex = -1;
      for (let i = 0; i < visible.length; i++) {
        if (!inserted.has(visible[i].id)) continue;
        if (firstIndex === -1) firstIndex = i;
        lastIndex = i;
      }
      if (firstIndex === -1) return; // none of the inserted rows are visible
      this.scrollRangeIntoView(
        visible[firstIndex].id,
        visible[lastIndex].id,
        firstIndex,
        lastIndex
      );
    };
    requestAnimationFrame(() => requestAnimationFrame(reveal));
  }

  // NOTE: sortedRows / getSortedRows deliberately return the RAW numeric-`order`
  // sequence, NOT the group-contiguous one. syncGroupMembershipForRow and the
  // single-row drag math derive group membership from a row's RAW position —
  // detecting a drag-OUT precisely because the row leaves its group's contiguous
  // run in raw order. Feeding them the arranged order would mask that signal and
  // break drag-out / auto-dissolve (PSTUDIO-3159). Only the RENDER + index-map
  // source (getAllSortedRows) is arranged (PSTUDIO-3867); reconciling the raw
  // drag math with the arranged render is tracked as a follow-up.
  get sortedRows() {
    return this.rows.slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  }

  getSortedRows = computedFn(() => {
    return this.rows.slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  });

  getAllSortedRows = computedFn(() => {
    const sorted = [...this.rows, ...this.pendingRowsToRender]
      .slice()
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    // PSTUDIO-3867: guarantee group members render contiguously right after
    // their header so the CSS group-boundary adjacency can never detach a
    // member. No-op (same reference) for already-contiguous datasets.
    return arrangeGroupsContiguously(sorted);
  });

  clearMetadata() {
    this.lastServerSync = undefined;
    // this.lastServerSyncHash = undefined;
    this.lastServerValidation = undefined;
    this.lastServerValidationHash = undefined;
    this.lastServerValidationMessages = [];
    this.hasUnvalidatedChangesInManualMode = false;
    this.columnsSizes = defaultColumnWidths;
    this.columnPinningSettings = defaultColumnPinningSettings;
    this.columnWrapSettings = { ...defaultColumnWrapSettings };
    tableStore.setErrors([]);
    // PSTUDIO-4120: the verdict belongs to the dataset that is going away, so it
    // dies with it — alongside `hasUnvalidatedChangesInManualMode` above.
    // Without this the flag survives the switch: `clearTable()` does call
    // `validateAndSync()`, but with NO mode argument, which in Manual takes
    // `validate()`'s short-circuit before either empty-table exit — the only
    // exits that clear it. The user then opens a DIFFERENT solution and is told
    // its validation did not finish, with Publish refused, over a solution this
    // session never validated at all.
    //
    // `pifErrors` and the raw diagnostics go too, for the same reason and for a
    // sharper one: while a failure stands, the popover deliberately re-serves
    // `originalPifCompilerDiagnostics`, and those would otherwise be findings
    // from the PREVIOUS solution re-addressed against the new solution's rows.
    tableStore.setPifErrors([]);
    this.originalPifCompilerDiagnostics = [];
    // The compiler payload is per-dataset too. It is only ever read by the
    // "Download Agent Compiler Data" archive, and leaving it behind made that
    // archive mix solutions: `data_for_compiling.json` from the PREVIOUS
    // solution beside an empty `diagnostics.json` and the new solution's rows.
    // That file gets sent to compiler support.
    this.dataForCompiling = '';
    this.setValidationFailure(null);
  }

  clearTable = action(() => {
    this.dropDataset();
    this.validateAndSync();
  });

  /**
   * `clearTable` without its `validateAndSync`, which uploads the (now empty)
   * rows as the version's `bot.csv`. PSTUDIO-4450: a change of row source
   * drops the other source's rows but keeps the version, so it must not
   * publish an empty compiler artefact for it.
   */
  dropDataset = action(() => {
    // PSTUDIO-4449: a pass a coworker's change scheduled is for THIS dataset;
    // run against the next one it validates the wrong rows — or, mid-switch,
    // none at all ("Bot Name is required").
    this.debouncedValidateAndSync.cancel();
    clearTimeout(this.liveLocalValidation);
    // PSTUDIO-1433: the dataset is going away, so any open edit/menu on it is
    // gone too. Without this the panel session stays registered against a row
    // that no longer exists, `hasActiveCellEdit` never goes false again, and the
    // cross-tab reload is blocked for the rest of the session.
    this.endPanelEdit();
    this.requestCloseAllContextMenus();
    // PSTUDIO-4342: the refused-write records of the rows going away, reported
    // (see _failedNodeSaves) — before they go, so the loss is counted against
    // them.
    this._dropRefusedWrites();
    this.rows.replace([]);
    this.pendingRowsToRender = [];
    // PSTUDIO-3990: the dataset is going away, so a soft-delete marker left by
    // a delete whose idle callback never ran must not survive into the NEXT
    // one, where a row carrying the same id would be silently dropped from the
    // publish gate.
    this.pendingDeletionRowIds.clear();
    this.rowMap.clear();
    this.fuse.setCollection([]);
    this.virtualWindowStart = 0;
    this.virtualWindowEnd = 200;
    // PSTUDIO-2154: drop undo/redo history when the table is cleared (e.g. on a
    // solution-version switch) so commands can't reference rows from a
    // different dataset. Also reset the (non-persisted) sort so it doesn't carry
    // over to a different dataset.
    this.sortingState = [];
    // PSTUDIO-3866: drop any in-flight group-color drag preview so a stale key
    // can't survive a solution-version switch and mis-tint a group in the next
    // dataset whose id happens to collide with an orphaned one.
    this.groupColorPreview.clear();
    this.clearHistory();
  });

  clearRows(rowIds: string[]) {
    emitOnce(() => {
      rowIds.forEach((id) => {
        this.clearRow(id);
      });
    });
  }

  clearRow(rowId: string) {
    // PSTUDIO-2821: route through the shared teardown so clearing a row mid-edit
    // also drops the draft/intent/editingCellId, not just the legacy flags.
    this._clearCellEditingState();
    const row = this.rows.find((r) => r.id === rowId);
    if (row) {
      const newData = {
        ...defaultRowData,
        [NodeColumns.id]: row.id,
        [NodeColumns.solutionVersionId]: row.solutionVersionId,
        [NodeColumns.order]: row.order,
        [NodeColumns.nodeNumber]: row.nodeNumber,
      };

      const oldValues = { ...row };

      const command: Command = {
        execute: () => {
          const rowIndex = this.rows.findIndex((r) => r.id === rowId);
          if (rowIndex !== -1) {
            this.updateRow({
              rowId,
              updates: newData,
              skipValidationRules: false,
              saveImmediately: true,
            });
          }
        },
        undo: () => {
          const rowIndex = this.rows.findIndex((r) => r.id === rowId);
          if (rowIndex !== -1) {
            this.updateRow({
              rowId,
              updates: oldValues,
              skipValidationRules: false,
              saveImmediately: true,
            });
          }
        },
        description: `Clear  row ${rowId}`,
        focusTarget: { rowId },
      };

      this.executeCommand(command);
    }
  }

  // clearSelectedCells() {
  //   this.selectedCells.forEach(({ rowId, columnId }) => {
  //     const row = this.rows.find((r) => r.id === rowId);
  //     if (row) {
  //       // Handle special cases for status and priority
  //       if (columnId === 'status') {
  //         this.updateRow(rowId, { [columnId]: 'pending' } as Partial<TableRow>);
  //       } else if (columnId === 'priority') {
  //         this.updateRow(rowId, { [columnId]: 3 } as Partial<TableRow>);
  //       } else {
  //         this.updateRow(rowId, { [columnId]: '' } as Partial<TableRow>);
  //       }
  //     }
  //   });
  // }

  toggleOutOfScopeIntent(rowId: string) {
    if (!rowId) {
      throw Error('rowId is required!');
    }

    const row = this.rows.find((r) => r.id === rowId);
    if (!row) {
      throw Error('Row not found');
    }

    const currentIntent = row.intent;
    const isCurrentlyOutOfScope = currentIntent === 'out_of_scope';

    const rowsToUpdate: TableRow[] = [];
    const previousRowStates: { [key: string]: TableRow } = {};

    if (!isCurrentlyOutOfScope) {
      const existingOutOfScopeRow = this.rows.find(
        (r) => r.id !== rowId && r.intent === 'out_of_scope'
      );

      if (existingOutOfScopeRow) {
        previousRowStates[existingOutOfScopeRow.id] = {
          ...existingOutOfScopeRow,
        };
        rowsToUpdate.push({
          ...existingOutOfScopeRow,
          intent: undefined,
        });
      }
    }

    previousRowStates[rowId] = { ...row };
    rowsToUpdate.push({
      ...row,
      intent: isCurrentlyOutOfScope ? undefined : 'out_of_scope',
    });

    const command: Command = {
      execute: () => {
        rowsToUpdate.forEach((updatedRow) => {
          const rowIndex = this.rows.findIndex((r) => r.id === updatedRow.id);
          if (rowIndex !== -1) {
            set(this.rows, rowIndex, updatedRow);
            // Keep rowMap in sync — native cells read intent from rowMap
            // (PSTUDIO-2406), and the NL Entry tooltip won't appear without
            // this update.
            set(this.rowMap, updatedRow.id, updatedRow);
            this.fuse.remove((doc) => doc.id === updatedRow.id);
            this.fuse.add(updatedRow);
          }
        });
        this.saveRowsToDatabase(rowsToUpdate);
      },
      undo: () => {
        Object.entries(previousRowStates).forEach(([id, prevState]) => {
          const rowIndex = this.rows.findIndex((r) => r.id === id);
          if (rowIndex !== -1) {
            set(this.rows, rowIndex, prevState);
            set(this.rowMap, id, prevState);
            this.fuse.remove((doc) => doc.id === id);
            this.fuse.add(prevState);
          }
        });
        this.saveRowsToDatabase(Object.values(previousRowStates));
      },
      description: `Toggle out_of_scope intent for row ${rowId}`,
      focusTarget: { rowId },
    };

    this.executeCommand(command);
  }

  canDeleteRow(rowId: string) {
    if (!rowId) return false;

    if (this.rows.find((row) => row.id === rowId)) {
      return true;
    }

    return false;
    // return this.rows.length > 1; // Prevent deleting the last row
  }

  // PSTUDIO perf: id→position indexes built once per row-list change (O(N)),
  // so the position getters below are O(1) lookups. A render of ~50 visible
  // rows previously ran ~50 × O(N) filter+findIndex scans, re-triggered by any
  // row edit; on a large (5,000-node) workspace that dominated edit latency.
  // The values are byte-identical to the old scans (row-index-maps.test.ts).
  get visibleRowIndexById(): Map<string, number> {
    return buildVisibleRowIndexMap(this.getAllSortedRows());
  }

  get displayNumberById(): Map<string, number> {
    // The canonical `isGroupRow`, not a raw `=== G`: it is case-INSENSITIVE,
    // and legacy/imported data may carry 'G' (PSTUDIO-3159). With the raw
    // compare this map counted such a header as a node while the node-number
    // derivation and the publish gate skipped it — so the popover printed a row
    // number one too high for every node below a legacy group header.
    return buildDisplayNumberMap(this.getAllSortedRows(), (row) =>
      isGroupRow(row)
    );
  }

  getRowIndex = computedFn((rowId: string) => {
    return this.visibleRowIndexById.get(rowId) ?? -1;
  });

  getSortedRowIndex = computedFn((rowId: string) => {
    return this.visibleRowIndexById.get(rowId) ?? -1;
  });

  // Visual 1-based row position, skipping group-header rows. A group header is
  // a structural separator, not a node, so it must not consume a position —
  // otherwise every node after a group is numbered one too high (9 → header
  // → 11 instead of 9 → header → 10). Returns 0 for a group row (unnumbered).
  //
  // Hidden subRows are deliberately NOT filtered out here: collapsing a group
  // must not renumber the rows below it. A collapsed group's children keep
  // owning their positions (which simply hide along with them), so a node that
  // reads as 13 while the group above is expanded stays 13 when it collapses,
  // rather than jumping up by the number of hidden children.
  getRowDisplayNumber = computedFn((rowId: string) => {
    return this.displayNumberById.get(rowId) ?? 0;
  });

  // Highest node number in use, ignoring group-header rows. Group rows are
  // created with a nodeNumber of their own but are not nodes, so counting them
  // inflated the next number (Add-node-to-group produced 23 instead of 22).
  getMaxNodeNumber(): string {
    // PSTUDIO-4453: compared by value — as text "9" would beat "10".
    return this.rows.reduce(
      (max, row) =>
        row.nodeType !== Tenancy_Node_Type_Enum_Ps_Enum.G &&
        isCanonicalNodeNumber(row.nodeNumber) &&
        compareNodeNumbers(row.nodeNumber, max) > 0
          ? row.nodeNumber
          : max,
      '0'
    );
  }

  /**
   * PSTUDIO-3992: the anchor an APPEND numbers from — the last row that carries
   * a usable number, group headers excluded (a header is a separator, never a
   * numbering reference). Feeding this to `assignNodeNumbers` as `reference`
   * with `upper = null` is what makes "add at the end" produce a number next to
   * the end, instead of `getMaxNodeNumber() + 1` — which jumps to whatever the
   * largest number in the solution happens to be, however far away. That was
   * the reported defect for paste; these entrances reach the same state.
   */
  getAppendNumberingAnchor(): number | null {
    // PSTUDIO-4083: `rowsForNumbering` — a row inside the soft-delete window is
    // not the neighbour an append should count on from. The arithmetic itself is
    // shared with `createTaskNode` (node-numbering-inputs.ts); the rows are the
    // only thing an entrance decides.
    return appendNumberingAnchorOf(this.rowsForNumbering);
  }

  /**
   * PSTUDIO-4083: is this row inside the soft-delete window? `_deleteRowsInternal`
   * flips `hidden` and marks the id, then only an idle callback later removes the
   * row from `this.rows` — so between the two every numbering path could still see
   * it. It must not: its number is about to be free, and it is not a neighbour the
   * next number should be derived from.
   *
   * The predicate itself is `isClaimedByDelete` (PSTUDIO-4120), shared with
   * `rowsForValidation` and the PIF row set — both halves are load-bearing and
   * each has been wrong once, so its docblock owns that argument and this stays
   * a one-line delegate over the store's own `pendingDeletionRowIds`.
   *
   * Worth adding to what that file says, because it is the case numbering hits
   * most often: the commonest revival of a marked row is not a `rows.replace`
   * at all — `applyGroupCollapseVisibility({ onlyIfChanged: true })` flips
   * `hidden` back to `false` for any row outside a collapsed group, and it runs
   * on each group toggle AND on the cross-tab merge's post-reconcile.
   * Requiring `hidden` is what makes such a row
   * count again on its own; a stale mark alone would keep the number it
   * visibly holds out of the collision set and hand it to the next node — a
   * duplicate, the direction PSTUDIO-3914 removed the auto-repair for.
   */
  isRowPendingDeletion(row: { id: string; hidden?: boolean }): boolean {
    return isClaimedByDelete(row, this.pendingDeletionRowIds);
  }

  /**
   * PSTUDIO-4083: the rows a numbering pass may look at — `this.rows` minus the
   * ones inside the soft-delete window. The mirror of `rowsForValidation`, and
   * for the same reason: one predicate applied in ONE place beats every entrance
   * remembering to filter (`createTaskNode` is the entrance that did not).
   *
   * Returns `this.rows` BY REFERENCE when nothing is being deleted — the
   * overwhelmingly common case — so the ordinary path allocates nothing and reads
   * one `size` instead of one `ObservableSet.has` per row.
   *
   * NOT a full mirror in one respect: `rowsForValidation` also folds in
   * `pendingRowsToRender`, and this deliberately does not. Numbering never
   * allocates into the unrendered tail, and that population path is dormant
   * anyway (`startingNodes.slice(50)`). If chunked loading returns, this is the
   * line to revisit — the tail's numbers would be missing from the collision
   * set.
   *
   * Read from actions only. Inside a reaction this computed would depend on
   * every row's `hidden`, so a group collapse (a mass `hidden` write) would
   * invalidate it and hand out a fresh array — the churn `.claude/rules/
   * react-apps.md` calls out. `readonly` because it can BE `this.rows`: sorting
   * or pushing through it would mutate the store's observable array in place.
   */
  get rowsForNumbering(): readonly TableRow[] {
    if (this.pendingDeletionRowIds.size === 0) return this.rows;
    return this.rows.filter((row) => !this.isRowPendingDeletion(row));
  }

  /**
   * PSTUDIO-4083: `rowsForNumbering` — a row on its way out holds a number that
   * is about to be free, and handing it out now is what the delete just
   * enabled. Group headers are NOT skipped: see numberingNumbersOf.
   *
   * Returns the two sets SEPARATELY because `assignNodeNumbers` treats them
   * differently: both block a collision, only `own` anchors Fallback. Walking
   * the rows is expensive (see the helper's measured cost), so a caller that
   * numbers more than once per gesture must hoist this out of its loop.
   */
  getNumberingNumbers(): { own: number[]; referenced: number[] } {
    return numberingNumbersOf(this.rowsForNumbering);
  }

  /**
   * PSTUDIO-2871: derive the (reference, upper) node numbers around an insertion
   * point for the smart-numbering algorithm. `reference` is the node directly
   * ABOVE the insertion point (Rule 2), `upper` the node directly below it. Group
   * headers and rows without a valid positive number are skipped. A missing
   * neighbour is null (reference=null ⇒ the algorithm falls back to max+1).
   */
  resolveNumberingContext(
    targetRowId: string,
    position: 'above' | 'below',
    /** PSTUDIO-3992 — paste-side adjustment; the insert path passes none.
     *
     *  PSTUDIO-3992 also carried an `excludeIds` option, which dropped a
     *  `'replace'`'s dying rows from the neighbour lookup so the clone could
     *  take the number that just died. The 2026-09-08 sign-off reversed that
     *  rule (NODE_NUMBERING_BEHAVIOR.md rule 1.4), which left the option with no
     *  caller at all — so it is gone rather than kept as an unreachable branch
     *  no test can check. */
    options?: {
      /** Rows that are `hidden` yet must still count as neighbours. `hidden` is
       *  overloaded, and the two things it marks pull in OPPOSITE directions:
       *
       *  - a member of a COLLAPSED group, and a row dying WITH the operation
       *    being numbered (a multi-row replace has already hidden its
       *    non-anchor selection): both are real rows the new ones land next to,
       *    and both MUST be passed here, or they stop bounding the slot;
       *  - a row marked by an EARLIER delete gesture: its number is already
       *    given up, and passing it back in would hand that number to the next
       *    node. Never put one of those in this set.
       *
       *  `pasteRow` is the only caller and discharges both halves where it
       *  builds `collapsedChildIds` (filtered) and `dyingIds` (readmitted). */
      visibleIds?: ReadonlySet<string>;
    }
  ): { reference: number | null; upper: number | null } {
    const { visibleIds } = options ?? {};
    // A row inside the soft-delete window is always `hidden`, so this filter
    // already drops it (PSTUDIO-4083). The one way back in is `visibleIds`,
    // which is why its doc above makes excluding those rows the CALLER's duty —
    // a second copy of the predicate here would be unreachable, and an
    // unreachable branch is a claim no test can check.
    const sorted = this.getAllSortedRows().filter(
      (r) => r.hidden !== true || visibleIds?.has(r.id)
    );
    const targetIndex = sorted.findIndex((r) => r.id === targetRowId);
    if (targetIndex === -1) return { reference: null, upper: null };

    const numberAt = (row: TableRow | undefined): number | null => {
      // PSTUDIO-3992: `isGroupRow`, not a strict `=== 'g'`. The enum value is
      // lowercase but legacy rows carry 'G' (see group-row-guards.ts), and a
      // strict check made the SAME row a group for the occupied set and a node
      // for the neighbour lookup — so a legacy header could become the
      // `reference` the next number is derived from.
      if (!row || isGroupRow(row)) return null;
      return toValidNodeNumber(row.nodeNumber) ?? null;
    };
    const nearestBelow = (fromIndex: number): number | null => {
      for (let i = fromIndex; i < sorted.length; i++) {
        const num = numberAt(sorted[i]);
        if (num !== null) return num;
      }
      return null;
    };
    const nearestAbove = (fromIndex: number): number | null => {
      for (let i = fromIndex; i >= 0; i--) {
        const num = numberAt(sorted[i]);
        if (num !== null) return num;
      }
      return null;
    };

    if (position === 'below') {
      return {
        reference: nearestAbove(targetIndex),
        upper: nearestBelow(targetIndex + 1),
      };
    }
    // 'above'
    return {
      reference: nearestAbove(targetIndex - 1),
      upper: nearestBelow(targetIndex),
    };
  }

  /**
   * PSTUDIO-2871: `count` unique node numbers for an insertion at
   * (targetRowId, position), via Edan's algorithm. Callers pass 1 for a single
   * insert; batch inserts pass the real count so Expansion/Family strategies use
   * the correct base.
   */
  computeSmartNodeNumbers(
    targetRowId: string,
    position: 'above' | 'below',
    count: number
  ): string[] {
    const { reference, upper } = this.resolveNumberingContext(
      targetRowId,
      position
    );
    const smartNumbers = this.getNumberingNumbers();
    return assignNodeNumberStrings(
      reference,
      upper,
      count,
      smartNumbers.own,
      smartNumbers.referenced
    );
  }

  // selectEntireRow(rowId: string) {
  //   const columns = [
  //     'nodeNumber',
  //     'nodeType',
  //     'nodeName',
  //     'message',
  //     'nextNodes',
  //     'richAssetType',
  //     'answerRequired',
  //     'behaviors',
  //     'richAssetContent',
  //     'variables',
  //   ];
  //   this.selectedCells = columns.map((columnId) => ({
  //     rowId,
  //     columnId,
  //   }));
  // }

  // Helper method to check if entire row is selected
  // isRowSelected(rowId: string) {
  //   const columns = [
  //     'nodeNumber',
  //     'nodeType',
  //     'nodeName',
  //     'message',
  //     'nextNodes',
  //     'richAssetType',
  //     'answerRequired',
  //     'behaviors',
  //     'richAssetContent',
  //     'variables',
  //   ];
  //   return columns.every((columnId) =>
  //     this.selectedCells.some(
  //       (cell) => cell.rowId === rowId && cell.columnId === columnId
  //     )
  //   );
  // }

  debouncedValidateAndSync = debounce(() => {
    this.validateAndSync();
  }, 2000);

  /** PSTUDIO-4452: this tab's CSV upload is running (see showCsvUploader). */
  csvUploadInProgress = false;

  setCsvUploadInProgress = action((uploading: boolean) => {
    this.csvUploadInProgress = uploading;
  });

  /**
   * PSTUDIO-4452: a CSV import replaced every node of the document (the server
   * re-creates them with new ids; the store has already cleared its undo).
   * Whatever the table held on to by node id is gone with them: the cell
   * being edited, the selection, the scroll position. The new rows themselves
   * arrive through the normal merge. Focus in a cell would fall to <body>
   * with its row, so it goes to the first imported row once that is in.
   */
  onLiveDocumentReset = action(() => {
    // The row of the focused cell is one the import removed; once the merge
    // has dropped it, the imported rows are in.
    const focusedRowId = (document.activeElement as HTMLElement | null)
      ?.closest?.('td')
      ?.getAttribute('data-row-id');
    if (hasActiveCellEdit(this)) this.cancelCellEditing();
    this.requestCloseAllContextMenus();
    this.deselectAllCells();
    this.deselectAllRows();
    this.clearSelectedStartCell();
    this.scrollToVirtualIndex?.(0);
    if (!focusedRowId) return;
    when(() => !this.getVisibleRows().some((r) => r.id === focusedRowId), {
      timeout: LIVE_RESET_FOCUS_MS,
    })
      .then(() => {
        const first = this.getVisibleRows()[0];
        if (first)
          this.scrollToAndFocusCellById(first.id, NodeColumns.nodeNumber);
      })
      // An import of nothing, or the user left: nothing to focus.
      .catch(() => undefined);
  });

  /**
   * PSTUDIO-4451: the document stopped (or started) taking this user's writes
   * mid-session — a role change, an import lock. Going view-only drops an
   * open cell edit first: its editor unmounts with the state flip and would
   * otherwise take focus to <body> and lose the draft without a word. Focus
   * goes back to the cell. The banner and the page's status region say what
   * happened. Only `viewing` and `editing` are switched; a load, an add or a
   * migration in progress is left to finish.
   */
  onLiveEditabilityChange = action((readOnly: boolean) => {
    if (readOnly) {
      const cell = this.activeEditCell();
      if (hasActiveCellEdit(this)) this.cancelCellEditing();
      if (this.currentState === 'editing' || this.currentState === 'adding') {
        this.setState('viewing');
      }
      if (cell.rowId && cell.columnId) {
        this.scrollToAndFocusCellById(cell.rowId, cell.columnId as NodeColumns);
      }
      return;
    }
    if (this.currentState === 'viewing') this.setState('editing');
  });

  /**
   * The pending pass `onLiveDocumentChange('local')` scheduled (PSTUDIO-4449):
   * a timer handle, plumbing, never rendered.
   */
  private liveLocalValidation: ReturnType<typeof setTimeout> | undefined =
    undefined;

  /**
   * PSTUDIO-4451: when to validate a collab-fed table. The pass itself is the
   * editor's own (`validateAndSync` over the projected rows); only the trigger
   * follows the document. Its first load and this user's own change validate
   * at once, as a legacy load and save do; a coworker's change waits for them
   * to pause, so a typing coworker does not recompile on every keystroke.
   * Manual mode never runs a pass by itself — it only marks that the solution
   * has changed since the last one.
   */
  onLiveDocumentChange = action((origin: 'load' | 'local' | 'remote') => {
    if (this.validationMode === ValidationMode.Manual) {
      if (origin !== 'load') this.hasUnvalidatedChangesInManualMode = true;
      return;
    }
    if (origin === 'remote') {
      this.debouncedValidateAndSync();
    } else if (origin === 'local') {
      // The transaction is reported before the feed has merged it (an undo
      // is the case that shows); validate the rows once they carry it.
      clearTimeout(this.liveLocalValidation);
      this.liveLocalValidation = setTimeout(() => this.validateAndSync(), 0);
    } else {
      this.validateAndSync();
    }
  });

  validateOnBlur() {
    if (this.isEditing()) {
      this.validateAndSync();
    }
  }

  get rowsShallow() {
    return this.rows.slice();
  }

  private applyValidationRules(
    rowId: string,
    field: string,
    value: string | number | boolean
  ) {
    const row = this.rows.find((r) => r.id === rowId);
    if (!row) return;

    // Create a copy of the row with the updated value
    const updatedRow = { ...row, [field]: value };

    // Validate against the schema
    const validationResult = validateJsonSchema(updatedRow, tableRowSchema);

    if (validationResult.valid) {
      // If validation passes, apply any required updates from the schema
      const schema = tableRowSchema;
      const dependencies = schema.dependencies as Record<string, JSONSchema7>;

      if (dependencies && dependencies[field]) {
        const dependency = dependencies[field];
        const oneOf = dependency.oneOf as JSONSchema7[];

        if (oneOf) {
          oneOf.forEach((rule) => {
            const properties = rule.properties as Record<string, JSONSchema7>;
            if (properties) {
              Object.entries(properties).forEach(([prop, propSchema]) => {
                if ('const' in propSchema) {
                  this.updateRow({
                    rowId,
                    updates: { [prop]: propSchema.const },
                    skipValidationRules: true,
                    saveImmediately: true,
                  });
                }
              });
            }
          });
        }
      }
    }
  }

  hasCell(rowId: string, columnId: string) {
    // First check if the row exists
    const row = this.rows.find((r) => r.id === rowId);

    if (!row) {
      console.warn(`Cannot select cell: Row with id ${rowId} not found`);
      return;
    }

    // Then check if the column exists in the row
    if (!(columnId in row)) {
      console.warn(
        `Cannot select cell: Column ${columnId} not found in row ${rowId}`
      );
      return;
    }

    return true;
  }

  isSelectedCellRenderable() {
    if (this.selectedCells.length !== 1) return false;

    return true;
  }

  isSelectedCellEditable() {
    const selectedCell = this.selectedCell();
    const columnId = selectedCell?.columnId;
    const rowId = selectedCell?.rowId;
    const settings = rowId && this.getValue(rowId, NodeColumns.settings);

    const allowedColumnIds = [
      NodeColumns.analyticsEvent,
      // NodeColumns.behaviors,
      NodeColumns.parameterInput,
      NodeColumns.richAssetContent,
      NodeColumns.nextNodes,
      NodeColumns.command,
      NodeColumns.cssClassname,
      NodeColumns.decisionVariable,
      NodeColumns.description,
      NodeColumns.intent,
      NodeColumns.message,
      NodeColumns.nodeInput,
      NodeColumns.nodeName,
      NodeColumns.utterances,
      NodeColumns.nodeNumber,
      NodeColumns.nodeTags,
      NodeColumns.output,
      NodeColumns.platformFlag,
      // NodeColumns.richAssetType,
      NodeColumns.skillTag,
      NodeColumns.variables,
      NodeColumns.whatsNext,
    ];

    if (
      this.selectedCells.length !== 1 ||
      !columnId ||
      (columnId === NodeColumns.parameterInput &&
        settings?.shouldSyncWithSupervisorConfig) ||
      !allowedColumnIds.includes(columnId as NodeColumns) ||
      // PSTUDIO-3624: a node-type-dependent (CLEARED) cell — e.g. Message on an
      // Action node — is greyed & non-editable in the grid; the panel must agree
      // so it can't be edited after being reached by keyboard navigation.
      this.isSelectedCellClearedForNodeType()
    ) {
      return false;
    }

    return true;
  }

  // PSTUDIO-3624: is the single selected cell node-type-dependent (CLEARED) for
  // its row's node type — e.g. Message on an Action node, or Command on a
  // Decision node? The grid renders such a cell as an empty, greyed,
  // non-editable `data-disabled` <td> (nodeTypeDependentCells — cell.tsx). This
  // gate lets the Expanded Cell View panel mirror that exactly: it hides the
  // editor for a cleared cell and isSelectedCellEditable() returns false, so the
  // grid and the panel report the SAME enabled/disabled state (a cleared cell
  // reached by keyboard navigation can no longer be edited through the panel).
  // Keys off the same getRowById(rowId).nodeType the grid cell uses.
  isSelectedCellClearedForNodeType() {
    const selectedCell = this.selectedCells[0];
    if (!selectedCell) return false;
    return this.isCellClearedForNodeType(
      selectedCell.rowId,
      selectedCell.columnId as NodeColumns
    );
  }

  // The same question for any cell: does the grid show it as cleared for its
  // row's node type (`nodeTypeDependentCells`)? `isCellDisabled` does NOT answer
  // it — it knows only parameterInput / answerRequired / nluDisabled.
  isCellClearedForNodeType(rowId: string, columnId: NodeColumns): boolean {
    return isClearedForNodeType(this.getRowById(rowId)?.nodeType, columnId);
  }

  // PSTUDIO-4348: a single-cell paste into Command or Rich Asset Type stops at a
  // cell the grid shows as cleared (`NODE_TYPE_GATED_PASTE_COLUMNS` says why
  // only these two). The cell-range paste judges the node type the row will HOLD
  // after the fill instead, because the same fill can change it.
  isPasteBlockedByNodeType(rowId: string, columnId: keyof TableRow): boolean {
    return (
      (NODE_TYPE_GATED_PASTE_COLUMNS as readonly string[]).includes(columnId) &&
      this.isCellClearedForNodeType(rowId, columnId as NodeColumns)
    );
  }

  isCellDisabled = computedFn((rowId: string, columnId: keyof TableRow) => {
    const settings = rowId && this.getValue(rowId, NodeColumns.settings);

    if (
      columnId === NodeColumns.parameterInput &&
      settings?.shouldSyncWithSupervisorConfig
    ) {
      return true;
    }

    if (columnId === NodeColumns.answerRequired) {
      const assetType = tableStore.getValue(rowId, NodeColumns.richAssetType);
      const variables = tableStore.getValue(rowId, NodeColumns.variables);
      const answerRequired = tableStore.getValue(
        rowId,
        NodeColumns.answerRequired
      );
      const assetTypesWithOptionalAnswerRequiredField = [
        Tenancy_Rich_Asset_Type_Enum_Ps_Enum.None,
        Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Image,
        Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Video,
        Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Png,
        Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Document,
        Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Webview,
        Tenancy_Rich_Asset_Type_Enum_Ps_Enum.PersistentMenu,
        Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Audio,
      ];

      const isAssetTypeSet =
        assetType &&
        !assetTypesWithOptionalAnswerRequiredField.includes(assetType);

      const isVariablesSet =
        Array.isArray(variables) &&
        variables.length > 0 &&
        variables.some((v) => v.trim() !== '');
      const shouldDisable =
        (isAssetTypeSet || isVariablesSet) && Boolean(answerRequired);

      return shouldDisable;
    }

    if (columnId === NodeColumns.nluDisabled) {
      // When free text is closed (Disable Free Text behavior), the user can only
      // pick an option, so NLU never runs and "NLU Disabled" is not applicable.
      // See PSTUDIO-2425.
      const behaviors = tableStore.getValue(rowId, NodeColumns.behaviors);
      return Array.isArray(behaviors) && behaviors.includes('disable_input');
    }
    return false;
  });

  selectedCell() {
    if (!this.isSelectedCellRenderable) return undefined;

    const [selectedCell] = this.selectedCells;

    if (!selectedCell) return undefined;

    return selectedCell;
  }

  selectedRowId() {
    if (!this.isSelectedCellRenderable) return undefined;

    const [selectedCell] = this.selectedCells;

    if (!selectedCell) return undefined;

    return selectedCell.rowId;
  }

  doesRowContainSelectedCells = computedFn((rowId: string) => {
    if (!rowId) return false;

    if (this.selectedCells.length === 0) return false;

    const rowIdsForSelectedCells = this.selectedCells.map((cell) => cell.rowId);

    if (rowIdsForSelectedCells.length === 0) return false;

    if (!rowIdsForSelectedCells.includes(rowId)) return false;

    return true;
  });

  selectedCellColumnIndex() {
    const selectedCellId = this.selectedCell()?.columnId;
    if (!selectedCellId) return undefined;

    return columnOrder.indexOf(selectedCellId as NodeColumns) + 1;
  }

  selectedCellRowIndex() {
    const selectedRowId = this.selectedRowId();

    if (!selectedRowId) return undefined;

    return this.getSortedRowIndex(selectedRowId) + 1;
  }

  /**
   * The stored value of ANY cell, formatted for an editor to display.
   *
   * Extracted from `selectedCellValue()` (which now delegates) so the
   * node-scoped property panel can render a field that is NOT the selected
   * cell. Duplicating this would have been the real risk: the three special
   * cases below are each load-bearing, and a second copy would drift.
   *
   * - `behaviors` is an array joined with ", " — the shape the multi-select
   *   and the grid cell both display.
   * - a JSON column is re-serialized through `getOrderedJson`, which restores
   *   the author's key ORDER from the column's `*Map` sibling. Plain
   *   `JSON.stringify` would silently reorder the keys under the user's cursor.
   * - `whatsNext` joins with `|`, not `,`: that is the separator the compiler's
   *   `parse-whats-next.ts` reads, so any other join round-trips wrong.
   */
  cellValueForEditor(rowId: string, columnId: string) {
    // One implementation for both callers: `cellDisplayValue` (PSTUDIO-1433)
    // extracted the same body from `selectedCellValue()` on candidate.
    return this.cellDisplayValue(rowId, columnId as keyof TableNode);
  }

  selectedCellValue() {
    if (!this.isSelectedCellRenderable) return '';

    const [selectedCell] = this.selectedCells;

    if (!selectedCell) return undefined;

    return this.cellDisplayValue(selectedCell.rowId, selectedCell.columnId);
  }

  /**
   * PSTUDIO-1433: the editor-facing string for an EXPLICIT cell — the body that
   * used to live inline in `selectedCellValue()`, which now delegates here.
   *
   * A caller that must stay bound to one cell needs this rather than the
   * selection-derived reader: the Expanded Cell View panel commits on Enter and
   * then moves the selection down, so a follow-up read through
   * `selectedCellValue()` would return the NEXT cell's value while the commit
   * still targets the previous row — writing one cell's content into another.
   */
  cellDisplayValue(rowId: string, columnId: keyof TableNode) {
    const value = this.getValue(rowId, columnId);

    const isJsonColumn = tableStore.isJsonColumn(rowId, columnId);

    if (columnId === NodeColumns.behaviors) {
      return Array.isArray(value) ? value.join(', ') : '';
    } else if (isJsonColumn) {
      if (typeof value === 'object') {
        return getOrderedJson({
          value,
          rowId,
          columnId: columnId as keyof _Node,
          stringify: true,
        });
      } else {
        return value;
      }
    } else if (columnId === NodeColumns.whatsNext) {
      if (typeof value === 'string') {
        return value;
      } else if (Array.isArray(value)) {
        return value?.join('|') || '';
      }
      return '';
    }

    return value || '';
  }

  // PSTUDIO-3624: the value the Expanded Cell View panel should render. Prefers
  // the observable live draft while THIS selected cell is the active INLINE-edit
  // target (so the panel tracks keystrokes typed in the grid), else the
  // committed selectedCellValue(). selectedCellValue() is read eagerly here so
  // the panel observer also re-renders when the committed value changes (cell
  // switch, undo/redo, panel-side edits). Decision logic is the pure
  // resolvePanelCellValue helper (unit-tested in isolation).
  liveCellValueForPanel() {
    return resolvePanelCellValue({
      selectedCell: this.selectedCells[0],
      editingRowId: this.editingRowId,
      editingColumnId: this.editingColumnId,
      liveDraft: this.liveCellDraft,
      committedValue: this.selectedCellValue(),
    });
  }

  selectCell = action(
    ({
      cellId,
      rowId,
      columnId,
    }: {
      cellId: string;
      rowId: string;
      columnId: NodeColumns;
    }) => {
      // if (!row) {
      //   console.warn(`Cannot select cell: Row with id ${rowId} not found`);
      //   return;
      // }

      // // Then check if the column exists in the row
      // if (!(columnId in row)) {
      //   console.warn(
      //     `Cannot select cell: Column ${columnId} not found in row ${rowId}`
      //   );
      //   return;
      // }

      // const cellId = `${rowId}-${columnId}`;
      // this.getCellId(rowId, columnId);

      // console.log('select cell', cellId);

      // Check if cell is already selected using the unique cell ID
      const isAlreadySelected = this.selectedCells.find(
        (c) => c.cellId === cellId
      );

      if (!isAlreadySelected) {
        this.selectedCells.push({
          cellId,
          rowId,
          columnId,
        });
      }

      if (!this.selectedStartCell) {
        this.setSelectedStartCell({
          cellId,
          rowId,
          columnId,
        });
      }

      // if (!isAlreadySelected) {
      //   // Add new cell selection
      //   // console.log('not selected so adding...');
      // } else {
      //   // console.log('already selected...');
      // }
    }
  );

  deselectCell = action((cellId: string) => {
    // const cellId = `${rowId}-${columnId}`;
    // const isAlreadySelected = this.selectedCells.has(cellId);

    const targetCell = this.selectedCells.find(
      (cell) => cell.cellId === cellId
    );

    if (targetCell) {
      this.selectedCells.remove(targetCell);
    }

    // if (isAlreadySelected) {
    // } else {
    //   // console.log('already selected...');
    // }
  });

  deselectAllCells = action((otherThanCellId?: string) => {
    if (this.selectedCells.length === 0) return;

    // console.log('deselectAllCells', otherThanCellId);
    if (otherThanCellId) {
      // emitOnce(() => {
      const cellFound = this.selectedCells.find(
        (cell) => cell.cellId === otherThanCellId
      );

      if (cellFound && this.selectedCells.length === 1) return;

      if (cellFound) {
        this.selectedCells.replace([cellFound]);
      } else {
        this.selectedCells.clear();
      }
      // });
    } else {
      // console.log('deselectAllCells', this.selectedCells.length);
      this.selectedCells.clear();
    }
  });

  // Add a method to get cell ID for a given row and column
  getCellId = computedFn((rowId: string, columnId: string) => {
    return `${rowId}-${columnId}`;
  });

  // Add a method to check if a cell is selected using the cell ID
  isCellSelected = computedFn((cellId: string) => {
    return this.selectedCells.some((cell) => cell.cellId === cellId);
  });

  // Get the actual cell selection object if needed
  getCellSelection = computedFn((cellId: string) => {
    return this.selectedCells.find((cell) => cell.cellId === cellId);
  });

  deselectAllRows = action(() => {
    if (this.isSelectionLocked) return;

    emitOnce(() => {
      if (this.selectedRows.size !== 0) {
        this.selectedRows.clear();
      }

      if (this.rowSelectionAnchor !== null) {
        this.rowSelectionAnchor = null;
      }

      if (this.rowSelectionLead !== null) {
        this.rowSelectionLead = null;
      }
    });
  });

  setResizingRowId = (rowId: string | null) => {
    this.resizingRowId = rowId;
  };

  isSelectedRow = computedFn((rowId: string) => {
    if (this.dragSession?.isMulti) {
      return this.dragSession.selectedRowIds.includes(rowId);
    }
    return this.selectedRows.has(rowId);
  });

  selectRow = action((rowId: string) => {
    if (this.isSelectionLocked) return;
    emitOnce(() => {
      this.clearSelectedStartCell();
      this.deselectAllCells();
      this.selectedRows.add(rowId);
    });
    // return this.selectedRows.has(rowId);
  });

  deSelectRow = action((rowId: string) => {
    if (this.isSelectionLocked) return;
    this.selectedRows.delete(rowId);
    // return this.selectedRows.has(rowId);
  });

  /**
   * Keyboard row selection (PSTUDIO-1964). Select the entire row that
   * contains the currently focused cell — the keyboard twin of clicking a
   * row's index cell (draggable-row.tsx handleIndexCellClick). Renders the
   * SAME visible state (data-selected → .c-editor-table-tr--selected) because
   * it drives the same `selectedRows` the mouse path does. Anchor and lead
   * are set to the row's visible-sorted index so a follow-up Shift+Up/Down
   * extends the row range via the existing shift+arrow handler
   * (extendRowSelectionByKeyboard). No-op for a row that isn't visible.
   */
  selectFocusedRowByKeyboard = action((rowId: string) => {
    if (this.isSelectionLocked) return;
    // Group-header rows carry data-rowid too, but render no selected state
    // (draggable-row.tsx never applies --selected/data-selected to them), so a
    // keyboard selection here would be invisible yet still honored by
    // Delete/Cmd+C/Cmd+X. No-op — matching the mouse single-click path (a group
    // control cell wires no selecting onClick) and the arrow-navigation
    // exclusion (PSTUDIO-3168).
    const row = this.getRowById(rowId);
    if (!row || isGroupRow(row)) return;
    const index = this.getSortedRowIndex(rowId);
    if (index === -1) return; // not a visible row (unknown / collapsed) — no-op
    this.deselectAllRows();
    this.selectRow(rowId);
    this.setRowSelectionAnchor(index);
  });

  rowRangeSelection: {
    startRowIndex: number;
    isSelecting: boolean;
  } | null = null;

  // Anchor row index for Shift+Click range selection
  rowSelectionAnchor: number | null = null;

  // Moving end ("lead") of the row range for Shift+Arrow keyboard extension
  // (PSTUDIO-2372). The anchor stays fixed; the lead is the end that moves.
  // Both are indices into getAllSortedRows().filter(r => r.hidden !== true).
  rowSelectionLead: number | null = null;

  setRowSelectionAnchor = action((index: number) => {
    this.rowSelectionAnchor = index;
    this.rowSelectionLead = index;
  });

  startRowRangeSelection = action((startRowIndex: number) => {
    if (this.isSelectionLocked) return;
    this.rowRangeSelection = { startRowIndex, isSelecting: true };
    this.deselectAllRows();
    this.rowSelectionAnchor = startRowIndex;
    this.rowSelectionLead = startRowIndex;
    const visibleSortedRows = this.getAllSortedRows().filter(
      (row) => row.hidden !== true
    );
    const row = visibleSortedRows[startRowIndex];
    if (row) this.selectRow(row.id);
  });

  selectRowRange = action((targetRowIndex: number) => {
    if (this.isSelectionLocked) return;

    let anchorIndex: number;
    if (this.rowSelectionAnchor !== null) {
      anchorIndex = this.rowSelectionAnchor;
    } else if (this.selectedRows.size > 0) {
      const firstSelectedId = [...this.selectedRows.values()][0];
      anchorIndex = this.getSortedRowIndex(firstSelectedId);
    } else {
      anchorIndex = 0;
    }

    const startIndex = Math.min(anchorIndex, targetRowIndex);
    const endIndex = Math.max(anchorIndex, targetRowIndex);

    const visibleSortedRows = this.getAllSortedRows().filter(
      (row) => row.hidden !== true
    );

    this.deselectAllRows();

    for (let i = startIndex; i <= endIndex; i++) {
      const row = visibleSortedRows[i];
      if (row) this.selectRow(row.id);
    }

    this.rowSelectionAnchor = anchorIndex;
    this.rowSelectionLead = targetRowIndex;
  });

  /**
   * Extend the row range selection by one row up/down via Shift+Arrow
   * (PSTUDIO-2372), mirroring Google Sheets: the anchor stays fixed while the
   * lead end moves one row per keypress. Uses the same visible-sorted index
   * space as selectRowRange (group headers included, collapsed children
   * excluded), so keyboard extension matches Shift+Click exactly.
   */
  extendRowSelectionByKeyboard = action((direction: 'up' | 'down') => {
    if (this.isSelectionLocked) return;
    if (this.selectedRows.size === 0) return;

    const visibleSortedRows = this.getAllSortedRows().filter(
      (row) => row.hidden !== true
    );
    if (visibleSortedRows.length === 0) return;

    let lead = this.rowSelectionLead ?? this.rowSelectionAnchor;
    if (lead === null) {
      // Post-drag fallback — same as selectRowRange
      const firstSelectedId = [...this.selectedRows.values()][0];
      lead = this.getSortedRowIndex(firstSelectedId);
    }
    if (lead === -1) return;
    // Stale-lead clamp (rows may have been deleted since the lead was set)
    lead = Math.min(Math.max(lead, 0), visibleSortedRows.length - 1);

    const newLead = Math.min(
      Math.max(lead + (direction === 'down' ? 1 : -1), 0),
      visibleSortedRows.length - 1
    );
    if (newLead === lead) return; // at table edge — Sheets no-op

    this.selectRowRange(newLead); // re-selects [anchor..newLead], keeps anchor

    // Scroll the lead row into view. The virtualizer indexes the
    // search-filtered getVisibleRows() space — convert via row id.
    // scrollToVirtualIndex is a nullable callback (null in unit tests).
    const leadRow = visibleSortedRows[newLead];
    const visibleIndex = this.getVisibleRows().findIndex(
      (row) => row.id === leadRow.id
    );
    if (visibleIndex !== -1) this.scrollToVirtualIndex?.(visibleIndex);
  });

  updateRowRangeSelection = action((currentRowIndex: number) => {
    if (this.isSelectionLocked) return;
    if (!this.rowRangeSelection?.isSelecting) return;

    const startIndex = Math.min(
      this.rowRangeSelection.startRowIndex,
      currentRowIndex
    );
    const endIndex = Math.max(
      this.rowRangeSelection.startRowIndex,
      currentRowIndex
    );

    const visibleSortedRows = this.getAllSortedRows().filter(
      (row) => row.hidden !== true
    );

    this.deselectAllRows();
    for (let i = startIndex; i <= endIndex; i++) {
      const row = visibleSortedRows[i];
      if (row) this.selectRow(row.id);
    }

    this.rowSelectionLead = currentRowIndex;
  });

  endRowRangeSelection = action(() => {
    this.rowRangeSelection = null;
    this.suppressNextRowClick = true;
  });

  /**
   * Select every currently-visible row (Cmd/Ctrl+A), mirroring Google Sheets.
   * Hidden rows (collapsed group children) are excluded so they are not
   * silently copied/cut. Clears any cell selection (row-mode is exclusive).
   * No-op while a multi-drag holds the selection lock.
   */
  selectAllRows = action(() => {
    if (this.isSelectionLocked) return;
    emitOnce(() => {
      this.clearSelectedStartCell();
      this.deselectAllCells();
      const visibleSortedRows = this.getAllSortedRows().filter(
        (row) => row.hidden !== true
      );
      this.selectedRows.clear();
      visibleSortedRows.forEach((row) => this.selectedRows.add(row.id));
      this.rowSelectionAnchor = visibleSortedRows.length > 0 ? 0 : null;
      this.rowSelectionLead =
        visibleSortedRows.length > 0 ? visibleSortedRows.length - 1 : null;
    });
  });

  // Flag to suppress click handler after range selection completes
  suppressNextRowClick = false;

  consumeSuppressNextRowClick = action(() => {
    if (this.suppressNextRowClick) {
      this.suppressNextRowClick = false;
      return true;
    }
    return false;
  });

  get isSelectionContinuous(): boolean {
    if (this.selectedRows.size <= 1) return true;

    const visibleSortedRows = this.getAllSortedRows().filter(
      (row) => row.hidden !== true
    );

    const selectedIndices = [...this.selectedRows.values()]
      .map((id) => visibleSortedRows.findIndex((row) => row.id === id))
      .filter((idx) => idx !== -1)
      .sort((a, b) => a - b);

    if (selectedIndices.length <= 1) return true;

    for (let i = 1; i < selectedIndices.length; i++) {
      if (selectedIndices[i] !== selectedIndices[i - 1] + 1) {
        return false;
      }
    }
    return true;
  }

  async loadPendingRows() {
    if (this.pendingRowsToRender.length > 0) {
      // Use runInAction to ensure MobX tracks the changes properly
      requestIdleCallback(() => {
        runInAction(() => {
          const batchSize = 100;
          const batch = this.pendingRowsToRender.splice(0, batchSize);
          this.rows.push(...batch);
          batch.forEach((row) => {
            set(this.rowMap, row.id, row);
            this.fuse.add(row);
          });
          // Update the pending state based on remaining items
          this.hasPendingRowsToRender = this.pendingRowsToRender.length > 0;

          // If there are still more rows to load, schedule the next batch
          if (this.pendingRowsToRender.length > 0) {
            this.loadPendingRows();
          }
        });
      });
    } else {
      // No pending rows at all, ensure state is false
      this.hasPendingRowsToRender = false;
    }
  }
  // syncRowSelectionWithTable(tableRowSelection: RowSelectionState) {
  //   this.selectedRows.clear();
  //   Object.entries(tableRowSelection).forEach(([rowId, isSelected]) => {
  //     if (isSelected) {
  //       this.selectedRows.set(rowId, true);
  //     }
  //   });
  // }

  /**
   * PSTUDIO-4083: create task nodes for SEVERAL tasks as ONE numbering gesture.
   *
   * One panel save, or one remote sync, can introduce several tasks at once —
   * and rule 1.2 says a gesture produces one unbroken run. Calling the
   * single-node path N times does not: each call allocates `count = 1`
   * independently, which on a `1 · 8 · 2 · 4` table answered `5, 6, 7, 70`, a
   * run with a hole. One `count = tasks.length` allocation answers
   * `40, 41, 42, 43`.
   *
   * It is also what stops the collection walk from running per task.
   * `numberingNumbersOf` is O(rows x rich-asset size) — measured at ~37 ms on a
   * 5,000-row table with a 2 KB asset per row — so thirty tasks used to cost
   * about a second of synchronous work for a set that does not change between
   * them. The numbers are locked ONCE, the same way `addEmptyRows` locks its
   * block (PSTUDIO-2555).
   *
   * Each node still gets its OWN undo command: the numbering is one gesture,
   * the history entries are not, and collapsing them would change what a single
   * Ctrl+Z does here.
   */
  createTaskNodesWithIds = action(
    (tasks: Array<{ id: string; name: string; description?: string }>) => {
      if (tasks.length === 0) return;

      // PSTUDIO-4083: `rowsForNumbering`, not `rows`. A task node is created
      // from a REMOTE sync too (`syncTasksWithNodes`), which can land inside
      // the soft-delete window — and a row in that window still sits in `rows`
      // holding a number that is already gone.
      const { rowsForNumbering } = this;
      const { own, referenced } = this.getNumberingNumbers();
      const numbers = assignNodeNumberStrings(
        this.getAppendNumberingAnchor(),
        null,
        tasks.length,
        own,
        referenced
      );

      tasks.forEach((task, index) => {
        const newTaskNode = createTaskNode({
          taskName: task.name,
          taskDescription: task.description ?? '',
          taskId: task.id,
          solutionVersionId: this.solutionVersionId,
          existingRows: rowsForNumbering,
          order: this.getNextNodeOrder(),
          nodeNumber: numbers[index],
        });

        const command: Command = {
          execute: () => {
            this.rows.push(newTaskNode);
            set(this.rowMap, newTaskNode.id, newTaskNode);
            this.fuse.add(newTaskNode);
            this.saveRowsToDatabase([newTaskNode]);
            this.setState(this.isViewer ? 'viewing' : 'editing');
          },
          undo: () => {
            const targetRow = this.rows.find((r) => r.id === newTaskNode.id);
            if (targetRow) {
              this.rows.remove(targetRow);
              this.rowMap.delete(newTaskNode.id);
              this.fuse.remove((doc) => doc.id === newTaskNode.id);
            }
            // PSTUDIO-4121: see the insert paths above — the node leaves the
            // DATABASE too. `execute` saved it with `saveRowsToDatabase`, so an
            // undo that only splices `rows` left the node alive server-side.
            //
            // KNOWN LIMIT, and it is why this is not a full undo: the TASK is
            // not ours to remove. It was created by the supervisor panel's own
            // save, `createTaskNodesWithIds` only materialises a node for it,
            // and `deleteSolutionTask` therefore stays exclusive to the delete
            // gestures (`_runDeferredDelete`, `deleteRow`). So the next
            // `syncTasksWithNodes` — a tab switch on the Tasks panel is enough —
            // finds a task with no node and materialises a NEW one, with a new
            // id and a newly allocated node number. Deleting here is still the
            // better half of that trade: leaving the node behind produced the
            // same re-materialisation PLUS the old row coming back on the next
            // load, i.e. two nodes for one task.
            this._deleteNodesInDb([newTaskNode.id]);
          },
          description: `Create task node for ${task.name}`,
          focusTarget: { rowId: newTaskNode.id },
        };

        this.executeCommand(command);
      });
    }
  );

  createTaskNodeWithId = action(
    (taskName: string, taskDescription: string, taskId: string) => {
      this.createTaskNodesWithIds([
        { id: taskId, name: taskName, description: taskDescription },
      ]);
    }
  );

  updateTaskNode = action(
    (taskId: string, updates: { name: string; description?: string }) => {
      const taskNode = this.rows.find((row) => row.settings?.taskId === taskId);

      if (taskNode) {
        const updatedNode = updateTaskNode(taskNode, updates);
        this.updateRow({
          rowId: taskNode.id,
          updates: updatedNode,
        });
      }
    }
  );

  deleteTaskNode = action((taskId: string) => {
    const taskNode = this.rows.find((row) => row.settings?.taskId === taskId);

    if (taskNode) {
      this.deleteRow(taskNode.id);
    } else {
      console.error('Task node not found for taskId:', taskId);
    }
  });

  syncTasksWithNodes = action(
    (tasks: Array<{ id: string; name: string; description?: string }>) => {
      // PSTUDIO-4450/4451: tasks are nodes in the collab document, and the
      // Supervisor panel writes them itself. These are Hasura task ids, which
      // no collab-fed row carries (its taskId is its own node id) — matching
      // would miss every task and add a duplicate node for each.
      if (this.liveEditingSource) return;
      const missing: Array<{
        id: string;
        name: string;
        description?: string;
      }> = [];
      tasks.forEach((task) => {
        // Check ALL rows including hidden ones, not just visible ones
        // Check by taskId first, then by name (for cases where taskId changed after publish)
        // `hidden` is set for two different reasons. A collapsed group sets it:
        // that task node still exists and has to count, or the sync creates a
        // duplicate. The soft-delete window sets it too (`deleteRows` marks the
        // id in `pendingDeletionRowIds` and hides the row until an idle callback
        // removes it): that row is on its way out, and counting it would make the
        // sync adopt it and never create the task's node. `isRowPendingDeletion`
        // is the marker AND `hidden`, so only the second case is skipped.
        const existingNodeByTaskId = this.rows.find(
          (row) =>
            !this.isRowPendingDeletion(row) && row.settings?.taskId === task.id
        );

        const existingNodeByName = this.rows.find(
          (row) =>
            !this.isRowPendingDeletion(row) &&
            row.settings?.taskId &&
            row.nodeName === task.name
        );

        // Also check pending rows that haven't been rendered yet
        const existingPendingNode = this.pendingRowsToRender.find(
          (row) =>
            !this.isRowPendingDeletion(row) &&
            (row.settings?.taskId === task.id ||
              (row.settings?.taskId && row.nodeName === task.name))
        );

        const existingNode = existingNodeByTaskId || existingNodeByName;

        if (!existingNode && !existingPendingNode) {
          console.log('[Task Sync] Creating task node:', task.id, task.name);
          // PSTUDIO-4083: collected, not created here. Every task this sync is
          // missing belongs to ONE gesture, so they are numbered as one block
          // below rather than one `count = 1` allocation each.
          missing.push(task);
        } else {
          console.log('[Task Sync] Task node already exists:', task.id);
          // Update taskId in settings if it changed (e.g., after publish)
          if (existingNodeByName && !existingNodeByTaskId) {
            console.log(
              '[Task Sync] Updating taskId for node:',
              existingNodeByName.id
            );
            this.updateRow({
              rowId: existingNodeByName.id,
              updates: {
                settings: { ...existingNodeByName.settings, taskId: task.id },
              },
            });
          }
        }
      });

      // One allocation for every task this sync was missing — see
      // `createTaskNodesWithIds`. A no-op when nothing is missing, which is the
      // overwhelmingly common case for a sync.
      this.createTaskNodesWithIds(missing);
    }
  );

  isTaskRow = computedFn((rowId: string) => {
    return isTaskRow(rowId);
  });

  updateSolutionForTaskRow = action(
    (taskId: string, updates: Partial<TableRow>) => {
      // PSTUDIO-4451: under live editing a task is its node — the cell's own
      // save already wrote it to the document; there is no Hasura task.
      if (this.liveEditingSource) return;
      updateSolutionForTaskRow(taskId, updates);
    }
  );

  updateSupervisorConfigRow = action(
    (configData: {
      brandName: string;
      supervisorName: string;
      knowledgeAgentTone: string;
    }) => {
      const plainRows = toJS(this.rows);
      const supervisorConfigRow = plainRows.find(
        (row) => row.settings?.shouldSyncWithSupervisorConfig
      );

      if (!supervisorConfigRow) {
        console.warn('Supervisor config row not found');
        return;
      }

      const updatedParameterInput = {
        set: {
          BRAND_NAME_FOR_LLM: configData.brandName,
          SOLUTION_NAME_FOR_LLM: configData.supervisorName,
          KNOWLEDGE_AGENT_TONE: configData.knowledgeAgentTone,
        },
      };

      this.updateRow({
        rowId: supervisorConfigRow.id,
        updates: {
          parameterInput: updatedParameterInput,
        },
      });
    }
  );

  isJsonAssetType = computedFn(
    (assetType: Tenancy_Rich_Asset_Type_Enum_Ps_Enum) => {
      const jsonAssetTypes = [
        Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Buttons,
        Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Listpicker,
        Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Imagebutton,
        Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Carousel,
        Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Webview,
        Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Image,
        Tenancy_Rich_Asset_Type_Enum_Ps_Enum.FileUpload,
        Tenancy_Rich_Asset_Type_Enum_Ps_Enum.PersistentMenu,
        Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Star,
        Tenancy_Rich_Asset_Type_Enum_Ps_Enum.QuickReply,
        Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Audio,
      ];

      return jsonAssetTypes.includes(assetType);
    }
  );

  isJsonColumn = computedFn(
    (rowId: string | undefined, columnId: keyof TableNode | undefined) => {
      if (!columnId || !rowId) return false;

      const assetType = this.getValue(rowId, NodeColumns.richAssetType);

      return (
        columnId === NodeColumns.parameterInput ||
        (columnId === NodeColumns.richAssetContent &&
          this.isJsonAssetType(assetType)) ||
        columnId === NodeColumns.analyticsEvent
      );
    }
  );
}

/**
 * Converts text input to appropriate values for TableNode column types
 * @param text - The text input to convert
 * @param columnId - The column ID to determine the target type
 * @returns The converted value compatible with the column type
 *
 * @example
 * // Boolean conversion
 * convertTextToColumnValue('asdf', 'answerRequired') // returns true
 * convertTextToColumnValue('false', 'nluDisabled') // returns false
 *
 * // Enum conversion (case-insensitive)
 * convertTextToColumnValue('Action', 'nodeType') // returns Tenancy_Node_Type_Enum_Ps_Enum.A
 * convertTextToColumnValue('Button', 'richAssetType') // returns Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Button
 *
 * // Number conversion
 * convertTextToColumnValue('123', 'nodeNumber') // returns '123'
 *
 * // Array conversion
 * convertTextToColumnValue('none,inline', 'behaviors') // returns ['none', 'inline']
 * convertTextToColumnValue('1,2,3', 'nextNodes') // returns [1, 2, 3]
 *
 * // String conversion (default)
 * convertTextToColumnValue('Hello World', 'message') // returns 'Hello World'
 */
export function convertTextToColumnValue(
  text: string,
  columnId: keyof TableNode
): unknown {
  if (!text || text.trim() === '') {
    return getDefaultValueForColumn(columnId);
  }

  const trimmedText = text.trim();

  switch (columnId) {
    // Boolean columns
    case NodeColumns.answerRequired:
    case NodeColumns.nluDisabled:
      return convertToBoolean(trimmedText.toLowerCase());

    // Enum columns
    case NodeColumns.nodeType:
      // convertToNodeType normalizes case internally
      return convertToNodeType(trimmedText);

    case NodeColumns.richAssetType:
      return convertToRichAssetType(trimmedText.toLowerCase());

    // PSTUDIO-4453: a node number is a canonical string — or the text as
    // pasted, kept for validation to flag ("hello" no longer becomes 0).
    case NodeColumns.nodeNumber:
      return toNodeNumberCell(trimmedText);

    case NodeColumns.order:
      return convertToNumber(trimmedText);

    // Array columns
    case NodeColumns.behaviors:
      return convertToBehaviorsArray(trimmedText);

    case NodeColumns.nextNodes:
      return convertToNextNodesArray(trimmedText);

    case NodeColumns.variables:
      return convertToVariablesArray(trimmedText);

    case NodeColumns.whatsNext:
      return convertToWhatsNextArray(trimmedText);

    case NodeColumns.output:
      return convertToOutputArray(trimmedText);

    // Object columns
    case NodeColumns.richAssetContent:
    case NodeColumns.parameterInput:
    case NodeColumns.analyticsEvent:
      return convertToObjectString(trimmedText);

    // PSTUDIO-4348: the pasted function name, verbatim. This converter owns the
    // SHAPE of a value, not whether a script of that name exists: the dropdown
    // only offers known names, but blanking an unknown one here would destroy
    // what the user pasted (a node copied from a solution whose script this one
    // lacks). `validateAndSync` hands the compiler the script names so that it can
    // detect a missing one; whether it flags a pasted unknown name is not
    // exercised by a test here.
    case NodeColumns.command:
      return trimmedText;

    // String columns (default case)
    case NodeColumns.message:
    case NodeColumns.nodeName:
    case NodeColumns.description:
    case NodeColumns.cssClassname:
    case NodeColumns.decisionVariable:
    case NodeColumns.intent:
    case NodeColumns.nodeInput:
    case NodeColumns.nodeTags:
    case NodeColumns.platformFlag:
    case NodeColumns.skillTag:
    case NodeColumns.solutionVersionId:
    case NodeColumns.id:
      return text; // Return as-is for string columns

    default:
      return text; // Fallback to original text
  }
}

/**
 * Gets the default value for a column type
 */
export function getDefaultValueForColumn(columnId: keyof TableNode): unknown {
  switch (columnId) {
    case NodeColumns.answerRequired:
    case NodeColumns.nluDisabled:
      return false;

    case NodeColumns.nodeType:
      return Tenancy_Node_Type_Enum_Ps_Enum.D;

    case NodeColumns.richAssetType:
      return Tenancy_Rich_Asset_Type_Enum_Ps_Enum.None;

    case NodeColumns.nodeNumber:
      return undefined;

    case NodeColumns.order:
      return 0;

    case NodeColumns.behaviors:
    case NodeColumns.nextNodes:
    case NodeColumns.variables:
    case NodeColumns.whatsNext:
    case NodeColumns.output:
      return [];

    case NodeColumns.richAssetContent:
    case NodeColumns.parameterInput:
    case NodeColumns.analyticsEvent:
      return ``;

    default:
      return '';
  }
}

/**
 * Compares a cell value with its default value, handling arrays and other complex types
 */
export function isCellValueEqualToDefault(
  value: unknown,
  defaultValue: unknown
): boolean {
  // Handle null/undefined cases
  if (value === defaultValue) return true;

  // Handle array comparison
  if (Array.isArray(value) && Array.isArray(defaultValue)) {
    if (value.length !== defaultValue.length) return false;
    return value.every((item, index) => item === defaultValue[index]);
  }

  // Handle object comparison (for JSON strings)
  if (typeof value === 'string' && typeof defaultValue === 'string') {
    try {
      const parsedValue = JSON.parse(value);
      const parsedDefault = JSON.parse(defaultValue);
      return JSON.stringify(parsedValue) === JSON.stringify(parsedDefault);
    } catch {
      // If parsing fails, do string comparison
      return value === defaultValue;
    }
  }

  // Handle primitive comparison
  return value === defaultValue;
}

/**
 * Converts text to boolean
 */
function convertToBoolean(text: string): boolean {
  const truthyValues = ['true', '1', 'yes', 'y', 'on', 'enabled'];
  const falsyValues = ['false', '0', 'no', 'n', 'off', 'disabled'];
  if (truthyValues.includes(text)) {
    return true;
  }

  if (falsyValues.includes(text)) {
    return false;
  }

  // For any other text, return false
  return false;
}

/**
 * Converts text to node type enum
 */
function convertToNodeType(text: string): Tenancy_Node_Type_Enum_Ps_Enum {
  if (text.toLowerCase() === 'action' || text.toLowerCase() === 'a') {
    return Tenancy_Node_Type_Enum_Ps_Enum.A;
  }

  if (text.toLowerCase() === 'decision' || text.toLowerCase() === 'd') {
    return Tenancy_Node_Type_Enum_Ps_Enum.D;
  }

  // Default to decision
  return Tenancy_Node_Type_Enum_Ps_Enum.D;
}

/**
 * Converts text to rich asset type enum
 */
function convertToRichAssetType(
  text: string
): Tenancy_Rich_Asset_Type_Enum_Ps_Enum {
  const typeMap: Record<string, Tenancy_Rich_Asset_Type_Enum_Ps_Enum> = {
    button: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Button,
    buttons: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Buttons,
    carousel: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Carousel,
    datepicker: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Datepicker,
    deeplink: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Deeplink,
    document: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Document,
    file_upload: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.FileUpload,
    image: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Image,
    imagebutton: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Imagebutton,
    listpicker: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Listpicker,
    location: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Location,
    login_button: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.LoginButton,
    // nav: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Nav,
    none: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.None,
    persistent_menu: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.PersistentMenu,
    png: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Png,
    quick_reply: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.QuickReply,
    secure: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Secure,
    selection: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Selection,
    star: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Star,
    video: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Video,
    audio: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Audio,
    webview: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Webview,
  };

  // Own keys only: `typeMap` is a plain object, so a pasted word naming an
  // inherited property (`constructor`, `toString`, ...) resolved to a function.
  return Object.prototype.hasOwnProperty.call(typeMap, text)
    ? typeMap[text]
    : Tenancy_Rich_Asset_Type_Enum_Ps_Enum.None;
}

/**
 * Converts text to number
 */
function convertToNumber(text: string): number {
  const num = parseInt(text, 10);
  return isNaN(num) ? 0 : num;
}

/**
 * Converts text to behaviors array
 */
function convertToBehaviorsArray(text: string): string[] {
  // PSTUDIO-4167: the vocabulary lives in `behaviors-vocabulary`, once. This
  // function used to carry its own copy which accepted `none` — a value the
  // compiler rejects as unrecognized — and DROPPED anything it did not know.
  // Dropping is data loss dressed up as cleanup: the token the user typed or
  // imported vanished from the row, from the CSV download and from the publish
  // payload without a word, while CSV import (which had no vocabulary at all)
  // published the same token verbatim. Both entrances now agree: normalize, and
  // keep whatever survives. An unrecognized token is reported on its cell by
  // `buildJsonFieldErrors`, which blocks publish through `canPublish` — visible
  // and fixable, instead of silently altered.
  //
  // `normalizeBehaviorToken` keeps the PSTUDIO-4081 guard that made the old
  // dropping necessary in the first place: a prototype key resolves to its own
  // TEXT, never to `Object.prototype` or the `Object` function, so nothing but
  // a string can reach the row and be rejected by `[String!]`.
  return normalizeBehaviorTokens(text.split(/[,;|]/));
}

/**
 * Converts text to next nodes array
 */
function convertToNextNodesArray(text: string): number[] {
  const numbers = text.split(/[,;|]/).map((n) => parseInt(n.trim(), 10));
  return numbers.filter((n) => !isNaN(n));
}

/**
 * Converts text to variables array
 */
function convertToVariablesArray(text: string): string[] {
  const variables = text.split(/[,;|]/).map((v) => v.trim().toUpperCase());
  const isAllValid = variables.every((v) => isValidGlobalVariableName(v));
  if (isAllValid) {
    return variables;
  }
  return [];
}

/**
 * Converts text to whats next array
 */
function convertToWhatsNextArray(text: string): string[] {
  const items = text.split(/[,;|]/).map((item) => item.trim());
  return items.filter((item) => item !== '');
}

/**
 * Converts text to output array
 */
function convertToOutputArray(text: string): string[] {
  const outputs = text.split(/[,;|]/).map((o) => o.trim());
  return outputs.filter((o) => o !== '');
}

/**
 * Converts text to rich asset content, parameter input, analytics event string.
 *
 * Valid JSON is re-serialised compactly. Anything else is kept as typed or
 * pasted (`convertTextToColumnValue` has already trimmed it), so the validator
 * sees what the user wrote. This function does not judge the text: whether it is
 * acceptable depends on the column and, for Asset Content, on the asset type
 * (`json-field-errors.ts` lets Webview keep a legacy `label~url` string).
 */
function convertToObjectString(text: string): string {
  try {
    // Try to parse as JSON first
    const parsedObject = JSON.parse(text);
    return JSON.stringify(parsedObject);
  } catch {
    return text;
  }
}

export const tableStore = new TableStore();

// MOBX REACTIONS ⬇️

/**
 * PSTUDIO-4215: which VISIT a fetch belongs to.
 *
 * PSTUDIO-3953 pins every async apply to the identity string the request was
 * issued for, and that was enough while a version could only be re-entered by
 * leaving for a DIFFERENT one. Releasing the identity on the way out (this
 * ticket) makes `A -> '' -> A` an ordinary navigation, and across that round
 * trip the string is the same — so the first visit's in-flight response passes
 * every `fetchedFor` check, re-raises the waiting flag and overwrites the one
 * subscription slot, orphaning whatever was in it. The counter distinguishes
 * the two visits; it lives in the module rather than on the store because
 * nothing outside this reaction reads it, and an observable write here would
 * re-render the table for bookkeeping.
 *
 * The known limitation named in `.claude/rules/cross-cutting-invariants.md`
 * ("identity is not a GENERATION") is therefore closed for THIS path only: the
 * reaction's own fetch and the subscriptions it opens. Other readers still
 * compare the string alone.
 */
let versionVisitEpoch = 0;

// PSTUDIO-4450: the row source the last visit loaded from.
let visitSource = false;

/**
 * PSTUDIO-4449: drop the dataset the user is leaving. `clearTable` ends in
 * `validateAndSync`, which uploads the (now empty) rows to bot-manager under
 * the machine context — by now the DESTINATION's. With a collab draft on
 * either side that upload must not happen at all: it is not Hasura's to
 * compile (the route-sync rule, PSTUDIO-4450), so the dataset is dropped
 * quietly. Hasura → Hasura keeps `clearTable` exactly as before; its empty
 * upload for the destination is a long-standing defect, out of scope here.
 * Both checks are needed: route sync flips `liveEditingSource` in the same
 * action as the id, so the flag alone names the side being ENTERED and
 * `visitSource` the side being left.
 */
const leaveDataset = () => {
  if (visitSource || tableStore.liveEditingSource) tableStore.dropDataset();
  else tableStore.clearTable();
};

reaction(
  () =>
    [
      toJS(tableStore.solutionVersionId),
      tableStore.versionRevisitRequests,
    ] as const,
  ([currentValue, currentRevisits], [previousValue, previousRevisits]) => {
    // PSTUDIO-4215: an identity change ends the previous VISIT, which is not
    // the same question as "a different version". Leaving the builder sets the
    // id to '' (`use-table-store-sync`, first-level pages) and coming back
    // re-enters the SAME id, so both edges are identity changes and both have
    // to end the visit that came before them.
    const identityChanged = currentValue !== previousValue;
    // PSTUDIO-4240: an entrance that finds the store parked on the id it is
    // entering asks for a new visit of that same id (`restartVersionVisit`).
    // The string did not move, so `identityChanged` is false and the teardown
    // below is skipped — correctly: that method has already closed the previous
    // visit's subscription, and the metadata belongs to a version that has not
    // changed. What the new visit DOES need is everything keyed on
    // `startsNewVisit`: the loader, and the fetch itself.
    const revisitRequested = currentRevisits !== previousRevisits;
    const startsNewVisit = identityChanged || revisitRequested;

    if (identityChanged) {
      tableStore.clearMetadata();
      // PSTUDIO-3953 closed the previous version's subscription, but only on a
      // switch between two non-empty versions, and only after the two early
      // returns below. PSTUDIO-4215: leaving the builder trips both of them —
      // a first-level page clears `botId` BEFORE it clears the id, so the
      // reaction returned on the empty `botId` and never reached any cleanup,
      // and Builder's unmount calls cleanupSolutionVersionSubscription(false),
      // which returns early on exactly the flag it would have to clear (see
      // that method, `force` guard). The waiting flag and its open
      // subscription therefore outlived the visit, and the next entry into the
      // same solution took the `isAlreadyWaiting` return below without ever
      // issuing a fetch: zero nodes, no spinner, no error, and a publish
      // button stuck behind "We're still setting things up"
      // (publish-button.tsx reads the same flag) until a hard reload.
      tableStore.cleanupSolutionVersionSubscription(true);
    }

    if (!tableStore.botId) {
      leaveDataset();
      return;
    }

    // because reactions could occur at any time, ignore any possible data changes until the migrating logic wraps up
    if (tableStore.currentState === 'migrating') {
      return;
    }

    // Was a substring check (`currentValue.includes(botId)`), which assumed the version id
    // embeds the solution. v2 ids are uuids, so it never matched and the table stayed
    // empty. See versionPsIdBelongsToBot.
    const belongsToCurrentBot = versionPsIdBelongsToBot(
      currentValue,
      tableStore.botId
    );

    if (tableStore.botId !== '' && !belongsToCurrentBot) {
      leaveDataset();
    }

    if (currentValue !== '' && belongsToCurrentBot) {
      // PSTUDIO-4215: the `isAlreadyWaiting` early return that used to sit here
      // is GONE rather than narrowed, and the distinction matters to whoever
      // reads this next. It skipped the fetch while the store was waiting on a
      // subscription for what it judged "the same version", counting an EMPTY
      // previous value as the same — which is what made a re-entry render an
      // empty table for good. Nothing is lost by removing it: every entry tears
      // the previous visit down and re-fetches, and the epoch below is what
      // keeps a late response from the previous visit out.
      //
      // PSTUDIO-4240 note for anyone tempted to reinstate it as
      // `currentValue === previousValue`: that WOULD now be reachable. The old
      // argument for calling it dead code — mobx invokes an effect only when
      // the tracked value changed — stopped holding when the tracked value
      // became a tuple, because a revisit moves the counter and leaves the
      // string alone. `startsNewVisit` below is the condition that replaced it.

      // PSTUDIO-4240: the revisit's teardown happens HERE, below the three
      // early returns above (`!botId`, `migrating`, id/botId mismatch) and
      // immediately before a fetch that is unconditional from this point on.
      // Run any earlier — as it was, inside `restartVersionVisit` — a restart
      // swallowed by one of those returns emptied the subscription slot and
      // dropped the live-corpus wait with no request on the way; see that
      // method's docblock for what that costs.
      if (revisitRequested) {
        tableStore.endVisitForRevisit();
      }

      if (startsNewVisit) {
        // PSTUDIO-4240: a new visit is a clean slate for the outcome flag. A
        // visit that SUCCEEDS therefore leaves it false without anyone having
        // to clear it on the success path.
        tableStore.versionVisitFailed = false;

        // Entering a version — a different one, or the SAME one after leaving
        // the builder (PSTUDIO-4215) — shows the loader so the user does not
        // see stale data while the new fetch is in flight.
        //
        // Dropping the rows is gated on the IDENTITY moving, not on the visit
        // restarting, and the difference is a data-loss bug review caught.
        // `clearTable()` ends in `validateAndSync()`, which on an entry (where
        // the XState context already carries project/customer/bot/draft) runs
        // `upload()` and POSTs the CURRENT rows as `bot.csv` to bot-manager —
        // and it does so AFTER `rows.replace([])`. On a PSTUDIO-4240 revisit
        // the rows are the ones the user is looking at and they belong to the
        // version being re-entered, so clearing them here published an EMPTY
        // compiler artefact for that very version (measured on the real store:
        // one POST to `/rest/bot-manager/.../templates` carrying an empty CSV).
        // The old `rows.length > 0` check did not prevent that; it only skipped
        // the call where the table was already empty, which is the PSTUDIO-4215
        // path alone.
        //
        // A revisit therefore keeps its rows under the loader. They are a
        // correct snapshot of this version, and the arriving payload replaces
        // them.
        // PSTUDIO-4450: a change of row source (Hasura ↔ collab document) on
        // the same version drops the rows too — the two need not agree — but
        // quietly: the version did not change, so nothing is uploaded for it.
        const sourceChanged = tableStore.liveEditingSource !== visitSource;
        if (identityChanged && tableStore.rows.length > 0) {
          // Quietly when either side is a collab draft (see `leaveDataset`).
          leaveDataset();
        } else if (sourceChanged && tableStore.rows.length > 0) {
          tableStore.dropDataset();
        }
        visitSource = tableStore.liveEditingSource;
        tableStore.setState('loading');
        // Also clear the previous solution's search filter. searchQuery
        // is persisted on the store (separate from TanStack's React
        // state, which Builder already resets by unmounting the Table on
        // swap). Without this, getVisibleRows() keeps filtering with the
        // old solution's searchResultIds and the new workflow renders
        // empty until the user retypes in the search box.
        //
        // Guarded because this arm now runs on EVERY entry (it used to need a
        // non-empty previous id), and `search('')` writes `searchQuery`
        // unconditionally — an equal-value write still re-renders the table,
        // which is the whole point of the write-minimality rule.
        if (tableStore.searchQuery) {
          tableStore.search('');
        }
      }

      // PSTUDIO-3953: pin the identity this fetch — and every subscription it
      // opens below — is issued for. Each of those callbacks resolves
      // asynchronously and previously re-read `tableStore.solutionVersionId` at
      // apply time, so a response that landed after the store moved to another
      // version was written over the current one.
      // PSTUDIO-4450: the collab feed delivers this visit's rows. The epoch
      // still moves, so a Hasura response from before the switch is dropped.
      if (tableStore.liveEditingSource) {
        versionVisitEpoch += 1;
        // A Hasura visit this one replaced may have raised the waits, and
        // only a Hasura payload lowers them — Publish would stay gated.
        tableStore.isWaitingForBackendData = false;
        tableStore.isWaitingForLiveCorpusIdOnly = false;
        return;
      }

      const fetchedFor = tableStore.solutionVersionId;
      versionVisitEpoch += 1;
      const fetchedDuring = versionVisitEpoch;
      /** The store is still on the version AND still on the visit we fetched for. */
      const belongsToThisVisit = () =>
        tableStore.solutionVersionId === fetchedFor &&
        versionVisitEpoch === fetchedDuring;

      urqlGql
        .getProStudioVersionData({
          solutionVersionId: fetchedFor,
        })
        .toPromise()
        .then((res) => {
          runInAction(async () => {
            // PSTUDIO-3953: the identity check belongs HERE, not only inside
            // processVersionData. The guard in the store protects `rows`; it
            // cannot stop the orchestration around this call — the waiting
            // flags, `needsLiveCorpusId` computed from the payload, and the
            // subscription opened below, whose unsubscribe handle overwrites a
            // SINGLE slot. A late response for a version the store has left
            // would otherwise open a subscription for it and orphan the live
            // one. The store-side guard stays as the second line of defence.
            if (!belongsToThisVisit()) {
              return;
            }

            const versionData = res.data?.tenancy_solution_versions_ps_by_pk;

            console.log(
              '[SolutionVersionSubscription] getProStudioVersionData result:',
              {
                solutionVersionId: tableStore.solutionVersionId,
                hasVersionData: versionData != null,
                nodesCount: versionData?.solution_version_nodes_ps?.length ?? 0,
              }
            );

            if (versionData) {
              const applied = await tableStore.processVersionData(versionData, {
                forSolutionVersionId: fetchedFor,
              });
              // PSTUDIO-3953: the entry guard above is not enough on its own —
              // processVersionData awaits a macrotask before returning, and the
              // reaction runs on that same queue, so the store can move while it
              // is in flight. Everything below belongs to `fetchedFor`: the
              // waiting flags, `needsLiveCorpusId` computed FROM this payload,
              // and a subscription whose handle overwrites the single slot the
              // current version's subscription lives in.
              if (!applied || !belongsToThisVisit()) {
                return;
              }
              // Checked against the loaded rows as well as the server's: a
              // solution created with no knowledge base has its empty-corpus
              // RAG node dropped while loading (`applySkipKnowledgeCorpus`),
              // so there is nothing left to wait for.
              const needsLiveCorpusId =
                hasRowWithEmptyLiveCorpusId(
                  versionData.solution_version_nodes_ps
                ) && hasRowWithEmptyLiveCorpusId(tableStore.rows);
              console.log(
                '[SolutionVersionSubscription] After processVersionData (initial fetch):',
                {
                  needsLiveCorpusId,
                  nodesCount:
                    versionData.solution_version_nodes_ps?.length ?? 0,
                }
              );
              if (needsLiveCorpusId) {
                tableStore.isWaitingForBackendData = true;
                tableStore.isWaitingForLiveCorpusIdOnly = true;
                try {
                  const { subscribe } = urqlGql.solutionVersionSubscription({
                    solutionVersionId: fetchedFor,
                  });
                  const { unsubscribe } = subscribe((subData) => {
                    // PSTUDIO-3953: a callback for a version the store has left
                    // must not reach the block below — it sends a success
                    // message ("you can now publish this solution") about the
                    // version the user LEFT, and calls
                    // cleanupSolutionVersionSubscription, which unsubscribes
                    // whatever the single handle currently points at, i.e. the
                    // CURRENT version's live subscription.
                    if (!belongsToThisVisit()) {
                      return;
                    }
                    console.log(
                      '[SolutionVersionSubscription] (live_corpus_id) subscription callback fired:',
                      {
                        hasError: !!subData.error,
                        hasData: !!subData.data,
                        versionDataIsNull:
                          subData.data?.tenancy_solution_versions_ps_by_pk ==
                          null,
                        nodesCount:
                          subData.data?.tenancy_solution_versions_ps_by_pk
                            ?.solution_version_nodes_ps?.length ?? 0,
                      }
                    );
                    try {
                      if (subData.error) {
                        console.error(
                          'Solution version subscription error:',
                          subData.error
                        );
                        runInAction(() => {
                          tableStore.isWaitingForBackendData = false;
                          tableStore.isWaitingForLiveCorpusIdOnly = false;
                          tableStore.setState(
                            tableStore.isViewer ? 'viewing' : 'editing'
                          );
                        });
                        return;
                      }
                      const subscriptionVersionData =
                        subData.data?.tenancy_solution_versions_ps_by_pk;
                      if (
                        subscriptionVersionData !== null &&
                        subscriptionVersionData !== undefined
                      ) {
                        const stillHasEmptyLiveCorpusId =
                          hasRowWithEmptyLiveCorpusId(
                            subscriptionVersionData.solution_version_nodes_ps
                          );
                        if (stillHasEmptyLiveCorpusId) {
                          console.log(
                            '[SolutionVersionSubscription] (live_corpus_id) data still has empty live_corpus_id, skipping process to avoid re-renders'
                          );
                          return;
                        }
                        console.log(
                          '[SolutionVersionSubscription] (live_corpus_id) live_corpus_id filled, processing once and cleaning up'
                        );
                        runInAction(async () => {
                          const appliedFromSub =
                            await tableStore.processVersionData(
                              subscriptionVersionData,
                              { forSolutionVersionId: fetchedFor }
                            );
                          // PSTUDIO-3953: same macrotask window as the initial
                          // fetch. Announcing success and tearing the
                          // subscription down are only ours to do if the payload
                          // was actually applied.
                          if (!appliedFromSub || !belongsToThisVisit()) {
                            return;
                          }
                          // Project Bobcat: this is the moment a brand-new
                          // Knowledge Agent solution's corpus finishes
                          // provisioning — if it is waiting on its first
                          // auto-publish, fire it now instead of the normal
                          // "you can now publish" toast. See
                          // `pendingAutoPublish`'s doc comment.
                          const pending =
                            tableStore.tryConsumePendingAutoPublish();

                          if (pending && tableStore.draftVersion) {
                            void autoPublishSolutionVersion({
                              draftVersionId: tableStore.draftVersion,
                              botType: pending.botType,
                            });
                          } else {
                            sendUserMessage({
                              type: 'success',
                              text: 'Setup is complete. You can now publish this solution.',
                              autoClose: 3000,
                            });
                          }
                          tableStore.cleanupSolutionVersionSubscription(true);
                        });
                      } else {
                        console.log(
                          '[SolutionVersionSubscription] (live_corpus_id) callback fired but version data is null/undefined – NOT closing subscription, waiting for data'
                        );
                      }
                    } catch (error) {
                      console.error(
                        'Error processing solution version subscription data:',
                        error
                      );
                      runInAction(() => {
                        tableStore.isWaitingForBackendData = false;
                        tableStore.isWaitingForLiveCorpusIdOnly = false;
                        tableStore.setState(
                          tableStore.isViewer ? 'viewing' : 'editing'
                        );
                      });
                    }
                  });
                  tableStore.solutionVersionSubscriptionUnsubscribe =
                    unsubscribe;
                  console.log(
                    '[SolutionVersionSubscription] (live_corpus_id) subscription set up successfully'
                  );
                } catch (error) {
                  console.error(
                    'Failed to initialize solution version subscription (live_corpus_id):',
                    error
                  );
                  runInAction(() => {
                    tableStore.isWaitingForBackendData = false;
                    tableStore.isWaitingForLiveCorpusIdOnly = false;
                    // PSTUDIO-4240: the wait is over but no data arrived, so a
                    // later entrance must still be allowed to retry.
                    tableStore.versionVisitFailed = true;
                  });
                }
              } else {
                // Project Bobcat: no corpus wait needed (e.g. a Supervisor-only
                // solution) — if a brand-new solution is waiting on ITS FIRST
                // publish, this is the earliest moment it is safe to fire.
                // See `pendingAutoPublish`'s doc comment.
                const pending = tableStore.tryConsumePendingAutoPublish();
                if (pending && tableStore.draftVersion) {
                  void autoPublishSolutionVersion({
                    draftVersionId: tableStore.draftVersion,
                    botType: pending.botType,
                  });
                }
              }
            } else {
              // Data doesn't exist yet - subscribe in background, keep table visible
              console.log(
                '[SolutionVersionSubscription] No data found, setting up subscription and waiting for:',
                tableStore.solutionVersionId
              );
              tableStore.isWaitingForBackendData = true;
              tableStore.setState(tableStore.isViewer ? 'viewing' : 'editing');

              try {
                const { subscribe } = urqlGql.solutionVersionSubscription({
                  solutionVersionId: fetchedFor,
                });

                const { unsubscribe } = subscribe((subData) => {
                  // PSTUDIO-3953: same identity check as the live_corpus_id
                  // subscription above — see that comment.
                  if (!belongsToThisVisit()) {
                    return;
                  }
                  console.log(
                    '[SolutionVersionSubscription] (no initial data) subscription callback fired:',
                    {
                      hasError: !!subData.error,
                      hasData: !!subData.data,
                      versionDataIsNull:
                        subData.data?.tenancy_solution_versions_ps_by_pk ==
                        null,
                      nodesCount:
                        subData.data?.tenancy_solution_versions_ps_by_pk
                          ?.solution_version_nodes_ps?.length ?? 0,
                    }
                  );
                  try {
                    if (subData.error) {
                      console.error(
                        'Solution version subscription error:',
                        subData.error
                      );
                      runInAction(() => {
                        tableStore.isWaitingForBackendData = false;
                        // PSTUDIO-4240: see `versionVisitFailed`.
                        tableStore.versionVisitFailed = true;
                        tableStore.setState(
                          tableStore.isViewer ? 'viewing' : 'editing'
                        );
                      });
                      return;
                    }

                    const subscriptionVersionData =
                      subData.data?.tenancy_solution_versions_ps_by_pk;

                    if (
                      subscriptionVersionData !== null &&
                      subscriptionVersionData !== undefined
                    ) {
                      console.log(
                        '[SolutionVersionSubscription] (no initial data) got version data, processing and cleaning up:',
                        {
                          nodesCount:
                            subscriptionVersionData.solution_version_nodes_ps
                              ?.length ?? 0,
                        }
                      );
                      runInAction(async () => {
                        const appliedFromSub =
                          await tableStore.processVersionData(
                            subscriptionVersionData,
                            { forSolutionVersionId: fetchedFor }
                          );
                        // PSTUDIO-3953: see the live_corpus_id callback —
                        // cleanupSolutionVersionSubscription unsubscribes
                        // whatever the single slot points at NOW, which after a
                        // switch is the current version's live subscription.
                        if (!appliedFromSub || !belongsToThisVisit()) {
                          return;
                        }
                        tableStore.cleanupSolutionVersionSubscription(true);
                      });
                    } else {
                      console.log(
                        '[SolutionVersionSubscription] (no initial data) callback fired but version data is null/undefined – NOT closing subscription, waiting for data'
                      );
                    }
                  } catch (error) {
                    console.error(
                      'Error processing solution version subscription data:',
                      error
                    );
                    runInAction(() => {
                      tableStore.isWaitingForBackendData = false;
                      // PSTUDIO-4240: see `versionVisitFailed`.
                      tableStore.versionVisitFailed = true;
                      tableStore.setState(
                        tableStore.isViewer ? 'viewing' : 'editing'
                      );
                    });
                  }
                });

                tableStore.solutionVersionSubscriptionUnsubscribe = unsubscribe;
                console.log(
                  '[SolutionVersionSubscription] (no initial data) Successfully subscribed, waiting for data...'
                );
              } catch (error) {
                console.error(
                  'Failed to initialize solution version subscription:',
                  error
                );
                runInAction(() => {
                  tableStore.isWaitingForBackendData = false;
                  // PSTUDIO-4240: see `versionVisitFailed`.
                  tableStore.versionVisitFailed = true;
                  tableStore.setState(
                    tableStore.isViewer ? 'viewing' : 'editing'
                  );
                });
              }
            }
          });
        })
        .catch((error) => {
          console.error('Error fetching pro studio version data:', error);
          // PSTUDIO-3953: a failed fetch for a version the store has LEFT must
          // not clear the CURRENT version's loading state — that would hide its
          // loader over an empty table and unblock publish prematurely.
          if (!belongsToThisVisit()) {
            return;
          }
          runInAction(() => {
            tableStore.isWaitingForBackendData = false;
            // PSTUDIO-4240: the fetch this visit was made of failed, so the
            // table is empty with the flags down — the state an entrance used
            // to refuse. See `versionVisitFailed`.
            tableStore.versionVisitFailed = true;
            tableStore.setState(tableStore.isViewer ? 'viewing' : 'editing');
          });
        });
    }
  },
  {
    // A tuple data function is allocated fresh on every evaluation, so the
    // default comparer (`Object.is`) would report a change every time and the
    // "tracked value did not move" safeguard would be gone. Nothing depends on
    // that safeguard today — mobx does not invalidate an observer on an
    // equal-valued write, and `set solutionVersionId` returns early on one —
    // but the fetch below is unconditional, so restoring it is what keeps a
    // future equal-valued notification from costing a request.
    equals: comparer.shallow,
    // NOT a debounce, and not removable without reading this first: mobx
    // computes `runSync = !opts.scheduler && !opts.delay`, and `0` is falsy, so
    // the effect runs SYNCHRONOUSLY at the end of the action that moved the
    // tracked value. `pages/builder.tsx` depends on it — its entry effect reads
    // the waiting flags expecting the assignment above it to have already been
    // through this reaction. Any non-zero delay reverses that order and makes a
    // genuine version switch issue two fetches.
    delay: 0,
  }
);
